import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  ArrowLeft, Upload, Check, Clock, Image, ChevronDown, ChevronLeft, ChevronRight, Link2,
  Users, Shirt, MapPin, FileText, Loader2, AlertCircle, Download,
  RefreshCw, Send, ArrowRight, Package, UploadCloud, Play, Lock, Unlock, Video, Eye, List, Film, CheckCircle, DollarSign,
  Plus, Trash2, GripVertical, Edit3, X, Save, Square, Music, Sparkles, RotateCcw, Undo,
  Sun,
  Home, FolderOpen, Layers, Wand2, Music2, Star, Zap, Menu, MessageSquare, Bot, Paperclip, Mic, Settings, Bell,
  LogOut, Shield, ClipboardPaste, XCircle, Beaker, ImagePlus, Box, Camera, ExternalLink, Search, ArrowUpCircle, Copy, Compass, AlertTriangle, Share2, Pencil, Palette
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { VideoCard } from '../components/VideoCard';
import { BrandDNAPanel } from '../components/BrandDNAPanel';
import { LookFeelRefsPanel } from '../components/LookFeelRefsPanel';
import { SceneLookFeel } from '../components/SceneLookFeel';
import { NapkinSketchesPanel } from '../components/NapkinSketchesPanel';
import { DirectorPromptHistory } from '../components/DirectorPromptHistory';
import NapkinWorkbench from '../components/NapkinWorkbench';
import { FaceLockButton, FaceLockAllButton, CastFaceRefControl } from '../components/FaceLock';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { useProjectActivity } from '../hooks/useProjectActivity';
import { EipiAssistantPanel } from '../components/chat/EipiAssistantPanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TimelineEditor from '../components/timeline/TimelineEditor';
import SnapshotHistory from '../components/SnapshotHistory';
import { toast } from '../utils/toast';
import DraggableFrameGrid from '../components/DraggableFrameGrid';
import ActionButton from '../components/ActionButton';
import MjIterationModal from '../components/MjIterationModal';
import GeminiIterationModal from '../components/GeminiIterationModal';
import Stage55Section from '../components/Stage55Section';
import SketchSamModal from '../components/SketchSamModal';
import NavigableLightbox from '../components/NavigableLightbox';
import BrandDeckPanel from '../components/BrandDeckPanel';
import LookFeelPanel from '../components/LookFeelPanel';
import LookbookPanel from '../components/LookbookPanel';
import DeckPicker from '../components/DeckPicker';
import StageProgressionSidebar from '../components/StageProgressionSidebar';
import SceneGeographyPanel from '../components/SceneGeographyPanel';
import MockShootPanel, { MockShootLockBanner } from './MockShootPanel';
import { TemplatizedDraftShotsPanel } from '../components/TemplatizedDraftShotsPanel';
import BulkIteratePanel from '../components/BulkIteratePanel';
import ShareLinkModal from '../components/ShareLinkModal';
import ScriptTemplatizerModal from '../components/ScriptTemplatizerModal';
import ShootingScriptModal from '../components/ShootingScriptModal';
import Igk2pSketchPanel from '../components/igk2p/Igk2pSketchPanel';
import Igk2pStatusBar from '../components/igk2p/Igk2pStatusBar';
import Igk2pBlueprintBoard from '../components/igk2p/Igk2pBlueprintBoard';
import { IGK2P_ON, hasIgk2pRun, genBlueprintsBatch } from '../components/igk2p/igk2pApi';

const NodeWorkflow = lazy(() => import('../components/pipeline/NodeWorkflow'));
const ImageBoardModal = lazy(() => import('../components/ImageBoardModal'));

const BASE_URL = window.__API_BASE;
const resolveUrl = (url) => !url ? '' : url.startsWith('http') ? url : `${BASE_URL}${url}`;
const API = `${BASE_URL}/api`;

// Canonical shot-address formatting. scene_number/shot_number are stored
// inconsistently across projects (bare "1"/"1" vs already-prefixed
// "SC01"/"SH01"), which previously rendered doubled labels ("SCSC01-SHSH02").
// These strip any existing prefix then re-apply the canonical one so addresses
// always render as SC01-SH02 — the platform's shot spine.
const fmtScene = (sc) => {
  const raw = String(sc ?? '').trim();
  if (!raw) return '';
  const n = raw.replace(/^sc/i, '').trim();
  return n ? `SC${n}` : '';
};
const fmtShot = (sh) => {
  const raw = String(sh ?? '').trim();
  if (!raw) return '';
  const n = raw.replace(/^sh/i, '').trim();
  return n ? `SH${n}` : '';
};
const fmtAddr = (sc, sh) => [fmtScene(sc), fmtShot(sh)].filter(Boolean).join('-');

// ═══════════ SHARED ANIMATIC HOOK (used by both SketchCard + KeyframeCard) ═══════════
// Encapsulates Veo 3.1 animatic state, polling, and trigger for a single shot.
// Backend route prefers shot.sketch_url over shot.keyframe_url so the same hook
// works for sketches and photoreal keyframes.
function useAnimatic(shot, projectId) {
  const [animaticProcessing, setAnimaticProcessing] = useState(!!shot.animatic_processing);
  const [animaticUrl, setAnimaticUrl] = useState(shot.animatic_url || null);
  const [animaticError, setAnimaticError] = useState(shot.animatic_error || '');
  const [showAnimatic, setShowAnimatic] = useState(false);
  const [animaticElapsed, setAnimaticElapsed] = useState(0);
  const animaticPollRef = useRef(null);

  // Reset state when shot props change (e.g., after a project reload).
  useEffect(() => {
    setAnimaticUrl(shot.animatic_url || null);
    setAnimaticProcessing(!!shot.animatic_processing);
    setAnimaticError(shot.animatic_error || '');
  }, [shot.animatic_url, shot.animatic_processing, shot.animatic_error]);

  // Poll animatic-status every 8s while processing; clear when done.
  useEffect(() => {
    if (!animaticProcessing) {
      if (animaticPollRef.current) clearInterval(animaticPollRef.current);
      animaticPollRef.current = null;
      setAnimaticElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const tick = async () => {
      setAnimaticElapsed(Math.floor((Date.now() - startedAt) / 1000));
      try {
        const res = await axios.get(`${API}/projects/${projectId}/shots/${shot.id}/animatic-status`);
        const d = res.data || {};
        if (!d.processing) {
          setAnimaticProcessing(false);
          setAnimaticUrl(d.animatic_url || null);
          setAnimaticError(d.error || '');
          if (d.animatic_url) toast.success(`Animatic ready: ${shot.scene_number} · ${shot.shot_number}`);
          else if (d.error) toast.error(`Animatic failed: ${d.error}`);
        }
      } catch (e) { /* keep polling */ }
      if (Date.now() - startedAt > 600_000) {
        setAnimaticProcessing(false);
        setAnimaticError('Animation timed out — try again');
      }
    };
    tick();
    animaticPollRef.current = setInterval(tick, 8000);
    return () => {
      if (animaticPollRef.current) clearInterval(animaticPollRef.current);
      animaticPollRef.current = null;
    };
  }, [animaticProcessing, projectId, shot.id, shot.scene_number, shot.shot_number]);

  const triggerAnimatic = async () => {
    if (animaticProcessing) return;
    try {
      await axios.post(`${API}/projects/${projectId}/shots/${shot.id}/animate`, {
        duration: 6,
        quality: 'fast',
      });
      setAnimaticProcessing(true);
      setAnimaticError('');
      toast.info(`Animating ${shot.scene_number} · ${shot.shot_number} via Veo (~60–120s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to start animatic');
    }
  };

  return {
    animaticProcessing, animaticUrl, animaticError, showAnimatic,
    animaticElapsed, setShowAnimatic, triggerAnimatic,
  };
}



// Sidebar navigation items
const SIDEBAR_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/' },
  { id: 'projects', label: 'Projects', icon: FolderOpen, path: '/' },
  { id: 'script', label: 'Script', icon: FileText, stage: 0 },
  { id: 'preproduction', label: 'Pre-Production', icon: Layers, stage: 2 },
  { id: 'production', label: 'Production', icon: Video, stage: 7 },
  { id: 'postprod', label: 'Post-Production', icon: Wand2, stage: 14 },
  { id: 'video', label: 'Video Studio', icon: Video, stage: 13 },
  { id: 'assets', label: 'Assets', icon: Star, stage: 3 },
  { id: 'audio', label: 'Audio', icon: Music2, stage: 17 },
  { id: 'exports', label: 'Exports', icon: Download, stage: 16 },
];

/* ═══════════ INTERACTIVE PANORAMA VIEWER ═══════════ */
function PanoramaViewer({ src, alt }) {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [position, setPosition] = useState(50); // percentage position (0-100)

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX || e.touches?.[0]?.pageX);
    setScrollLeft(position);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX || e.touches?.[0]?.pageX;
    const walk = (startX - x) * 0.15; // Sensitivity
    let newPos = scrollLeft + walk;
    // Clamp between 0 and 100
    newPos = Math.max(0, Math.min(100, newPos));
    setPosition(newPos);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`relative rounded-lg overflow-hidden border border-ai-border bg-black select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onTouchStart={handleMouseDown}
      onTouchMove={handleMouseMove}
      style={{ touchAction: 'none' }}
      data-testid="panorama-viewer"
    >
      <div 
        className="relative w-[200%] aspect-[4/1] transition-transform duration-75"
        style={{ transform: `translateX(-${position / 2}%)` }}
      >
        <img 
          src={src} 
          alt={alt}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>
      {/* Instruction overlay */}
      <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 rounded text-[8px] text-white font-medium flex items-center gap-1.5">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 12h8m-4 5h4M4 4l4 4m0 0l-4 4m4-4H3m18-4l-4 4m0 0l4 4m-4-4h1" />
        </svg>
        Drag to explore 360°
      </div>
      {/* Position indicator */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-ai-bg-secondary/30 rounded-lg overflow-hidden">
        <div 
          className="h-full bg-ai-bg-secondary rounded-lg transition-all duration-75"
          style={{ width: '30%', marginLeft: `${position * 0.7}%` }}
        />
      </div>
    </div>
  );
}

/* ═══════════ CAST T-POSE GALLERY (Character Turnaround) ═══════════ */
function CastTposeGallery({ candidate, baseUrl, onSelectView }) {
  const tposeViews = candidate.tpose_views || [];
  const [activeIdx, setActiveIdx] = useState(null); // null = mugshot, number = tpose index

  if (tposeViews.length === 0) return null;

  const handleSelect = (idx) => {
    const newIdx = activeIdx === idx ? null : idx;
    setActiveIdx(newIdx);
    if (onSelectView) {
      onSelectView(newIdx !== null ? tposeViews[newIdx]?.url : null, newIdx !== null ? tposeViews[newIdx]?.direction : null);
    }
  };

  const handleBackToMugshot = () => {
    setActiveIdx(null);
    if (onSelectView) onSelectView(null, null);
  };

  return (
    <div className="mt-2" data-testid="cast-tpose-gallery">
      {activeIdx !== null && (
        <button onClick={handleBackToMugshot} className="text-[9px] text-ai-accent hover:underline mb-1.5 block">
          ← Back to Mugshot
        </button>
      )}
      {/* T-Pose Thumbnails */}
      <div>
        <div className="text-[9px] text-white/50 mb-1 font-medium">T-Pose Turnaround (click to preview):</div>
        <div className="grid grid-cols-4 gap-1">
          {tposeViews.map((view, vi) => (
            <button 
              key={vi} 
              onClick={() => handleSelect(vi)}
              className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                activeIdx === vi ? 'border-ai-accent ring-2 ring-ai-accent/20' : 'border-ai-border hover:border-ai-accent/50'
              }`}
            >
              <img src={view.url?.startsWith('http') ? view.url : `${baseUrl}${view.url}`} alt={view.direction} className="w-full aspect-square object-cover" />
              <div className={`absolute bottom-0 left-0 right-0 text-white text-[7px] text-center py-0.5 font-medium ${
                activeIdx === vi ? 'bg-ai-accent' : 'bg-black/60'
              }`}>
                {view.direction}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════ LOCATION GALLERY (Panorama + Wall Views) ═══════════ */
function LocationGallery({ candidate, baseUrl, aspectClass = 'aspect-video', onSelectView }) {
  const [activeView, setActiveView] = useState('panorama'); // 'panorama' or wall index
  const panoramaUrl = candidate.panorama_url || candidate.url;
  const walls = candidate.wall_angles || [];

  const getDisplayImage = () => {
    if (activeView === 'panorama') {
      return { url: panoramaUrl, label: '360° Panorama', isPanorama: true };
    }
    const wall = walls[activeView];
    return wall ? { url: wall.url, label: wall.direction, isPanorama: false } : { url: panoramaUrl, label: '360° Panorama', isPanorama: true };
  };

  const display = getDisplayImage();

  // Notify parent (CastAssetWithTpose) so the main AssetCard above can
  // override its primary image with whichever wall the user picked. When
  // the user goes back to the 360° panorama, clear the override (pass
  // null) so the AssetCard returns to its native asset image — we do NOT
  // want to push a panorama into the AssetCard, since that's exactly the
  // "fisheye main tile" complaint that triggered this fix.
  //
  // Skip the very first effect-fire so we don't trigger a "revert to
  // hero" backend call just because the gallery mounted with the default
  // `activeView === 'panorama'`. Only react to *user-initiated* changes.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!onSelectView) return;
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (activeView === 'panorama') {
      onSelectView(null, null);
    } else {
      const w = walls[activeView];
      if (w) onSelectView(w.url, w.direction);
    }
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-2" data-testid="location-gallery">
      {/* Main Display - Interactive Panorama or Selected Wall */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] text-white/50 font-medium">{display.label}</span>
          {activeView !== 'panorama' && (
            <button 
              onClick={() => setActiveView('panorama')}
              className="text-[8px] text-ai-accent hover:underline"
            >
              ← Back to 360° View
            </button>
          )}
        </div>
        
        {display.isPanorama ? (
          <PanoramaViewer src={display.url?.startsWith('http') ? display.url : `${baseUrl}${display.url}`} alt={display.label} />
        ) : (
          <div className="relative rounded-lg overflow-hidden border border-ai-border bg-black">
            <img 
              src={display.url?.startsWith('http') ? display.url : `${baseUrl}${display.url}`} 
              alt={display.label} 
              className={`w-full ${aspectClass} object-cover`}
            />
          </div>
        )}
      </div>

      {/* Wall Thumbnails */}
      {walls.length > 0 && (
        <div>
          <div className="text-[9px] text-white/50 mb-1 font-medium">Wall Views (click to preview):</div>
          <div className="grid grid-cols-4 gap-1">
            {walls.map((wall, wi) => (
              <button 
                key={wi} 
                onClick={() => setActiveView(wi)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                  activeView === wi ? 'border-ai-accent ring-2 ring-ai-accent/20' : 'border-ai-border hover:border-[#D1D1D6]'
                }`}
              >
                <img src={wall.url?.startsWith('http') ? wall.url : `${baseUrl}${wall.url}`} alt={wall.direction} className="w-full aspect-square object-cover" />
                <div className={`absolute bottom-0 left-0 right-0 text-white text-[7px] text-center py-0.5 font-medium ${
                  activeView === wi ? 'bg-ai-accent' : 'bg-black/60'
                }`}>
                  {wall.direction.replace('Wall ', '').replace(' (', '\n(')}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════ CAST/COSTUME/LOCATION ASSET WITH GALLERIES ═══════════ */
function CastAssetWithTpose({ candidate, label, field, idx, projectId, onUpdate, onImageClick, brandDeck, assetBible }) {
  const [tposeOverride, setTposeOverride] = useState(null); // { url, label }
  // Location wall-views: the picked wall is now persisted to the DB as
  // `candidate.url` (via /asset-set-primary-view) so every downstream
  // pipeline — Insanely Good Sketches/Keyframes, Ref Plan, Veo 3.1 —
  // uses the user's chosen reference. We keep a tiny optimistic-UI
  // override so the AssetCard updates instantly while the POST is in
  // flight; once `onUpdate` refetches we clear the override and the
  // card naturally renders the new persisted `candidate.url`.
  const [optimisticLocation, setOptimisticLocation] = useState(null);
  const activeOverride = tposeOverride || optimisticLocation;

  const handleLocationViewSelect = async (url, dir) => {
    if (!url) {
      // User went "Back to 360° View" — revert to the saved hero.
      try {
        setOptimisticLocation(null);
        await axios.post(`${API}/projects/${projectId}/asset-set-primary-view`, {
          category: 'corner_candidates', index: idx, view_url: '', view_label: ''
        });
        toast.success('Reverted to hero image');
        onUpdate?.();
      } catch (err) {
        const msg = err.response?.data?.detail || '';
        // Silent if "No saved hero to revert to" — that just means the
        // user never picked a wall yet, which is the common case.
        if (!msg.toLowerCase().includes('no saved hero')) toast.error(msg || 'Revert failed');
      }
      return;
    }
    // Optimistic-UI: AssetCard updates instantly.
    setOptimisticLocation({ url, label: dir });
    try {
      await axios.post(`${API}/projects/${projectId}/asset-set-primary-view`, {
        category: 'corner_candidates', index: idx, view_url: url, view_label: dir,
      });
      toast.success(`Primary reference set to: ${dir}`, {
        description: 'All downstream sketches/keyframes will now use this view.',
      });
      onUpdate?.();
      setOptimisticLocation(null);
    } catch (err) {
      // Roll back the optimistic update on failure.
      setOptimisticLocation(null);
      toast.error(err.response?.data?.detail || 'Failed to set primary reference');
    }
  };

  return (
    <div>
      <AssetCard candidate={candidate} label={label}
        category={field} index={idx} projectId={projectId} onUpdate={onUpdate}
        overrideImageUrl={activeOverride?.url} overrideLabel={activeOverride?.label}
        onImageClick={onImageClick} brandDeck={brandDeck} assetBible={assetBible} />
      {/* Cast: T-pose turnaround gallery */}
      {field === 'cast_candidates' && (
        <CastTposeGallery candidate={candidate} baseUrl={BASE_URL}
          onSelectView={(url, dir) => setTposeOverride(url ? { url, label: dir } : null)} />
      )}
      {/* Location: interactive panorama viewer — HIDDEN 2026-06-24 (Rohit:
          unused in current pipeline; re-enable by un-commenting). */}
      {false && field === 'corner_candidates' && (
        <LocationGallery candidate={candidate} baseUrl={BASE_URL}
          onSelectView={handleLocationViewSelect} />
      )}
    </div>
  );
}

/* ═══════════ DOP SPEC PANEL (read-only) ═══════════ */
// Phase B ship-order steps 2+3 (May 16 brief v2): renders structured
// cinematography spec below the Director's Prompt expandable. Read-only in
// this slice — edit / regenerate / upgrade-CTA land in Phase C.
const DOP_CAMERA_HEIGHT_PROSE = {
  hip: 'Hip-level',
  chest: 'Chest-level',
  eye: 'Eye-level',
  high: 'High angle',
  ground: 'Ground-level',
  ceiling: 'Ceiling-level',
  dutch: 'Dutch tilt',
  low_dutch: 'Low Dutch',
};
const DOP_COMPOSITION_PROSE = {
  rule_of_thirds: 'Rule of thirds',
  asymmetric_weight: 'Asymmetric weight',
  frame_within_frame: 'Frame within a frame',
  foreground_occlusion: 'Foreground occlusion',
  negative_space_dominant: 'Negative space dominant',
  centered_symmetric: 'Centered, symmetric',
  leading_lines: 'Leading lines',
  triangular: 'Triangular',
};
const DOP_MOTIVATED_KEY_PROSE = {
  window_warm: 'Warm window (3200–4000K)',
  window_cool: 'Cool daylight window (5600K+)',
  practical_lamp: 'Practical lamp in frame',
  practical_neon: 'Neon signage',
  practical_screen: 'Screen / monitor glow',
  firelight: 'Firelight',
  dashboard_glow: 'Dashboard glow',
  sunlight_hard: 'Hard sunlight',
  sunlight_soft: 'Soft sunlight',
  overcast: 'Flat overcast',
  streetlight_sodium: 'Sodium streetlight',
  streetlight_LED: 'LED streetlight',
  fluorescent_overhead: 'Overhead fluorescent',
  mixed_motivated: 'Mixed motivated',
};
const DOP_SIGNATURE_LABEL = {
  roger_deakins: 'Roger Deakins',
  hoyte_van_hoytema: 'Hoyte van Hoytema',
  bradford_young: 'Bradford Young',
  emmanuel_lubezki: 'Emmanuel Lubezki',
  greig_fraser: 'Greig Fraser',
  darius_khondji: 'Darius Khondji',
  rachel_morrison: 'Rachel Morrison',
};

function isAiDefaultTrifecta(spec) {
  if (!spec) return false;
  return spec.camera_height === 'eye'
    && Number(spec.lens_mm) === 50
    && spec.composition_rule === 'centered_symmetric';
}

function dopFieldsCount(spec) {
  if (!spec) return 0;
  let n = 0;
  if (spec.camera_height) n += 1;
  if (spec.lens_mm) n += 1;
  if (spec.composition_rule) n += 1;
  if (spec.motivated_key_source) n += 1;
  if (Array.isArray(spec.practicals_in_frame) && spec.practicals_in_frame.length) n += 1;
  if (spec.dop_signature_reference) n += 1;
  return n;
}

// ── DOP cascade soft-stale detection ──
// Per 2026-05-17 brief (option 1b/2b/soft-stale): editing DOP spec at Stage 2
// invalidates Stage 4 sketches + Stage 6 keyframes for that shot. We do NOT
// auto-regenerate (preserve manual iterations) — just surface an amber badge
// so the operator knows downstream artifacts predate the DOP edit.
// Comparison is timestamp-based: stale if shot.dop_spec_updated_at is more
// recent than the artifact's own updated_at field.
export function isSketchDopStale(shot) {
  if (!shot) return false;
  const dopAt = shot.dop_spec_updated_at;
  if (!dopAt) return false;
  if (!(shot.sketch_url || shot.sketch_s3_url)) return false;
  const sketchAt = shot.sketch_updated_at;
  // If the sketch was rendered before the DOP edit (or sketch has no
  // timestamp — legacy), assume stale. If sketch was rendered after, fresh.
  if (!sketchAt) return true;
  try { return new Date(dopAt) > new Date(sketchAt); } catch { return false; }
}

export function isKeyframeDopStale(shot) {
  if (!shot) return false;
  const dopAt = shot.dop_spec_updated_at;
  if (!dopAt) return false;
  if (!(shot.keyframe_url || shot.keyframe_s3_url || shot.keyframe_hyperreal_url)) return false;
  const kfAt = shot.keyframe_updated_at || shot.keyframe_hyperreal_accepted_pre_vlm_at;
  if (!kfAt) return true;  // Legacy keyframe with no timestamp — assume stale on DOP edit
  try { return new Date(dopAt) > new Date(kfAt); } catch { return false; }
}

function DopSpecPanel({ shot, dopSpecsProcessing }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [err, setErr] = useState('');
  const spec = shot?.dop_spec || null;
  const filled = dopFieldsCount(spec);
  const trifecta = isAiDefaultTrifecta(spec);
  const status = shot?.dop_spec_status;
  const userEdited = !!spec?.user_edited;

  React.useEffect(() => {
    if (editing) {
      setDraft({
        camera_height: spec?.camera_height || '',
        lens_mm: spec?.lens_mm ?? '',
        composition_rule: spec?.composition_rule || '',
        motivated_key_source: spec?.motivated_key_source || '',
        practicals_in_frame: Array.isArray(spec?.practicals_in_frame) ? spec.practicals_in_frame.join(', ') : '',
        dop_signature_reference: spec?.dop_signature_reference || '',
      });
      setErr('');
    }
  }, [editing, shot?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = async () => {
    setSaving(true);
    setErr('');
    try {
      const payload = {
        ...(draft.camera_height ? { camera_height: draft.camera_height } : {}),
        ...(draft.lens_mm !== '' ? { lens_mm: Number(draft.lens_mm) } : {}),
        ...(draft.composition_rule ? { composition_rule: draft.composition_rule } : {}),
        ...(draft.motivated_key_source ? { motivated_key_source: draft.motivated_key_source } : {}),
        ...(draft.dop_signature_reference ? { dop_signature_reference: draft.dop_signature_reference } : {}),
        practicals_in_frame: (draft.practicals_in_frame || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, 8),
      };
      const res = await axios.patch(`${API}/shots/${shot.id}/dop-spec`, payload);
      if (res.data?.dop_spec && shot && typeof shot === 'object') {
        Object.assign(shot, { dop_spec: res.data.dop_spec });
      }
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    setErr('');
    try {
      const res = await axios.post(`${API}/shots/${shot.id}/dop-spec/regenerate`);
      if (res.data?.dop_spec && shot && typeof shot === 'object') {
        Object.assign(shot, { dop_spec: res.data.dop_spec, dop_spec_status: 'ok' });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="inline-flex items-center gap-1 text-[10px] text-white/50 hover:text-ai-accent transition-colors"
        data-testid={`dop-spec-toggle-${shot.id}`}
      >
        <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span className="font-semibold tracking-wide">DOP SPEC</span>
        {filled === 0 ? (
          status === 'running' ? (
            <span className="text-[9px] text-ai-accent/70 inline-flex items-center gap-1">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />inferring…
            </span>
          ) : status === 'error' ? (
            <span className="text-[9px] text-red-400/70" title={shot?.dop_spec_error || 'failed'}>· failed</span>
          ) : dopSpecsProcessing ? (
            // 2026-05-18 — project-level fallback when the bulk DOP fan-out
            // is in flight but this shot hasn't been picked up yet.
            <span className="text-[9px] text-ai-accent/70 inline-flex items-center gap-1">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />queued…
            </span>
          ) : (
            <span className="text-[9px] text-white/30">· not set</span>
          )
        ) : (
          <span className="text-[9px] text-emerald-400/70">· {filled}/6 fields</span>
        )}
        {userEdited && (
          <span
            className="ml-1 inline-flex items-center text-[9px] text-blue-300 bg-blue-500/15 border border-blue-500/30 rounded px-1 py-[1px]"
            title="Operator-edited. Auto-fire chains won't overwrite."
          >user-edited</span>
        )}
        {spec?.auto_populated && !userEdited && (
          <span
            className="text-[9px] text-amber-300/80"
            title="Inferred from Director's Prompt prose — verify accuracy"
          >· auto</span>
        )}
        {trifecta && (
          <span
            className="ml-1 inline-flex items-center gap-0.5 text-[9px] text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1 py-[1px]"
            title="Eye-level + 50mm + centered_symmetric = the AI default. Confirm intent or change."
            data-testid={`dop-spec-trifecta-warning-${shot.id}`}
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            AI default
          </span>
        )}
      </button>
      {/* Inline trigger removed 2026-02: replaced with footer-level
       *  "Generate all pending" button that fires the entire Stage 2
       *  chain (Director's Prompt → DOP Spec ∥ Asset bindings) in one
       *  click. Per-row buttons were noisy and required N clicks. */}
      {expanded && (
        <div className="mt-1.5 rounded-lg border border-ai-border p-2" style={{ background: 'rgba(11, 15, 25, 0.6)' }}>
          {/* Edit / read-only toggle row */}
          <div className="flex items-center justify-between mb-1.5 -mt-0.5">
            {spec?.rationale && !editing ? (
              <p className="text-[9px] text-white/40 italic flex-1 mr-2" title={spec.rationale}>
                {spec.rationale.length > 90 ? spec.rationale.slice(0, 90) + '…' : spec.rationale}
              </p>
            ) : <span />}
            <div className="flex items-center gap-1 ml-auto">
              {!editing ? (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="px-1.5 py-0.5 rounded text-[9px] text-ai-accent hover:bg-ai-accent/10 border border-ai-accent/30"
                    data-testid={`dop-spec-edit-${shot.id}`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={regenerate}
                    disabled={regenerating}
                    className="px-1.5 py-0.5 rounded text-[9px] text-white/60 hover:bg-white/5 border border-white/10 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`dop-spec-regen-${shot.id}`}
                  >
                    {regenerating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <span>↻</span>}
                    Re-infer
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={saveDraft}
                    disabled={saving}
                    className="px-1.5 py-0.5 rounded text-[9px] text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/30 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`dop-spec-save-${shot.id}`}
                  >
                    {saving && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditing(false); setErr(''); }}
                    className="px-1.5 py-0.5 rounded text-[9px] text-white/50 hover:bg-white/5 border border-white/10"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          {err && (
            <p className="text-[9px] text-red-400 mb-1.5" data-testid={`dop-spec-error-${shot.id}`}>{err}</p>
          )}
          {editing && draft ? (
            <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-[10px]">
              <div>
                <label className="block text-white/40 text-[9px] mb-0.5">Camera height</label>
                <select
                  value={draft.camera_height}
                  onChange={e => setDraft(d => ({ ...d, camera_height: e.target.value }))}
                  className="w-full bg-white/[0.06] text-white rounded px-1 py-0.5 text-[10px] outline-none border border-white/10"
                  data-testid={`dop-camera-height-${shot.id}`}
                >
                  <option value="">— pick —</option>
                  {Object.entries(DOP_CAMERA_HEIGHT_PROSE).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/40 text-[9px] mb-0.5">Lens (mm)</label>
                <select
                  value={String(draft.lens_mm)}
                  onChange={e => setDraft(d => ({ ...d, lens_mm: e.target.value }))}
                  className="w-full bg-white/[0.06] text-white rounded px-1 py-0.5 text-[10px] outline-none border border-white/10"
                  data-testid={`dop-lens-mm-${shot.id}`}
                >
                  <option value="">— pick —</option>
                  {[14, 18, 24, 28, 35, 50, 85, 100, 135, 200].map(n => (
                    <option key={n} value={n}>{n}mm</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/40 text-[9px] mb-0.5">Composition</label>
                <select
                  value={draft.composition_rule}
                  onChange={e => setDraft(d => ({ ...d, composition_rule: e.target.value }))}
                  className="w-full bg-white/[0.06] text-white rounded px-1 py-0.5 text-[10px] outline-none border border-white/10"
                  data-testid={`dop-composition-${shot.id}`}
                >
                  <option value="">— pick —</option>
                  {Object.entries(DOP_COMPOSITION_PROSE).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/40 text-[9px] mb-0.5">Motivated key</label>
                <select
                  value={draft.motivated_key_source}
                  onChange={e => setDraft(d => ({ ...d, motivated_key_source: e.target.value }))}
                  className="w-full bg-white/[0.06] text-white rounded px-1 py-0.5 text-[10px] outline-none border border-white/10"
                  data-testid={`dop-motivated-key-${shot.id}`}
                >
                  <option value="">— pick —</option>
                  {Object.entries(DOP_MOTIVATED_KEY_PROSE).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-white/40 text-[9px] mb-0.5">Practicals in frame (comma-separated, ≤8)</label>
                <input
                  type="text"
                  value={draft.practicals_in_frame}
                  onChange={e => setDraft(d => ({ ...d, practicals_in_frame: e.target.value }))}
                  placeholder="table lamp, neon sign"
                  className="w-full bg-white/[0.06] text-white rounded px-1.5 py-0.5 text-[10px] outline-none border border-white/10"
                  data-testid={`dop-practicals-${shot.id}`}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-white/40 text-[9px] mb-0.5">DOP signature reference</label>
                <select
                  value={draft.dop_signature_reference}
                  onChange={e => setDraft(d => ({ ...d, dop_signature_reference: e.target.value }))}
                  className="w-full bg-white/[0.06] text-white rounded px-1 py-0.5 text-[10px] outline-none border border-white/10"
                  data-testid={`dop-signature-${shot.id}`}
                >
                  <option value="">— none —</option>
                  {Object.entries(DOP_SIGNATURE_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            filled === 0 ? (
              <p className="text-[10px] text-white/40 italic" data-testid={`dop-spec-empty-${shot.id}`}>
                No structured DOP fields set yet. Click <span className="text-ai-accent">Re-infer</span> to fire the Director AI, or <span className="text-ai-accent">Edit</span> to set manually.
              </p>
            ) : (
              <ul className="space-y-1 text-[10px] text-white/75 font-mono" data-testid={`dop-spec-list-${shot.id}`}>
                {spec.camera_height && (
                  <li><span className="text-white/40">Camera height:</span> <span className="text-white/90">{DOP_CAMERA_HEIGHT_PROSE[spec.camera_height] || spec.camera_height}</span></li>
                )}
                {spec.lens_mm && (
                  <li><span className="text-white/40">Lens:</span> <span className="text-white/90">{spec.lens_mm}mm</span></li>
                )}
                {spec.composition_rule && (
                  <li><span className="text-white/40">Composition:</span> <span className="text-white/90">{DOP_COMPOSITION_PROSE[spec.composition_rule] || spec.composition_rule.replace(/_/g, ' ')}</span></li>
                )}
                {spec.motivated_key_source && (
                  <li><span className="text-white/40">Motivated key:</span> <span className="text-white/90">{DOP_MOTIVATED_KEY_PROSE[spec.motivated_key_source] || spec.motivated_key_source.replace(/_/g, ' ')}</span></li>
                )}
                {Array.isArray(spec.practicals_in_frame) && spec.practicals_in_frame.length > 0 && (
                  <li><span className="text-white/40">Practicals:</span> <span className="text-white/90">{spec.practicals_in_frame.join(', ')}</span></li>
                )}
                {spec.dop_signature_reference && (
                  <li><span className="text-white/40">DOP signature:</span> <span className="text-white/90">{DOP_SIGNATURE_LABEL[spec.dop_signature_reference] || spec.dop_signature_reference.replace(/_/g, ' ')}</span></li>
                )}
              </ul>
            )
          )}
          {spec?.source_pass && !editing && (
            <p className="mt-1.5 pt-1.5 border-t border-white/5 text-[9px] text-white/30 font-mono">
              source: {spec.source_pass}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════ BULK ASSET BIBLE BINDINGS CTA ═══════════ */
// Project-level Asset Bible audit banner (May 16 brief — Steps 9-10 close-out).
// Replaces the legacy single-CTA card. Surfaces THREE call-to-actions in one
// place: bind unbound shots, regenerate stale keyframes, audit the continuity
// map. Polls /audit so counts stay accurate after batch work.
function AssetBibleSpecAuditBanner({ shots, projectId, assetBible, project, onComplete }) {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState({ rebuild: false, hydrate: false, dopRebuild: false, rebuildDelta: false });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [showMap, setShowMap] = useState(false);
  // Add-Shot Phase 1 (2026-05-25) — "Rebuild new only (N)" delta button.
  // `dirtyInfo` shape: {dirty_count, unresolved_entity_shots,
  // bindings_rebuild_completed_at, bindings_rebuild_status, delta_button_available}
  const [dirtyInfo, setDirtyInfo] = useState(null);

  const hasBible = !!(assetBible && (
    (assetBible.cast_candidates || []).length ||
    (assetBible.costume_candidates || []).length ||
    (assetBible.corner_candidates || []).length
  ));

  // ── Sparse-bible auto-hydrate (data-bug fix Rohit caught on iphone18) ──
  // When derived_ccc is confirmed but the asset bible has gaps (image gen
  // hasn't finished), fire one hydrate pass to materialize stub cards so
  // every dropdown has the full picker list. Idempotent server-side.
  // We only act when there's a real delta — never on a project that
  // already has a complete bible or no derived_ccc.
  useEffect(() => {
    if (!projectId || !project?.derived_ccc) return;
    // Gate hydrate behind cast tier confirmation. Per Rohit (2026-05-18):
    // no stub cards should exist on the backend until the user explicitly
    // confirms Primary vs Secondary tiering. Hydrate is the path that
    // creates those stubs — keep it dormant until confirmation flips.
    if (!project?.cast_confirmed) return;
    const dccc = project.derived_ccc;
    const derivedCounts = {
      cast: (dccc.cast || []).length,
      costume: (dccc.costumes || []).length,
      corner: (dccc.locations || []).length,
    };
    const bibleCounts = {
      cast: (assetBible?.cast_candidates || []).length,
      costume: (assetBible?.costume_candidates || []).length,
      corner: (assetBible?.corner_candidates || []).length,
    };
    const hasGap = (
      derivedCounts.cast > bibleCounts.cast ||
      derivedCounts.costume > bibleCounts.costume ||
      derivedCounts.corner > bibleCounts.corner
    );
    const hasAnyDerived = derivedCounts.cast + derivedCounts.costume + derivedCounts.corner > 0;
    if (!hasGap || !hasAnyDerived || busy.hydrate) return;
    let cancelled = false;
    (async () => {
      setBusy(b => ({ ...b, hydrate: true }));
      try {
        const res = await axios.post(`${API}/projects/${projectId}/asset-bible/hydrate-from-derived-ccc`);
        if (cancelled) return;
        const added = res.data?.added || {};
        if ((added.cast || 0) + (added.costume || 0) + (added.corner || 0) > 0) {
          if (typeof onComplete === 'function') onComplete();
        }
      } catch (e) {
        // silent — banner still surfaces unbound count
        // eslint-disable-next-line no-console
        console.warn('[AssetBibleSpec] hydrate-from-derived-ccc skipped:', e?.response?.data || e?.message);
      } finally {
        if (!cancelled) setBusy(b => ({ ...b, hydrate: false }));
      }
    })();
    return () => { cancelled = true; };
    // intentionally exclude `busy.hydrate` from deps so we don't loop after toggling it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project?.derived_ccc, assetBible?.cast_candidates?.length, assetBible?.costume_candidates?.length, assetBible?.corner_candidates?.length]);

  // Load audit + poll while batch work is in-flight
  useEffect(() => {
    if (!projectId || !hasBible) return;
    let cancelled = false;
    const fetchAudit = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/asset-bible-spec/audit`);
        if (!cancelled) setAudit(res.data);
      } catch {/* surface only on action failure */}
    };
    fetchAudit();
    const t = setInterval(fetchAudit, busy.rebuild ? 4000 : 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId, hasBible, busy.rebuild, shots?.length]);

  // Add-Shot Phase 1 — poll dirty-count to drive "Rebuild new only (N)" button
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const fetchDirty = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/shots/dirty-count`);
        if (!cancelled) setDirtyInfo(res.data);
      } catch {/* silent — button just stays hidden */}
    };
    fetchDirty();
    const t = setInterval(fetchDirty, busy.rebuildDelta || busy.rebuild ? 4000 : 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId, busy.rebuildDelta, busy.rebuild, shots?.length]);

  // Poll the rebuild-status endpoint while a rebuild is in flight
  useEffect(() => {
    if (!projectId || !busy.rebuild) return;
    let cancelled = false;
    const started = Date.now();
    const handle = setInterval(async () => {
      if (Date.now() - started > 180000) {
        clearInterval(handle);
        if (!cancelled) {
          setBusy(b => ({ ...b, rebuild: false }));
          setErr('Rebuild still running — refresh in a minute to see final counts');
        }
        return;
      }
      try {
        const st = await axios.get(`${API}/projects/${projectId}/asset-bible-spec/rebuild-status`);
        if (cancelled) return;
        if (st.data?.status === 'completed' || st.data?.status === 'failed') {
          clearInterval(handle);
          setBusy(b => ({ ...b, rebuild: false }));
          if (st.data.status === 'completed') {
            setResult({ kind: 'rebuild', ...st.data });
          } else {
            setErr(st.data?.error || st.data?.summary || 'Rebuild failed');
          }
          if (typeof onComplete === 'function') onComplete();
        }
      } catch {/* keep polling on transient errors */}
    }, 4000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [projectId, busy.rebuild, onComplete]);

  // Auto-clear local DOP rebuild spinner when the project-level flag clears.
  // The parent's shotsPollRef polls `dop_specs_processing`; once it flips
  // false we drop the spinner regardless of who started the rebuild.
  useEffect(() => {
    if (busy.dopRebuild && project && project.dop_specs_processing === false) {
      setBusy(b => ({ ...b, dopRebuild: false }));
      if (typeof onComplete === 'function') onComplete();
    }
  }, [project?.dop_specs_processing, busy.dopRebuild, onComplete]);

  if (!projectId || !hasBible) return null;

  const unbound = audit?.unbound_count ?? (shots || []).filter(s => !s?.asset_bible_spec).length;
  const stale = audit?.stale_count ?? (shots || []).filter(s => s?.keyframe_state === 'stale' && (s?.keyframe_url || s?.keyframe_s3_url)).length;
  // 2026-05-17 — Rohit ask: surface a sibling "Rebuild DOP Specs" button next
  // to "Rebuild bindings" for the case where the auto-fire fan-out failed on
  // some shots. Count shots with missing/errored dop_spec; same banner shape.
  const dopMissing = (shots || []).filter(s => !s?.dop_spec).length;
  const dopErrored = (shots || []).filter(s => s?.dop_spec_status === 'error').length;
  const dopProcessing = !!project?.dop_specs_processing;

  // Stage-2 derived-chain staleness (2026-07-10) — flagged when a shot's
  // canonical keyframe_prompt changes (napkin approve / manual edit). Loud UI,
  // human decides when to rebuild. Prefer server audit counts.
  const dopStale = audit?.dop_spec_stale_count ?? (shots || []).filter(s => s?.dop_spec_stale).length;
  const bindingsStale = audit?.bindings_stale_count ?? (shots || []).filter(s => s?.bindings_stale).length;

  // 2026-05-18 — count duplicate-named cards so the banner surfaces even when
  // there are no unbound shots / no DOP issues, so the user can run dedupe.
  const _normForDup = (s) => (s || '').trim().toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+/g, ' ');
  const _countDups = (arr) => {
    const counts = {};
    (arr || []).forEach(c => {
      const n = _normForDup(c?.name);
      if (n) counts[n] = (counts[n] || 0) + 1;
    });
    return Object.values(counts).reduce((acc, n) => acc + (n > 1 ? n - 1 : 0), 0);
  };
  const dupCount = _countDups(assetBible?.cast_candidates) + _countDups(assetBible?.costume_candidates) + _countDups(assetBible?.corner_candidates);

  // Hide entirely when nothing to surface AND no in-flight rebuild
  if (!unbound && !stale && !busy.rebuild && !busy.dopRebuild && !dopMissing && !dopErrored && !dopProcessing && !dupCount && !result && !dopStale && !bindingsStale) return null;

  const runRebuild = async () => {
    setBusy(b => ({ ...b, rebuild: true })); setErr(''); setResult(null);
    try {
      await axios.post(`${API}/projects/${projectId}/asset-bible-spec/rebuild`);
      // polling effect takes over from here
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Rebuild failed to start');
      setBusy(b => ({ ...b, rebuild: false }));
    }
  };

  // Add-Shot Phase 1 — delta rebuild on dirty shots only
  const runRebuildDelta = async () => {
    setBusy(b => ({ ...b, rebuildDelta: true })); setErr(''); setResult(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/asset-bible-spec/rebuild-delta`);
      setResult({ kind: 'rebuild_delta_started', summary: res?.data?.message || 'Delta rebuild started' });
      // Status polls via project bindings_rebuild_status; refresh dirty-count
      setTimeout(() => {
        setBusy(b => ({ ...b, rebuildDelta: false }));
        if (typeof onComplete === 'function') onComplete();
      }, 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'UNRESOLVED_ENTITIES') {
        setErr(`${detail.shot_ids?.length || 0} shots have new entities — re-confirm cast tiers first.`);
      } else if (typeof detail === 'object' && detail?.code === 'NO_BASELINE_REBUILD') {
        setErr('Run a full Rebuild bindings once before delta-only is available.');
      } else {
        setErr(typeof detail === 'string' ? detail : (e.message || 'Delta rebuild failed'));
      }
      setBusy(b => ({ ...b, rebuildDelta: false }));
    }
  };

  // 2026-05-17 — bulk DOP spec rebuild (recovery path for missing auto-fires)
  const runDopRebuild = async () => {
    setBusy(b => ({ ...b, dopRebuild: true })); setErr(''); setResult(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/dop-specs/rebuild`);
      // The shotsPollRef in the parent component already polls
      // `dop_specs_processing`; refresh project data once to pick up the flag
      // and let the parent's polling clear `busy.dopRebuild` via the effect.
      if (typeof onComplete === 'function') setTimeout(onComplete, 1000);
      // Auto-clear local busy when project flag flips false (handled by effect below)
      // Surface the response message as a transient result so operator gets confirmation.
      setResult({ kind: 'dop_rebuild_started', summary: res?.data?.message || 'DOP specs rebuild started' });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'DOP rebuild failed to start');
      setBusy(b => ({ ...b, dopRebuild: false }));
    }
  };

  // 2026-05-22 — full Stage 2 regen cascade. Wipes director prompts +
  // DOP spec + asset_bible_spec, fires `_chain_director_then_dop`. Used
  // after script/cast edits or after backend prompt-format fixes (e.g.
  // the M-1 hotfix that shipped 2026-05-22) so existing projects can
  // re-derive cleanly. Confirms with the operator before nuking caches —
  // the cost is ~30-90s of LLM calls + 27¢ in API spend per ~30 shots.
  const runRegenDirectorChain = async () => {
    if (!window.confirm(
      'Regenerate director prompts for ALL shots?\n\n' +
      'This will:\n' +
      '  • Wipe & regenerate every shot\'s Director\'s Prompt\n' +
      '  • Re-fire DOP spec inference\n' +
      '  • Re-parse Asset Bible bindings (assets from parsing)\n\n' +
      'Shot descriptions are preserved. ~30-90s for ~30 shots.\n' +
      'Hand-edited prompts will be lost.'
    )) return;
    setBusy(b => ({ ...b, regenChain: true })); setErr(''); setResult(null);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/director-prompts/regenerate-chain`
      );
      const wiped = res?.data?.wiped_shots || 0;
      setResult({
        kind: 'regen_chain_started',
        summary: `Cleared ${wiped} shots. Director prompts regenerating; ` +
                 `DOP + bindings will auto-fire once they complete.`,
      });
      // Ping parent to refresh polling state
      if (typeof onComplete === 'function') setTimeout(onComplete, 1500);
      // Local busy auto-clears when parent project flag (director_prompts_processing)
      // flips false via the polling effect; release immediately so the
      // button stays clickable if the operator wants to bail/retry.
      setTimeout(() => setBusy(b => ({ ...b, regenChain: false })), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Regen chain failed to start');
      setBusy(b => ({ ...b, regenChain: false }));
    }
  };

  // 2026-05-18 — one-shot dedupe for the "Maya Alvarez × 2" stub-duplicate bug
  const runDedupe = async () => {
    setBusy(b => ({ ...b, dedupe: true })); setErr(''); setResult(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/asset-bible/dedupe-stubs?apply=true`);
      const merged = res?.data?.merged || { cast: 0, costume: 0, corner: 0 };
      const total = (merged.cast || 0) + (merged.costume || 0) + (merged.corner || 0);
      setResult({
        kind: 'dedupe_done',
        summary: total
          ? `Merged ${merged.cast || 0} cast · ${merged.costume || 0} costume · ${merged.corner || 0} corner duplicate(s).`
          : 'No duplicates found — Asset Bible is clean.',
      });
      if (typeof onComplete === 'function') setTimeout(onComplete, 600);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Dedupe failed to run');
    } finally {
      setBusy(b => ({ ...b, dedupe: false }));
    }
  };

  return (
    <>
      <div
        className="mb-3 rounded-lg border border-ai-accent/30 bg-ai-accent/5 px-3 py-2"
        data-testid="asset-bible-spec-audit-banner"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2 min-w-0">
            <Wand2 className="w-4 h-4 text-ai-accent mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[12px] text-white/90 font-semibold">
                Asset Bible bindings
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                {busy.rebuild && (
                  <span className="text-ai-accent">
                    Rebuilding bindings… Director AI is mapping wardrobe arc + binding {audit?.total_shots || shots?.length || '?'} shot(s)
                  </span>
                )}
                {!busy.rebuild && busy.dopRebuild && (
                  <span className="text-ai-accent">
                    Rebuilding DOP specs… Director AI filling missing specs (~$0.02 per shot · operator-edited preserved)
                  </span>
                )}
                {!busy.rebuild && !busy.dopRebuild && unbound > 0 && <span data-testid="audit-unbound-count">{unbound} unbound</span>}
                {!busy.rebuild && !busy.dopRebuild && unbound > 0 && stale > 0 && <span className="text-white/30"> · </span>}
                {!busy.rebuild && !busy.dopRebuild && stale > 0 && <span className="text-amber-300/80" data-testid="audit-stale-count">{stale} stale keyframe{stale === 1 ? '' : 's'}</span>}
                {!busy.rebuild && !busy.dopRebuild && (unbound > 0 || stale > 0) && (dopMissing > 0 || dopErrored > 0) && <span className="text-white/30"> · </span>}
                {!busy.rebuild && !busy.dopRebuild && dopMissing > 0 && <span className="text-amber-300/80" data-testid="audit-dop-missing-count">{dopMissing} DOP spec{dopMissing === 1 ? '' : 's'} missing</span>}
                {!busy.rebuild && !busy.dopRebuild && dopMissing > 0 && dopErrored > 0 && <span className="text-white/30"> · </span>}
                {!busy.rebuild && !busy.dopRebuild && dopErrored > 0 && <span className="text-red-400/80" data-testid="audit-dop-errored-count">{dopErrored} DOP spec error{dopErrored === 1 ? '' : 's'}</span>}
                {!busy.rebuild && !busy.dopRebuild && (dopMissing > 0 || dopErrored > 0) && (dopStale > 0 || bindingsStale > 0) && <span className="text-white/30"> · </span>}
                {!busy.rebuild && !busy.dopRebuild && dopStale > 0 && <span className="text-orange-300 font-semibold" data-testid="audit-dop-stale-count">⚠ {dopStale} DOP spec{dopStale === 1 ? '' : 's'} stale (prompt changed)</span>}
                {!busy.rebuild && !busy.dopRebuild && dopStale > 0 && bindingsStale > 0 && <span className="text-white/30"> · </span>}
                {!busy.rebuild && !busy.dopRebuild && bindingsStale > 0 && <span className="text-orange-300 font-semibold" data-testid="audit-bindings-stale-count">⚠ {bindingsStale} binding set{bindingsStale === 1 ? '' : 's'} stale (prompt changed)</span>}
                {!busy.rebuild && !busy.dopRebuild && unbound === 0 && stale === 0 && dopMissing === 0 && dopErrored === 0 && dopStale === 0 && bindingsStale === 0 && (
                  <span className="text-emerald-400/70">All shots bound · DOP specs complete · keyframes fresh</span>
                )}
              </div>
              {result?.kind === 'rebuild' && (
                <div className="text-[10px] text-emerald-400/80 mt-0.5" data-testid="audit-rebuild-result">
                  ✓ {result.summary || `Rebuilt · ${result.map_hits} via map · ${result.opus_hits} via Director AI${result.failures ? ` · ${result.failures} failed` : ''}`}
                </div>
              )}
              {err && (
                <div className="text-[10px] text-red-400 mt-0.5" data-testid="audit-error">{err}</div>
              )}
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 w-full sm:w-auto sm:shrink-0 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded"
            data-testid="audit-action-row"
          >
            <button
              type="button"
              onClick={() => setShowMap(true)}
              disabled={busy.rebuild}
              className="px-2 py-1 rounded text-[10px] font-medium border border-white/15 text-white/70 hover:text-white hover:border-ai-accent/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 whitespace-nowrap"
              data-testid="audit-open-map-button"
              title="Edit the scene-by-scene wardrobe continuity map"
            >
              Audit map
            </button>
            {/* Add-Shot Phase 1 (2026-05-25) — "Rebuild new only (N)" delta button */}
            {dirtyInfo?.delta_button_available && (dirtyInfo?.dirty_count || 0) > 0 && (
              <button
                type="button"
                onClick={runRebuildDelta}
                disabled={busy.rebuild || busy.rebuildDelta || (dirtyInfo?.unresolved_entity_shots || 0) > 0}
                className="px-2.5 py-1 rounded text-[11px] font-semibold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 whitespace-nowrap"
                data-testid="audit-rebuild-new-only-button"
                title={
                  (dirtyInfo?.unresolved_entity_shots || 0) > 0
                    ? 'Re-confirm new cast tiers before delta rebuild'
                    : `Delta rebuild — binds only the ${dirtyInfo.dirty_count} dirty/new shot(s). Cheap, fast, preserves all other locked bindings.`
                }
              >
                {busy.rebuildDelta ? (
                  <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Delta rebuilding…</span>
                ) : `Rebuild new only (${dirtyInfo.dirty_count})`}
              </button>
            )}
            <button
              type="button"
              onClick={runDopRebuild}
              disabled={busy.rebuild || busy.dopRebuild || (dopMissing === 0 && dopErrored === 0 && dopStale === 0 && !dopProcessing)}
              className="px-2.5 py-1 rounded text-[11px] font-semibold border border-ai-accent/40 text-ai-accent hover:bg-ai-accent/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 whitespace-nowrap"
              data-testid="audit-rebuild-dop-button"
              title="Re-fires Director AI across every shot whose DOP spec is missing or errored. Operator-edited specs (user_edited=true) are preserved. ~$0.02/shot · 30-90s total."
            >
              {busy.dopRebuild || dopProcessing ? (
                <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Rebuilding DOP…</span>
              ) : 'Rebuild DOP specs'}
            </button>
            <button
              type="button"
              onClick={runRegenDirectorChain}
              disabled={busy.regenChain || busy.rebuild || busy.dopRebuild}
              className="px-2.5 py-1 rounded text-[11px] font-semibold border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 whitespace-nowrap"
              data-testid="audit-regen-director-chain-button"
              title="FULL Stage 2 regen: wipes & regenerates every shot's Director's Prompt, re-fires DOP spec inference, and re-parses Asset Bible bindings. Shot descriptions preserved. Hand-edited prompts lost. ~30-90s total."
            >
              {busy.regenChain ? (
                <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Regenerating…</span>
              ) : 'Regen Director + DOP + Bindings'}
            </button>
            <button
              type="button"
              onClick={runDedupe}
              disabled={busy.dedupe}
              className="px-2.5 py-1 rounded text-[11px] font-semibold bg-white/[0.06] text-white/80 hover:bg-white/[0.10] border border-white/15 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 whitespace-nowrap"
              data-testid="audit-dedupe-button"
              title="Merge duplicate cast/costume/corner cards by name. Use when an Asset Bible card appears twice (e.g., 'Maya Alvarez' stub next to a rendered 'Maya Alvarez')."
            >
              {busy.dedupe ? (
                <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Merging…</span>
              ) : 'Merge duplicates'}
            </button>
            <button
              type="button"
              onClick={runRebuild}
              disabled={busy.rebuild}
              className="px-2.5 py-1 rounded text-[11px] font-semibold bg-ai-accent text-white hover:bg-ai-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 whitespace-nowrap"
              data-testid="audit-rebuild-button"
              title="Re-derives the continuity map and re-binds every shot. The recovery path for wardrobe changes, Asset Bible regeneration, or post-backfill propagation."
            >
              {busy.rebuild ? (
                <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Rebuilding…</span>
              ) : 'Rebuild bindings'}
            </button>
          </div>
        </div>
      </div>
      {showMap && (
        <ContinuityMapAuditModal
          projectId={projectId}
          assetBible={assetBible}
          onClose={() => setShowMap(false)}
          onSaved={() => { if (typeof onComplete === 'function') onComplete(); }}
        />
      )}
    </>
  );
}

// Continuity Map Audit modal (Step 0.5b — editable scene-by-scene grid).
// Directors think globally about wardrobe arc; this surface lets them fix
// Opus's misfires once at the map level vs whack-a-mole across N shot cards.
function ContinuityMapAuditModal({ projectId, assetBible, onClose, onSaved }) {
  const [mapData, setMapData] = useState({});
  const [meta, setMeta] = useState({ status: null, source: null, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [cascade, setCascade] = useState(true);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);

  const cast = assetBible?.cast_candidates || [];
  const costumes = assetBible?.costume_candidates || [];

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr('');
      try {
        const res = await axios.get(`${API}/projects/${projectId}/costume-continuity-map`);
        if (!cancelled) {
          setMapData(res.data.map || {});
          setMeta({
            status: res.data.status,
            source: res.data.source,
            updated_at: res.data.updated_at,
          });
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || 'Failed to load map');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const sceneKeys = Object.keys(mapData).sort((a, b) => {
    const na = parseInt(a, 10), nb = parseInt(b, 10);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });

  const setBinding = (sceneN, castId, costumeId) => {
    setMapData(prev => {
      const next = { ...prev };
      const scene = { ...(next[sceneN] || {}) };
      if (costumeId) {
        scene[castId] = costumeId;
      } else {
        delete scene[castId];
      }
      if (Object.keys(scene).length === 0) {
        delete next[sceneN];
      } else {
        next[sceneN] = scene;
      }
      return next;
    });
    setDirty(true);
  };

  const addCastToScene = (sceneN, castId) => {
    setMapData(prev => ({
      ...prev,
      [sceneN]: { ...(prev[sceneN] || {}), [castId]: '' },
    }));
    setDirty(true);
  };

  const removeCastFromScene = (sceneN, castId) => {
    setMapData(prev => {
      const next = { ...prev };
      const scene = { ...(next[sceneN] || {}) };
      delete scene[castId];
      if (Object.keys(scene).length === 0) delete next[sceneN]; else next[sceneN] = scene;
      return next;
    });
    setDirty(true);
  };

  const rebuild = async () => {
    setRebuilding(true); setErr('');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/costume-continuity-map/build`);
      setMapData(res.data.map || {});
      setMeta({ status: res.data.status, source: res.data.source, updated_at: new Date().toISOString() });
      setDirty(false);
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message || 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      // Drop empty-costume entries before save — server treats "" as unset
      const clean = {};
      Object.entries(mapData).forEach(([sk, scene]) => {
        const filtered = {};
        Object.entries(scene).forEach(([cid, cost]) => { if (cost) filtered[cid] = cost; });
        if (Object.keys(filtered).length) clean[sk] = filtered;
      });
      const res = await axios.patch(`${API}/projects/${projectId}/costume-continuity-map`, {
        map: clean,
        cascade_relock: cascade,
      });
      setMeta(m => ({ ...m, status: 'user_edited', source: 'user_edit', updated_at: new Date().toISOString() }));
      setDirty(false);
      if (typeof onSaved === 'function') onSaved();
      if (res.data.cascaded_shots) {
        // brief inline success so user knows the cascade fired
        setErr(`✓ Saved · relocked ${res.data.cascaded_shots} auto-bound shot(s)`);
      } else {
        setErr('✓ Saved');
      }
      setTimeout(() => setErr(''), 2500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'INVALID_MAP_IDS') {
        setErr(`Invalid IDs in map: ${detail.invalid?.length || 0} reference(s) point at entities not in this Asset Bible.`);
      } else {
        setErr(detail?.message || detail || e.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="continuity-map-audit-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] rounded-xl border border-ai-border bg-ai-bg-secondary shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-ai-border">
          <div>
            <h2 className="text-[14px] font-semibold text-white">Continuity map audit</h2>
            <p className="text-[10px] text-white/50 mt-0.5">
              Edit the scene-by-scene wardrobe map · changes cascade to every auto-bound shot
              {meta.source && <span className="text-white/30"> · {meta.source}</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors"
            data-testid="continuity-map-audit-close"
          ><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="text-[11px] text-white/50 flex items-center gap-2 py-6 justify-center">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading map…
            </div>
          ) : sceneKeys.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[11px] text-white/50 italic">
                No continuity map yet. Build it from the current script + asset bible.
              </p>
              <button
                type="button"
                onClick={rebuild}
                disabled={rebuilding}
                className="mt-3 px-3 py-1.5 rounded text-[11px] font-semibold bg-ai-accent text-white hover:bg-ai-accent/90 disabled:opacity-50 transition-colors"
                data-testid="continuity-map-audit-build-empty"
              >
                {rebuilding ? 'Building…' : 'Build map (Director AI)'}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {sceneKeys.map(sk => {
                const scene = mapData[sk] || {};
                const boundCastIds = Object.keys(scene);
                const unboundCast = cast.filter(c => c.id && !boundCastIds.includes(c.id));
                return (
                  <div
                    key={sk}
                    className="rounded-lg border border-ai-border bg-ai-bg/40 px-3 py-2"
                    data-testid={`continuity-map-audit-scene-${sk}`}
                  >
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="text-[11px] font-mono text-ai-accent">Scene {sk}</span>
                      <span className="text-[9px] text-white/30">
                        {boundCastIds.length} cast bound
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[10px]">
                      {boundCastIds.map(cid => {
                        const c = cast.find(x => x.id === cid);
                        const cName = c?.display_slug || c?.slug || c?.name || cid;
                        const allowed = costumes.filter(x => !x.for_cast || x.for_cast === cid);
                        const cost = costumes.find(x => x.id === scene[cid]);
                        const costName = cost?.display_slug || cost?.slug || cost?.name || '';
                        const hasCostume = !!scene[cid];
                        return (
                          <span key={cid} className="inline-flex items-center gap-1 group">
                            <span className="text-white/90 font-medium">{cName}</span>
                            <span className="text-white/40 italic">in</span>
                            <span className={`inline-flex items-center rounded ${hasCostume ? 'bg-ai-accent/10 border border-ai-accent/30' : 'border border-dashed border-white/20'} hover:border-ai-accent/50 transition-colors`}>
                              <select
                                value={scene[cid] || ''}
                                onChange={e => setBinding(sk, cid, e.target.value)}
                                className={`bg-transparent ${hasCostume ? 'text-white/90' : 'text-white/40'} px-1.5 py-[2px] outline-none cursor-pointer hover:bg-ai-accent/10 max-w-[200px] appearance-none border-0`}
                                style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                                title={costName || 'pick costume'}
                                data-testid={`continuity-map-audit-costume-${sk}-${cid}`}
                              >
                                <option value="" className="bg-ai-bg-secondary">pick costume…</option>
                                {allowed.map(x => {
                                  const lbl = x.display_slug || x.slug || x.name || x.id;
                                  return <option key={x.id} value={x.id} className="bg-ai-bg-secondary">{lbl.slice(0, 60)}</option>;
                                })}
                              </select>
                              <ChevronRight className="w-2.5 h-2.5 text-white/40 rotate-90 mr-1 -ml-0.5 pointer-events-none" />
                            </span>
                            <button
                              type="button"
                              onClick={() => removeCastFromScene(sk, cid)}
                              className="text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              title={`Remove ${cName} from this scene`}
                            >×</button>
                          </span>
                        );
                      })}
                      {unboundCast.length > 0 && (
                        <details className="relative inline-block">
                          <summary className="cursor-pointer text-white/40 hover:text-ai-accent border border-dashed border-white/15 hover:border-ai-accent/40 rounded px-1.5 py-[1px] list-none select-none">
                            + cast
                          </summary>
                          <div className="mt-1 absolute z-20 bg-ai-bg-secondary border border-ai-border rounded-md shadow-lg p-1 min-w-[140px]">
                            {unboundCast.map(c => {
                              const cn = c.display_slug || c.slug || c.name || c.id;
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => addCastToScene(sk, c.id)}
                                  className="block w-full text-left text-[10px] text-white/80 hover:bg-ai-accent/15 px-2 py-1 rounded transition-colors"
                                >{cn}</button>
                              );
                            })}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-ai-border">
          <div className="flex items-center gap-2 text-[10px] text-white/60">
            <input
              type="checkbox"
              id="cascade-toggle"
              checked={cascade}
              onChange={e => setCascade(e.target.checked)}
              className="accent-ai-accent"
              data-testid="continuity-map-audit-cascade-toggle"
            />
            <label htmlFor="cascade-toggle" className="cursor-pointer select-none">
              Cascade · relock auto-bound shots in edited scenes
            </label>
          </div>
          <div className="flex items-center gap-2">
            {err && <span className={`text-[10px] ${err.startsWith('✓') ? 'text-emerald-400' : 'text-red-400'}`} data-testid="continuity-map-audit-status">{err}</span>}
            <button
              type="button"
              onClick={rebuild}
              disabled={rebuilding || saving}
              className="px-2.5 py-1 rounded text-[10px] font-medium border border-white/15 text-white/70 hover:text-white hover:border-ai-accent/40 disabled:opacity-50 transition-colors"
              data-testid="continuity-map-audit-rebuild"
              title="Re-derive the entire map from script + asset bible (Director AI)"
            >
              {rebuilding ? 'Rebuilding…' : 'Rebuild via Director AI'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="px-3 py-1 rounded text-[11px] font-semibold bg-ai-accent text-white hover:bg-ai-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="continuity-map-audit-save"
            >
              {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════ ASSET BIBLE SPEC PANEL (CCC bindings) ═══════════ */
// Phase B Steps 4-7 (May 16 brief). Edits ARE live: PATCH writes through
// to shot.asset_bible_spec, IGK/ISG prompt builders pick up the change on
// next render. Auto-pop button fires Opus 4.7 inference for this shot.
function dopFilledFromAB(spec) {
  if (!spec) return 0;
  let n = 0;
  if (Array.isArray(spec.cast_bindings)) n += spec.cast_bindings.length;
  // Either a resolved corner_id OR a parsed corner_label (preview mode)
  // counts as 1 environment binding for the "filled" rollup.
  if (spec.corner_id || spec.corner_label) n += 1;
  return n;
}

function AssetBibleSpecPanel({ shot, projectId, assetBible, onUpdated, bindingsProcessing }) {
  const [expanded, setExpanded] = useState(false);
  const [spec, setSpec] = useState(shot?.asset_bible_spec || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setSpec(shot?.asset_bible_spec || null); }, [shot?.id, shot?.asset_bible_spec]);

  const cast = assetBible?.cast_candidates || [];
  const costumes = assetBible?.costume_candidates || [];
  const corners = assetBible?.corner_candidates || [];
  const filled = dopFilledFromAB(spec);

  const bindings = spec?.cast_bindings || [];
  const boundCastIds = bindings.map(b => b.cast_id);
  const unboundCast = cast.filter(c => !boundCastIds.includes(c.id));

  const writeLocal = (next) => {
    setSpec(next);
  };

  const persist = async (next) => {
    if (!projectId || !shot?.id) return;
    setBusy(true); setErr('');
    try {
      const res = await axios.patch(
        `${API}/projects/${projectId}/shots/${shot.id}/asset-bible-spec`,
        { cast_bindings: next.cast_bindings || [], corner_id: next.corner_id || null },
      );
      setSpec(res.data.asset_bible_spec || next);
      if (typeof onUpdated === 'function') onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'INVALID_ENTITY_IDS') {
        setErr(`Invalid binding: ${detail.invalid?.map(x => `${x.field}=${x.id}`).join(', ')}`);
      } else {
        setErr(typeof detail === 'string' ? detail : (detail?.message || e.message || 'Save failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const addCast = (castId) => {
    if (!castId) return;
    const next = { ...(spec || {}), cast_bindings: [...bindings, { cast_id: castId, costume_id: null }], corner_id: spec?.corner_id || null };
    writeLocal(next);
    persist(next);
  };
  const removeCast = (castId) => {
    const next = { ...(spec || {}), cast_bindings: bindings.filter(b => b.cast_id !== castId), corner_id: spec?.corner_id || null };
    writeLocal(next);
    persist(next);
  };
  const setCostume = (castId, costumeId) => {
    const next = { ...(spec || {}), cast_bindings: bindings.map(b => b.cast_id === castId ? { ...b, costume_id: costumeId || null } : b), corner_id: spec?.corner_id || null };
    writeLocal(next);
    persist(next);
  };
  const setCorner = (cornerId) => {
    const next = { ...(spec || {}), cast_bindings: bindings, corner_id: cornerId || null };
    writeLocal(next);
    persist(next);
  };

  const regenerate = async () => {
    if (!projectId || !shot?.id) return;
    setBusy(true); setErr('');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/shots/${shot.id}/asset-bible-spec/regenerate`);
      setSpec(res.data.asset_bible_spec);
      if (typeof onUpdated === 'function') onUpdated();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message || 'Regenerate failed');
    } finally {
      setBusy(false);
    }
  };

  const stale = shot?.keyframe_state === 'stale';

  const hasAB = !!(assetBible && (cast.length || costumes.length || corners.length));
  const cornerName = (() => {
    const c = corners.find(x => x.id === spec?.corner_id);
    if (!c) return null;
    return c.display_slug || c.slug || c.name || c.id;
  })();

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="inline-flex items-center gap-1 text-[10px] text-white/50 hover:text-ai-accent transition-colors"
        data-testid={`asset-bible-spec-toggle-${shot.id}`}
      >
        <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span className="font-semibold">Assets from parsing</span>
        {filled === 0 ? (
          bindingsProcessing ? (
            <span className="text-[9px] text-ai-accent/70 inline-flex items-center gap-1">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />reading prompt…
            </span>
          ) : (
            <span className="text-[9px] text-white/30" title="Parser found no matches in the Asset Bible. Either the bible has no candidates yet, or the prompt doesn't name a known cast/corner.">· not bound</span>
          )
        ) : (
          <span className="text-[9px] text-emerald-400/70">
            · {bindings.length} cast{(spec?.corner_id || spec?.corner_label) ? ' · 1 corner' : ''}
          </span>
        )}
        {spec?.auto_populated === false && (
          <span className="text-[9px] text-white/40" title="User-edited">· user-edited</span>
        )}
        {spec?.auto_populated && (() => {
          // Surface the source pass — directors_prompt_* means we parsed the
          // Director's Prompt deterministically (no LLM); visibility_* means
          // the OBJECT_ONLY / HANDS_ONLY short-circuit fired; continuity_map_v1
          // is a deterministic costume lookup; opus_* is the legacy LLM
          // fallback. Distinct labels so directors trust the binding source.
          const src = spec.source_pass || '';
          if (src.startsWith('directors_prompt_') || src.startsWith('visibility_')) {
            return (
              <span className="text-[9px] text-ai-accent/70" title={`Parsed from Director's Prompt (${src})`}>· from prompt</span>
            );
          }
          if (src === 'continuity_map_v1') {
            return (
              <span className="text-[9px] text-emerald-400/70" title="Resolved via project continuity map (deterministic)">· from map</span>
            );
          }
          if (src.startsWith('opus_')) {
            return (
              <span className="text-[9px] text-amber-300/80" title="AI-inferred fallback — verify">· AI inferred</span>
            );
          }
          return (
            <span className="text-[9px] text-amber-300/80" title="Auto-populated — verify">· auto</span>
          );
        })()}
        {stale && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1 py-[1px]" title="Keyframe is stale — bindings changed after last render">
            <AlertTriangle className="w-2.5 h-2.5" /> keyframe stale
          </span>
        )}
      </button>
      {expanded && (
        <div className="mt-1.5 rounded-lg border border-ai-border px-2.5 py-1.5" style={{ background: 'rgba(11, 15, 25, 0.6)' }}>
          {!hasAB ? (
            <p className="text-[10px] text-white/40 italic" data-testid={`asset-bible-spec-no-bible-${shot.id}`}>
              Asset Bible not built yet — generate Stage 3 assets first to bind CCC.
            </p>
          ) : (
            <>
              {/* v3 Table layout — CAST | COSTUME | CORNER, one row per cast binding.
                  Corner is a shot-level field (not per-cast), so its cell is
                  merged (rowSpan) across every cast row for this shot. */}
              <table className="w-full text-[10px] border-collapse" data-testid={`asset-bible-spec-table-${shot.id}`}>
                <thead>
                  <tr>
                    <th className="text-left pb-1 pr-2 text-[8px] uppercase tracking-wide text-white/30 font-semibold">Cast</th>
                    <th className="text-left pb-1 pr-2 text-[8px] uppercase tracking-wide text-white/30 font-semibold">Costume</th>
                    <th className="text-left pb-1 pr-2 text-[8px] uppercase tracking-wide text-white/30 font-semibold">Corner</th>
                    <th className="w-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {bindings.length === 0 ? (
                    <tr>
                      <td className="py-1 pr-2 text-white/30 italic" colSpan={2}>no cast bound</td>
                      <td className="py-1 pr-2 align-middle">
                        <span
                          className={`inline-flex items-center rounded ${spec?.corner_id ? 'bg-ai-accent/10 border border-ai-accent/30' : 'border border-dashed border-white/20'} hover:border-ai-accent/50 transition-colors`}
                        >
                          <select
                            disabled={busy}
                            value={spec?.corner_id || ''}
                            onChange={e => setCorner(e.target.value)}
                            className={`bg-transparent ${spec?.corner_id ? 'text-white/90' : 'text-white/40'} px-1.5 py-[2px] outline-none cursor-pointer hover:bg-ai-accent/10 max-w-[240px] appearance-none border-0`}
                            style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                            title={cornerName || 'pick corner'}
                            data-testid={`asset-bible-spec-corner-select-${shot.id}`}
                          >
                            <option value="" className="bg-ai-bg-secondary">pick corner…</option>
                            {corners.map(co => {
                              const lbl = co.display_slug || co.slug || co.name || co.id;
                              return <option key={co.id} value={co.id} className="bg-ai-bg-secondary">{lbl.slice(0, 80)}</option>;
                            })}
                          </select>
                          <ChevronRight className="w-2.5 h-2.5 text-white/40 rotate-90 mr-1 -ml-0.5 pointer-events-none" />
                        </span>
                      </td>
                      <td></td>
                    </tr>
                  ) : bindings.map((b, i) => {
                    const c = cast.find(x => x.id === b.cast_id);
                    const cName = c?.display_slug || c?.slug || c?.name || b.cast_id;
                    const co = costumes.find(x => x.id === b.costume_id);
                    const coName = co ? (co.display_slug || co.slug || co.name || co.id) : null;
                    const allowed = costumes.filter(x => !x.for_cast || x.for_cast === b.cast_id);
                    const hasCostume = !!b.costume_id;
                    return (
                      <tr key={b.cast_id} className="border-t border-white/[0.04]" data-testid={`asset-bible-spec-cast-row-${shot.id}-${b.cast_id}`}>
                        <td className="py-1 pr-2 text-white/90 font-medium whitespace-nowrap">{cName}</td>
                        <td className="py-1 pr-2">
                          <span
                            className={`inline-flex items-center rounded ${hasCostume ? 'bg-ai-accent/10 border border-ai-accent/30' : 'border border-dashed border-white/20'} hover:border-ai-accent/50 transition-colors`}
                          >
                            <select
                              disabled={busy}
                              value={b.costume_id || ''}
                              onChange={e => setCostume(b.cast_id, e.target.value)}
                              className={`bg-transparent ${hasCostume ? 'text-white/90' : 'text-white/40'} px-1.5 py-[2px] outline-none cursor-pointer hover:bg-ai-accent/10 max-w-[180px] appearance-none border-0`}
                              style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                              title={coName || 'pick costume'}
                              data-testid={`asset-bible-spec-costume-select-${shot.id}-${b.cast_id}`}
                            >
                              <option value="" className="bg-ai-bg-secondary">pick costume…</option>
                              {allowed.map(x => {
                                const lbl = x.display_slug || x.slug || x.name || x.id;
                                return <option key={x.id} value={x.id} className="bg-ai-bg-secondary">{lbl.slice(0, 60)}</option>;
                              })}
                            </select>
                            <ChevronRight className="w-2.5 h-2.5 text-white/40 rotate-90 mr-1 -ml-0.5 pointer-events-none" />
                          </span>
                        </td>
                        {i === 0 && (
                          <td className="py-1 pr-2 align-middle" rowSpan={bindings.length}>
                            <span
                              className={`inline-flex items-center rounded ${spec?.corner_id ? 'bg-ai-accent/10 border border-ai-accent/30' : 'border border-dashed border-white/20'} hover:border-ai-accent/50 transition-colors`}
                            >
                              <select
                                disabled={busy}
                                value={spec?.corner_id || ''}
                                onChange={e => setCorner(e.target.value)}
                                className={`bg-transparent ${spec?.corner_id ? 'text-white/90' : 'text-white/40'} px-1.5 py-[2px] outline-none cursor-pointer hover:bg-ai-accent/10 max-w-[240px] appearance-none border-0`}
                                style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                                title={cornerName || 'pick corner'}
                                data-testid={`asset-bible-spec-corner-select-${shot.id}`}
                              >
                                <option value="" className="bg-ai-bg-secondary">pick corner…</option>
                                {corners.map(co => {
                                  const lbl = co.display_slug || co.slug || co.name || co.id;
                                  return <option key={co.id} value={co.id} className="bg-ai-bg-secondary">{lbl.slice(0, 80)}</option>;
                                })}
                              </select>
                              <ChevronRight className="w-2.5 h-2.5 text-white/40 rotate-90 mr-1 -ml-0.5 pointer-events-none" />
                            </span>
                            {bindings.length > 1 && (
                              <div className="text-[8px] text-white/25 italic mt-0.5">shared · one corner per shot</div>
                            )}
                          </td>
                        )}
                        <td
                          className="py-1 text-white/30 hover:text-red-400 cursor-pointer text-center"
                          onClick={() => removeCast(b.cast_id)}
                          title={`Remove ${cName}`}
                          data-testid={`asset-bible-spec-remove-cast-${shot.id}-${b.cast_id}`}
                        >×</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex items-center gap-2 mt-1.5">
                {/* + add cast ghost button */}
                {unboundCast.length > 0 && (
                  <details className="relative inline-block" data-testid={`asset-bible-spec-cast-add-${shot.id}`}>
                    <summary className="cursor-pointer text-[10px] text-white/40 hover:text-ai-accent border border-dashed border-white/15 hover:border-ai-accent/40 rounded px-1.5 py-[1px] list-none select-none">
                      + add cast
                    </summary>
                    <div className="mt-1 absolute z-20 bg-ai-bg-secondary border border-ai-border rounded-md shadow-lg p-1 min-w-[140px]">
                      {unboundCast.map(c => {
                        const cn = c.display_slug || c.slug || c.name || c.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            disabled={busy}
                            onClick={() => addCast(c.id)}
                            className="block w-full text-left text-[10px] text-white/80 hover:bg-ai-accent/15 px-2 py-1 rounded transition-colors"
                          >{cn}</button>
                        );
                      })}
                    </div>
                  </details>
                )}

                <span className="flex-1 min-w-[8px]" />

                {/* Regen icon — Opus auto-populate, metadata in tooltip */}
                <button
                  type="button"
                  disabled={busy}
                  onClick={regenerate}
                  className="text-white/40 hover:text-ai-accent disabled:opacity-50 transition-colors"
                  title={`Auto-populate via Director AI${spec?.source_pass ? ` · last source: ${spec.source_pass}` : ''}`}
                  data-testid={`asset-bible-spec-regenerate-${shot.id}`}
                >
                  {busy ? (
                    <span className="text-[10px]">…</span>
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {err && (
                <p className="mt-1.5 text-[10px] text-red-400" data-testid={`asset-bible-spec-error-${shot.id}`}>{err}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════ SHOT EDIT CARD ═══════════ */
function ShotEditCard({ shot, index, totalShots, onUpdate, onDelete, onReorder, assetBible, projectId, sketchGateReady, bindingsProcessing, promptsProcessing, dopSpecsProcessing }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    scene_number: shot.scene_number,
    shot_number: shot.shot_number,
    description: shot.description,
    duration: shot.duration || 5,
    camera_angle: shot.camera_angle || '',
    camera_movement: shot.camera_movement || 'Static'
  });
  const [saving, setSaving] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [promptGenerating, setPromptGenerating] = useState(false);
  const [localPrompt, setLocalPrompt] = useState(shot.keyframe_prompt || shot.mj_prompt || '');
  const [promptEditing, setPromptEditing] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [promptSaving, setPromptSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Quick napkin sketch preview (pre-Asset-Bible visual gut-check for the CD)
  const [quickSketchUrl, setQuickSketchUrl] = useState(shot.quick_sketch_url || '');
  const [sketching, setSketching] = useState(false);
  const [sketchError, setSketchError] = useState(null);
  const [showSketchLightbox, setShowSketchLightbox] = useState(false);
  useEffect(() => {
    setQuickSketchUrl(shot.quick_sketch_url || '');
  }, [shot.quick_sketch_url]);

  const generateQuickSketch = async (force = false) => {
    if (sketching) return;
    setSketching(true);
    setSketchError(null);
    try {
      // CD-3 async sweep: quick-sketch is now an async JOB. POST returns
      // {job_id, status} (or {cached, quick_sketch_url}); poll /jobs/{id} until
      // ready/error/stalled so the proxy timeout can never surface as "Network Error".
      const res = await axios.post(`${API}/shots/${shot.id}/quick-sketch`, { force });
      const data = res.data || {};
      if (data.quick_sketch_url) {                 // cached fast-path
        setQuickSketchUrl(data.quick_sketch_url);
        onUpdate?.();
        setSketching(false);
        return;
      }
      const jobId = data.job_id;
      if (!jobId) { setSketching(false); return; }
      for (let i = 0; i < 120; i++) {              // ~6 min ceiling
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: job } = await axios.get(`${API}/jobs/${jobId}`);
          if (job.status === 'generating') continue;
          setSketching(false);
          if (job.status === 'ready') {
            const url = job.result?.quick_sketch_url || '';
            setQuickSketchUrl(url);
            if (url && !job.result?.cached) toast.success('Quick sketch ready');
            onUpdate?.();
          } else {
            setSketchError(job.error || (job.status === 'stalled'
              ? 'Render stalled (worker stopped responding) — Retry.'
              : 'Sketch generation failed — Retry.'));
          }
          return;
        } catch (_) { /* transient — keep polling */ }
      }
      setSketching(false);
      setSketchError('Timed out waiting for the sketch — Retry.');
    } catch (e) {
      setSketching(false);
      setSketchError(e.response?.data?.detail || e.message || 'Sketch failed — Retry.');
    }
  };

  // Keep local prompt in sync with parent shot prop (e.g. after refetch)
  useEffect(() => {
    setLocalPrompt(shot.keyframe_prompt || shot.mj_prompt || '');
  }, [shot.keyframe_prompt, shot.mj_prompt]);

  const generateDirectorPrompt = async (force = false) => {
    setPromptGenerating(true);
    try {
      const res = await axios.post(
        `${API}/projects/${shot.project_id}/shots/${shot.id}/director-prompt`,
        { force }
      );
      setLocalPrompt(res.data.keyframe_prompt || '');
      setPromptExpanded(true);
      toast.success(res.data.cached ? "Loaded cached director's prompt" : "Director's prompt generated");
      onUpdate?.();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPromptGenerating(false);
    }
  };

  const savePromptEdit = async () => {
    const next = (promptDraft || '').trim();
    if (!next) { toast.error('Prompt cannot be empty'); return; }
    setPromptSaving(true);
    try {
      const res = await axios.put(
        `${API}/projects/${shot.project_id}/shots/${shot.id}/director-prompt`,
        { keyframe_prompt: next }
      );
      setLocalPrompt(res.data.keyframe_prompt || next);
      setPromptEditing(false);
      toast.success('Prompt saved · prior version kept in history · DOP + bindings marked stale');
      onUpdate?.();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPromptSaving(false);
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(localPrompt);
      toast.success('Prompt copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/shots/${shot.id}`, editData);
      setIsEditing(false);
      onUpdate();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    { // confirm removed - non-blocking
      onDelete(shot.id);
    }
  };

  if (isEditing) {
    return (
      <div className="p-3 border border-ai-border rounded-xl" style={{ background: 'rgba(14, 165, 233, 0.08)' }} data-testid={`shot-edit-${shot.id}`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <input 
            className="px-2 py-1.5 text-xs text-white border border-ai-border rounded-lg focus:outline-none focus:border-ai-accent"
            style={{ background: 'rgba(11, 15, 25, 0.9)' }}
            placeholder="Scene #"
            value={editData.scene_number}
            onChange={(e) => setEditData({...editData, scene_number: e.target.value})}
          />
          <input 
            className="px-2 py-1.5 text-xs text-white border border-ai-border rounded-lg focus:outline-none focus:border-ai-accent"
            style={{ background: 'rgba(11, 15, 25, 0.9)' }}
            placeholder="Shot #"
            value={editData.shot_number}
            onChange={(e) => setEditData({...editData, shot_number: e.target.value})}
          />
          <input 
            className="px-2 py-1.5 text-xs text-white border border-ai-border rounded-lg focus:outline-none focus:border-ai-accent"
            style={{ background: 'rgba(11, 15, 25, 0.9)' }}
            placeholder="Duration (sec)"
            type="number"
            min="1"
            max="60"
            value={editData.duration}
            onChange={(e) => setEditData({...editData, duration: parseInt(e.target.value) || 5})}
          />
          <select 
            className="px-2 py-1.5 text-xs text-white border border-ai-border rounded-lg focus:outline-none focus:border-ai-accent"
            style={{ background: 'rgba(11, 15, 25, 0.9)' }}
            value={editData.camera_movement}
            onChange={(e) => setEditData({...editData, camera_movement: e.target.value})}
          >
            <option value="Static" style={{ background: '#0B0F19', color: 'white' }}>Static</option>
            <option value="Pan left" style={{ background: '#0B0F19', color: 'white' }}>Pan left</option>
            <option value="Pan right" style={{ background: '#0B0F19', color: 'white' }}>Pan right</option>
            <option value="Tilt up" style={{ background: '#0B0F19', color: 'white' }}>Tilt up</option>
            <option value="Tilt down" style={{ background: '#0B0F19', color: 'white' }}>Tilt down</option>
            <option value="Dolly in" style={{ background: '#0B0F19', color: 'white' }}>Dolly in</option>
            <option value="Dolly out" style={{ background: '#0B0F19', color: 'white' }}>Dolly out</option>
            <option value="Tracking" style={{ background: '#0B0F19', color: 'white' }}>Tracking</option>
            <option value="Crane" style={{ background: '#0B0F19', color: 'white' }}>Crane</option>
            <option value="Handheld" style={{ background: '#0B0F19', color: 'white' }}>Handheld</option>
          </select>
        </div>
        <input 
          className="w-full px-2 py-1.5 text-xs text-white border border-ai-border rounded-lg focus:outline-none focus:border-ai-accent mb-2"
          style={{ background: 'rgba(11, 15, 25, 0.9)' }}
          placeholder="Camera Angle (e.g., Wide, Close-up, Medium)"
          value={editData.camera_angle}
          onChange={(e) => setEditData({...editData, camera_angle: e.target.value})}
        />
        <textarea 
          className="w-full px-2 py-1.5 text-xs text-white border border-ai-border rounded-lg focus:outline-none focus:border-ai-accent min-h-[60px]"
          style={{ background: 'rgba(11, 15, 25, 0.9)' }}
          placeholder="Shot description..."
          value={editData.description}
          onChange={(e) => setEditData({...editData, description: e.target.value})}
        />
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={() => setIsEditing(false)} className="px-3 py-1 text-xs text-white/50 hover:text-white/70">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-xs bg-[#22C55E] text-white rounded-lg hover:bg-ai-accent flex items-center gap-1">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex gap-2 p-3 glass-panel hover:bg-white/5 rounded-xl transition-colors" data-testid={`shot-item-${shot.id}`}>
      <div className="flex flex-col items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={() => onReorder(shot.id, 'up')} 
          disabled={index === 0}
          className="p-0.5 text-white/50 hover:text-ai-accent disabled:opacity-30"
          title="Move up"
        >
          <ChevronDown className="w-3.5 h-3.5 rotate-180" />
        </button>
        <GripVertical className="w-3.5 h-3.5 text-white/40" />
        <button 
          onClick={() => onReorder(shot.id, 'down')} 
          disabled={index === totalShots - 1}
          className="p-0.5 text-white/50 hover:text-ai-accent disabled:opacity-30"
          title="Move down"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono font-semibold text-ai-accent">{fmtAddr(shot.scene_number, shot.shot_number)}</span>
          <ShotAssetAmberBadge projectId={projectId} shotId={shot.id} />
          {shot.duration && <span className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded text-white/60">{shot.duration}s</span>}
          {shot.camera_angle && <span className="text-[10px] px-1.5 py-0.5 bg-ai-accent/10 rounded text-ai-accent">{shot.camera_angle}</span>}
          {shot.camera_movement && shot.camera_movement !== 'Static' && <span className="text-[10px] px-1.5 py-0.5 bg-[#FF9500]/10 rounded text-[#FF9500]">{shot.camera_movement}</span>}
        </div>
        {shot.sketch_divergence && (
          <div
            className="mb-1.5 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-2.5 py-1.5"
            data-testid={`shot-divergence-warning-${shot.id}`}
          >
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-[#FF3B30] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] text-[#FF6B60] leading-snug">
                  {shot.sketch_divergence_reason || 'This sketch no longer matches the planned shot. Update the shot plan, or revert the sketch.'}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                  className="mt-1 text-[10px] font-semibold text-[#FF3B30] underline underline-offset-2 hover:text-[#FF6B60]"
                  data-testid={`shot-divergence-edit-plan-${shot.id}`}
                >
                  Edit shot description →
                </button>
                <span className="ml-2 text-[10px] text-white/40">(or revert the sketch in the workbench)</span>
              </div>
            </div>
          </div>
        )}
        <p className="text-xs text-white/60 leading-relaxed line-clamp-2 cursor-pointer hover:text-white/80 transition-colors"
          onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-2'); }}
        >{shot.description}</p>
        {shot.dialogue && (
          <p className="text-[10px] text-cyan-400/80 mt-1 leading-relaxed line-clamp-1 cursor-pointer hover:line-clamp-none transition-colors" data-testid={`shot-dialogue-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-cyan-400 mr-1">DLG:</span>"{shot.dialogue}"
          </p>
        )}
        {shot.voiceover && (
          <p className="text-[10px] text-amber-400/80 mt-0.5 leading-relaxed line-clamp-1 cursor-pointer hover:line-clamp-none transition-colors" data-testid={`shot-voiceover-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-amber-400 mr-1">VO:</span>"{shot.voiceover}"
          </p>
        )}
        {/* Director's Prompt (Hybrid lazy) */}
        <div className="mt-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!localPrompt && !promptExpanded) {
                // First-time: expand and auto-generate
                setPromptExpanded(true);
                generateDirectorPrompt(false);
              } else {
                setPromptExpanded(!promptExpanded);
              }
            }}
            className="inline-flex items-center gap-1 text-[10px] text-white/50 hover:text-ai-accent transition-colors"
            data-testid={`director-prompt-toggle-${shot.id}`}
          >
            <ChevronRight className={`w-3 h-3 transition-transform ${promptExpanded ? 'rotate-90' : ''}`} />
            <span className="font-semibold tracking-wide">DIRECTOR'S PROMPT</span>
            {localPrompt && shot.keyframe_prompt_stale ? (
              // 2026-06-17 — when the shot's description / cast / camera
              // grammar was edited AFTER the prompt was generated, the
              // cached prose no longer reflects the new shot. Show an
              // amber "stale" badge so the operator clicks Regenerate
              // instead of trusting the dead text.
              <span
                className="text-[9px] text-amber-400/90 inline-flex items-center gap-1"
                title={`Shot edited after prompt was generated. Reason: ${shot.keyframe_prompt_stale_reason || 'shot_edit'}. Click Regenerate to refresh.`}
              >
                <AlertTriangle className="w-2.5 h-2.5" />stale · regenerate
              </span>
            ) : localPrompt ? (
              <span className="text-[9px] text-emerald-400/70">· cached</span>
            ) : shot.director_prompt_status === 'running' ? (
              <span className="text-[9px] text-ai-accent/70 inline-flex items-center gap-1">
                <Loader2 className="w-2.5 h-2.5 animate-spin" />generating…
              </span>
            ) : shot.director_prompt_status === 'error' ? (
              <span className="text-[9px] text-red-400/70" title={shot.director_prompt_error || 'failed'}>· failed</span>
            ) : promptsProcessing ? (
              // 2026-05-18 — project-level fallback when the batch background
              // job is in flight but per-shot status hasn't been stamped yet.
              // Keeps the UI from looking frozen while Opus runs on N shots
              // serially or in batches.
              <span className="text-[9px] text-ai-accent/70 inline-flex items-center gap-1">
                <Loader2 className="w-2.5 h-2.5 animate-spin" />queued…
              </span>
            ) : (
              <span className="text-[9px] text-white/30">· not generated</span>
            )}
          </button>
          {/* Inline trigger removed 2026-02: replaced with footer-level
           *  "Generate all pending" button at the bottom of Stage 2 that
           *  fires the entire chain (Director's Prompt → DOP Spec ∥
           *  Asset bindings) in one click. The in-panel "Generate
           *  director's prompt" button below stays for the expanded
           *  single-shot edit case. */}
          {promptExpanded && (
            <div className="mt-1.5 rounded-lg border border-ai-border p-2" style={{ background: 'rgba(11, 15, 25, 0.6)' }}>
              {promptGenerating ? (
                <div className="flex items-center gap-2 text-[10px] text-white/60 py-2">
                  <Loader2 className="w-3 h-3 animate-spin text-ai-accent" />
                  <span>Generating cinematic director's prompt…</span>
                </div>
              ) : promptEditing ? (
                <>
                  <textarea
                    value={promptDraft}
                    onChange={(e) => setPromptDraft(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    rows={10}
                    className="w-full text-[10px] text-white/90 bg-black/40 border border-ai-accent/40 rounded p-2 font-mono leading-relaxed focus:outline-none focus:border-ai-accent"
                    data-testid={`director-prompt-edit-textarea-${shot.id}`}
                  />
                  <div className="flex items-center justify-end gap-2 mt-1.5 pt-1.5 border-t border-white/5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPromptEditing(false); }}
                      className="text-[10px] text-white/50 hover:text-white transition-colors"
                      data-testid={`director-prompt-cancel-${shot.id}`}>Cancel</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); savePromptEdit(); }}
                      disabled={promptSaving}
                      className="text-[10px] text-emerald-400 hover:text-white transition-colors disabled:opacity-40"
                      data-testid={`director-prompt-save-${shot.id}`}>{promptSaving ? 'Saving…' : 'Save'}</button>
                  </div>
                </>
              ) : localPrompt ? (
                <>
                  <pre
                    className="text-[10px] text-white/80 whitespace-pre-wrap break-words font-mono max-h-64 overflow-y-auto leading-relaxed"
                    data-testid={`director-prompt-text-${shot.id}`}
                  >{localPrompt}</pre>
                  <div className="flex items-center justify-end gap-2 mt-1.5 pt-1.5 border-t border-white/5">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); copyPrompt(); }}
                      className="text-[10px] text-white/50 hover:text-white transition-colors"
                      data-testid={`director-prompt-copy-${shot.id}`}
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
                      className="text-[10px] text-white/50 hover:text-white transition-colors"
                      data-testid={`director-prompt-history-${shot.id}`}
                    >
                      History
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPromptDraft(localPrompt); setPromptEditing(true); }}
                      className="text-[10px] text-white/50 hover:text-white transition-colors"
                      data-testid={`director-prompt-edit-${shot.id}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); generateDirectorPrompt(true); }}
                      disabled={promptGenerating}
                      className="text-[10px] text-ai-accent hover:text-white transition-colors disabled:opacity-40"
                      data-testid={`director-prompt-regenerate-${shot.id}`}
                    >
                      Regenerate
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); generateDirectorPrompt(false); }}
                  className="w-full text-[11px] text-ai-accent hover:text-white py-1.5 rounded border border-ai-accent/30 hover:border-ai-accent hover:bg-ai-accent/10 transition-colors"
                  data-testid={`director-prompt-generate-${shot.id}`}
                >
                  Generate director's prompt
                </button>
              )}
            </div>
          )}
        </div>
        {/* DOP Spec — structured cinematography directives (May 16 brief v2) */}
        <DopSpecPanel shot={shot} dopSpecsProcessing={dopSpecsProcessing} />
        {/* ASSETS · structured CCC bindings (May 16 brief Steps 4-7) */}
        <AssetBibleSpecPanel shot={shot} projectId={projectId} assetBible={assetBible} onUpdated={onUpdate} bindingsProcessing={bindingsProcessing} />
        {/* Director's Prompt version history (versioned document platform) */}
        <DirectorPromptHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          shotId={shot.id}
          shotLabel={fmtAddr(shot.scene_number, shot.shot_number)}
          onRestored={() => { generateDirectorPrompt(false); onUpdate?.(); }}
        />
      </div>
      <div className="flex items-start gap-1">
        <button onClick={() => setIsEditing(true)} className="p-1.5 text-ai-accent hover:text-ai-text hover:bg-ai-accent/10 rounded-lg" title="Edit">
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        {/* Quick napkin sketch — pre-Asset-Bible visual gut-check for the CD.
            Gated on cast_confirmed (same explicit user action that enables
            the Image Board + Approve buttons downstream). */}
        {sketchGateReady && (
          quickSketchUrl ? (
            <button
              onClick={() => setShowSketchLightbox(true)}
              className="p-0.5 rounded-lg overflow-hidden border border-ai-border hover:border-ai-accent transition-colors"
              title="Open quick sketch preview"
              data-testid={`quick-sketch-thumb-${shot.id}`}
            >
              <img src={quickSketchUrl} alt="Quick sketch" className="w-6 h-6 object-cover rounded" />
            </button>
          ) : sketchError ? (
            <button
              onClick={() => generateQuickSketch(false)}
              disabled={sketching}
              className="p-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg disabled:opacity-40 flex items-center gap-1"
              title={`${sketchError} — click to retry`}
              data-testid={`quick-sketch-retry-${shot.id}`}
            >
              {sketching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              <span className="text-[9px] font-semibold">Retry</span>
            </button>
          ) : (
            <button
              onClick={() => generateQuickSketch(false)}
              disabled={sketching}
              className="p-1.5 text-purple-400/80 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg disabled:opacity-40"
              title="Generate quick napkin sketch (rough concept, no assets needed)"
              data-testid={`quick-sketch-btn-${shot.id}`}
            >
              {sketching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />}
            </button>
          )
        )}
        <button onClick={handleDelete} className="p-1.5 text-red-400/70 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-lg" title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Quick sketch lightbox — portaled to body so it breaks out of the
          shot-list's overflow-auto scroll container which would otherwise
          clip the fixed positioning. */}
      {showSketchLightbox && quickSketchUrl && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setShowSketchLightbox(false)}
          data-testid={`quick-sketch-lightbox-${shot.id}`}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-purple-300/80 uppercase tracking-wider font-semibold">Quick Sketch · napkin preview</div>
                <div className="text-[11px] text-white/50">{fmtScene(shot.scene_number)} Shot {String(shot.shot_number ?? '').replace(/^sh/i,'')} — {shot.description?.slice(0, 80)}{(shot.description || '').length > 80 ? '…' : ''}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => generateQuickSketch(true)}
                  disabled={sketching}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 flex items-center gap-1.5 disabled:opacity-40"
                  data-testid={`quick-sketch-regen-btn-${shot.id}`}
                >
                  {sketching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  {sketching ? 'Regenerating…' : 'Regenerate'}
                </button>
                <button
                  onClick={() => setShowSketchLightbox(false)}
                  className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
                  data-testid={`quick-sketch-close-btn-${shot.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <img src={quickSketchUrl} alt="Quick sketch" className="w-full max-h-[75vh] object-contain rounded-xl bg-white/5" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ═══════════ ASSET CARD WITH FEEDBACK + REFRESH ═══════════ */
/* ═══════════ BOUND-SHOTS BADGE (Asset Bible card) ═══════════ */
// "🔗 used in N shots" badge on each Asset Bible card. Click → tooltip
// list of bound shots. Powers the delete-block UX in a future slice.
function BoundShotsBadge({ projectId, entityId }) {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!projectId || !entityId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/entities/${entityId}/bound-shots`);
        if (!cancelled) setData(res.data);
      } catch (_e) {
        if (!cancelled) setData({ count: 0, shots: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, entityId]);

  if (!data || data.count === 0) return null;

  return (
    <div
      className="relative inline-flex items-center gap-1 mb-1 text-[9px] text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-[1px] cursor-help"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`bound-shots-badge-${entityId}`}
    >
      <span>🔗 used in {data.count} shot{data.count === 1 ? '' : 's'}</span>
      {hover && data.shots.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-50 w-64 max-h-48 overflow-y-auto rounded-lg border border-ai-border bg-ai-bg-secondary shadow-lg p-2 text-[10px] text-white/80" data-testid={`bound-shots-popover-${entityId}`}>
          {data.shots.slice(0, 8).map(s => (
            <div key={s.id} className="py-0.5 border-b border-white/5 last:border-0">
              <span className="text-white/40 mr-1">{fmtAddr(s.scene_number, s.shot_number)}</span>
              <span className="text-white/70">{(s.description || '').slice(0, 60)}{(s.description || '').length > 60 ? '…' : ''}</span>
            </div>
          ))}
          {data.shots.length > 8 && (
            <div className="text-white/40 italic mt-1">+ {data.shots.length - 8} more…</div>
          )}
        </div>
      )}
    </div>
  );
}

// Pillar 2 — "unresolved asset" amber pill on a shot card. Self-fetches the
// per-shot resolution; shows nothing when every slot resolves cleanly.
function ShotAssetAmberBadge({ projectId, shotId }) {
  const [flags, setFlags] = useState(null);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!projectId || !shotId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/shots/${shotId}/asset-resolution`);
        if (!cancelled) setFlags(res.data?.amber_flags || []);
      } catch (_e) {
        if (!cancelled) setFlags([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, shotId]);

  if (!flags || flags.length === 0) return null;
  return (
    <div
      className="relative inline-flex items-center gap-1 text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-[1px] cursor-help"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`shot-asset-amber-${shotId}`}
    >
      <AlertTriangle className="w-2.5 h-2.5" />
      <span>{flags.length} unresolved asset{flags.length === 1 ? '' : 's'}</span>
      {hover && (
        <div className="absolute left-0 top-full mt-1 z-50 w-64 rounded-lg border border-amber-500/30 bg-ai-bg-secondary shadow-lg p-2 text-[10px] text-amber-200/90" data-testid={`shot-asset-amber-popover-${shotId}`}>
          {flags.map((f, i) => (<div key={i} className="py-0.5">• {f}</div>))}
        </div>
      )}
    </div>
  );
}


// ── Crew Pre-Production Brief Editor ───────────────────────────────
// Inline textarea for editing a single Casting / Costume / Art-Director
// brief. Saves via PATCH on blur or explicit Save. Any edit clears the
// project-level `briefs_approved_at` (handled server-side) so the
// operator must re-approve before Stage 3 can proceed.
//
// REFERENCE IMAGES (2026-06-14, per-card identity anchors):
// Each card has a 📎 reference uploader that POSTs to
// /api/projects/{pid}/crew-preprod/{kind}/{candidate_id}/references
// (multipart, multiple files). The backend saves up to 6 images per card
// under /app/backend/storage/crew_refs and appends a `reference_images`
// array on the candidate. Asset Bible image-gen (Stage 3) picks them up
// when present; absent ⇒ existing text-only flow (no regression).
function CrewBriefEditor({ projectId, kind, asset, briefField, onSaved }) {
  const initial = (asset?.[briefField] || '');
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [refs, setRefs] = useState(asset?.reference_images || []);
  const [refUploading, setRefUploading] = useState(false);
  const [refDragOver, setRefDragOver] = useState(false);
  const [refPanelOpen, setRefPanelOpen] = useState(false);
  const [briefSource, setBriefSource] = useState(asset?.[`${briefField}_source`] || null);
  const [briefSynthing, setBriefSynthing] = useState(false);
  // ── Brief Preview (2026-06-25) ─────────────────────────────────────
  // One-click gpt-image-2 thumbnail of the brief — cheap visual sanity
  // check before running the (expensive) full Asset Bible pipeline.
  const [previewUrl, setPreviewUrl] = useState(asset?.brief_preview_url || null);
  const [previewAt, setPreviewAt] = useState(asset?.brief_preview_at || null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const refFileInputRef = useRef(null);
  const REF_MAX = 6;
  const REF_ACCEPT = "image/jpeg,image/png,image/webp";

  useEffect(() => {
    setText(asset?.[briefField] || '');
    setDirty(false);
    setRefs(asset?.reference_images || []);
    setBriefSource(asset?.[`${briefField}_source`] || null);
    setPreviewUrl(asset?.brief_preview_url || null);
    setPreviewAt(asset?.brief_preview_at || null);
  }, [asset?.id, briefField, asset]);

  // Fire gpt-image-2 LOW for a quick visual of this brief. ~$0.006/image.
  // Result is rendered as a 64px thumbnail; click to expand to lightbox.
  const generatePreview = async () => {
    if (previewLoading) return;
    if (!(text || '').trim()) {
      toast.error('Brief is empty — write or generate the brief first');
      return;
    }
    // If dirty, save first so the server renders what's on screen.
    if (dirty) {
      await handleSave();
    }
    setPreviewLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/crew-preprod/${kind}/${asset.id}/generate-preview`,
      );
      setPreviewUrl(data.preview_url);
      setPreviewAt(data.generated_at);
      toast.success('Preview generated', { description: 'gpt-image-2 · low quality' });
      onSaved && onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'OPENAI_QUOTA') {
        toast.error('OpenAI quota / billing limit reached', { description: detail.message, duration: 8000 });
      } else if (typeof detail === 'object' && detail?.code === 'NO_BRIEF') {
        toast.error(detail.message);
      } else {
        toast.error('Preview failed: ' + (typeof detail === 'string' ? detail : (detail?.message || e.message)));
      }
    }
    setPreviewLoading(false);
  };

  // Vision-driven brief synthesis from uploaded references.
  // Path B (this) replaces / complements the bulk script-driven path A.
  // Only available when at least one reference is uploaded on this card.
  const synthBriefFromRefs = async () => {
    if (briefSynthing) return;
    if (refs.length === 0) {
      toast.error('Upload at least one reference photo first');
      return;
    }
    // Confirm overwrite if the operator has edited a script-driven brief.
    if (text.trim() && briefSource !== 'from_photos' && dirty) {
      const ok = window.confirm(
        'This will replace your edited brief with one generated from the uploaded photos. Continue?'
      );
      if (!ok) return;
    }
    setBriefSynthing(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/crew-preprod/${kind}/${asset.id}/brief-from-refs`,
      );
      setText(data.brief || '');
      setBriefSource('from_photos');
      setDirty(false);
      toast.success(
        `Brief generated from ${data.reference_count} photo${data.reference_count === 1 ? '' : 's'} · ${data.model.replace('claude-', '')}`,
        { description: 'Re-approve briefs above to unlock Asset Bible gen', duration: 6000 }
      );
      onSaved && onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === 'object' ? detail.message : detail || e.message;
      toast.error('Brief synthesis failed: ' + message);
    }
    setBriefSynthing(false);
  };

  const uploadRefFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter(f => f && f.type.startsWith('image/'));
    if (files.length === 0) { toast.error('Drop image files only (JPEG / PNG / WebP)'); return; }
    const remaining = REF_MAX - refs.length;
    if (remaining <= 0) {
      toast.error(`Cap of ${REF_MAX} reference images reached on this card`);
      return;
    }
    const toSend = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`${files.length - remaining} file(s) skipped — card cap is ${REF_MAX}.`);
    }
    setRefUploading(true);
    try {
      const fd = new FormData();
      toSend.forEach(f => fd.append('files', f));
      const { data } = await axios.post(
        `${API}/projects/${projectId}/crew-preprod/${kind}/${asset.id}/references`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setRefs(data.reference_images || []);
      setRefPanelOpen(true);
      toast.success(`Added ${data.added} reference${data.added === 1 ? '' : 's'}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
    }
    setRefUploading(false);
  };

  const deleteRef = async (refId) => {
    try {
      const { data } = await axios.delete(
        `${API}/projects/${projectId}/crew-preprod/${kind}/${asset.id}/references/${refId}`,
      );
      setRefs(data.reference_images || []);
      toast.success('Reference removed');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/projects/${projectId}/crew-preprod/brief`, {
        kind, asset_id: asset.id, brief: text,
      });
      setDirty(false);
      toast.success('Brief saved — re-approve to enable Asset Bible gen');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const displayName = (asset.display_name || asset.name || '').trim() || 'Unnamed';
  const [copied, setCopied] = useState(false);
  const [shareGenerating, setShareGenerating] = useState(false);
  const handleCopy = async (e) => {
    e?.stopPropagation();
    if (!(text || '').trim()) { toast.error('Nothing to copy'); return; }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`Copied "${displayName}" brief`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Clipboard blocked by browser');
    }
  };

  // Public share link — generates a no-auth uploader URL for this single
  // card, copies it to clipboard. The DOP / stylist / scout can paste it
  // in WhatsApp and the receiver drops photos directly onto this card
  // without needing an Emergent login. 7-day expiry, revocable, capped
  // at the same 6-per-card limit as in-app uploads.
  const handleGenerateShareLink = async () => {
    if (shareGenerating) return;
    setShareGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/crew-preprod/${kind}/${asset.id}/share-link`,
        { ttl_days: 7 },
      );
      const url = data.share_url?.startsWith('http')
        ? data.share_url
        : `${window.location.origin}${data.share_url}`;
      try {
        await navigator.clipboard.writeText(url);
        const expiryDate = new Date(data.expires_at).toLocaleDateString();
        toast.success(`Share link copied — expires ${expiryDate}. Paste anywhere.`);
      } catch {
        // Clipboard blocked — fall back to a prompt so the user can
        // copy it manually.
        window.prompt('Copy this share link manually:', url);
      }
    } catch (e) {
      toast.error('Share link failed: ' + (e.response?.data?.detail || e.message));
    }
    setShareGenerating(false);
  };
  return (
    <div className="bg-black/20 rounded-lg p-3 border border-white/5">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-xs font-semibold text-white truncate">{displayName}</span>
          {asset.name && asset.display_name && asset.name !== asset.display_name && (
            <span className="text-[10px] text-white/40">({asset.name})</span>
          )}
          {briefSource === 'from_photos' && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 flex items-center gap-1 flex-shrink-0 uppercase tracking-wider font-medium"
              title="This brief was auto-generated from the uploaded reference photos."
              data-testid={`brief-source-from-photos-${kind}-${asset.id}`}
            >
              <Sparkles className="w-2.5 h-2.5" /> from photos
            </span>
          )}
          {refs.length > 0 && (
            <button
              onClick={() => setRefPanelOpen(o => !o)}
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 flex items-center gap-1 flex-shrink-0"
              title={`${refs.length} reference image${refs.length === 1 ? '' : 's'} attached`}
              data-testid={`refs-badge-${kind}-${asset.id}`}
            >
              <Paperclip className="w-3 h-3" /> {refs.length}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <input
            ref={refFileInputRef}
            type="file"
            accept={REF_ACCEPT}
            multiple
            className="hidden"
            onChange={e => { uploadRefFiles(e.target.files); e.target.value = ''; }}
            data-testid={`ref-file-input-${kind}-${asset.id}`}
          />
          <button
            onClick={() => {
              if (refs.length >= REF_MAX) {
                toast.error(`Cap of ${REF_MAX} reached — delete one to add another`);
                return;
              }
              refFileInputRef.current?.click();
            }}
            disabled={refUploading}
            title={refs.length >= REF_MAX
              ? `Max ${REF_MAX} references per card`
              : `Upload reference images (anchor identity for Asset Bible gen)`}
            className="p-1 rounded hover:bg-amber-500/10 transition-colors text-white/60 hover:text-amber-300 disabled:opacity-50 flex items-center gap-1"
            data-testid={`upload-refs-btn-${kind}-${asset.id}`}
          >
            {refUploading
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Paperclip className="w-3 h-3" />}
          </button>
          {dirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-2 py-0.5 rounded text-[10px] font-medium bg-ai-accent/80 hover:bg-ai-accent text-white disabled:opacity-50 flex items-center gap-1"
              data-testid={`save-brief-${kind}-${asset.id}`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          )}
          <button
            onClick={handleGenerateShareLink}
            disabled={shareGenerating}
            title="Copy a public link so anyone (DOP, stylist, scout) can drop reference photos straight onto this card — no Emergent login needed. 7-day expiry."
            className="p-1 rounded hover:bg-cyan-500/10 transition-colors text-white/50 hover:text-cyan-300 disabled:opacity-50"
            data-testid={`share-refs-btn-${kind}-${asset.id}`}
          >
            {shareGenerating
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Share2 className="w-3 h-3" />}
          </button>
          <button
            onClick={handleCopy}
            title="Copy brief to clipboard"
            className={`p-1 rounded hover:bg-white/10 transition-colors ${copied ? 'text-emerald-400' : 'text-white/50 hover:text-white/90'}`}
            data-testid={`copy-brief-${kind}-${asset.id}`}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </button>
        </div>
      </div>
      {/* Reference image drag-drop + thumbnail gallery */}
      {(refs.length > 0 || refPanelOpen) && (
        <div
          onDragOver={e => { e.preventDefault(); setRefDragOver(true); }}
          onDragLeave={() => setRefDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setRefDragOver(false);
            uploadRefFiles(e.dataTransfer?.files);
          }}
          className={`mb-2 rounded-md border ${refDragOver ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/10 bg-black/20'} p-2 transition-colors`}
          data-testid={`refs-panel-${kind}-${asset.id}`}
        >
          {refs.length === 0 ? (
            <div className="text-[10px] text-white/40 text-center py-2">
              Drop images here or click <Paperclip className="w-3 h-3 inline" /> to upload
            </div>
          ) : (
            <>
              <div className="grid grid-cols-6 gap-1.5">
                {refs.map(r => (
                  <div
                    key={r.id}
                    className="relative aspect-square rounded overflow-hidden bg-black/40 border border-white/10 group"
                    data-testid={`ref-thumb-${r.id}`}
                  >
                    <img
                      src={resolveUrl(r.url)}
                      alt={r.filename}
                      className="w-full h-full object-cover"
                      onError={e => { e.target.style.opacity = '0.3'; }}
                    />
                    <button
                      onClick={() => deleteRef(r.id)}
                      title={`Remove ${r.filename}`}
                      className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/70 text-white/80 opacity-0 group-hover:opacity-100 hover:bg-rose-500 hover:text-white transition-all"
                      data-testid={`ref-delete-${r.id}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
                {refs.length < REF_MAX && (
                  <button
                    onClick={() => refFileInputRef.current?.click()}
                    className="aspect-square rounded border border-dashed border-white/20 hover:border-amber-400/60 hover:bg-amber-500/10 text-white/40 hover:text-amber-300 flex items-center justify-center transition-colors"
                    title="Add more references"
                    data-testid={`ref-add-more-${kind}-${asset.id}`}
                  >
                    <Paperclip className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <button
                  onClick={synthBriefFromRefs}
                  disabled={briefSynthing}
                  title="Read the photos and auto-write the brief. Your existing brief will be replaced."
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-violet-500/15 text-violet-200 hover:bg-violet-500/30 hover:text-violet-100 text-[10px] font-medium disabled:opacity-50 transition-colors"
                  data-testid={`brief-from-refs-btn-${kind}-${asset.id}`}
                >
                  {briefSynthing
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Reading photos…</>
                    : <><Sparkles className="w-3 h-3" /> {text.trim() ? 'Regenerate brief from photos' : 'Generate brief from photos'}</>}
                </button>
                <span className="text-[10px] text-white/30">
                  {refs.length} of {REF_MAX} photo{refs.length === 1 ? '' : 's'} attached
                </span>
              </div>
            </>
          )}
        </div>
      )}
      <textarea
        value={text}
        onChange={e => { setText(e.target.value); setDirty(true); }}
        onBlur={handleSave}
        rows={6}
        className="w-full bg-black/30 border border-white/10 rounded p-2 text-[11px] text-white/85 leading-relaxed font-mono outline-none focus:border-amber-500/40 resize-y"
        placeholder="Brief will appear here after generation…"
        data-testid={`brief-textarea-${kind}-${asset.id}`}
      />
      {/* 2026-06-25 — Brief Preview row: one-click gpt-image-2 thumbnail
          for a quick visual sanity check before the expensive Asset
          Bible run. Thumbnail is 64px; click to expand to full lightbox. */}
      <div className="mt-2 flex items-center gap-2">
        {previewUrl ? (
          <button
            onClick={() => setPreviewExpanded(true)}
            className="relative w-14 h-14 rounded-md overflow-hidden border border-white/15 hover:border-amber-400/60 transition-colors flex-shrink-0 bg-black/40 group"
            title={previewAt ? `Generated ${new Date(previewAt).toLocaleString()} · click to expand` : 'Click to expand'}
            data-testid={`brief-preview-thumb-${kind}-${asset.id}`}
          >
            <img src={resolveUrl(previewUrl)} alt="Brief preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Search className="w-4 h-4 text-white" />
            </div>
          </button>
        ) : null}
        <button
          onClick={generatePreview}
          disabled={previewLoading || !(text || '').trim()}
          title={previewUrl
            ? 'Regenerate the preview thumbnail (gpt-image-2 · low quality · ~$0.006)'
            : 'Generate a quick thumbnail of this brief (gpt-image-2 · low quality · ~$0.006)'}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-sky-500/15 text-sky-200 hover:bg-sky-500/30 hover:text-sky-100 text-[10px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`brief-preview-gen-btn-${kind}-${asset.id}`}
        >
          {previewLoading
            ? <><Loader2 className="w-3 h-3 animate-spin" /> Rendering…</>
            : <><Sparkles className="w-3 h-3" /> {previewUrl ? 'Regenerate preview' : 'Generate preview'}</>}
        </button>
        {previewUrl && (
          <span className="text-[9px] text-white/30 uppercase tracking-wider">gpt-image-2 · low</span>
        )}
      </div>
      {/* Lightbox — full-size view, click backdrop / Esc / X to close */}
      {previewExpanded && previewUrl && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/85 p-6"
          onClick={() => setPreviewExpanded(false)}
          data-testid={`brief-preview-lightbox-${kind}-${asset.id}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewExpanded(false); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid={`brief-preview-close-${kind}-${asset.id}`}
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="max-w-4xl max-h-[90vh] flex flex-col gap-3 items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveUrl(previewUrl)}
              alt="Brief preview (expanded)"
              className="max-w-full max-h-[80vh] rounded-lg shadow-2xl border border-white/10"
            />
            <div className="text-[11px] text-white/50 flex items-center gap-3">
              <span className="font-medium text-white/80">{displayName}</span>
              <span>·</span>
              <span>{kind === 'cast' ? 'Casting' : kind === 'costume' ? 'Costume' : 'Art Direction'} brief preview</span>
              <span>·</span>
              <span>gpt-image-2 LOW</span>
              {previewAt && <><span>·</span><span>{new Date(previewAt).toLocaleString()}</span></>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AssetCard({ candidate, label, category, index, projectId, onUpdate, overrideImageUrl, overrideLabel, onImageClick, onRefresh, brandDeck, assetBible }) {
  const [imgError, setImgError] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [refPreview, setRefPreview] = useState(null);
  const [deckPickerOpen, setDeckPickerOpen] = useState(false);
  const [pickingFromDeck, setPickingFromDeck] = useState(false);
  const [slog3Regenerating, setSlog3Regenerating] = useState(false);
  const fileRef = useRef(null);
  const refInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // P1-9: "Pick from Brand Deck" available for cast / costumes / locations
  // when the project has at least one deck page uploaded. Each category hits
  // a different backend endpoint with the appropriate semantics:
  //   cast      → /cast/:idx/use-deck-page    (direct save; model headshots)
  //   costume   → /costumes/:idx/use-deck-page (flat-lay refinement)
  //   location  → /locations/:idx/use-deck-page (direct save; environment refs)
  const canPickFromDeck = (
    ['cast_candidates', 'costume_candidates', 'corner_candidates'].includes(category)
    && Array.isArray(brandDeck) && brandDeck.length > 0
  );

  const useDeckPage = async (deckPage) => {
    setPickingFromDeck(true);
    try {
      const subPath = (
        category === 'cast_candidates'    ? `cast/${index}/use-deck-page` :
        category === 'corner_candidates'  ? `locations/${index}/use-deck-page` :
        /* costume_candidates */            `costumes/${index}/use-deck-page`
      );
      const res = await axios.post(
        `${API}/projects/${projectId}/${subPath}`,
        { deck_id: deckPage.id },
      );
      if (res.data?.success) {
        toast.success('Reference updated from deck page');
        setDeckPickerOpen(false);
        onUpdate();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to use deck page');
    } finally {
      setPickingFromDeck(false);
    }
  };

  // 2026-08-12 (Rohit: "cast images look very low quality") — `candidate.url`
  // is the S-Log3 (cinema log color space) version: intentionally flat,
  // desaturated and low-contrast until graded — that's what was making
  // mugshots look washed-out/low-quality in the UI. `candidate.colored_url`
  // is the normal, properly-graded Rec.709 version. S-Log3 stays untouched
  // on the backend (still used as-is for actual video-gen keyframe
  // consistency via shot_asset_resolver.py) — this only changes what's
  // shown for human review here.
  // NOTE: any backend write of `url` on a cast/costume/corner candidate MUST
  // also write `colored_url` to the same value, or this display line will
  // silently keep rendering a stale image forever. See server.py upload
  // (2026-08-17) + asset-regenerate/asset-feedback write sites.
  const rawCandidateUrl = candidate.colored_url || candidate.url;
  const displayUrl = overrideImageUrl ? resolveUrl(overrideImageUrl) : (rawCandidateUrl && rawCandidateUrl.startsWith('/') ? resolveUrl(rawCandidateUrl) : rawCandidateUrl);
  const imgUrl = rawCandidateUrl && rawCandidateUrl.startsWith('/') ? resolveUrl(rawCandidateUrl) : rawCandidateUrl;
  const hasValidUrl = rawCandidateUrl && (rawCandidateUrl.startsWith('/api/storage/') || rawCandidateUrl.startsWith('https://'));
  
  // Handle refresh/regenerate
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/projects/${projectId}/asset-regenerate`, { category, index });
      toast.success('Regenerating asset...');
      // Start polling for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/projects/${projectId}/asset-iteration-status?category=${category}&index=${index}`);
          if (!statusRes.data.iterating) {
            clearInterval(pollInterval);
            setRefreshing(false);
            setImgError(false);
            onUpdate?.();
          }
        } catch (e) {
          clearInterval(pollInterval);
          setRefreshing(false);
        }
      }, 2000);
      // Timeout after 60 seconds
      setTimeout(() => { clearInterval(pollInterval); setRefreshing(false); }, 60000);
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
      setRefreshing(false);
    }
  };

  const toggleVoice = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { 
      alert('Voice input not supported in this browser. Please use Chrome on Android or Safari on iOS.'); 
      return; 
    }
    
    if (isListening) { 
      recognitionRef.current?.stop(); 
      setIsListening(false); 
      return; 
    }
    
    // Request microphone permission first (important for mobile)
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      alert('Microphone access denied. Please allow microphone access in your browser settings.');
      return;
    }
    
    const r = new SR(); 
    r.continuous = false;  // Changed to false for better mobile support
    r.interimResults = true; 
    r.lang = 'en-US';
    recognitionRef.current = r;
    
    let final = feedback;
    r.onresult = (e) => { 
      let interim = ''; 
      for (let i = e.resultIndex; i < e.results.length; i++) { 
        if (e.results[i].isFinal) {
          final += (final ? ' ' : '') + e.results[i][0].transcript; 
        } else {
          interim += e.results[i][0].transcript; 
        }
      } 
      setFeedback(final + (interim ? ' ' + interim : '')); 
    };
    r.onend = () => setIsListening(false);
    r.onerror = (e) => {
      console.error('Speech recognition error:', e.error);
      setIsListening(false);
      if (e.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone access.');
      } else if (e.error === 'no-speech') {
        // Silently ignore - user just didn't speak
      } else {
        alert(`Voice input error: ${e.error}`);
      }
    };
    
    try {
      r.start(); 
      setIsListening(true);
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      alert('Failed to start voice input. Please try again.');
    }
  };

  const sendFeedback = async () => {
    if (!feedback.trim() && !refPreview) return;
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); }
    setLoading(true);
    try {
      const payload = { category, index, feedback };
      // Include reference image if attached
      if (refPreview) {
        payload.reference_image = refPreview.split(',')[1]; // Strip data:image/...;base64, prefix
      }
      const res = await axios.post(`${API}/projects/${projectId}/asset-feedback`, payload);
      if (res.data.processing) {
        // Start polling for completion with 4-minute timeout
        let pollCount = 0;
        const maxPolls = 120; // 120 * 2s = 4 min timeout
        const pollInterval = setInterval(async () => {
          pollCount++;
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setLoading(false);
            alert('Iteration timed out. The image may still be processing — refresh the page to check.');
            onUpdate();
            return;
          }
          try {
            const statusRes = await axios.get(`${API}/projects/${projectId}/asset-iteration-status?category=${category}&index=${index}`);
            if (!statusRes.data.iterating) {
              clearInterval(pollInterval);
              setLoading(false);
              if (statusRes.data.error) {
                alert('Iteration failed: ' + statusRes.data.error);
              } else {
                onUpdate();
                setFeedback('');
                setRefPreview(null);
                setShowFeedback(false);
              }
            }
          } catch (e) {
            clearInterval(pollInterval);
            setLoading(false);
            console.error('Poll error:', e);
          }
        }, 2000);
      } else if (res.data.success) {
        onUpdate(); setFeedback(''); setShowFeedback(false);
        setLoading(false);
      }
    } catch (e) { 
      alert('Failed: ' + (e.response?.data?.detail || e.message)); 
      setLoading(false);
    }
  };

  const uploadReference = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append('category', category);
      form.append('index', index);
      form.append('file', file);
      const res = await axios.post(`${API}/projects/${projectId}/asset-upload-reference`, form);
      if (!res.data.success) throw new Error('Upload failed');
      onUpdate();
      if (!res.data.processing) {
        setLoading(false);
        toast.success('Reference uploaded');
        return;
      }
      // Cast/costume uploads run AI refinement (mugshot / flat-lay) in the
      // background — poll the same way "Refresh" does instead of hanging
      // on the initial request (2026-08-17 fix: this used to run inline
      // and could take 20-30s with zero feedback, looking broken).
      toast.message('Uploaded — refining reference...');
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/projects/${projectId}/asset-iteration-status?category=${category}&index=${index}`);
          if (!statusRes.data.iterating) {
            clearInterval(pollInterval);
            setLoading(false);
            onUpdate();
            if (statusRes.data.error) {
              toast.error('Reference refinement failed: ' + statusRes.data.error);
            } else {
              toast.success('Reference refined');
            }
          }
        } catch (err) {
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, 2000);
      setTimeout(() => {
        clearInterval(pollInterval);
        setLoading(false);
        toast.message('Still refining in the background — the card will update once it finishes.');
      }, 90000);
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  return (
    <div className="bg-ai-bg-secondary rounded-xl border border-ai-border shadow-sm hover:shadow-md transition-all" data-testid={`asset-card-${candidate.id}`}>
      <div className="aspect-square glass-panel flex items-center justify-center relative overflow-hidden flex-shrink-0">
        {/* Loading overlay for loading, refreshing, or backend iterating */}
        {(loading || refreshing || candidate.iterating) && (
          <div className="absolute inset-0 bg-ai-bg/70 z-10 flex flex-col items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-ai-accent" />
            {candidate.iterating && <span className="text-[9px] text-ai-accent mt-1">Iterating...</span>}
          </div>
        )}
        {hasValidUrl && !imgError ? (
          <img src={displayUrl} alt={label} loading="lazy" decoding="async" className="w-full h-full object-cover cursor-pointer" onError={() => setImgError(true)}
            onClick={() => onImageClick?.(displayUrl, label)} />
        ) : candidate.status === 'failed' || imgError ? (
          // ── FAILED — keep red error treatment with retry CTA ────────
          <div
            className="text-center p-4 bg-red-500/5"
            title={candidate.error || 'Generation failed — click refresh to retry this item'}
            data-testid={`asset-card-failed-${index}`}
          >
            <Image className="w-8 h-8 mx-auto mb-2 text-red-400/70" />
            <span className="text-xs font-medium text-red-400">Failed</span>
            {candidate.error && (
              <div className="text-[9px] text-white/50 mt-1 px-1 line-clamp-2 leading-tight">
                {candidate.error}
              </div>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="mt-2 p-2 rounded-lg transition-colors bg-red-500/20 hover:bg-red-500/40"
              title="Retry this failed item"
              data-testid={`asset-refresh-${index}`}
            >
              <RefreshCw className={`w-4 h-4 text-red-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        ) : (
          // ── STUB / IN-FLIGHT — cinematic placeholder (2026-05-18 polish) ──
          // Replaces the "broken image" error-stub UI with a clean placeholder
          // that signals "preparing" instead of "something went wrong". Initials
          // for cast, glyph for costume/corner. Subtle pulse. No retry button —
          // the canonical trigger is the bulk "Generate Asset Bible" CTA.
          <div
            className="relative w-full h-full flex flex-col items-center justify-center p-4 overflow-hidden"
            title="Awaiting generation — click 'Generate Asset Bible' to render"
            data-testid={`asset-card-generating-${index}`}
          >
            {/* Soft gradient + subtle moving shimmer */}
            <div className="absolute inset-0 bg-gradient-to-br from-ai-accent/[0.07] via-white/[0.02] to-transparent" />
            <div className="absolute inset-0 opacity-[0.04] [background:radial-gradient(circle_at_30%_30%,white_0%,transparent_50%)]" />
            {/* Glyph or initials */}
            {(() => {
              const isCast = category === 'cast_candidates';
              const isCostume = category === 'costume_candidates';
              const isCorner = category === 'corner_candidates';
              if (isCast) {
                const initials = (candidate.name || '?')
                  .split(/\s+/)
                  .slice(0, 2)
                  .map(w => w[0]?.toUpperCase() || '')
                  .join('') || '?';
                return (
                  <div className="relative w-14 h-14 rounded-full bg-ai-accent/10 border border-ai-accent/30 flex items-center justify-center mb-3 animate-pulse">
                    <span className="text-lg font-semibold text-ai-accent/90 tracking-wide">{initials}</span>
                  </div>
                );
              }
              const Glyph = isCostume ? Shirt : isCorner ? MapPin : Image;
              return (
                <div className="relative w-14 h-14 rounded-full bg-ai-accent/10 border border-ai-accent/30 flex items-center justify-center mb-3 animate-pulse">
                  <Glyph className="w-6 h-6 text-ai-accent/80" />
                </div>
              );
            })()}
            <span className="relative text-[10px] text-ai-accent/70 font-medium tracking-wider uppercase">Awaiting render</span>
            <span className="relative text-[9px] text-white/30 mt-1">Click "Generate Asset Bible" below</span>
          </div>
        )}
        {overrideLabel && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-ai-accent/80 rounded text-[8px] text-white font-medium">
            {overrideLabel}
          </div>
        )}
        {!overrideLabel && hasValidUrl && !imgError && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 rounded text-[8px] text-white font-medium">
            Character Reference
          </div>
        )}
        {/* Refresh + Delete buttons in corner for successful images too */}
        {hasValidUrl && !imgError && (
          <div className="absolute top-2 right-2 flex items-center gap-1">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-1.5 rounded-lg bg-black/50 hover:bg-black/70 transition-colors"
              title="Regenerate this asset"
              data-testid={`asset-refresh-${index}`}
            >
              <RefreshCw className={`w-3 h-3 text-white ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {['cast_candidates', 'costume_candidates', 'corner_candidates'].includes(category) && (
              <button
                onClick={async () => {
                  if (slog3Regenerating) return;
                  setSlog3Regenerating(true);
                  try {
                    const r = await axios.post(`${API}/projects/${projectId}/asset-regenerate-slog3`, {
                      category, index,
                    });
                    toast.success(r.data?.message || 'S-Log3 regenerated');
                    onUpdate && onUpdate();
                  } catch (e) {
                    toast.error('S-Log3 regen failed: ' + (e.response?.data?.detail || e.message));
                  } finally {
                    setSlog3Regenerating(false);
                  }
                }}
                disabled={slog3Regenerating}
                className={`p-1.5 rounded-lg transition-colors ${candidate.slog3_error ? 'bg-amber-500/60 hover:bg-amber-500/80' : 'bg-black/50 hover:bg-black/70'}`}
                title={candidate.slog3_error
                  ? `S-Log3 failed on last gen: ${candidate.slog3_error} — click to retry`
                  : 'Force-regenerate S-Log3 from the Rec.709 colored master'}
                data-testid={`asset-force-slog3-${category}-${index}`}
              >
                <Sparkles className={`w-3 h-3 text-white ${slog3Regenerating ? 'animate-pulse' : ''}`} />
              </button>
            )}
            <button
              onClick={async () => {
                if (!candidate?.id) return;
                const kindMap = { cast_candidates: 'cast', costume_candidates: 'costume', corner_candidates: 'corner' };
                const kind = kindMap[category];
                if (!kind) return;
                if (!confirm(`Delete this ${kind} permanently? This will also clear bindings on every shot pointing at it. The Asset Bible will need to be re-saved before moving on.`)) return;
                try {
                  const r = await axios.delete(`${API}/projects/${projectId}/asset-bible/${kind}/${candidate.id}`);
                  toast.success(`Deleted "${r.data?.deleted_name || 'asset'}" (${r.data?.shots_cascaded || 0} shot binding${(r.data?.shots_cascaded || 0) === 1 ? '' : 's'} cleared) — re-save the Asset Bible before continuing.`);
                  onUpdate && onUpdate();
                } catch (e) {
                  toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
                }
              }}
              className="p-1.5 rounded-lg bg-red-500/40 hover:bg-red-500/60 transition-colors"
              title="Delete this asset (cascades to shot bindings)"
              data-testid={`asset-delete-${category}-${index}`}
            >
              <Trash2 className="w-3 h-3 text-white" />
            </button>
          </div>
        )}
      </div>
      <div className="p-3">
        {(() => {
          // 2026-05-25 — Always show the operator-assigned canonical
          // display_name (e.g. "Maya") when set, falling back to the
          // role name. Costumes get the same treatment by swapping the
          // "<role> — Scene N" prefix with "<display_name> — Scene N".
          // The operator typed these names in Stage 2's bindings panel
          // for the explicit purpose of identity anchoring — so they
          // MUST show up everywhere the user sees a tile title.
          let titleName = candidate.name;
          if (!titleName) return null;
          if (category === 'cast_candidates') {
            titleName = (candidate.display_name || '').trim() || candidate.name;
          } else if (category === 'costume_candidates' && assetBible) {
            const owner = (assetBible.cast_candidates || []).find(
              c => c?.id === candidate.for_cast
            );
            const ownerDisplay = (owner?.display_name || '').trim();
            const ownerRole = (owner?.name || '').trim();
            if (ownerDisplay && ownerRole && titleName.toLowerCase().startsWith(ownerRole.toLowerCase())) {
              titleName = ownerDisplay + titleName.slice(ownerRole.length);
            }
          }
          return <div className="text-xs font-semibold text-white mb-0.5 truncate" title={candidate.name}>{titleName}</div>;
        })()}
        <BoundShotsBadge projectId={projectId} entityId={candidate.id} />
        {category === 'cast_candidates' && candidate.id && candidate.url && (
          <div className="mb-1">
            <CastFaceRefControl projectId={projectId} candidate={candidate} />
          </div>
        )}
        <p className="text-[10px] text-white/50 line-clamp-2 mb-2 cursor-pointer hover:text-white/70 transition-colors"
          onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-2'); }}
        >{candidate.prompt?.slice(0, 80)}</p>
        {hasValidUrl ? (
          <div className="flex gap-1.5">
            <button onClick={() => setShowFeedback(!showFeedback)} data-testid={`asset-feedback-toggle-${index}`}
              title="Iterate — refine this image"
              className="flex-1 h-7 rounded-lg glass-panel border border-ai-border hover:bg-white/10 transition-colors text-ai-accent flex items-center justify-center">
              <Wand2 className="w-3.5 h-3.5" />
            </button>
            {category === 'cast_candidates' && (
              <button
                onClick={async () => {
                  // One-click "Fix Skin" pass — sends a fixed prompt to the
                  // existing /asset-feedback endpoint. Uses the same polling
                  // logic as manual iterate so we don't duplicate flow.
                  // Prompt is intentionally short and identity-safe:
                  //   "make skin fairer + realistic with minor pores and blemishes"
                  // (2026-07-08 per operator request — fine-tunes cast skin
                  // tone which the Step 4/5 pipeline sometimes leaves warm.)
                  if (loading) return;
                  setLoading(true);
                  try {
                    const res = await axios.post(`${API}/projects/${projectId}/asset-feedback`, {
                      category,
                      index,
                      feedback: 'make skin fairer and realistic with minor pores and blemishes; preserve identity exactly — do not change gender, age, hair, or facial features',
                    });
                    if (res.data.processing) {
                      let pollCount = 0;
                      const maxPolls = 60;
                      const iv = setInterval(async () => {
                        pollCount++;
                        if (pollCount >= maxPolls) {
                          clearInterval(iv);
                          setLoading(false);
                          toast.error('Fair-skin pass timed out. Refresh to check status.');
                          onUpdate();
                          return;
                        }
                        try {
                          const s = await axios.get(`${API}/projects/${projectId}/asset-iteration-status?category=${category}&index=${index}`);
                          if (!s.data.iterating) {
                            clearInterval(iv);
                            setLoading(false);
                            if (s.data.error) {
                              toast.error('Fair-skin pass failed: ' + s.data.error);
                            } else {
                              toast.success('Skin brightened');
                              onUpdate();
                            }
                          }
                        } catch (err) {
                          clearInterval(iv);
                          setLoading(false);
                          console.error('Fair-skin poll error:', err);
                        }
                      }, 2000);
                    } else if (res.data.success) {
                      toast.success('Skin brightened');
                      onUpdate();
                      setLoading(false);
                    }
                  } catch (err) {
                    setLoading(false);
                    toast.error('Fair-skin pass failed: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                disabled={loading}
                data-testid={`asset-fair-skin-${index}`}
                title="Fair skin pass — one-click brighten with natural pores and blemishes preserved"
                className="h-7 w-7 rounded-lg glass-panel border border-amber-400/40 hover:bg-amber-400/10 transition-colors text-amber-300 flex items-center justify-center disabled:opacity-40"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sun className="w-3.5 h-3.5" />}
              </button>
            )}
            {((candidate.url_history && candidate.url_history.length > 0) ||
              (candidate.original_url && candidate.original_url !== candidate.url)) && (
              <button onClick={async () => {
                setLoading(true);
                try {
                  const res = await axios.post(`${API}/projects/${projectId}/asset-undo`, { category, index });
                  toast.success(res.data?.message || 'Reverted');
                  onUpdate();
                } catch (err) { alert(err.response?.data?.detail || 'Undo failed'); }
                setLoading(false);
              }} data-testid={`asset-undo-${index}`}
                title="Undo — revert to previous version"
                className="h-7 w-7 rounded-lg glass-panel border border-yellow-500/30 hover:bg-yellow-500/10 transition-colors text-yellow-400 flex items-center justify-center font-bold text-[10px]">
                ↶
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} data-testid={`asset-upload-ref-${index}`}
              className="h-7 w-7 rounded-lg glass-panel border border-ai-accent/40 hover:bg-ai-accent/10 transition-colors text-ai-accent flex items-center justify-center" title="Upload reference image from your computer">
              <UploadCloud className="w-3.5 h-3.5" />
            </button>
            {canPickFromDeck && (
              <button onClick={() => setDeckPickerOpen(true)} data-testid={`asset-use-deck-${category}-${index}`}
                className="h-7 w-7 rounded-lg glass-panel border border-purple-400/40 hover:bg-purple-400/10 transition-colors text-purple-300 flex items-center justify-center"
                title="Pick a page from the uploaded brand deck as the reference">
                <Image className="w-3.5 h-3.5" />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadReference} />
          </div>
        ) : candidate.status !== 'failed' ? (
          // ── STUB action row — Generate + Upload Ref per card (icon-only) ──
          <div className="flex gap-1.5">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid={`asset-generate-stub-${index}`}
              className="flex-1 h-7 rounded-lg bg-ai-accent text-white hover:bg-ai-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              title="Render just this card now"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid={`asset-upload-ref-${index}`}
              className="h-7 w-7 rounded-lg glass-panel border border-ai-accent/40 hover:bg-ai-accent/10 transition-colors text-ai-accent flex items-center justify-center" title="Upload reference image from your computer">
              <UploadCloud className="w-3.5 h-3.5" />
            </button>
            {canPickFromDeck && (
              <button onClick={() => setDeckPickerOpen(true)} data-testid={`asset-use-deck-stub-${category}-${index}`}
                className="h-7 w-7 rounded-lg glass-panel border border-purple-400/40 hover:bg-purple-400/10 transition-colors text-purple-300 flex items-center justify-center"
                title="Pick a page from the uploaded brand deck as the reference">
                <Image className="w-3.5 h-3.5" />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadReference} />
          </div>
        ) : null}
        {canPickFromDeck && (
          <DeckPicker
            open={deckPickerOpen}
            projectId={projectId}
            onPick={useDeckPage}
            onClose={() => setDeckPickerOpen(false)}
            isPicking={pickingFromDeck}
          />
        )}
        {showFeedback && (
          <div className="mt-2 space-y-1.5">
            <div className="flex gap-1">
              <button onClick={toggleVoice} title={isListening ? "Stop recording" : "Voice input"} data-testid={`asset-voice-btn-${index}`}
                className={`p-1 rounded-lg border flex-shrink-0 transition-all ${isListening ? 'border-[#FF3B30] bg-[#FF3B30]/10 animate-pulse' : 'border-ai-border bg-ai-bg-secondary hover:glass-panel'}`}>
                {isListening ? <div className="w-3 h-3 rounded-sm bg-[#FF3B30]" /> : <svg className="w-3 h-3 text-ai-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>}
              </button>
              <input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder={isListening ? "Listening..." : "Describe changes or upload ref image..."}
                className={`flex-1 text-[10px] px-2 py-1 text-white glass-panel border rounded-lg outline-none ${isListening ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-ai-border focus:border-ai-accent'}`}
                onKeyDown={e => e.key === 'Enter' && sendFeedback()} data-testid={`asset-feedback-input-${index}`} />
              <button onClick={() => refInputRef.current?.click()} title="Attach reference image"
                className="p-1 rounded-lg border border-ai-border hover:bg-white/10 transition-colors text-ai-accent flex-shrink-0">
                <ImagePlus className="w-3 h-3" />
              </button>
              <input ref={refInputRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                // Resize to max 800px to avoid 413 payload too large
                const img = new window.Image();
                img.onload = () => {
                  const maxSize = 800;
                  let w = img.width, h = img.height;
                  if (w > maxSize || h > maxSize) {
                    if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
                    else { w = Math.round(w * maxSize / h); h = maxSize; }
                  }
                  const canvas = document.createElement('canvas');
                  canvas.width = w; canvas.height = h;
                  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                  setRefPreview(canvas.toDataURL('image/jpeg', 0.7));
                };
                img.src = URL.createObjectURL(file);
              }} />
              <button onClick={sendFeedback} disabled={loading || (!feedback.trim() && !refPreview)} data-testid={`asset-feedback-send-${index}`}
                className="p-1 rounded-lg bg-ai-accent text-white disabled:opacity-40">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
            {refPreview && (
              <div className="flex items-center gap-2 p-1 rounded-lg bg-white/[0.03] border border-white/10">
                <img src={refPreview} alt="ref" className="w-8 h-8 rounded object-cover" />
                <span className="text-[9px] text-white/40 flex-1">Reference attached</span>
                <button onClick={() => setRefPreview(null)} className="text-red-400/60 hover:text-red-400"><X className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════ SKETCH CARD WITH REFRESH + FEEDBACK ═══════════ */
function SketchCard({ shot, onRefresh, onReload, onFeedback, onUndo, onDelete, onImageClick, isGenerating, aspectClass = 'aspect-video', projectId, selectable = false, selected = false, onToggleSelect, castTierMap }) {
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [show3D, setShow3D] = useState(false);
  const [generating3D, setGenerating3D] = useState(false);
  const [showSamModal, setShowSamModal] = useState(false);
  const [genHRKf, setGenHRKf] = useState(false);
  // When an Insanely Good Keyframe exists, the user can toggle to it by
  // clicking the Sparkles icon. Default view is ALWAYS the sketch — the
  // sketch stage is for sketch review, the keyframe is a peek behind the
  // curtain accessed on demand.
  const [showKeyframeView, setShowKeyframeView] = useState(false);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // ── Veo 3.1 Animatic on sketches (backend prefers sketch_url over keyframe_url) ──
  const {
    animaticProcessing, animaticUrl, animaticError, showAnimatic,
    animaticElapsed, setShowAnimatic, triggerAnimatic,
  } = useAnimatic(shot, projectId);

  const has3D = !!shot.world_3d_id;
  const world3DUrl = shot.world_3d_url;
  const panoUrl = shot.world_3d_pano_url;

  const toggle3D = async () => {
    if (has3D) {
      // Direct navigate — no panorama
      if (window.confirm('This will open the 3D viewer on World Labs.\n\nA free World Labs account is needed to explore the 3D world.\n\nContinue?')) {
        window.open(world3DUrl, '_blank');
      }
      return;
    }
    // Generate 3D
    setGenerating3D(true);
    try {
      const API = window.__API_BASE + '/api';
      await axios.post(`${API}/projects/${projectId}/shots/${shot.id}/generate-3d`, { mode: 'draft' });
      toast.info('Generating 3D world — this takes 15-30 seconds...');
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/projects/${projectId}/shots/${shot.id}/3d-status`);
          if (res.data.has_3d) {
            clearInterval(poll);
            setGenerating3D(false);
            shot.world_3d_id = res.data.world_url?.split('/').pop();
            shot.world_3d_url = res.data.world_url;
            shot.world_3d_pano_url = res.data.pano_url;
            toast.success('3D world ready — click 3D button to explore');
          } else if (!res.data.processing && res.data.error) {
            clearInterval(poll);
            setGenerating3D(false);
            toast.error('3D failed: ' + res.data.error);
          }
        } catch(e) {}
      }, 4000);
      setTimeout(() => { clearInterval(poll); setGenerating3D(false); }, 300000);
    } catch(e) {
      setGenerating3D(false);
      toast.error('3D generation failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const captureAngle = async () => {
    toast.info('Use your device screenshot tool to capture this 3D view, then upload via the Iterate button');
  };

  const doRefresh = async () => {
    setRefreshing(true);
    await onRefresh(shot.id);
    setRefreshing(false);
  };

  // ── Generate / Regenerate Insanely Good Keyframe from THIS sketch ──
  // Only surfaced on "Insanely Good" sketches (sketch_engine = gpt-image-2*).
  // Hits the per-shot IGK endpoint so the user doesn't have to run the
  // whole project pipeline to refresh one card.
  // (Removed previous auto-flip-to-keyframe effect — the sketch stage
  // intentionally always defaults to the sketch view. Toggle is manual.)

  const regenHyperrealKeyframe = async () => {
    if (genHRKf || shot.keyframe_hyperreal_status === 'generating') return;
    setGenHRKf(true);
    try {
      const API_BASE = window.__API_BASE + '/api';
      await axios.post(`${API_BASE}/shots/${shot.id}/regenerate-hyperreal-keyframe`, {});
      toast.info(shot.keyframe_hyperreal_url
        ? 'Regenerating Insanely Good keyframe…'
        : 'Generating Insanely Good keyframe — typically 1–3 minutes.');
      // Poll per-shot iteration-status every 5s (cap 5 min). When the
      // IGK status flips off "generating", reload so the new
      // keyframe_hyperreal_url surfaces on the keyframe card.
      const maxPolls = 60;
      let pollCount = 0;
      const poll = setInterval(async () => {
        pollCount++;
        try {
          const res = await axios.get(`${API_BASE}/shots/${shot.id}/iteration-status`);
          const hr = res.data?.keyframe_hyperreal || {};
          if (!hr.generating) {
            clearInterval(poll);
            setGenHRKf(false);
            await onReload?.();
            if (hr.error) {
              toast.error('IGK regen failed: ' + hr.error);
            } else if (hr.url) {
              toast.success('Insanely Good keyframe ready');
            }
            return;
          }
        } catch {}
        if (pollCount >= maxPolls) {
          clearInterval(poll);
          setGenHRKf(false);
          await onReload?.();
          toast.warning('Still working — check the keyframe card in a minute.');
        }
      }, 5000);
    } catch (e) {
      setGenHRKf(false);
      toast.error('Failed to start IGK regen: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ── IGK-2P two-pass regen (Wave 2) ──────────────────────────────────
  // Composition-lock (neutral) pass → grade pass, each VLM-judged. Fires
  // the feature-flagged endpoint with force=true so it works in preview
  // even when IGK2P_ENABLED is off on the server. Same poll/reload shape
  // as the single-pass IGK regen above.
  const regenIGK2P = async () => {
    if (genHRKf || shot.keyframe_hyperreal_status === 'generating') return;
    setGenHRKf(true);
    try {
      const API_BASE = window.__API_BASE + '/api';
      await axios.post(`${API_BASE}/shots/${shot.id}/regenerate-keyframe-igk2p`, { force: true });
      toast.info('IGK-2P two-pass render started — composition lock, then grade (2–5 min).');
      const maxPolls = 90;
      let pollCount = 0;
      const poll = setInterval(async () => {
        pollCount++;
        try {
          const res = await axios.get(`${API_BASE}/shots/${shot.id}/iteration-status`);
          const hr = res.data?.keyframe_hyperreal || {};
          if (!hr.generating) {
            clearInterval(poll);
            setGenHRKf(false);
            await onReload?.();
            if (hr.error) toast.error('IGK-2P failed: ' + hr.error);
            else if (hr.url) toast.success('IGK-2P two-pass keyframe ready');
            return;
          }
        } catch {}
        if (pollCount >= maxPolls) {
          clearInterval(poll);
          setGenHRKf(false);
          await onReload?.();
          toast.warning('IGK-2P still working — check the card in a minute.');
        }
      }, 5000);
    } catch (e) {
      setGenHRKf(false);
      const msg = e.response?.data?.detail || e.message;
      toast.error('Failed to start IGK-2P: ' + msg);
    }
  };


  // ── KEYFRAME+ single-shot trigger ─────────────────────────────────────
  // Re-fires the K+ pipeline for THIS shot (composer → gpt-image-2 →
  // optional VLM). Used by:
  //   • the FAILED banner's "Retry K+" button
  //   • the icon row's "Re-fire K+" button (visible when K+ V1 already
  //     exists OR when the previous K+ attempt failed)
  // Polls /keyframe-plus-status every 5s until terminal (done/failed),
  // then reloads the card so the new V2 surfaces.
  const fireKeyframePlusForShot = async () => {
    const inFlight = ['queued','composing','generating','auditing'].includes(shot.keyframe_plus_status || '');
    if (inFlight) {
      toast.info('Keyframe+ already running on this shot.');
      return;
    }
    try {
      const API_BASE = window.__API_BASE + '/api';
      await axios.post(`${API_BASE}/shots/${shot.id}/keyframe-plus`, {
        quality: 'high',
        user_notes: '',
        look_feel_refs: [],
      });
      toast.success(shot.keyframe_plus_url
        ? 'Re-firing Keyframe+ (Photo engine) — V2 will land in 60–180s.'
        : 'Keyframe+ queued — typically 90–180s.');
      const maxPolls = 60;
      let pollCount = 0;
      const poll = setInterval(async () => {
        pollCount++;
        try {
          const res = await axios.get(`${API_BASE}/shots/${shot.id}/keyframe-plus-status`);
          const s = res.data || {};
          if (s.status === 'done' || s.status === 'failed') {
            clearInterval(poll);
            await onReload?.();
            if (s.status === 'failed') toast.error('Keyframe+ failed: ' + (s.error || 'unknown'));
            else toast.success('Keyframe+ V' + (s.version_count || 1) + ' ready');
            return;
          }
        } catch {}
        if (pollCount >= maxPolls) {
          clearInterval(poll);
          await onReload?.();
          toast.warning('Still working — check this card in a minute.');
        }
      }, 5000);
    } catch (e) {
      toast.error('Failed to start Keyframe+: ' + (e.response?.data?.detail || e.message));
    }
  };


  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRefImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setRefPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearRefImage = () => { setRefImage(null); setRefPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const toggleVoice = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { 
      alert('Voice input not supported in this browser. Please use Chrome on Android or Safari on iOS.'); 
      return; 
    }
    
    if (isListening) { 
      recognitionRef.current?.stop(); 
      setIsListening(false); 
      return; 
    }
    
    // Request microphone permission first (important for mobile)
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      alert('Microphone access denied. Please allow microphone access in your browser settings.');
      return;
    }
    
    const r = new SR();
    r.continuous = false;  // Better mobile support
    r.interimResults = true;
    r.lang = 'en-US';
    recognitionRef.current = r;
    
    let final = feedback;
    r.onresult = (e) => { 
      let interim = ''; 
      for (let i = e.resultIndex; i < e.results.length; i++) { 
        if (e.results[i].isFinal) {
          final += (final ? ' ' : '') + e.results[i][0].transcript; 
        } else {
          interim += e.results[i][0].transcript; 
        }
      } 
      setFeedback(final + (interim ? ' ' + interim : '')); 
    };
    r.onend = () => setIsListening(false);
    r.onerror = (e) => {
      console.error('Speech recognition error:', e.error);
      setIsListening(false);
      if (e.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone access.');
      } else if (e.error !== 'no-speech') {
        alert(`Voice input error: ${e.error}`);
      }
    };
    
    try {
      r.start();
      setIsListening(true);
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      alert('Failed to start voice input. Please try again.');
    }
  };

  const doFeedback = async () => {
    if (!feedback.trim() && !refImage) return;
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); }
    setSending(true);
    let refBase64 = null;
    if (refImage) { refBase64 = await new Promise((res) => { const r = new FileReader(); r.onload = (ev) => res(ev.target.result.split(',')[1]); r.readAsDataURL(refImage); }); }
    await onFeedback(shot.id, feedback, refBase64);
    setSending(false); setFeedback(''); setRefImage(null); setRefPreview(null); setShowFeedback(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      className={`glass-panel rounded-xl overflow-hidden relative ${selectable ? 'cursor-pointer' : ''} ${selectable && selected ? 'ring-2 ring-cyan-400 shadow-[0_0_0_1px_rgba(34,211,238,0.4)]' : ''} ${shot.keyframe_hyperreal_status === 'generating' ? 'ring-2 ring-amber-400 shadow-[0_0_24px_rgba(245,158,11,0.45)] animate-pulse' : ''}`}
      data-testid={`sketch-card-${shot.id}`}
      onClick={selectable ? (e) => {
        // Stop the click from hitting any inner image/button that would
        // open a lightbox / trigger a refresh while the user is just
        // trying to tick/untick a card for bulk delete.
        e.preventDefault();
        e.stopPropagation();
        onToggleSelect?.(shot.id);
      } : undefined}
    >
      {/* IGK GENERATING — unmissable card-level indicator. The old
       *  12px Loader2 inside the small ✨ corner button was easy to
       *  miss across a 28-card grid. This adds an amber pulsing ring
       *  on the card border + a top ribbon so the operator can spot
       *  the 3 shots being IGK-rendered at a glance.
       */}
      {shot.keyframe_hyperreal_status === 'generating' && (
        <div
          className="absolute top-0 left-0 right-0 z-30 bg-amber-500/90 text-amber-50 px-3 py-1 flex items-center justify-center gap-2 text-[11px] font-medium tracking-wide shadow-lg"
          data-testid={`igk-generating-banner-${shot.id}`}
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>GENERATING IGK · ~30–60s</span>
        </div>
      )}
      {/* Bulk-select checkbox overlay — top-RIGHT to avoid colliding with
          the drag-handle dots icon on the top-left of the card image. */}
      {selectable && (
        <div
          className={`absolute top-2 right-2 z-40 w-6 h-6 rounded-md flex items-center justify-center transition-all shadow-lg ${selected ? 'bg-cyan-500 text-white ring-2 ring-cyan-300/50' : 'bg-black/70 text-transparent border border-white/40 backdrop-blur-sm'}`}
          data-testid={`sketch-select-${shot.id}`}
        >
          {selected && <CheckCircle className="w-4 h-4" />}
        </div>
      )}
      <div className={`${aspectClass} glass-panel flex items-center justify-center relative overflow-hidden`}>
        {/* 3D Toggle Button — temporarily hidden (Rohit, 2026-05-13).
            World Labs 3D generation was producing inconsistent results and
            firing "3D generation failed: No image URL provided" errors on
            cards without sketches. Re-enable when the integration is fixed
            by flipping ENABLE_3D_BTN to true. */}
        {false && shot.sketch_url && (
        <button onClick={toggle3D} disabled={generating3D} data-testid={`3d-toggle-${shot.id}`}
          className={`absolute bottom-2 right-2 z-30 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 shadow-lg ${
            show3D ? 'bg-purple-600 text-white shadow-purple-500/40' :
            has3D ? 'bg-purple-500/30 text-purple-300 hover:bg-purple-500/50 border border-purple-500/30' :
            generating3D ? 'bg-purple-500/20 text-purple-400 animate-pulse' :
            'bg-black/60 text-white/80 hover:bg-black/80 border border-white/20'
          }`}>
          {generating3D ? <Loader2 className="w-3 h-3 animate-spin" /> : <Box className="w-3 h-3" />}
          3D
          {has3D && !show3D && <span className="w-1.5 h-1.5 rounded-full bg-purple-400 ml-0.5" />}
        </button>
        )}

        {/* DOP soft-stale badge — fires when DOP spec was edited at Stage 2
            AFTER this sketch was rendered. Operator manually re-fires from
            the existing Refresh button — we never auto-clobber iterations. */}
        {isSketchDopStale(shot) && (
          <div
            className="absolute top-2 left-2 z-30 px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-500/85 text-amber-50 shadow-md inline-flex items-center gap-1"
            title="DOP spec changed since this sketch was rendered. Click Refresh to re-render with the new directives."
            data-testid={`sketch-dop-stale-${shot.id}`}
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            DOP changed
          </div>
        )}
        {/* Loading overlay */}
        {(refreshing || sending || shot.sketch_iterating) && (
          <div className="absolute inset-0 bg-ai-bg/70 z-10 flex flex-col items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-ai-accent" />
            {shot.sketch_iterating && <span className="text-[9px] text-ai-accent mt-1">Iterating...</span>}
          </div>
        )}
        {shot.sketch_url && (() => {
          // When an IGK exists AND the toggle is in keyframe-view mode,
          // swap the image source to the photoreal output. The Sparkles
          // toggle flips this back to the sketch on demand.
          const showingKf = !!(showKeyframeView && shot.keyframe_hyperreal_url);
          const displayUrl = showingKf
            ? (shot.keyframe_hyperreal_s3_url || shot.keyframe_hyperreal_url)
            : (shot.sketch_s3_url || shot.sketch_url);
          const altLabel = showingKf ? 'Keyframe' : 'Sketch';
          return (
          showAnimatic && animaticUrl ? (
            <video
              src={animaticUrl}
              className="w-full h-full object-cover cursor-pointer"
              autoPlay loop muted playsInline
              data-testid={`sketch-animatic-video-${shot.id}`}
              onClick={(e) => { e.stopPropagation(); }}
            />
          ) : (
          <>
            <img src={resolveUrl(displayUrl)} alt={`${altLabel} ${shot.scene_number}-${shot.shot_number}`}
              loading="lazy" decoding="async"
              className="w-full h-full object-cover cursor-pointer" data-testid={`sketch-img-${shot.id}`}
              onClick={() => onImageClick?.(resolveUrl(displayUrl), `${altLabel} ${shot.scene_number}-${shot.shot_number}`)}
              onError={(e) => {
                // Non-destructive: retry up to 2x with cache-bust on transient S3/CDN blips,
                // then fade out (opacity-0) so the sibling fallback can show but the <img>
                // stays in DOM and can recover on re-render / next data refresh.
                const retries = parseInt(e.target.dataset.retries || '0', 10);
                if (retries < 2) {
                  e.target.dataset.retries = String(retries + 1);
                  const base = (e.target.src || '').split('?')[0];
                  setTimeout(() => { try { e.target.src = `${base}?r=${Date.now()}`; } catch {} }, 600 * (retries + 1));
                } else {
                  e.target.style.opacity = '0';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }
              }} />
            {/* Sketch | Preview toggle (2026-07-15) — appears once a keyframe
             *  render exists for this shot. Lets the CD flip the card image
             *  between the graphite sketch and the rendered keyframe inline,
             *  without leaving the sketch stage. Amber "PREVIEW" when the render
             *  is a preview-only (unlocked, not fed to video); green "KEYFRAME"
             *  when it's a real/locked keyframe. A pulsing dot on the Preview
             *  tab flags an unviewed render. */}
            {shot.keyframe_hyperreal_url && (
              <div
                className="absolute top-1.5 left-1.5 z-10 flex rounded-md overflow-hidden border border-white/20 bg-black/55 backdrop-blur-sm text-[7px] font-bold uppercase"
                data-testid={`sketch-view-toggle-${shot.id}`}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); setShowKeyframeView(false); }}
                  className={`px-1.5 py-px transition-colors ${!showingKf ? 'bg-white/25 text-white' : 'text-white/55 hover:text-white'}`}
                  data-testid={`sketch-view-sketch-${shot.id}`}
                >Sketch</button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowKeyframeView(true); }}
                  title={shot.keyframe_preview_only ? 'Preview render — unlocked, not fed to video' : 'Rendered keyframe'}
                  className={`relative px-1.5 py-px transition-colors ${showingKf ? (shot.keyframe_preview_only ? 'bg-amber-500/85 text-black' : 'bg-green-500/85 text-white') : 'text-white/55 hover:text-white'}`}
                  data-testid={`sketch-view-preview-${shot.id}`}
                >
                  {shot.keyframe_preview_only ? 'Preview' : 'Keyframe'}
                  {!showingKf && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400 ring-1 ring-black animate-pulse" />
                  )}
                </button>
              </div>
            )}
            {/* Pick variant (N) pill — Phase 3 (2026-05-19) ──
             *  Shows in the top-right when the shot has more than one
             *  sketch version in `sketch_versions[]` (Step 1 + Step 3,
             *  or any operator iterations on top). Clicking it opens
             *  the workbench at the active version. Stronger styling
             *  when `sketch_decision_required` is true (operator must
             *  pick a winner before downstream stages unlock — Phase 2
             *  will start setting this flag). */}
            {(() => {
              const vs = shot.sketch_versions || [];
              if (vs.length < 2) return null;
              const mustDecide = !!shot.sketch_decision_required;
              return (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowSamModal(true); }}
                  className={`absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded-sm text-[8px] font-bold uppercase shadow-sm transition-all hover:scale-105 ${
                    mustDecide
                      ? 'bg-amber-500/90 text-black animate-pulse'
                      : 'bg-cyan-500/85 text-white'
                  }`}
                  title={mustDecide ? `Pick a winner — ${vs.length} variants` : `${vs.length} variants available — open workbench to choose`}
                  data-testid={`sketch-pick-variant-${shot.id}`}
                >
                  {mustDecide ? `Pick (${vs.length})` : `${vs.length} variants`}
                </button>
              );
            })()}
            {/* Engine badge — bottom-left, mirrors keyframe card's GPT/GEM
             *  pattern so users can tell at a glance which sketch engine
             *  produced this frame. Hidden when in keyframe-view (the IGK
             *  has its own engine identity from the hyperreal pipeline). */}
            {!showingKf && shot.sketch_engine && (() => {
              const e = (shot.sketch_engine || '').toLowerCase();
              const isIGS = e.startsWith('gpt-image-2');
              const isGem = e.includes('gemini') || e.includes('nano-banana');
              const label = isIGS ? 'IGS' : (isGem ? 'GEM' : (shot.sketch_engine.length > 6 ? shot.sketch_engine.slice(0, 6).toUpperCase() : shot.sketch_engine.toUpperCase()));
              const tooltip = isIGS
                ? 'Sketch — graphite engine'
                : isGem
                  ? 'Sketch generated via Sketch engine'
                  : `Sketch engine: ${shot.sketch_engine}`;
              const color = isIGS ? '#10A37F' : (isGem ? '#A855F7' : '#94A3B8');
              const bg = isIGS ? 'bg-[#10A37F]/20' : (isGem ? 'bg-purple-500/20' : 'bg-slate-500/20');
              return (
                <div
                  className={`absolute bottom-1.5 left-1.5 z-10 px-1 py-px rounded-sm text-[7px] font-bold ${bg} border border-white/10 shadow-sm flex items-center gap-px`}
                  style={{ color }}
                  title={tooltip}
                  data-testid={`sketch-engine-badge-${shot.id}`}
                >
                  {isIGS ? <Sparkles className="w-1.5 h-1.5" /> : <Wand2 className="w-1.5 h-1.5" />}
                  <span>{label}</span>
                </div>
              );
            })()}
          </>
          )
          );
        })()}

        <div className="w-full h-full flex flex-col items-center justify-center" style={{ display: shot.sketch_url ? 'none' : 'flex' }}>
          {shot.sketch_url ? (
            <><AlertCircle className="w-5 h-5 text-yellow-400/60 mb-1" /><span className="text-[10px] text-yellow-400/60">Reload needed</span></>
          ) : (isGenerating || shot.sketch_status === 'generating') ? (
            <><Loader2 className="w-5 h-5 text-ai-accent animate-spin mb-1" /><span className="text-[10px] text-ai-accent font-medium">Generating...</span></>
          ) : (
            <><Clock className="w-5 h-5 text-white/40 mb-1" /><span className="text-[10px] text-white/40">Pending</span></>
          )}
        </div>
      </div>
      <div className="p-2.5">
        <div className="flex items-center justify-between mb-0.5">
          <div className="font-medium text-xs">{shot.scene_number} — {shot.shot_number}</div>
          <div className="flex gap-1">
            {shot.sketch_original_url && (
              <button onClick={() => onUndo?.(shot.id)} data-testid={`undo-sketch-${shot.id}`}
                title="Undo last iteration" className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-all">
                UNDO
              </button>
            )}
            <button onClick={doRefresh} disabled={refreshing} data-testid={`refresh-sketch-${shot.id}`}
              title="Regenerate this sketch" className="p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40">
              <RefreshCw className={`w-3 h-3 text-ai-accent ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {/* ── Insanely Good Keyframe controls — visible on EVERY sketch.
             *    The IGK pipeline accepts any sketch image as a composition
             *    blueprint, regardless of which engine produced the sketch.
             *    Engine gate was overly restrictive — dropped.
             *
             *    Visual states:
             *      No IGK yet:     ✨ cyan outlined  → generates IGK
             *      Generating:     ⟳ amber spinner   → disabled
             *      IGK exists:     ✨ green filled   → TOGGLES sketch ↔ keyframe view
             *                    + ↻ tiny refresh    → regenerates IGK */}
            {/* Sketch stage exposes ONLY the 2P (IGK-2P two-pass) generate
             *  control — mirrors the breakdown stage showing just the napkin
             *  sketch. IGK single-pass and K+ live at the Keyframe stage.
             *  Hidden unless REACT_APP_IGK2P_ENABLED === 'true'. */}
            {shot.sketch_url && process.env.REACT_APP_IGK2P_ENABLED === 'true' && (() => {
              const isGen = genHRKf || shot.keyframe_hyperreal_status === 'generating';
              return (
                <button
                  onClick={(e) => { e.stopPropagation(); regenIGK2P(); }}
                  disabled={isGen}
                  title={isGen
                    ? 'Two-pass render in progress — composition lock, then grade (2–5 min)'
                    : 'Generate keyframe · IGK-2P two-pass (composition lock → grade, VLM-judged). Other engines live at the Keyframe stage.'}
                  className={`p-1 rounded-md flex items-center gap-0.5 transition-colors disabled:cursor-not-allowed ${isGen
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40'
                    : 'bg-gradient-to-br from-fuchsia-500/10 to-amber-500/10 text-white/70 hover:from-fuchsia-500/25 hover:to-amber-500/25 hover:text-white'}`}
                  data-testid={`regen-igk2p-keyframe-${shot.id}`}
                >
                  {isGen ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  <span className="text-[8px] font-bold tracking-wider">2P</span>
                </button>
              );
            })()}
            {/* ── Veo 3.1 Animatic — color-coded state (sketch-only) ──
             *    idle:       cyan ▶
             *    animating:  amber spinner + elapsed seconds
             *    done:       green ▶ (toggle play/still)  + ↻ regen
             *    failed:     red ▶ (click to retry)
             *    Always:     ✎ to edit the prompt that's sent to Veo */}
            {shot.sketch_url && (() => {
              if (animaticProcessing) {
                return (
                  <div
                    className="px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-300 text-[9px] font-mono flex items-center gap-1 border border-amber-400/40"
                    data-testid={`sketch-animatic-processing-${shot.id}`}
                    title="Animating this sketch — typically 60–120s"
                  >
                    <Loader2 className="w-2.5 h-2.5 animate-spin" />
                    {animaticElapsed}s
                  </div>
                );
              }
              const renderEditPromptButton = () => (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const current = shot.description || '';
                    const next = window.prompt(
                      `Edit caption / shot description for ${fmtScene(shot.scene_number)} · ${fmtShot(shot.shot_number)}.\n\nThis is the storytelling description used across the project (DOP spec, Director's Prompt, downstream stages).`,
                      current,
                    );
                    if (next === null) return;
                    const trimmed = next.trim();
                    if (!trimmed) {
                      toast.error('Caption cannot be empty.');
                      return;
                    }
                    try {
                      await axios.patch(`${API}/shots/${shot.id}`, { description: trimmed });
                      toast.success('Caption updated');
                      shot.description = trimmed;
                      if (onReload) await onReload();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to save caption');
                    }
                  }}
                  className="p-1 rounded-md transition-colors hover:bg-white/10 text-white/40 hover:text-cyan-300"
                  title="Edit caption / shot description"
                  data-testid={`sketch-edit-description-${shot.id}`}
                >
                  <Edit3 className="w-3 h-3" />
                </button>
              );

              // DONE: green toggle + regen + edit-prompt
              if (animaticUrl) {
                return (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowAnimatic((v) => !v); }}
                      className={`p-1 rounded-md transition-colors ${
                        showAnimatic
                          ? 'bg-green-500/40 text-green-100 ring-1 ring-green-400'
                          : 'bg-green-500/20 text-green-300 hover:bg-green-500/35 hover:text-green-100'
                      }`}
                      title={showAnimatic
                        ? 'Hide animatic — show sketch still'
                        : `Animatic ready · click to play.\nPrompt used: "${(shot.animatic_prompt || shot.animatic_prompt_override || shot.description || '').slice(0,180)}"`}
                      data-testid={`sketch-animatic-toggle-${shot.id}`}
                    >
                      {showAnimatic ? <Image className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    </button>
                    {renderEditPromptButton()}
                    <button
                      onClick={(e) => { e.stopPropagation(); triggerAnimatic(); }}
                      className="p-1 rounded-md hover:bg-cyan-500/20 transition-colors text-cyan-300"
                      title={`Re-generate the animatic with the current prompt.\nPrompt: "${(shot.animatic_prompt_override || shot.description || '').slice(0,180)}"`}
                      data-testid={`sketch-animatic-regen-${shot.id}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                    </button>
                  </>
                );
              }

              // FAILED: red retry + edit-prompt
              if (animaticError) {
                return (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); triggerAnimatic(); }}
                      className="p-1 rounded-md bg-red-500/20 hover:bg-red-500/35 transition-colors text-red-300 relative"
                      title={`Animatic failed: ${animaticError}\n\nClick to retry.`}
                      data-testid={`sketch-animatic-start-${shot.id}`}
                    >
                      <Play className="w-3 h-3" />
                      <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />
                    </button>
                    {renderEditPromptButton()}
                  </>
                );
              }

              // IDLE: cyan ▶ + edit-prompt
              return (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); triggerAnimatic(); }}
                    className="p-1 rounded-md hover:bg-cyan-500/20 transition-colors text-cyan-300"
                    title={`Animate this sketch (Veo · ~60–120s).\nPrompt used: "${(shot.animatic_prompt_override || shot.description || 'Cinematic motion').slice(0,140)}${(shot.animatic_prompt_override || shot.description || '').length > 140 ? '…' : ''}"`}
                    data-testid={`sketch-animatic-start-${shot.id}`}
                  >
                    <Play className="w-3 h-3" />
                  </button>
                  {renderEditPromptButton()}
                </>
              );
            })()}
            <button onClick={() => setShowSamModal(true)} data-testid={`sketch-sam-btn-${shot.id}`}
              title="SAM-guided iteration — click a region + describe the change"
              className="relative px-1 py-0.5 rounded text-[7px] font-bold bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-all">
              <span>SA</span><span className="relative">M<Wand2 className="absolute -top-1.5 -right-1 w-1.5 h-1.5 text-cyan-400" /></span>
            </button>
            <button onClick={() => onDelete?.(shot)} data-testid={`delete-shot-${shot.id}`}
              title="Delete this shot" className="p-1 rounded-md hover:bg-[#FF3B30]/20 transition-colors text-white/30 hover:text-[#FF3B30]">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
        <p className="text-[10px] text-white/50 mt-0.5 line-clamp-2 cursor-pointer hover:text-white/70 transition-colors"
          onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-2'); }}
        >{shot.description}</p>
        {shot.dialogue && (
          <p className="text-[9px] text-cyan-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none" data-testid={`sketch-dialogue-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-cyan-400">DLG:</span> "{shot.dialogue}"
          </p>
        )}
        {shot.voiceover && (
          <p className="text-[9px] text-amber-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none" data-testid={`sketch-voiceover-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-amber-400">VO:</span> "{shot.voiceover}"
          </p>
        )}
        {IGK2P_ON && (
          <Igk2pSketchPanel shot={shot} onReload={onReload} castTierMap={castTierMap} />
        )}
        {showFeedback && (
          <div className="mt-2 space-y-1.5">
            {refPreview && (
              <div className="relative inline-block">
                <img src={refPreview} alt="ref" className="w-14 h-10 rounded-md object-cover border border-ai-border" />
                <button onClick={clearRefImage} className="absolute -top-1 -right-1 w-4 h-4 rounded-lg bg-[#FF3B30] text-white text-[8px] flex items-center justify-center font-bold leading-none">x</button>
              </div>
            )}
            <div className="flex gap-1">
              <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} data-testid={`sketch-ref-upload-${shot.id}`} />
              <button onClick={() => fileInputRef.current?.click()} title="Upload reference image"
                className="p-1 rounded-lg border border-ai-border bg-ai-bg-secondary hover:glass-panel transition-colors flex-shrink-0"
                data-testid={`sketch-ref-btn-${shot.id}`}>
                <Upload className="w-3 h-3 text-ai-accent" />
              </button>
              <button onClick={toggleVoice} title={isListening ? "Stop recording" : "Voice input"}
                className={`p-1 rounded-lg border flex-shrink-0 transition-all ${isListening ? 'border-[#FF3B30] bg-[#FF3B30]/10 animate-pulse' : 'border-ai-border bg-ai-bg-secondary hover:glass-panel'}`}
                data-testid={`sketch-voice-btn-${shot.id}`}>
                {isListening ? <div className="w-3 h-3 rounded-sm bg-[#FF3B30]" /> : <svg className="w-3 h-3 text-ai-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>}
              </button>
              <input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder={isListening ? "Listening..." : "Feedback + ref image..."}
                className={`flex-1 text-[10px] px-2 py-1 text-white bg-ai-bg-secondary border rounded-lg outline-none ${isListening ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-ai-border focus:border-ai-accent'}`}
                onKeyDown={e => e.key === 'Enter' && doFeedback()} data-testid={`sketch-feedback-input-${shot.id}`} />
              <button onClick={doFeedback} disabled={sending || (!feedback.trim() && !refImage)} data-testid={`sketch-feedback-send-${shot.id}`}
                className="p-1 rounded-lg bg-ai-accent text-white disabled:opacity-40">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SAM Sketch Iteration Modal */}
      {showSamModal && (
        <SketchSamModal
          shotId={shot.id}
          shot={shot}
          aspectClass={aspectClass}
          onClose={() => setShowSamModal(false)}
          onSelect={async (newUrl) => {
            if (onReload) await onReload();
            setShowSamModal(false);
          }}
        />
      )}
    </div>
  );
}

/* ═══════════ KEYFRAME CARD WITH REFRESH + FEEDBACK + IMAGE REF + VOICE ═══════════ */
function KeyframeCard({ shot: rawShot, onRefresh, onReload, onFeedback, onRevert, onRegenerate, onAnimate, onConvertRaw, onStop, onUndo, onDelete, onLockToggle, onImageClick, onSelectMjOption, isGenerating, aspectClass = 'aspect-video', projectId, userEmail, selectable = false, selected = false, onToggleSelect }) {
  // Transparent fallback: surface the "Insanely Good" hyperreal keyframe
  // (gpt-image-2-hyperreal) as the card's primary keyframe whenever it
  // exists — regardless of whether a legacy MJ/Gemini keyframe_url also
  // sits on the shot. The hyperreal output is intentionally the newer,
  // higher-quality replacement: when the user runs "Regenerate Insanely
  // Good Keyframes", they expect those frames to take over, not sit
  // hidden behind the previous MJ run. Aliasing here is pure render-time
  // — the underlying DB doc is untouched, and the original keyframe_url
  // remains available via rawShot if any caller needs it.
  // Also OR the IGK staleness flag into keyframe_stale so the existing
  // "Sketch updated · keyframe stale" badge fires for IGK-only shots and
  // for shots whose IGK is out of date even when the legacy keyframe is
  // still fresh.
  const _staleAny = !!(rawShot && (rawShot.keyframe_stale || rawShot.keyframe_hyperreal_stale));
  // 2026-08-17 fix — priority when a shot has BOTH a done Keyframe+ result
  // AND a Hyperreal/IGK result: Keyframe+ wins. It's the per-shot action the
  // operator deliberately triggered; a background IGK bulk sweep landing
  // later must never silently clobber it on screen (Rohit saw a good
  // Keyframe+ frame flip, with zero warning, to a Hyperreal frame with the
  // wrong locked costume color). scope=all IGK re-runs can still override
  // this deliberately from the workbench.
  const _hasDoneKfp = !!(rawShot && rawShot.keyframe_plus_status === 'done' && rawShot.keyframe_plus_url);
  const shot = (rawShot && rawShot.keyframe_hyperreal_url && !_hasDoneKfp)
    ? {
        ...rawShot,
        keyframe_url: rawShot.keyframe_hyperreal_url,
        keyframe_s3_url: rawShot.keyframe_hyperreal_s3_url || rawShot.keyframe_hyperreal_url,
        keyframe_engine: rawShot.keyframe_hyperreal_engine || 'gpt-image-2-hyperreal',
        keyframe_stale: _staleAny,
      }
    : (rawShot ? { ...rawShot, keyframe_stale: _staleAny } : {});

  // When the keyframe was produced by the Insanely Good (gpt-image-2-hyperreal)
  // pipeline, collapse the toolbar: hide alternate-engine iterate buttons
  // (MJ / GPT / Gemini), the 4-option MJ grid toggle, the auto-iteration
  // badge, the RAW/SLOG toggle, and the inline engine label. Keep only the
  // controls that actually apply: refresh (= regenerate hyperreal), play,
  // download video, 3D, delete, lock, Topaz upscale.
  const isHyperreal = shot.keyframe_engine === 'gpt-image-2-hyperreal';
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [showMjGrid, setShowMjGrid] = useState(false);
  const [selectingOption, setSelectingOption] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  // Graded ▸ Original toggle — shows the RENDERED graded file from S3
  // (shot.graded_video_url, stamped by the grade worker). Never a live
  // preview re-render. Defaults to graded when one exists.
  const [showGraded, setShowGraded] = useState(true);
  const [showAnimatePrompt, setShowAnimatePrompt] = useState(false);
  const [animatePrompt, setAnimatePrompt] = useState('');
  const [animating, setAnimating] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [convertingRaw, setConvertingRaw] = useState(false);
  const [show3D, setShow3D] = useState(false);
  const [generating3D, setGenerating3D] = useState(false);
  const [showMjIterModal, setShowMjIterModal] = useState(false);
  const [showGeminiIterModal, setShowGeminiIterModal] = useState(false);
  // Which engine to preselect when opening the iteration workbench (gpt-image-2 | nano-banana)
  const [iterEngine, setIterEngine] = useState('gpt-image-2');
  const [showingRaw, setShowingRaw] = useState(false);
  // For hyperreal cards: default display is the flat "Sony S-Log3 / S-Gamut3.Cine
  // Approx" file (the colorist's master — corrected v2 transform if available,
  // otherwise the legacy gamma-only v1). Toggling this flips the card to show
  // the Rec.709 graded-look preview. Underlying shot.keyframe_url stays Rec.709
  // so animation, 3D, and downstream ops still receive the proper graded frame.
  // 2026-07-02 (Rohit) — cards default to the Rec.709 graded look; the
  // SLOG3 toggle flips back to the S-Log3 grading master (reverses the
  // 2026-05-17 D1 default; D2 escape hatch unchanged).
  const [showRec709Preview, setShowRec709Preview] = useState(true);
  // For VLM-corrected hyperreal cards: toggle between the final corrected
  // image and the original pre-VLM (Step 1) output for side-by-side review.
  const [showPreVLM, setShowPreVLM] = useState(false);
  // Stale-video reveal (2026-05-23 Rohit fix) — set to true when the
  // operator explicitly clicks the ▶ pill on a tile whose keyframe has
  // been swapped after a video was rendered (`video_stale=true` on the
  // shot doc). While false, we hide the (now-orphaned) video so the new
  // keyframe is the only thing on the card; the ▶ pill in the corner
  // brings the previous clip back on demand. Resets automatically when
  // the underlying keyframe URL changes again.
  const [revealStaleVideo, setRevealStaleVideo] = useState(false);
  useEffect(() => { setRevealStaleVideo(false); }, [rawShot?.keyframe_url, rawShot?.keyframe_hyperreal_url]);
  // Workbench-target override for auto-open (see modal invocation below).
  // Set from the `keyframe-regen-done` event so the filmstrip pulls the
  // array that actually holds the fresh `pre_regen`/`regen_current` entries.
  const [workbenchTarget, setWorkbenchTarget] = useState(null);
  // 2026-05-23 (Rohit) — after a keyframe regen completes, auto-open the
  // SAM iteration workbench for THIS shot so the operator can A/B the new
  // frame vs the `pre_regen` filmstrip entry stashed by the backend.
  // Signalled via a window event dispatched from ProjectView's poll loop
  // (single source of truth: `keyframe-regen-done`).
  useEffect(() => {
    const handler = (e) => {
      if (!e?.detail || e.detail.shotId !== rawShot?.id) return;
      // Pre-select the engine that produced the newly regenerated frame
      // so the workbench canvas doesn't flip engines under the operator.
      const eng = (rawShot?.keyframe_engine || '').toLowerCase();
      if (eng === 'gpt-image-2' || eng === 'nano-banana') setIterEngine(eng);
      // Target the array that the regen backend actually wrote into.
      // isIGK=true → hyperreal_iterations; false → gemini_iterations.
      setWorkbenchTarget(e.detail.isIGK ? 'keyframe_hyperreal_url' : 'keyframe_url');
      setShowGeminiIterModal(true);
    };
    window.addEventListener('keyframe-regen-done', handler);
    return () => window.removeEventListener('keyframe-regen-done', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawShot?.id]);
  const fileInputRef = useRef(null);
  const keyframeUploadInputRef = useRef(null);
  const [uploadingKeyframe, setUploadingKeyframe] = useState(false);
  // ── Fix AI Look (GPT-Image-2 hyper-real cleanup) ──
  const [fixingAi, setFixingAi] = useState(false);
  const [undoingFix, setUndoingFix] = useState(false);
  const [canUndoFix, setCanUndoFix] = useState((rawShot?.fixai_history || []).length > 0);
  const handleFixAiLook = async (e) => {
    e.stopPropagation();
    if (fixingAi) return;
    setFixingAi(true);
    try {
      await axios.post(`${API}/shots/${shot.id}/fix-ai-look`);
      toast.info('Fixing AI look — cleaning to a real-photo finish (~1 min)…');
      // Poll status; the edit runs in a background task (30–120s).
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const res = await axios.get(`${API}/shots/${shot.id}/fix-ai-look/status`);
          const st = res.data?.status;
          if (st === 'done') {
            clearInterval(poll);
            setCanUndoFix(true);
            setFixingAi(false);
            toast.success('AI look cleaned up ✨');
            await onReload?.();
          } else if (st === 'failed') {
            clearInterval(poll);
            setFixingAi(false);
            toast.error(res.data?.error || 'Fix AI Look failed');
          }
        } catch (_) { /* keep polling */ }
        if (tries > 75) {
          clearInterval(poll);
          setFixingAi(false);
          toast.error('Fix AI Look timed out — please retry');
        }
      }, 4000);
    } catch (err) {
      setFixingAi(false);
      toast.error(err.response?.data?.detail || 'Fix AI Look failed');
    }
  };
  const handleUndoFix = async (e) => {
    e.stopPropagation();
    if (undoingFix) return;
    setUndoingFix(true);
    try {
      const res = await axios.post(`${API}/shots/${shot.id}/fix-ai-look/undo`);
      toast.success('Reverted to previous look');
      setCanUndoFix(!!res.data?.can_undo);
      await onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    } finally { setUndoingFix(false); }
  };

  const recognitionRef = useRef(null);

  const { user: authUser } = useAuth();
  const resolvedEmail = (userEmail || authUser?.email || '').toLowerCase();
  const [isPremiumMj, setIsPremiumMj] = useState(false);
  const [premiumRequestPending, setPremiumRequestPending] = useState(false);
  const [showUpgradePopover, setShowUpgradePopover] = useState(false);
  const [requestingUpgrade, setRequestingUpgrade] = useState(false);
  const hasMjGrid = !!(shot.mj_grid_urls && shot.mj_grid_urls.length > 0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/premium/status`).then(res => {
      if (!cancelled) {
        setIsPremiumMj(res.data.is_premium || false);
        setPremiumRequestPending(res.data.request_pending || false);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── KEYFRAME+ single-shot trigger (KeyframeCard scope) ──────────────
  // Used by the FAILED banner's "Retry K+" button. Polls /keyframe-plus-status
  // every 5s until terminal, then reloads the card so the new V surfaces.
  const fireKeyframePlusForShot = async () => {
    const inFlight = ['queued','composing','generating','auditing'].includes(shot.keyframe_plus_status || '');
    if (inFlight) {
      toast.info('Keyframe+ already running on this shot.');
      return;
    }
    try {
      await axios.post(`${API}/shots/${shot.id}/keyframe-plus`, {
        quality: 'high', user_notes: '', look_feel_refs: [],
      });
      toast.success(shot.keyframe_plus_url
        ? 'Re-firing Keyframe+ (Photo engine) — V2 will land in 60–180s.'
        : 'Keyframe+ queued — typically 90–180s.');
      const maxPolls = 60;
      let pollCount = 0;
      const poll = setInterval(async () => {
        pollCount++;
        try {
          const res = await axios.get(`${API}/shots/${shot.id}/keyframe-plus-status`);
          const s = res.data || {};
          if (s.status === 'done' || s.status === 'failed') {
            clearInterval(poll);
            await onReload?.();
            if (s.status === 'failed') toast.error('Keyframe+ failed: ' + (s.error || 'unknown'));
            else toast.success('Keyframe+ V' + (s.version_count || 1) + ' ready');
            return;
          }
        } catch {}
        if (pollCount >= maxPolls) {
          clearInterval(poll);
          await onReload?.();
          toast.warning('Still working — check this card in a minute.');
        }
      }, 5000);
    } catch (e) {
      toast.error('Failed to start Keyframe+: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Cross-card lightbox navigation — listen for "open" events broadcast by parent
  // when user pressed ←/→ in another card's lightbox.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.shotId === shot.id) setLightbox(true);
    };
    window.addEventListener('keyframe-lightbox-open', handler);
    return () => window.removeEventListener('keyframe-lightbox-open', handler);
  }, [shot.id]);

  // Global keyboard handler while THIS card's lightbox is open. The old
  // onKeyDown lived on the lightbox div with tabIndex+autofocus, but if
  // any child element (close button, ChevronRight) absorbed focus the
  // arrow keys never reached the handler. Listening on `window` while
  // open is bullet-proof and matches the cross-card 'nav' event contract.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); setLightbox(false); return; }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        setLightbox(false);
        window.dispatchEvent(new CustomEvent('keyframe-lightbox-nav', {
          detail: { currentShotId: shot.id, direction: e.key === 'ArrowLeft' ? -1 : 1 },
        }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, shot.id]);

  const has3D = !!shot.world_3d_id;
  const world3DUrl = shot.world_3d_url;
  const panoUrl = shot.world_3d_pano_url;

  const toggle3D = async () => {
    if (has3D) {
      if (window.confirm('This will open the 3D viewer on World Labs.\n\nA free World Labs account is needed.\n\nContinue?')) {
        window.open(world3DUrl, '_blank');
      }
      return;
    }
    setGenerating3D(true);
    try {
      const API = window.__API_BASE + '/api';
      await axios.post(`${API}/projects/${projectId}/shots/${shot.id}/generate-3d`, { mode: 'enhanced' });
      toast.info('Generating 3D world from keyframe...');
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/projects/${projectId}/shots/${shot.id}/3d-status`);
          if (res.data.has_3d) {
            clearInterval(poll);
            setGenerating3D(false);
            shot.world_3d_id = res.data.world_url?.split('/').pop();
            shot.world_3d_url = res.data.world_url;
            toast.success('3D world ready — click 3D to explore');
          } else if (!res.data.processing && res.data.error) {
            clearInterval(poll);
            setGenerating3D(false);
            toast.error('3D failed: ' + res.data.error);
          }
        } catch(e) {}
      }, 4000);
      setTimeout(() => { clearInterval(poll); setGenerating3D(false); }, 300000);
    } catch(e) {
      setGenerating3D(false);
      toast.error('3D failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const doRefresh = async () => {
    setRefreshing(true);
    await onRefresh(shot.id);
    setRefreshing(false);
  };

  // Operator-uploaded keyframe (Stage 5 "small upload icon under each tile").
  // POSTs the file to /api/shots/:id/keyframe/upload — the backend stores it
  // in S3 and appends a new version row (source=user_upload), then flips the
  // top-level keyframe_hyperreal_url to point at it.
  const doUploadKeyframe = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (.png, .jpg, .webp).');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Image too large (25 MB max).');
      return;
    }
    setUploadingKeyframe(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/shots/${shot.id}/keyframe/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Uploaded keyframe (${Math.round((r.data.size_bytes || file.size) / 1024)} KB)`);
      // Soft-refresh: re-fetch all shots so the new image renders in the tile
      // (and in the SAM workbench's version history) WITHOUT triggering the
      // IGK regenerate pipeline. NB: `onRefresh` is wired to regenerateKeyframe
      // which would overwrite the upload — use onReload (= loadProjectData).
      if (onReload) await onReload();
    } catch (err) {
      console.error('keyframe upload failed', err);
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKeyframe(false);
      if (keyframeUploadInputRef.current) keyframeUploadInputRef.current.value = '';
    }
  };


  // Download the rendered video as an .mp4 (uses blob fetch to bypass S3 cross-origin
  // "Save Video As" issues; falls back to opening in a new tab on failure).
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const doDownloadVideo = async () => {
    const videoUrl = shot.video_s3_url || shot.video_url || shot.video_seedance_url || shot.video_kling_url;
    if (!videoUrl) return;
    const resolved = videoUrl.startsWith('http') ? videoUrl : `${window.__API_BASE || ''}${videoUrl}`;
    const filename = `${shot.scene_number || 'SC'}-${shot.shot_number || 'SH'}_${(shot.id || 'shot').slice(0, 8)}.mp4`;
    setDownloadingVideo(true);
    try {
      const resp = await fetch(resolved, { credentials: 'omit' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.warn('Video blob download failed, falling back to new tab:', err);
      window.open(resolved, '_blank');
    } finally {
      setDownloadingVideo(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRefImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setRefPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearRefImage = () => {
    setRefImage(null);
    setRefPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleVoice = async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { 
      alert('Voice input not supported in this browser. Please use Chrome on Android or Safari on iOS.'); 
      return; 
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    
    // Request microphone permission first (important for mobile)
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      alert('Microphone access denied. Please allow microphone access in your browser settings.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;  // Better mobile support
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    let finalTranscript = feedback;
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? ' ' : '') + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setFeedback(finalTranscript + (interim ? ' ' + interim : ''));
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (e) => {
      console.error('Speech recognition error:', e.error);
      setIsListening(false);
      if (e.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone access.');
      } else if (e.error !== 'no-speech') {
        alert(`Voice input error: ${e.error}`);
      }
    };
    
    try {
      recognition.start();
      setIsListening(true);
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      alert('Failed to start voice input. Please try again.');
    }
  };

  const doFeedback = async () => {
    if (!feedback.trim() && !refImage) return;
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); }
    setSending(true);
    let refBase64 = null;
    if (refImage) {
      refBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
        reader.readAsDataURL(refImage);
      });
    }
    await onFeedback(shot.id, feedback, refBase64);
    setSending(false);
    setFeedback('');
    setRefImage(null);
    setRefPreview(null);
    setShowFeedback(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      className={`glass-panel rounded-xl overflow-hidden relative ${selectable ? 'cursor-pointer' : ''} ${selectable && selected ? 'ring-2 ring-cyan-400 shadow-[0_0_0_1px_rgba(34,211,238,0.4)]' : ''} ${shot.keyframe_hyperreal_status === 'generating' ? 'ring-2 ring-amber-400 shadow-[0_0_24px_rgba(245,158,11,0.45)] animate-pulse' : ''}`}
      data-testid={`keyframe-card-${shot.id}`}
      onClick={selectable ? (e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleSelect?.(shot.id);
      } : undefined}
    >
      {/* IGK GENERATING — unmissable card-level indicator (mirrors SketchCard) */}
      {shot.keyframe_hyperreal_status === 'generating' && (
        <div
          className="absolute top-0 left-0 right-0 z-30 bg-amber-500/90 text-amber-50 px-3 py-1 flex items-center justify-center gap-2 text-[11px] font-medium tracking-wide shadow-lg"
          data-testid={`igk-generating-banner-keyframe-${shot.id}`}
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>GENERATING IGK · ~30–60s</span>
        </div>
      )}
      {/* Guardrail 4 — ESCALATION WEARS AMBER. A shot whose look/WB grade
          couldn't converge shows a persistent amber 'look unresolved' pill
          until the CD rules (re-grade the scene or lock the keyframe). */}
      {shot.llg_look_unresolved && shot.keyframe_hyperreal_status !== 'generating' && (
        <div
          className="absolute top-2 right-2 z-30 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-lg flex items-center gap-1 bg-amber-500/95 text-amber-950 pointer-events-none"
          title={shot.llg_look_unresolved_reason || 'Look unresolved — the scene grade did not converge. Re-decode/apply the scene look or lock this keyframe to rule.'}
          data-testid={`llg-look-unresolved-${shot.id}`}
        >
          <AlertTriangle className="w-3 h-3" /> Look unresolved
        </div>
      )}
      {/* KEYFRAME+ live banner — fires on queued / composing / generating /
          auditing. Distinct violet→cyan gradient so the operator can tell
          KFP processing apart from IGK at a glance. */}
      {['queued','composing','generating','auditing'].includes(shot.keyframe_plus_status || '') && (
        <div
          className="absolute top-0 left-0 right-0 z-30 px-3 py-1 flex items-center justify-center gap-2 text-[11px] font-medium tracking-wide shadow-lg text-white"
          style={{ background: 'linear-gradient(90deg, rgba(124,58,237,0.92) 0%, rgba(6,182,212,0.92) 100%)' }}
          data-testid={`keyframe-plus-status-banner-${shot.id}`}
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>KEYFRAME+ · {String(shot.keyframe_plus_status).toUpperCase()}</span>
        </div>
      )}
      {/* KEYFRAME+ done — small badge on the shot card so operator knows
          this shot has a KFP V1 (independent of any IGK / standard keyframe). */}
      {shot.keyframe_plus_status === 'done' && shot.keyframe_plus_url && (
        <div
          className="absolute top-2 left-2 z-30 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-lg flex items-center gap-1 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #06B6D4 100%)', color: 'white' }}
          title={`Keyframe+ V1 · score=${shot.keyframe_plus_audit?.overall_score ?? '?'} · ${shot.keyframe_plus_audit?.recommended_action || 'pending'}`}
          data-testid={`keyframe-plus-done-badge-${shot.id}`}
        >
          <Sparkles className="w-3 h-3" />
          K+ V1
        </div>
      )}
      {/* KEYFRAME+ failed — red banner with inline retry */}
      {shot.keyframe_plus_status === 'failed' && (
        <div
          className="absolute top-0 left-0 right-0 z-30 bg-rose-600/90 text-rose-50 px-3 py-1 flex items-center gap-2 text-[11px] font-medium tracking-wide shadow-lg overflow-hidden whitespace-nowrap"
          title={shot.keyframe_plus_error || 'Keyframe+ failed'}
          data-testid={`keyframe-plus-failed-banner-${shot.id}`}
        >
          <span className="flex-1 truncate">KEYFRAME+ FAILED · {(shot.keyframe_plus_error || '').slice(0, 80)}</span>
          <button
            onClick={(e) => { e.stopPropagation(); fireKeyframePlusForShot(); }}
            className="flex-shrink-0 p-0.5 rounded hover:bg-black/30 transition-colors"
            title="Re-fire Keyframe+ (Photo engine). Tip: if the previous attempt was safety-rejected, edit the shot description first."
            data-testid={`keyframe-plus-retry-${shot.id}`}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {selectable && (
        <div
          className={`absolute top-2 right-2 z-40 w-6 h-6 rounded-md flex items-center justify-center transition-all shadow-lg ${selected ? 'bg-cyan-500 text-white ring-2 ring-cyan-300/50' : 'bg-black/70 text-transparent border border-white/40 backdrop-blur-sm'}`}
          data-testid={`keyframe-select-${shot.id}`}
        >
          {selected && <CheckCircle className="w-4 h-4" />}
        </div>
      )}
      <div className={`${aspectClass} glass-panel flex items-center justify-center relative cursor-pointer`} onClick={(e) => {
        if (selectable) return; // bulk-select mode owns the click
        if (show3D) return; // Don't open lightbox when 3D is showing
        (shot.video_url || shot.keyframe_url || shot.keyframe_plus_url) && setLightbox(true);
      }}>
        {/* DOP soft-stale badge — fires when DOP spec was edited at Stage 2
            AFTER this keyframe was rendered. Re-render via the existing
            Regenerate action; we never auto-clobber. */}
        {isKeyframeDopStale(shot) && (
          <div
            className="absolute top-2 left-2 z-30 px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-500/85 text-amber-50 shadow-md inline-flex items-center gap-1 pointer-events-none"
            title="DOP spec changed since this keyframe was rendered. Regenerate to apply the new directives."
            data-testid={`keyframe-dop-stale-${shot.id}`}
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            DOP changed
          </div>
        )}
        {/* 3D Toggle Button — temporarily hidden (see SketchCard rationale). */}
        {false && (shot.keyframe_url || has3D) && (
          <button onClick={(e) => { e.stopPropagation(); toggle3D(); }} disabled={generating3D} data-testid={`kf-3d-toggle-${shot.id}`}
            className={`absolute bottom-2 right-2 z-30 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 shadow-md ${
              show3D ? 'bg-purple-600 text-white shadow-purple-500/40' :
              has3D ? 'bg-purple-500/30 text-purple-300 hover:bg-purple-500/50 border border-purple-500/30' :
              generating3D ? 'bg-purple-500/20 text-purple-400 animate-pulse' :
              'bg-black/60 text-white/80 hover:bg-black/80 border border-white/20'
            }`}>
            {generating3D ? <Loader2 className="w-3 h-3 animate-spin" /> : <Box className="w-3 h-3" />}
            3D
            {has3D && !show3D && <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-0.5" />}
          </button>
        )}

        {/* Fix AI Look — one-tap GPT-Image-2 hyper-real cleanup + undo.
            Icon-only, bottom-right (top-right is used by PICK/VARIANTS badges).
            Shows for every keyframe model: MJ/Gemini/GPT (keyframe_url),
            IGK (keyframe_hyperreal_url) and Keyframe+ (keyframe_plus_url). */}
        {(shot.keyframe_url || shot.keyframe_hyperreal_url || shot.keyframe_plus_url) && (
          <div className="absolute bottom-2 right-2 z-30 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {/* Face Lock — FaceFusion identity-correction pass (spec §3.1) */}
            <FaceLockButton shot={shot} media="keyframe" onReload={onReload} />
            <button
              onClick={handleFixAiLook}
              disabled={fixingAi}
              className={`relative group p-1 rounded-md flex items-center justify-center shadow-md transition-all backdrop-blur-sm ${
                fixingAi
                  ? 'bg-emerald-500/20 text-emerald-300 animate-pulse cursor-wait'
                  : canUndoFix
                    ? 'bg-emerald-500/25 text-emerald-200 hover:bg-emerald-500/40 border border-emerald-400/60 active:scale-95'
                    : 'bg-black/60 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-400/40 active:scale-95'
              }`}
              data-testid={`keyframe-fix-ai-look-${shot.id}`}
            >
              {fixingAi ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              {canUndoFix && !fixingAi && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 text-black text-[7px] font-black flex items-center justify-center leading-none">✓</span>
              )}
              <span className="pointer-events-none absolute bottom-full right-0 mb-1.5 px-2 py-1 rounded-md bg-black/90 border border-white/10 text-white/90 text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                {fixingAi ? 'Fixing AI look…' : canUndoFix ? 'Fixed ✓ — Fix Again' : 'Fix AI Look'}
              </span>
            </button>
            {canUndoFix && !fixingAi && (
              <button
                onClick={handleUndoFix}
                disabled={undoingFix}
                className="relative group p-1 rounded-md bg-black/60 text-white/80 hover:bg-black/80 border border-white/20 shadow-md active:scale-95"
                data-testid={`keyframe-fix-undo-${shot.id}`}
              >
                {undoingFix ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo className="w-3 h-3" />}
                <span className="pointer-events-none absolute bottom-full right-0 mb-1.5 px-2 py-1 rounded-md bg-black/90 border border-white/10 text-white/90 text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                  Undo Fix AI Look
                </span>
              </button>
            )}
          </div>
        )}

        {/* Auto-iteration engine badge — shows which engine ran during generation.
            Hidden for hyperreal cards (the dedicated "Insanely Good" badge
            below the image already conveys the engine). */}
        {!isHyperreal && shot.keyframe_url && Array.isArray(shot.keyframe_iter_engines) && shot.keyframe_iter_engines.length > 0 && (() => {
          const engines = shot.keyframe_iter_engines;
          const last = engines[engines.length - 1];
          const didFallback = engines.length > 1;
          const isGpt = last === 'gpt-image-2';
          const label = isGpt ? 'GPT' : 'GEM';
          const color = isGpt ? '#10A37F' : '#A855F7';
          const bgColor = isGpt ? 'bg-[#10A37F]/20' : 'bg-purple-500/20';
          const tooltip = didFallback
            ? `Auto-iterated: ${engines.join(' → ')} (fell back)`
            : `Auto-iterated via ${isGpt ? 'Photo engine' : 'Sketch engine'}`;
          return (
            <div
              className={`absolute bottom-1.5 left-1.5 z-30 px-1 py-px rounded-sm text-[7px] font-bold ${bgColor} border border-white/10 shadow-sm flex items-center gap-px`}
              style={{ color }}
              title={tooltip}
              data-testid={`kf-auto-iter-badge-${shot.id}`}
            >
              {isGpt ? <Sparkles className="w-1.5 h-1.5" /> : <Wand2 className="w-1.5 h-1.5" />}
              <span>{label}</span>
              {didFallback && <span className="opacity-60 ml-px">↺</span>}
            </div>
          );
        })()}
        
        {/* Loading overlay for refresh, sending, iterating, or video processing */}
        {(refreshing || sending || shot.keyframe_iterating || shot.video_status === 'processing') && (
          <div className="absolute inset-0 bg-ai-bg/70 z-10 flex flex-col items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#FF9500]" />
            {shot.keyframe_iterating && <span className="text-[9px] text-[#FF9500] mt-1">Iterating...</span>}
            {shot.video_status === 'processing' && (
              <>
                <span className="text-[9px] text-cyan-400 mt-1">Generating Video...</span>
                <button 
                  onClick={(e) => { e.stopPropagation(); onStop?.(shot.id); }}
                  className="mt-2 px-3 py-1 rounded text-[9px] font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                  data-testid={`stop-video-${shot.id}`}
                >
                  Stop
                </button>
              </>
            )}
          </div>
        )}
        {shot.keyframe_stale && shot.keyframe_url && (
          <div
            className="absolute top-2 right-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold bg-yellow-400/95 text-black shadow-lg border border-yellow-600/40"
            data-testid={`stale-badge-${shot.id}`}
            title={
              shot.sketch_updated_at
                ? `Sketch updated ${new Date(shot.sketch_updated_at).toLocaleString()} — keyframe needs regeneration`
                : "Sketch updated since keyframe was generated"
            }
          >
            <span>Sketch updated · keyframe stale</span>
            {onRefresh && (
              <button
                onClick={(e) => { e.stopPropagation(); onRefresh(shot.id); }}
                className="ml-1 px-1.5 py-0.5 rounded bg-black/80 text-yellow-100 hover:bg-black text-[9px] uppercase tracking-wide"
                data-testid={`stale-regenerate-${shot.id}`}
              >
                Regenerate
              </button>
            )}
          </div>
        )}
        {shot.face_consistency_engine && (
          <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded text-[7px] font-bold bg-ai-accent/70 text-white">
            {shot.face_consistency_engine === 'insightface' ? 'Face Swapped' : 'AI Face Match'}
          </div>
        )}
        {/* Show video if available, otherwise show keyframe */}
        {/* Stale-guard (2026-05-23): when the operator Accepted a new
            keyframe from the SAM workbench, backend sets `video_stale=true`
            because the rendered video no longer matches. Hide the video
            overlay so the accepted keyframe is the primary tile content;
            the ▶ pill in the top-left brings the previous clip back on
            demand for A/B QA. */}
        {shot.video_url && !(shot.video_stale && !revealStaleVideo) ? (() => {
          const gradedUrl = shot.graded_video_url;
          const useGraded = !!(gradedUrl && showGraded);
          const clipSrc = useGraded ? gradedUrl : (shot.video_s3_url || shot.video_url);
          return (
          <>
            {/* Always show keyframe underneath as fallback */}
            {shot.keyframe_url && (
              <img 
                src={resolveUrl(shot.keyframe_s3_url || shot.keyframe_url)} 
                alt="" 
                className="absolute inset-0 w-full h-full object-cover z-0"
              />
            )}
            <video 
              key={clipSrc}
              src={resolveUrl(clipSrc)} 
              poster={shot.keyframe_url ? resolveUrl(shot.keyframe_s3_url || shot.keyframe_url) : undefined}
              className="w-full h-full object-cover relative z-[1]" 
              data-testid={`keyframe-video-${shot.id}`}
              muted
              loop
              playsInline
              onMouseEnter={(e) => e.target.play().catch(() => {})}
              onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
              onError={(e) => {
                // Graded file failed → drop back to the original clip once.
                if (useGraded) { setShowGraded(false); return; }
                // Try S3 fallback once on transient failures; do NOT hide the keyframe img beneath.
                // The video element being broken/transparent will let the underlying keyframe show.
                if (shot.video_s3_url && e.target.src !== resolveUrl(shot.video_s3_url)) {
                  e.target.src = resolveUrl(shot.video_s3_url);
                } else {
                  // Use opacity (keeps layout + allows recovery on re-render); keyframe underneath shows through
                  e.target.style.opacity = '0';
                  e.target.style.pointerEvents = 'none';
                }
              }}
            />
            <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded text-[7px] font-bold bg-cyan-500/90 text-white flex items-center gap-1">
              <Play className="w-2.5 h-2.5" /> VIDEO
            </div>
            {gradedUrl && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowGraded(!showGraded); }}
                data-testid={`graded-toggle-${shot.id}`}
                title={useGraded ? "Showing graded render — click for original" : "Showing original — click for graded render"}
                className={`absolute bottom-2 left-2 z-10 px-2 py-0.5 rounded text-[8px] font-bold tracking-wide transition-colors shadow ${
                  useGraded
                    ? 'bg-orange-500/90 text-black hover:bg-orange-400'
                    : 'bg-black/60 text-white/80 hover:bg-black/80 border border-white/20'
                }`}
              >
                {useGraded ? 'GRADED ▸' : '◂ ORIGINAL'}
              </button>
            )}
          </>
          );
        })() : shot.keyframe_url ? (
          <img src={resolveUrl(
            // Highest priority: explicit pre-VLM A/B view
            isHyperreal && showPreVLM && rawShot.keyframe_hyperreal_pre_vlm_url
              ? rawShot.keyframe_hyperreal_pre_vlm_url
              // Then: legacy RAW toggle
              : (showingRaw && shot.keyframe_raw_url
                  ? shot.keyframe_raw_url
                  // Then: hyperreal default = corrected v2 (Sony S-Log3 / S-Gamut3.Cine)
                  // if available, otherwise legacy gamma-only v1, unless the user
                  // toggled to Rec.709.
                  : (isHyperreal && !showRec709Preview
                        && (rawShot.keyframe_hyperreal_slog3_v2_url
                            || rawShot.keyframe_hyperreal_slog3_url)
                      ? (rawShot.keyframe_hyperreal_slog3_v2_url
                          || rawShot.keyframe_hyperreal_slog3_url)
                      : shot.keyframe_url))
          )} alt={`${shot.scene_number}-${shot.shot_number}`}
            className="w-full h-full object-cover cursor-pointer" data-testid={`keyframe-img-${shot.id}`}
            onError={(e) => {
              // Try fallback: MJ grid selected option URL, then S3
              const fallbacks = [];
              if (shot.mj_grid_urls && shot.mj_selected_index != null) {
                fallbacks.push(shot.mj_grid_urls[shot.mj_selected_index]);
              }
              if (shot.keyframe_s3_url) fallbacks.push(resolveUrl(shot.keyframe_s3_url));
              if (shot.mj_grid_urls) fallbacks.push(...shot.mj_grid_urls.filter(u => u));
              const tried = e.target.dataset.triedFallbacks ? JSON.parse(e.target.dataset.triedFallbacks) : [];
              const next = fallbacks.find(u => u && !tried.includes(u) && u !== e.target.src);
              if (next) {
                tried.push(next);
                e.target.dataset.triedFallbacks = JSON.stringify(tried);
                e.target.src = next.startsWith('http') ? next : resolveUrl(next);
                return;
              }
              // Non-destructive final state: retry with cache-bust up to 2x for transient
              // S3/CDN issues, then fade out (opacity) instead of display:none so the
              // <img> stays in the DOM and can recover automatically on the next render.
              const retries = parseInt(e.target.dataset.retries || '0', 10);
              if (retries < 2) {
                e.target.dataset.retries = String(retries + 1);
                const base = (e.target.src || '').split('?')[0];
                setTimeout(() => { try { e.target.src = `${base}?r=${Date.now()}`; } catch {} }, 600 * (retries + 1));
              } else {
                e.target.style.opacity = '0';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }
            }} />
        ) : null}

        {/* Stale-video ▶ pill (2026-05-23 Rohit) — appears only after the
            operator Accepted a new keyframe from the SAM workbench and a
            rendered video exists on this shot. One tiny amber pill in the
            top-left; clicking flips revealStaleVideo=true to bring the
            previous clip back into view for A/B QA. Auto-resets when the
            keyframe changes again. */}
        {shot.video_url && shot.video_stale && !revealStaleVideo && (
          <button
            onClick={(e) => { e.stopPropagation(); setRevealStaleVideo(true); }}
            data-testid={`play-stale-video-${shot.id}`}
            title={shot.video_stale_reason || 'Play the previous video — keyframe was updated after it was rendered'}
            className="absolute top-2 left-2 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/85 hover:bg-amber-400 text-black text-[9px] font-bold shadow-md transition-colors"
          >
            <Play className="w-2.5 h-2.5" /> old video
          </button>
        )}

        {/* Pick variant (N) pill — Phase 3 keyframe-VLM parity (2026-05-23).
         *  Mirror of the IGS pill (`Pick (N)` / `N variants`) for IGK
         *  hyperreal keyframes. Shows top-right when the shot has more
         *  than one entry in `keyframe_hyperreal_versions[]` (Step 1 +
         *  Step 3 written by the dual-write hook). Amber + pulsing when
         *  `keyframe_hyperreal_decision_required` is true — the operator
         *  must pick Step 1 (pre-VLM) or Step 3 (corrected) before the
         *  downstream animatic / Veo i2v stages consume the keyframe. */}
        {(() => {
          const kvs = rawShot?.keyframe_hyperreal_versions || [];
          if (kvs.length < 2) return null;
          const mustDecide = !!rawShot?.keyframe_hyperreal_decision_required;
          return (
            <button
              onClick={(e) => { e.stopPropagation(); setShowGeminiIterModal(true); }}
              className={`absolute top-1.5 right-1.5 z-30 px-1.5 py-0.5 rounded-sm text-[8px] font-bold uppercase shadow-sm transition-all hover:scale-105 ${
                mustDecide
                  ? 'bg-amber-500/90 text-black animate-pulse'
                  : 'bg-cyan-500/85 text-white'
              }`}
              title={mustDecide ? `Pick a winner — ${kvs.length} keyframe variants (Step 1 vs Step 3)` : `${kvs.length} keyframe variants available — open workbench to compare`}
              data-testid={`keyframe-pick-variant-${shot.id}`}
            >
              {mustDecide ? `Pick (${kvs.length})` : `${kvs.length} variants`}
            </button>
          );
        })()}

        {/* MJ 4-Grid Selection Overlay */}
        {showMjGrid && shot.mj_grid_urls && shot.mj_grid_urls.length > 1 && (
          <div className="absolute inset-0 z-20 bg-black/90 flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid={`mj-grid-panel-${shot.id}`}>
            <div className="px-2 py-1 flex items-center justify-between border-b border-white/10 flex-shrink-0">
              <span className="text-[9px] font-bold text-purple-300">Pick an option</span>
              <button onClick={() => setShowMjGrid(false)} className="text-white/40 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className={`flex-1 overflow-auto min-h-0 p-1 ${
              aspectClass === 'aspect-[9/16]' 
                ? 'grid grid-cols-2 gap-1 auto-rows-min' 
                : 'grid grid-cols-2 grid-rows-2 gap-1'
            }`}>
              {shot.mj_grid_urls.map((url, i) => (
                <button key={i}
                  onClick={async () => {
                    setSelectingOption(i);
                    try {
                      await onSelectMjOption?.(shot.id, i);
                      setShowMjGrid(false);
                    } catch (err) { alert(err.message || 'Selection failed'); }
                    setSelectingOption(null);
                  }}
                  disabled={selectingOption !== null}
                  className={`relative rounded overflow-hidden border-2 transition-all ${aspectClass} ${shot.mj_selected_index === i ? 'border-purple-500' : 'border-transparent hover:border-purple-500/50'}`}
                  data-testid={`mj-option-${shot.id}-${i}`}
                >
                  <img src={url} alt={`Option ${i+1}`} className="w-full h-full object-cover" />
                  {selectingOption === i && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-0.5">
                    <span className={`text-[8px] font-bold ${shot.mj_selected_index === i ? 'text-purple-300' : 'text-white/70'}`}>
                      {shot.mj_selected_index === i ? '● Selected' : `Option ${i+1}`}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="w-full h-full flex flex-col items-center justify-center" style={{ display: (shot.video_url || shot.keyframe_url || shot.keyframe_plus_url) ? 'none' : 'flex' }}>
          {(isGenerating || shot.keyframe_generating) ? (
            <><Loader2 className="w-6 h-6 text-[#FF9500] animate-spin mb-1" /><span className="text-[10px] text-[#FF9500] font-medium animate-pulse">Generating keyframe...</span></>
          ) : (
            <><Clock className="w-5 h-5 text-white/20 mb-1" /><span className="text-[10px] text-white/30">Waiting</span></>
          )}
        </div>
        {/* KEYFRAME+ image render — shown when no standard keyframe / video
            exists on this shot but a Keyframe+ output is present. Isolated
            silo, so this branch never collides with the keyframe_url /
            keyframe_hyperreal_url paths above. Parent div owns the click
            (opens lightbox); use object-cover to match card grid framing
            (the lightbox shows the full image without cropping). */}
        {!shot.video_url && !shot.keyframe_url && shot.keyframe_plus_url && (
          <img
            src={resolveUrl(shot.keyframe_plus_url)}
            alt={`KFP ${shot.scene_number}-${shot.shot_number}`}
            className="w-full h-full object-cover"
            data-testid={`keyframe-plus-img-${shot.id}`}
          />
        )}
      </div>
      <div className="p-2.5">
        <div className="flex items-center justify-between mb-0.5 gap-2 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="font-medium text-xs whitespace-nowrap flex-shrink-0">{shot.scene_number} — {shot.shot_number}</span>
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1" data-testid={`keyframe-badges-${shot.id}`}>
            {isHyperreal ? (
              <>
                <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300 flex items-center flex-shrink-0"
                  title="Insanely Good Keyframe — generated by Photo engine with VLM audit + correction"
                  data-testid={`engine-badge-hyperreal-${shot.id}`}>
                  <Sparkles className="w-2.5 h-2.5" />
                </span>
                {/* Casting Plan badge — surfaces the per-shot ref plan so the
                    user can see what type GPT was told this shot is (drone WS /
                    title card / OTS / etc.) and which audit rubric was applied.
                    Hover tooltip shows the full plan summary. */}
                {rawShot.ref_plan?.shot_type && (
                  <span
                    className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 flex-shrink-0 uppercase"
                    title={
                      `Casting Plan (${rawShot.ref_plan.source || 'auto'}):\n`
                      + `• Type: ${rawShot.ref_plan.shot_type}\n`
                      + `• Cast in frame: ${rawShot.ref_plan.cast_names?.join(', ') || '—'}\n`
                      + `• Costume: ${rawShot.ref_plan.costume_names?.join(', ') || '—'}\n`
                      + `• Location: ${rawShot.ref_plan.location_name || '—'}\n`
                      + `• Product in frame: ${rawShot.ref_plan.product_in_frame ? 'yes' : 'no'}\n`
                      + `• Audit rubric: ${rawShot.ref_plan.audit_rubric?.join(' + ') || '—'}\n`
                      + `• Skip Step 3 correction: ${rawShot.ref_plan.skip_step3 ? 'yes' : 'no'}`
                      + (rawShot.ref_plan.reasoning ? `\n• Why: ${rawShot.ref_plan.reasoning}` : '')
                    }
                    data-testid={`refplan-badge-${shot.id}`}
                  >
                    {rawShot.ref_plan.shot_type.charAt(0)}
                  </span>
                )}
                {(rawShot.keyframe_hyperreal_slog3_v2_url || rawShot.keyframe_hyperreal_slog3_url) ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowRec709Preview(p => !p); }}
                    data-testid={`slog3-toggle-${shot.id}`}
                    title={showRec709Preview
                      ? "Currently showing Rec.709 graded-look preview. Tap to switch to the Sony S-Log3 / S-Gamut3.Cine Approx grading master (technical transform — no creative grade applied)."
                      : (rawShot.keyframe_hyperreal_slog3_v2_url
                          ? "Currently showing Sony S-Log3 / S-Gamut3.Cine Approx (v2). Flat log gamma + S-Gamut3.Cine primaries — drop into Resolve/Baselight and apply your own grade. Tap to preview the Rec.709 graded look."
                          : "Currently showing legacy S-Log3 (v1 gamma-only). A corrected v2 hasn't been generated for this shot yet. Tap to preview the Rec.709 graded look.")}
                    className={`text-[7px] font-bold px-1.5 py-0.5 rounded transition-all flex-shrink-0 whitespace-nowrap ${
                      showRec709Preview
                        ? 'bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-500/60'
                        : 'bg-zinc-500/20 text-zinc-300 hover:bg-zinc-500/30'
                    }`}>
                    {showRec709Preview
                      ? '709 ✓'
                      : (rawShot.keyframe_hyperreal_slog3_v2_url ? 'SLOG3' : 'S-LOG3 v1')}
                  </button>
                ) : (shot.keyframe_url || shot.keyframe_hyperreal_url || shot.keyframe_plus_url) && (
                  <span
                    data-testid={`slog3-toggle-${shot.id}`}
                    title="This keyframe is Rec.709 — no S-Log3 grading master has been generated for it (uploads and plain keyframes are 709-only)."
                    className="text-[7px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap bg-emerald-500/15 text-emerald-300/60 cursor-help">
                    709
                  </span>
                )}
                {/* Pre-VLM A/B toggle — only meaningful when VLM ran Step 3 correction.
                    Lets the user A/B compare the corrected vs original output.
                    Hidden when audit passed (pre-VLM == final, nothing to compare). */}
                {rawShot.keyframe_hyperreal_pre_vlm_url
                  && rawShot.keyframe_hyperreal_pre_vlm_url !== rawShot.keyframe_hyperreal_url
                  && (rawShot.keyframe_hyperreal_audit?.overall_match === false) && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowPreVLM(p => !p); }}
                      data-testid={`pre-vlm-toggle-${shot.id}`}
                      title={showPreVLM
                        ? "Currently showing PRE-VLM (Step 1 Photo engine output, before correction). Tap to return to the corrected final."
                        : "VLM flagged issues on this shot and Step 3 corrected them. Tap to view the original PRE-VLM output for comparison."}
                      className={`text-[7px] font-bold px-1.5 py-0.5 rounded transition-all flex items-center gap-0.5 flex-shrink-0 whitespace-nowrap ${
                        showPreVLM
                          ? 'bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/60'
                          : 'bg-zinc-500/20 text-zinc-300 hover:bg-zinc-500/30'
                      }`}>
                      <Eye className="w-2 h-2" /> {showPreVLM ? 'PRE-VLM ✓' : 'PRE-VLM'}
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(
                          'Accept PRE-VLM as the active hyperreal keyframe?\n\n'
                          + 'This will replace the corrected (post-VLM) version with the original Step 1 output. '
                          + 'The post-VLM file is archived (not deleted) so you can revisit if needed. '
                          + 'The S-Log3 v2 sibling will be regenerated from the pre-VLM source.'
                        )) return;
                        try {
                          await axios.post(`${API}/shots/${shot.id}/accept-pre-vlm`);
                          toast.success('Pre-VLM accepted. S-Log3 v2 regenerated.');
                          onLockToggle?.();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to accept pre-VLM');
                        }
                      }}
                      data-testid={`accept-pre-vlm-${shot.id}`}
                      title="Accept the PRE-VLM image as the final hyperreal keyframe. The corrected version is archived (not deleted) and the S-Log3 v2 sibling is regenerated from the pre-VLM source."
                      className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-all flex items-center gap-0.5 flex-shrink-0 whitespace-nowrap"
                    >
                      <Check className="w-2 h-2" /> ACCEPT PRE-VLM
                    </button>
                  </>
                )}
              </>
            ) : (shot.keyframe_engine && (() => {
              // Map engine ids → display badges. Falls back to 'GEMINI' for
              // any unmapped engine that's not midjourney (preserves legacy).
              const eng = shot.keyframe_engine;
              const isMJ = eng === 'midjourney';
              const isKP = eng === 'gpt-image-2-keyframe-plus' || eng === 'keyframe-plus' || eng === 'kfp';
              const isIGK = eng === 'gpt-image-2' || eng === 'igk' || eng === 'gpt-image';
              const label = isMJ ? 'MJ' : isKP ? 'K+' : isIGK ? 'IGK' : 'GEMINI';
              const cls = isMJ
                ? 'bg-[#FF9500]/20 text-[#FF9500]'
                : isKP
                  ? 'bg-gradient-to-br from-purple-500/30 to-cyan-500/30 text-cyan-200 border border-cyan-400/30'
                  : isIGK
                    ? 'bg-emerald-500/20 text-emerald-300'
                    : 'bg-blue-500/20 text-blue-400';
              return (
                <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${cls}`}
                  data-testid={`engine-badge-${shot.id}`}>
                  {label}
                </span>
              );
            })())}
            {/* 709 colour chip — non-hyperreal keyframes have no S-Log3
                master, so surface their colour state (always Rec.709). */}
            {!isHyperreal && (shot.keyframe_url || shot.keyframe_plus_url) && (
              <span
                data-testid={`slog3-toggle-${shot.id}`}
                title="This keyframe is Rec.709 — no S-Log3 grading master has been generated for it (uploads and plain keyframes are 709-only)."
                className="text-[7px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap bg-emerald-500/15 text-emerald-300/60 cursor-help">
                709
              </span>
            )}
            </div>
          </div>
          <div className="flex items-center gap-0.5 flex-nowrap flex-shrink-0 overflow-x-auto min-w-0 scrollbar-none">
            {shot.keyframe_url_original && (
              <button onClick={(e) => { e.stopPropagation(); onRevert?.(shot.id); }} title="Revert to original (before face swap)"
                className="p-1 rounded-md hover:bg-white/10 transition-colors text-yellow-400 flex-shrink-0" data-testid={`revert-keyframe-${shot.id}`}>
                <RotateCcw className="w-3 h-3" />
              </button>
            )}
            {/* RAW/GRADE toggle for color grading — hidden on hyperreal cards
                (the photoreal pipeline already produces a graded frame). */}
            {!isHyperreal && shot.keyframe_url && (
              <button 
                onClick={async (e) => { 
                  e.stopPropagation();
                  if (shot.keyframe_raw_url && !showingRaw) {
                    // RAW exists but not showing — toggle to show it
                    setShowingRaw(true);
                  } else if (showingRaw) {
                    // Currently showing RAW — toggle back to graded/original
                    setShowingRaw(false);
                  } else {
                    // No RAW yet — generate it
                    setConvertingRaw(true);
                    try {
                      await onConvertRaw?.(shot.id, 'raw');
                      setShowingRaw(true);
                    } finally {
                      setConvertingRaw(false);
                    }
                  }
                }} 
                disabled={convertingRaw}
                title={showingRaw ? "Switch back to graded view" : (shot.keyframe_raw_url ? "Switch to S-Log3 RAW view" : "Generate S-Log3 RAW version")}
                className={`px-1 py-0.5 rounded text-[7px] font-bold transition-all ${
                  showingRaw 
                    ? 'bg-green-500/30 text-green-300 ring-1 ring-green-400/50' 
                    : shot.keyframe_raw_url
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                } ${convertingRaw ? 'opacity-50' : ''}`}
                data-testid={`raw-grade-toggle-${shot.id}`}
              >
                {convertingRaw ? '...' : (showingRaw ? 'SLOG' : 'RAW')}
              </button>
            )}
            {/* MJ 4-Grid toggle — hidden on hyperreal cards (no MJ grid exists) */}
            {!isHyperreal && shot.mj_grid_urls && shot.mj_grid_urls.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); setShowMjGrid(!showMjGrid); }}
                title={showMjGrid ? "Hide MJ options" : "Show all 4 MJ options"}
                className={`px-1.5 py-0.5 rounded text-[8px] font-bold transition-all ${showMjGrid ? 'bg-purple-500/30 text-purple-300' : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'}`}
                data-testid={`mj-grid-toggle-${shot.id}`}
              >
                {showMjGrid ? 'HIDE' : '4 OPT'}
              </button>
            )}
            <button onClick={doRefresh} disabled={refreshing || shot.keyframe_generating || shot.keyframe_regen_in_progress} data-testid={`refresh-keyframe-${shot.id}`}
              title={(shot.keyframe_generating || shot.keyframe_regen_in_progress) ? "Regenerating… (workbench will open when ready)" : "Regenerate this keyframe"}
              className="p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40">
              <RefreshCw className={`w-3 h-3 text-[#FF9500] ${(refreshing || shot.keyframe_generating || shot.keyframe_regen_in_progress) ? 'animate-spin' : ''}`} />
            </button>
            {/* Operator keyframe upload — replaces the current keyframe with a
                user-supplied image. Hidden file input + small icon button. */}
            <input
              type="file"
              ref={keyframeUploadInputRef}
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={doUploadKeyframe}
              data-testid={`keyframe-upload-input-${shot.id}`}
            />
            <button
              onClick={() => keyframeUploadInputRef.current?.click()}
              disabled={uploadingKeyframe || shot.keyframe_generating}
              data-testid={`keyframe-upload-btn-${shot.id}`}
              title="Upload your own keyframe (replaces current)"
              className="p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              {uploadingKeyframe
                ? <Loader2 className="w-3 h-3 animate-spin text-[#0EA5E9]" />
                : <UploadCloud className="w-3 h-3 text-[#0EA5E9]" />}
            </button>
            {/* Edit caption (✎) — operator-friendly inline edit of the shot's
                description. Replaces the old behaviour where the pencil was
                a hidden alias for the Veo motion prompt — that prompt now
                lives on the Play (▶) button's animate input where it
                semantically belongs ("press play, type how it should move").
                The description here is the storytelling caption shown under
                each card and feeds every downstream stage that references
                `shot.description` (DOP spec, Director's Prompt, etc.). */}
            {shot.keyframe_url && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const current = shot.description || '';
                  const next = window.prompt(
                    `Edit caption / shot description for ${shot.scene_number} · ${shot.shot_number}.\n\nThis is the storytelling description used across the project (DOP spec, Director's Prompt, downstream stages).`,
                    current,
                  );
                  if (next === null) return;
                  const trimmed = next.trim();
                  if (!trimmed) {
                    toast.error('Caption cannot be empty.');
                    return;
                  }
                  try {
                    await axios.patch(`${API}/shots/${shot.id}`, { description: trimmed });
                    toast.success('Caption updated');
                    // Local optimistic update so the card text refreshes immediately
                    shot.description = trimmed;
                    if (onReload) await onReload();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to save caption');
                  }
                }}
                data-testid={`keyframe-edit-description-${shot.id}`}
                title="Edit caption / shot description"
                className="p-1 rounded-md transition-colors hover:bg-white/10 text-white/40 hover:text-cyan-300"
              >
                <Edit3 className="w-3 h-3" />
              </button>
            )}
            {/* Animate / Regenerate Video button */}
            {shot.keyframe_url && (
              <button onClick={() => {
                  // Seed the input with the user's saved Veo prompt override,
                  // last-used i2v prompt, or the shot description so the box
                  // never opens blank. Empty seed was the #1 reported bug —
                  // users would click Play and stare at an empty field.
                  if (!showAnimatePrompt) {
                    const seed = (shot.animatic_prompt_override
                      || shot.i2v_prompt
                      || shot.description
                      || '').trim();
                    setAnimatePrompt(seed);
                  }
                  setShowAnimatePrompt(!showAnimatePrompt);
                }}
                data-testid={`animate-keyframe-${shot.id}`}
                title={shot.video_url ? "Regenerate video from this keyframe" : "Generate video from this keyframe"}
                className={`p-1 rounded-md hover:bg-white/10 transition-colors ${showAnimatePrompt ? 'text-green-400' : (shot.video_url ? 'text-[#FF9500]' : 'text-cyan-400')}`}>
                {shot.video_url ? <RotateCcw className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              </button>
            )}
            {/* Download Video button — only when a video exists */}
            {shot.video_url && (
              <button onClick={(e) => { e.stopPropagation(); doDownloadVideo(); }}
                disabled={downloadingVideo}
                data-testid={`download-video-${shot.id}`}
                title="Download video"
                className="p-1 rounded-md hover:bg-white/10 transition-colors text-cyan-400 disabled:opacity-40">
                {downloadingVideo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              </button>
            )}
            {/* MJ Iterate button — hidden on hyperreal cards. */}
            {!isHyperreal && shot.keyframe_url && (hasMjGrid || shot.keyframe_engine === 'midjourney') && (
              isPremiumMj ? (
                <button onClick={shot.keyframe_original_url ? undefined : () => setShowMjIterModal(true)} data-testid={`mj-iterate-btn-${shot.id}`}
                  title={shot.keyframe_original_url ? "Revert edits to re-enable MJ iteration" : "Iterate with MJ"}
                  className={`relative px-1 py-0.5 rounded text-[7px] font-bold transition-all ${
                    shot.keyframe_original_url
                      ? 'bg-white/5 text-white/20 cursor-not-allowed'
                      : 'bg-[#FF9500]/20 text-[#FF9500] hover:bg-[#FF9500]/30 cursor-pointer'
                  }`}>
                  <span>M</span><span className="relative">J<Wand2 className={`absolute -top-1.5 -right-1 w-1.5 h-1.5 ${shot.keyframe_original_url ? 'text-white/20' : 'text-[#FF9500]'}`} /></span>
                </button>
              ) : (
                <div className="relative" onMouseEnter={() => setShowUpgradePopover(true)} onMouseLeave={() => setShowUpgradePopover(false)}>
                  <button data-testid={`mj-upgrade-btn-${shot.id}`}
                    className="relative px-1 py-0.5 rounded text-[7px] font-bold bg-white/5 text-white/20 cursor-default">
                    <span>M</span><span className="relative">J<Wand2 className="absolute -top-1.5 -right-1 w-1.5 h-1.5 text-white/20" /></span>
                  </button>
                  {showUpgradePopover && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 pb-2 z-50">
                      <div className="w-48 p-3 rounded-xl border border-[#FF9500]/30 shadow-xl"
                        style={{ background: '#1A1033' }}>
                      <p className="text-[10px] text-white/80 mb-2 text-center">Midjourney iteration is a <span className="text-[#FF9500] font-bold">Premium</span> feature</p>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (premiumRequestPending) return;
                          setRequestingUpgrade(true);
                          try {
                            await axios.post(`${API}/premium/request`);
                            setPremiumRequestPending(true);
                            toast.success('Upgrade request sent to admin!');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Request failed');
                          } finally {
                            setRequestingUpgrade(false);
                            setShowUpgradePopover(false);
                          }
                        }}
                        disabled={premiumRequestPending || requestingUpgrade}
                        className={`w-full py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1 ${
                          premiumRequestPending 
                            ? 'bg-white/10 text-white/40 cursor-default'
                            : 'bg-gradient-to-r from-[#FF9500] to-[#FF6B00] text-white hover:opacity-90'
                        }`}
                        data-testid={`mj-upgrade-request-${shot.id}`}
                      >
                        {premiumRequestPending ? 'Request Pending' : requestingUpgrade ? 'Sending...' : 'Upgrade to Premium'}
                      </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
            {/* Iterate buttons:
                - Non-hyperreal: both GPT Image 2 + Gemini options
                - Hyperreal: GPT Image 2 only (per user requirement — Insanely Good
                  Keyframes never fall back to Gemini, and the workbench writes to
                  keyframe_hyperreal_url with auto S-Log3 v2 regeneration). */}
            {/* Unified SAM workbench button — matches the IGS sketch card UX.
                Opens the GeminiIterationModal which carries the same
                engine picker (gpt-image-2 ↔ nano-banana), mask paint,
                reference uploads, and iteration history that the IGS SAM
                modal provides. Single button keeps the keyframe card
                bottom de-cluttered. Backend routes the request to
                /iterate-keyframe with the correct target_field (keyframe
                or hyperreal) based on the card type. */}
            <button
              onClick={() => { setIterEngine('gpt-image-2'); setShowGeminiIterModal(true); }}
              data-testid={isHyperreal ? `keyframe-sam-hyperreal-${shot.id}` : `keyframe-sam-${shot.id}`}
              title={isHyperreal
                ? "SAM workbench — iterate this Insanely Good Keyframe with text, mask, and references. Engine picker (Photo / Sketch) inside."
                : "SAM workbench — iterate this keyframe with text, mask, and references. Engine picker (Photo / Sketch) inside."}
              className="relative px-1 py-0.5 rounded text-[7px] font-bold bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-all flex-shrink-0"
            >
              <span>SA</span><span className="relative">M<Wand2 className="absolute -top-1.5 -right-1 w-1.5 h-1.5 text-cyan-400" /></span>
            </button>
            <button onClick={() => onDelete?.(shot)} data-testid={`delete-keyframe-${shot.id}`}
              title="Delete this shot" className="p-1 rounded-md hover:bg-[#FF3B30]/20 transition-colors text-white/30 hover:text-[#FF3B30]">
              <Trash2 className="w-3 h-3" />
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const newLock = !shot.keyframe_locked;
                  await axios.put(`${API}/shots/${shot.id}`, { keyframe_locked: newLock });
                  onLockToggle?.();
                  toast.success(newLock ? 'Keyframe locked' : 'Keyframe unlocked');
                } catch (err) { toast.error('Failed to update lock'); }
              }}
              className={`p-1 rounded-md transition-colors ${shot.keyframe_locked ? 'text-[#FF9500]' : 'text-white/30 hover:text-white/60'}`}
              title={shot.keyframe_locked ? 'Locked — batch regeneration will skip this shot' : 'Unlocked — click to protect this keyframe from batch regeneration'}
              data-testid={`lock-keyframe-${shot.id}`}
            >
              {shot.keyframe_locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const st = shot.topaz_upscale?.status;
                if (st === 'Processing') return; // still processing, do nothing
                if (st === 'Completed' || st === 'Reverted') {
                  // Toggle between upscaled and original
                  try {
                    const res = await axios.post(`${API}/shots/${shot.id}/upscale-toggle`);
                    toast.success(res.data.status === 'Reverted' ? 'Reverted to original' : 'Upscaled version restored');
                    const shotsRes = await axios.get(`${API}/projects/${project.id}/shots`);
                    setShots(sortShots(shotsRes.data));
                  } catch (err) { toast.error(err.response?.data?.detail || 'Toggle failed'); }
                } else {
                  // Start new upscale
                  try {
                    const res = await axios.post(`${API}/shots/${shot.id}/upscale`, { target: 'keyframe', scale: 2 });
                    toast.success(res.data.message || 'Upscale started');
                    const pollId = setInterval(async () => {
                      try {
                        const st = await axios.get(`${API}/shots/${shot.id}/upscale-status`);
                        const d = st.data;
                        setShots(prev => prev.map(s => s.id === shot.id ? { ...s, topaz_upscale: d } : s));
                        if (d.status === 'Completed') {
                          clearInterval(pollId);
                          toast.success(`Upscaled to ${d.output_width}x${d.output_height}`);
                          const shotsRes = await axios.get(`${API}/projects/${project.id}/shots`);
                          setShots(sortShots(shotsRes.data));
                        } else if (d.status === 'Failed') {
                          clearInterval(pollId);
                          toast.error(d.error || 'Upscale failed');
                        }
                      } catch { clearInterval(pollId); }
                    }, 5000);
                  } catch (err) {
                    const msg = err.response?.data?.detail || 'Upscale failed';
                    toast.error(msg);
                  }
                }
              }}
              disabled={shot.topaz_upscale?.status === 'Processing'}
              className={`p-1 rounded-md transition-colors ${
                shot.topaz_upscale?.status === 'Processing' ? 'text-cyan-400 animate-pulse' :
                shot.topaz_upscale?.status === 'Completed' ? 'text-[#22C55E]' :
                shot.topaz_upscale?.status === 'Reverted' ? 'text-white/20' :
                'text-white/30 hover:text-cyan-400'
              }`}
              title={
                shot.topaz_upscale?.status === 'Processing' ? `Upscaling... ${shot.topaz_upscale?.progress || 0}%` :
                shot.topaz_upscale?.status === 'Completed' ? 'Click to revert to original' :
                shot.topaz_upscale?.status === 'Reverted' ? 'Click to restore upscaled version' :
                'Upscale 2x with Topaz Bloom Realism'
              }
              data-testid={`upscale-keyframe-${shot.id}`}
            >
              <span className="relative inline-flex">
                <ArrowUpCircle className={`w-3.5 h-3.5 ${shot.topaz_upscale?.status === 'Processing' ? 'animate-spin' : ''}`} />
                {shot.topaz_upscale?.status === 'Completed' && (
                  <span className="absolute -bottom-0.5 -right-1 bg-[#22C55E] rounded-lg w-2 h-2 flex items-center justify-center">
                    <Check className="w-1.5 h-1.5 text-black stroke-[4]" />
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>
        <p className="text-[10px] text-white/50 mt-0.5 line-clamp-2 cursor-pointer hover:text-white/70 transition-colors" 
          onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-2'); }}
          title="Click to expand"
        >{shot.description}</p>
        {shot.dialogue && (
          <p className="text-[9px] text-cyan-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none" data-testid={`keyframe-dialogue-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-cyan-400">DLG:</span> "{shot.dialogue}"
          </p>
        )}
        {shot.voiceover && (
          <p className="text-[9px] text-amber-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none" data-testid={`keyframe-voiceover-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
            <span className="font-semibold text-amber-400">VO:</span> "{shot.voiceover}"
          </p>
        )}
        {IGK2P_ON && (
          <Igk2pStatusBar shot={rawShot} onReload={onReload} />
        )}
        
        {/* Animate prompt input */}
        {showAnimatePrompt && (
          <div className="mt-2 p-2 rounded-lg border border-cyan-500/30" style={{ background: 'rgba(6, 182, 212, 0.1)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1">
                <Play className="w-3 h-3 text-cyan-400" />
                <span className="text-[10px] font-medium text-cyan-300">Image to Video</span>
              </div>
              <span className={`text-[9px] font-mono ${animatePrompt.length > 460 ? 'text-amber-300' : 'text-white/40'}`} data-testid={`animate-prompt-charcount-${shot.id}`}>
                {animatePrompt.length}/460
              </span>
            </div>
            <div className="flex gap-1">
              <input 
                value={animatePrompt} 
                onChange={e => setAnimatePrompt(e.target.value)} 
                placeholder="Enter motion prompt (e.g., 'slow zoom in, subtle wind')"
                className="flex-1 text-[10px] px-2 py-1.5 text-white bg-ai-bg-secondary border border-cyan-500/20 rounded-lg outline-none focus:border-cyan-500"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && animatePrompt.trim()) {
                    setAnimating(true);
                    try {
                      await onAnimate?.(shot.id, animatePrompt);
                      setAnimatePrompt('');
                      setShowAnimatePrompt(false);
                    } catch (err) {
                      toast.error('Animation failed: ' + (err.message || 'Unknown error'));
                    } finally {
                      setAnimating(false);
                    }
                  }
                }}
                data-testid={`animate-prompt-input-${shot.id}`} 
              />
              <button
                onClick={async () => {
                  if (enhancing) return;
                  setEnhancing(true);
                  try {
                    const { data } = await axios.post(
                      `${API}/shots/${shot.id}/enhance-motion-prompt`,
                      { user_input: animatePrompt }
                    );
                    setAnimatePrompt(data.enhanced_prompt || '');
                    toast.success(`Enhanced (${data.char_count}/460) · used ${data.source_fields?.length || 0} Stage-2 fields${data.used_keyframe ? ' + keyframe' : ''}`);
                  } catch (err) {
                    toast.error('Enhance failed: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
                  } finally {
                    setEnhancing(false);
                  }
                }}
                disabled={enhancing}
                title="AI-enhance prompt using the keyframe + Stage-2 fields"
                data-testid={`animate-enhance-${shot.id}`}
                className="px-2 py-1 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] font-medium disabled:opacity-40 hover:bg-amber-500/30 flex items-center gap-1"
              >
                {enhancing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              </button>
              <button 
                onClick={async () => {
                  if (!animatePrompt.trim()) return;
                  setAnimating(true);
                  try {
                    await onAnimate?.(shot.id, animatePrompt);
                    setAnimatePrompt('');
                    setShowAnimatePrompt(false);
                  } catch (err) {
                    toast.error('Animation failed: ' + (err.message || 'Unknown error'));
                  } finally {
                    setAnimating(false);
                  }
                }}
                disabled={animating || !animatePrompt.trim()} 
                data-testid={`animate-submit-${shot.id}`}
                className="px-2 py-1 rounded-lg bg-cyan-500 text-white text-[10px] font-medium disabled:opacity-40 flex items-center gap-1">
                {animating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Go
              </button>
            </div>
          </div>
        )}
        
        {showFeedback && (
          <div className="mt-2 space-y-1.5">
            {refPreview && (
              <div className="relative inline-block">
                <img src={refPreview} alt="ref" className="w-14 h-10 rounded-md object-cover border border-ai-border" />
                <button onClick={clearRefImage} className="absolute -top-1 -right-1 w-4 h-4 rounded-lg bg-[#FF3B30] text-white text-[8px] flex items-center justify-center font-bold leading-none">x</button>
              </div>
            )}
            <div className="flex gap-1">
              <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} data-testid={`keyframe-ref-upload-${shot.id}`} />
              <button onClick={() => fileInputRef.current?.click()} title="Upload reference image"
                className="p-1 rounded-lg border border-ai-border bg-ai-bg-secondary hover:glass-panel transition-colors flex-shrink-0"
                data-testid={`keyframe-ref-btn-${shot.id}`}>
                <Upload className="w-3 h-3 text-[#FF9500]" />
              </button>
              <button onClick={toggleVoice} title={isListening ? "Stop recording" : "Voice input"}
                className={`p-1 rounded-lg border flex-shrink-0 transition-all ${isListening ? 'border-[#FF3B30] bg-[#FF3B30]/10 animate-pulse' : 'border-ai-border bg-ai-bg-secondary hover:glass-panel'}`}
                data-testid={`keyframe-voice-btn-${shot.id}`}>
                {isListening
                  ? <div className="w-3 h-3 rounded-sm bg-[#FF3B30]" />
                  : <svg className="w-3 h-3 text-ai-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
                }
              </button>
              <input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder={isListening ? "Listening..." : "Feedback + ref image..."}
                className={`flex-1 text-[10px] px-2 py-1 text-white bg-ai-bg-secondary border rounded-lg outline-none ${isListening ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-ai-border focus:border-[#FF9500]'}`}
                onKeyDown={e => e.key === 'Enter' && doFeedback()} data-testid={`keyframe-feedback-input-${shot.id}`} />
              <button onClick={doFeedback} disabled={sending || (!feedback.trim() && !refImage)} data-testid={`keyframe-feedback-send-${shot.id}`}
                className="p-1 rounded-lg bg-[#FF9500] text-white disabled:opacity-40">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox — rendered via portal to escape card overflow */}
      {lightbox && (shot.keyframe_url || shot.video_url || shot.keyframe_plus_url) && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-8" 
          onClick={() => setLightbox(false)} 
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); setLightbox(false); return; }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              setLightbox(false);
              window.dispatchEvent(new CustomEvent('keyframe-lightbox-nav', {
                detail: { currentShotId: shot.id, direction: e.key === 'ArrowLeft' ? -1 : 1 },
              }));
            }
          }}
          tabIndex={0}
          ref={(el) => el && el.focus()}
          style={{ background: 'rgba(0,0,0,0.9)', outline: 'none' }}>
          <div className="relative w-full h-full max-w-[85vw] max-h-[85vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightbox(false)} className="absolute top-0 right-0 p-3 text-white/60 hover:text-white z-10" data-testid="lightbox-close">
              <X className="w-8 h-8" />
            </button>
            {/* ← / → navigation buttons */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(false);
                window.dispatchEvent(new CustomEvent('keyframe-lightbox-nav', {
                  detail: { currentShotId: shot.id, direction: -1 },
                }));
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition z-10"
              title="Previous (←)"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(false);
                window.dispatchEvent(new CustomEvent('keyframe-lightbox-nav', {
                  detail: { currentShotId: shot.id, direction: 1 },
                }));
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition z-10"
              title="Next (→)"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
            
            {/* Lightbox always shows the keyframe image (this is KeyframeCard's
             *  own lightbox — the video player belongs to the Video stage's
             *  VideoCard). Video preview on hover is still allowed in the
             *  tile thumbnail, but click-to-expand should always reveal the
             *  keyframe at full resolution. */}
            {shot.keyframe_url_original ? (
              <div className={`relative ${aspectClass} rounded-xl overflow-hidden select-none`}>
                {/* Original (full width behind) */}
                <img src={resolveUrl(shot.keyframe_url_original)} alt="Original" className="absolute inset-0 w-full h-full object-cover" />
                {/* Face-swapped (clipped by slider) */}
                <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
                  <img src={resolveUrl(shot.keyframe_url)} alt="Face swapped" className="w-full h-full object-cover" style={{ width: `${10000 / sliderPos}%`, maxWidth: 'none' }} />
                </div>
                {/* Slider line */}
                <div className="absolute top-0 bottom-0 w-0.5 bg-white z-10 cursor-col-resize" style={{ left: `${sliderPos}%` }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.parentElement.getBoundingClientRect();
                    const move = (ev) => { setSliderPos(Math.max(5, Math.min(95, ((ev.clientX - rect.left) / rect.width) * 100))); };
                    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
                    window.addEventListener('mousemove', move);
                    window.addEventListener('mouseup', up);
                  }}>
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-lg bg-white shadow-lg flex items-center justify-center">
                    <ChevronLeft className="w-3 h-3 text-black" /><ChevronRight className="w-3 h-3 text-black -ml-1" />
                  </div>
                </div>
                {/* Labels */}
                <div className="absolute bottom-3 left-3 px-2 py-1 rounded text-[10px] font-bold bg-black/70 text-white">Original</div>
                <div className="absolute bottom-3 right-3 px-2 py-1 rounded text-[10px] font-bold bg-ai-accent/80 text-white">Face Swapped</div>
              </div>
            ) : (
              /* Simple enlarged view.
               * 2026-05-17 — for IGK Hyperreal shots, default to the S-Log3
               * sibling (matches the card thumbnail at L3391). The existing
               * 709 toggle on the card flips this lightbox back to Rec.709
               * via showRec709Preview (D2 = keep escape hatch). */
              <img
                src={resolveUrl(
                  isHyperreal && !showRec709Preview
                    && (rawShot.keyframe_hyperreal_slog3_v2_url
                        || rawShot.keyframe_hyperreal_slog3_url)
                  ? (rawShot.keyframe_hyperreal_slog3_v2_url
                      || rawShot.keyframe_hyperreal_slog3_url)
                  : (shot.keyframe_url || shot.keyframe_plus_url)
                )}
                alt={`${shot.scene_number}-${shot.shot_number}`}
                className="max-w-full max-h-[80vh] w-auto h-auto rounded-xl object-contain"
                data-testid={`lightbox-keyframe-${shot.id}`}
              />
            )}
            
            <div className="flex items-center justify-between mt-3">
              <span className="text-sm text-white font-medium">{shot.scene_number} — {shot.shot_number}</span>
              {shot.keyframe_url_original && (
                <div className="flex gap-2">
                  <button onClick={() => { onRevert?.(shot.id); setLightbox(false); }}
                    className="px-3 py-1.5 rounded-lg text-xs text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/10">
                    <RotateCcw className="w-3 h-3 inline mr-1" /> Revert to Original
                  </button>
                </div>
              )}
            </div>
            
            {/* Editable Prompt — click to edit, Enter to regenerate */}
            <div className="w-full mt-3">
              <textarea
                defaultValue={shot.keyframe_prompt || shot.mj_prompt || shot.description || ''}
                className="w-full px-4 py-3 rounded-sm text-sm font-ai text-white/80 resize-none focus:text-white transition-colors"
                style={{ background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.2)' }}
                rows={2}
                placeholder="Click to edit prompt. Press Enter to regenerate keyframe."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const newPrompt = e.target.value.trim();
                    if (newPrompt) {
                      onRegenerate?.(shot.id, newPrompt);
                      setLightbox(false);
                    }
                  }
                }}
                data-testid="lightbox-prompt-edit"
              />
              <p className="text-[10px] text-white/30 mt-1 font-ai">Press Enter to regenerate keyframe with edited prompt. Shift+Enter for new line.</p>
            </div>
          </div>
        </div>
      , document.body)}

      {/* MJ Iteration Modal */}
      {showMjIterModal && (
        <MjIterationModal
          shotId={shot.id}
          shot={shot}
          aspectClass={aspectClass}
          onClose={() => setShowMjIterModal(false)}
          onSelect={() => { setShowMjGrid(false); setShowingRaw(false); onLockToggle?.(); }}
        />
      )}

      {/* Gemini Iteration Modal */}
      {showGeminiIterModal && (
        <GeminiIterationModal
          shotId={shot.id}
          shot={shot}
          aspectClass={aspectClass}
          preselectedEngine={iterEngine}
          // Target-array selection (2026-05-23 Rohit) —
          // Priority:
          //   1. `workbenchTarget` override set by the auto-open event
          //      (matches the array the regen backend just wrote to).
          //   2. `isHyperreal` derived from the aliased shot (true when
          //      `keyframe_hyperreal_url` exists, i.e. IGK has been run) →
          //      target `keyframe_hyperreal_url` so the filmstrip pulls
          //      the `hyperreal_iterations` array where fresh IGK regen
          //      entries (pre_regen / regen_current) actually live.
          //   3. Otherwise target the classic `keyframe_url` (gemini_iterations).
          targetField={workbenchTarget || (isHyperreal ? 'keyframe_hyperreal_url' : 'keyframe_url')}
          onClose={() => { setShowGeminiIterModal(false); setWorkbenchTarget(null); }}
          onSelect={() => { setShowMjGrid(false); setShowFeedback(false); setShowingRaw(false); onLockToggle?.(); }}
        />
      )}
    </div>
  );
}

function ElapsedTimerBar({ project }) {
  const [now, setNow] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Support both old typo 'startd_at' and correct 'started_at', with fallback to created_at
  const stageTimestamps = project.stage_timestamps || {};
  const stage1 = stageTimestamps.stage_1 || {};
  const startTime = stage1.started_at || stage1.startd_at || project.created_at;
  if (!startTime) return null;
  
  const start = new Date(startTime.endsWith('Z') || startTime.includes('+') ? startTime : startTime + 'Z');
  const diffMs = now - start;
  const diffS = Math.max(0, Math.floor(diffMs / 1000));
  const hrs = Math.floor(diffS / 3600);
  const mins = Math.floor((diffS % 3600) / 60);
  const secs = diffS % 60;
  const elapsedStr = hrs > 0 
    ? `${hrs}h ${String(mins).padStart(2,'0')}m ${String(secs).padStart(2,'0')}s`
    : `${mins}m ${String(secs).padStart(2,'0')}s`;
  
  const startStr = start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  const nowStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' });
  const progress = Math.min(100, (project.current_stage / 17) * 100);
  
  // Per-stage timeline dots - support both old typo and correct field names
  const stages = stageTimestamps;
  
  return (
    <div className="px-4 py-2 border-b border-ai-border" style={{ background: 'rgba(19, 27, 44, 0.5)' }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/40">Started {startStr}</span>
          <span className="text-[10px] text-white/60 font-mono font-semibold">{elapsedStr}</span>
        </div>
        <span className="text-[10px] text-ai-text font-mono">{nowStr}</span>
      </div>
      <div className="relative h-1.5 rounded-lg overflow-hidden" style={{ background: 'rgba(14,165,233,0.1)' }}>
        <div className="h-full rounded-lg transition-all duration-1000" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #0EA5E9, #14B8A6)' }} />
      </div>
      <div className="flex gap-1 mt-1">
        {Object.entries(stages).sort().map(([key, val]) => {
          const num = key.replace('stage_', '');
          const done = !!val.completed_at;
          // Support both old typo 'startd_at' and correct 'started_at'
          const hasStarted = val.started_at || val.startd_at;
          const active = hasStarted && !val.completed_at;
          const dur = val.duration_seconds;
          return (
            <div key={key} className="flex items-center gap-0.5" title={`Stage ${num}: ${dur ? Math.round(dur) + 's' : active ? 'running...' : 'pending'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${done ? 'bg-green-400' : active ? 'bg-yellow-400 animate-pulse' : 'bg-white/10'}`} />
              <span className={`text-[7px] font-mono ${done ? 'text-white/40' : active ? 'text-yellow-400' : 'text-white/15'}`}>
                {num}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function CollapsibleSection({ title, icon, badge, expanded, onToggle, children, testId, rightContent, duration, stageTime }) {
  return (
    <div className="rounded-sm overflow-hidden bg-ai-bg-secondary shadow-sm" data-testid={testId}>
      <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle()} className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 hover:bg-white/[0.02] transition-colors text-left cursor-pointer">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          {icon}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-ai font-medium text-xs sm:text-sm text-ai-text tracking-tight whitespace-nowrap truncate">{title}</span>
              {badge}
              {duration && <span className="text-[9px] text-ai-text-secondary font-ai whitespace-nowrap hidden sm:inline">{duration}</span>}
            </div>
            {stageTime && <span className="text-[8px] text-ai-text-secondary/50 font-ai mt-0.5">{stageTime}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {rightContent}
          {expanded ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t border-ai-border pt-4 animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
}

/* ═══════════ PROGRESS BAR ═══════════ */
function ProgressBar({ label, progress, color, onStop, taskType }) {
  const queuePos = Number(progress?.queue_position || 0);
  const isQueued = queuePos > 0;
  return (
    <div className="mt-3" data-testid={`progress-${taskType || 'bar'}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-white/50">{label}</span>
        <div className="flex items-center gap-2">
          {isQueued ? (
            <span
              className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/25"
              title="The server is busy. Your job is queued and will start automatically when a slot frees up."
              data-testid={`queue-position-${taskType}`}
            >
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              Queued · position {queuePos}
            </span>
          ) : (
            <span style={{ color }}>{progress.completed}/{progress.total} ({progress.percentage}%)</span>
          )}
          {onStop && (
            <button 
              onClick={() => onStop(taskType)}
              className="px-2 py-0.5 text-[10px] bg-[#FF3B30] text-white rounded-full hover:bg-[#FF3B30]/90 transition-colors"
              data-testid={`stop-${taskType}-btn`}
            >
              Stop
            </button>
          )}
        </div>
      </div>
      {isQueued ? (
        <div className="w-full bg-white/10 rounded-lg h-1.5 overflow-hidden">
          <div className="h-1.5 rounded-lg w-1/3 animate-pulse" style={{ background: '#F59E0B' }} />
        </div>
      ) : (
        <div className="w-full bg-white/10 rounded-lg h-1.5">
          <div className="h-1.5 rounded-lg transition-all duration-500" style={{ width: `${progress.percentage}%`, background: color }} />
        </div>
      )}
    </div>
  );
}

/* ═══════════ MAIN PROJECT VIEW ═══════════ */

// Canonical shot ordering — display shots strictly in sequential
// (scene, then shot) order, which is what the pipeline and the operator expect.
// order_index is demoted to a tiebreaker only: a stale/anchor-inserted
// order_index (e.g. an added shot that grabbed order_index 0) must NOT hijack
// the sequence and float a late-scene shot to the top. Manual reorder renumbers
// shot_number itself, so canonical order still honours deliberate reordering.
const compareShots = (a, b) => {
  const scA = parseInt(String(a.scene_number || '').replace(/\D/g, ''), 10) || 0;
  const scB = parseInt(String(b.scene_number || '').replace(/\D/g, ''), 10) || 0;
  if (scA !== scB) return scA - scB;
  const mA = String(a.shot_number || '').match(/(\d+)([a-z]*)/i) || [];
  const mB = String(b.shot_number || '').match(/(\d+)([a-z]*)/i) || [];
  const nA = parseInt(mA[1], 10) || 0, nB = parseInt(mB[1], 10) || 0;
  if (nA !== nB) return nA - nB;
  const suffix = String(mA[2] || '').localeCompare(String(mB[2] || ''));
  if (suffix !== 0) return suffix;
  const ao = a.order_index, bo = b.order_index;
  if (typeof ao === 'number' && typeof bo === 'number' && ao !== bo) return ao - bo;
  return 0;
};
const sortShots = (arr) => (Array.isArray(arr) ? [...arr].sort(compareShots) : arr);

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Layout state
  const [showSidebar, setShowSidebar] = useState(false);
  const [viewMode, setViewMode] = useState('stages'); // 'stages' or 'pipeline'
  // AI Chat panel - always collapsed on mobile, open on desktop
  const [showChat, setShowChat] = useState(false);
  const [activeSidebarItem, setActiveSidebarItem] = useState('preproduction');
  const [activeStageGroup, setActiveStageGroup] = useState('script');
  const [showSettings, setShowSettings] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTemplatizerModal, setShowTemplatizerModal] = useState(false);
  const [showShootingScriptModal, setShowShootingScriptModal] = useState(false);
  const [showImageBoardModal, setShowImageBoardModal] = useState(false);
  const [scriptViewTab, setScriptViewTab] = useState('edit'); // 'edit' | 'original' | 'templatized' | 'shooting_script'
  
  // AI Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: `Hello! 👋 I'm your AI production assistant. How can I help you today?` }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  const [project, setProject] = useState(null);
  const [shots, setShots] = useState([]);

  // Test mode: "Show all sections" — persisted. When on, every
  // stage-gate returns true so users can browse every section at once
  // for review/QA. Read via `effectiveStage` / `isPpmApproved()`
  // helpers only — do NOT reference project.current_stage or
  // project.ppm_approved directly for section gating.
  const [showAllStages, setShowAllStages] = useState(() => {
    try { return localStorage.getItem('showAllStages') === '1'; } catch { return false; }
  });

  // ── Mock Shoot gate — 2026-06-22 strict button enforcement ─────────────
  // The gate is a HARD lock for every non-owner account: sketches, IGS,
  // and IGK buttons must be VISUALLY disabled (not just API-rejected)
  // until every shot has both mock_image_url + mock_video_url. Owner
  // (rohit@eipimedia.com) bypasses entirely — all buttons stay enabled
  // regardless of mock shoot state.
  const OWNER_EMAIL = 'rohit@eipimedia.com';
  const isOwner = (user?.email || '').toLowerCase() === OWNER_EMAIL;
  const [mockShootUnlocked, setMockShootUnlocked] = useState(null);  // null = unknown
  // True when buttons may fire: owner (always) OR gate is unlocked.
  // null (still loading) is treated as locked so we never flash an
  // enabled button before the probe returns.
  const sketchGateOpen = isOwner || mockShootUnlocked === true;

  // Pre-fetch the gate state on project load so the buttons render in
  // their final disabled/enabled state on first paint (no flicker).
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/mock-shoot/progress`);
        if (!cancelled) setMockShootUnlocked(!!r.data?.unlocked);
      } catch { if (!cancelled) setMockShootUnlocked(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Animatic Reel — bulk concat state
  const [reelStatus, setReelStatus] = useState({
    status: 'idle', progress_pct: 0, mp4_url: null, error: '', last_message: '',
    shots_total: 0, shots_animated: 0,
  });
  const [reelKicking, setReelKicking] = useState(false);
  const reelPollRef = useRef(null);

  // Cross-card lightbox navigation: when a KeyframeCard fires
  // 'keyframe-lightbox-nav' (←/→), find the next/prev shot in the global shots
  // array and tell THAT card to open its own lightbox.
  useEffect(() => {
    const handler = (e) => {
      const { currentShotId, direction } = e.detail || {};
      if (!currentShotId || !direction || !shots.length) return;
      // Only navigate among shots that have something viewable.
      // Includes keyframe_plus_url so K+-only projects (where the legacy
      // keyframe_url is empty) still get arrow-key navigation.
      const eligible = shots.filter((s) => s.keyframe_url || s.keyframe_plus_url || s.video_url);
      if (eligible.length === 0) return;
      const idx = eligible.findIndex((s) => s.id === currentShotId);
      const startIdx = idx === -1 ? 0 : idx;
      const nextIdx = (startIdx + direction + eligible.length) % eligible.length;
      const nextShot = eligible[nextIdx];
      if (!nextShot) return;
      // Slight delay so the closing card finishes its state update before opening the next
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('keyframe-lightbox-open', { detail: { shotId: nextShot.id } }));
      }, 50);
    };
    window.addEventListener('keyframe-lightbox-nav', handler);
    return () => window.removeEventListener('keyframe-lightbox-nav', handler);
  }, [shots]);

  // Animatic Reel polling — runs only while a reel is processing.
  const pollReelOnce = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/animatic-reel/status`);
      setReelStatus(res.data || {});
      return res.data;
    } catch (e) {
      return null;
    }
  };
  // One-shot read on mount + when shots refresh, so the button has accurate counts.
  useEffect(() => {
    if (!projectId) return;
    pollReelOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, shots.length]);
  // Active polling while a job is in-flight.
  useEffect(() => {
    if (reelStatus.status !== 'processing') {
      if (reelPollRef.current) clearInterval(reelPollRef.current);
      reelPollRef.current = null;
      return;
    }
    reelPollRef.current = setInterval(pollReelOnce, 5000);
    return () => {
      if (reelPollRef.current) clearInterval(reelPollRef.current);
      reelPollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelStatus.status, projectId]);

  const generateAnimaticsReel = async () => {
    if (reelKicking || reelStatus.status === 'processing') return;
    setReelKicking(true);
    try {
      await axios.post(`${API}/projects/${projectId}/animatic-reel/generate`);
      toast.success('Animatic Reel stitching started — usually 1–3 min');
      // Optimistic flip to processing so polling kicks in immediately.
      setReelStatus((p) => ({ ...p, status: 'processing', progress_pct: 0, last_message: 'Queued' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to start reel');
    } finally {
      setReelKicking(false);
    }
  };

  // ── Sample Music (Suno) — sits under the Animatic Reel section.
  const [musicStatus, setMusicStatus] = useState({ status: 'none' });
  const [musicKicking, setMusicKicking] = useState(false);
  const musicPollRef = useRef(null);

  const pollMusicOnce = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/animatic-music/status`);
      setMusicStatus(res.data || { status: 'none' });
      return res.data;
    } catch (e) {
      return null;
    }
  };
  useEffect(() => {
    if (!projectId) return;
    pollMusicOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  useEffect(() => {
    if (musicStatus.status !== 'processing') {
      if (musicPollRef.current) clearInterval(musicPollRef.current);
      musicPollRef.current = null;
      return;
    }
    musicPollRef.current = setInterval(pollMusicOnce, 8000);
    return () => {
      if (musicPollRef.current) clearInterval(musicPollRef.current);
      musicPollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicStatus.status, projectId]);

  const generateAnimaticMusic = async () => {
    if (musicKicking || musicStatus.status === 'processing') return;
    setMusicKicking(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/animatic-music/generate`);
      setMusicStatus(res.data || { status: 'processing' });
      toast.success('Composing sample music — usually 1–2 min');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to start music');
    } finally {
      setMusicKicking(false);
    }
  };

  // Recipe count for the sidebar badge — always tallies the true total
  // (same source as the /recipes page "N recipes available").
  const [recipeCount, setRecipeCount] = useState(null);
  useEffect(() => {
    axios.get(`${API}/recipes`)
      .then(r => {
        const list = r.data?.recipes || [];
        if (Array.isArray(list)) setRecipeCount(list.length);
      })
      .catch(() => {});
  }, []);

  const [assetBible, setAssetBible] = useState(null);
  const [derivedCCC, setDerivedCCC] = useState(null);
  // May 2026 — Cast tiering state. Gates Asset Bible image-gen behind explicit
  // Primary/Secondary picks + a Confirm click. Loaded on demand from GET
  // /cast-tiering. `castTiering` shape: {cast, confirmed, primary_count,
  // total_count, needs_reconfirm, new_cast_since_confirm}.
  const [castTiering, setCastTiering] = useState(null);
  const [castTieringSaving, setCastTieringSaving] = useState(false);
  const [castTieringError, setCastTieringError] = useState(null);
  // May 2026 — Shot Map expandable view. Shows the per-shot-per-cast
  // consensus output from the 3-LLM extraction (GPT-5.5 + Opus 4.7 +
  // Gemini 3 Pro reconciled by Opus). Collapsed by default to keep the
  // main AB Bindings panel compact.
  const [showShotMap, setShowShotMap] = useState(false);
  const loadCastTiering = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/cast-tiering`);
      setCastTiering(res.data);
    } catch (err) {
      // Non-fatal — panel just won't render until the next reload.
    }
  };
  // Debounced auto-save for derived_ccc edits. Without this, deletes/edits
  // never reach the backend and the Asset Bible generates from the stale list
  // (or worse, re-hallucinates deleted characters via the script-LLM fallback).
  const cccSaveTimerRef = useRef(null);
  const updateCCC = (updater, { immediate = false } = {}) => {
    setDerivedCCC(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Schedule persist. Structural changes (add/delete) save immediately;
      // text edits debounce 600ms to avoid POST spam on every keystroke.
      if (cccSaveTimerRef.current) {
        clearTimeout(cccSaveTimerRef.current);
        cccSaveTimerRef.current = null;
      }
      const doSave = () => {
        axios.post(`${API}/projects/${projectId}/derived-ccc`, next)
          .catch(err => console.warn('[CCC] auto-save failed', err));
      };
      if (immediate) doSave();
      else cccSaveTimerRef.current = setTimeout(doSave, 600);
      return next;
    });
  };
  const [validationPolling, setValidationPolling] = useState(false);
  const [validationStatus, setValidationStatus] = useState('');
  const [showValidateForm, setShowValidateForm] = useState(false);
  const [downloadingShootingScript, setDownloadingShootingScript] = useState(false);

  // NEW FLOW (2026-04-30 rev 2): shooting script is auto-generated in the
  // background when the user saves their script. This handler just streams
  // the pre-generated .docx — no Opus call at download time. Matches the
  // PPM-PDF download pattern used elsewhere in the app.
  const downloadShootingScript = async () => {
    if (!projectId) return;
    setDownloadingShootingScript(true);
    const tid = toast.loading('Preparing shooting script…');
    try {
      const res = await fetch(`${API}/projects/${projectId}/shooting-script.docx`, {
        method: 'GET',
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); detail = j.detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'shooting_script.docx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Shooting script downloaded', { id: tid });
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`, { id: tid });
    } finally {
      setDownloadingShootingScript(false);
    }
  };

  // Pull the auto-generated shooting script (Opus 4.7) directly into the
  // shot breakdown stage — saves the user from downloading the .docx and
  // re-uploading it. Uses the same parser as the paste route.
  const pullFromShootingScript = async () => {
    if (!projectId) return;
    if (!project?.shooting_script_text) {
      if (project?.shooting_script_processing) {
        toast.error('Opus is still writing your shooting script — wait for the green Ready card.');
      } else {
        toast.error("No shooting script yet — click 'Generate Shooting Script' first, then pull shots from it.");
      }
      return;
    }
    setUploading(true); setUploadingAction('breakdown');
    const tid = toast.loading('Pulling shots from shooting script…');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/shot-breakdown/from-shooting-script`);
      toast.success(`Parsing ${res.data.chars_received?.toLocaleString() || ''} chars… shots will appear shortly.`, { id: tid });
      // Refresh + start the breakdown polling loop the existing flow uses.
      loadProjectData();
      setExpandedSections(prev => ({ ...prev, shots: true }));
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Pull failed: ${detail}`, { id: tid });
      setUploading(false); setUploadingAction(null);
    }
  };

  // Trigger a regeneration (only exposed when user has edited their script
  // after an earlier generation). Fires the background job; UI polling
  // will pick up `shooting_script_processing` and swap the button label.
  const regenerateShootingScript = async () => {
    if (!projectId) return;
    const tid = toast.loading('Requesting shooting script…');
    try {
      await axios.post(`${API}/projects/${projectId}/generate-shooting-script`);
      toast.success('Generation started (~60–120s)', { id: tid });
      setShootingScriptStartedAt(Date.now());
      setShootingScriptElapsed(0);
      // loadProjectData is defined later in the component; call via window
      // of the enclosing closure — since these handlers are recreated on
      // every render, the latest loadProjectData is always in scope.
      setTimeout(() => { if (typeof loadProjectData === 'function') loadProjectData(); }, 1500);
      pollShootingScriptStatus();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`, { id: tid });
    }
  };

  // Poll project status while shooting-script generation is in flight.
  // Refreshes project data every 5s; stops when processing flips false.
  // 6-min hard cap so a stuck job can't poll forever.
  const pollShootingScriptStatus = () => {
    if (shootingScriptPollRef.current) clearInterval(shootingScriptPollRef.current);
    const startedAt = Date.now();
    const POLL_TIMEOUT_MS = 6 * 60 * 1000;
    shootingScriptPollRef.current = setInterval(async () => {
      try {
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          clearInterval(shootingScriptPollRef.current);
          shootingScriptPollRef.current = null;
          setShootingScriptStartedAt(null);
          // Background task likely orphaned (hot reload / pod restart). The
          // backend row is stuck on `processing: True` so the UI shows a
          // disabled spinner with no Retry button. Force-clear the stuck
          // state by hitting the regen endpoint, which resets processing +
          // hash and kicks a fresh task — same as a user clicking Retry.
          toast.error('Shooting script generation timed out — restarting it for you…');
          try {
            await axios.post(`${API}/projects/${projectId}/generate-shooting-script`);
            setShootingScriptStartedAt(Date.now());
            setShootingScriptElapsed(0);
            pollShootingScriptStatus();
          } catch (regenErr) {
            toast.error(`Auto-retry failed: ${regenErr.response?.data?.detail || regenErr.message}. Reload the page to see the Retry button.`);
          }
          if (typeof loadProjectData === 'function') loadProjectData();
          return;
        }
        // Tick the elapsed counter for the visible status pill.
        setShootingScriptElapsed(Math.floor((Date.now() - startedAt) / 1000));
        const res = await axios.get(`${API}/projects/${projectId}`);
        const proj = res.data;
        if (!proj.shooting_script_processing) {
          clearInterval(shootingScriptPollRef.current);
          shootingScriptPollRef.current = null;
          setShootingScriptStartedAt(null);
          if (proj.shooting_script_text) {
            toast.success('Shooting script ready — click Download.');
          } else if (proj.shooting_script_error) {
            toast.error(`Shooting script failed: ${proj.shooting_script_error}`);
          }
          if (typeof loadProjectData === 'function') loadProjectData();
        }
      } catch (e) {
        // Don't kill the loop on a single transient failure — let the
        // 6-min cap eventually shut it down.
        console.error('Shooting-script poll error:', e);
      }
    }, 5000);
  };
  const [showAllAgents, setShowAllAgents] = useState(false);
  const [searchAgent, setSearchAgent] = useState('');
  const [filterStance, setFilterStance] = useState('all');
  const [selectedValidateMode, setSelectedValidateMode] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [scriptText, setScriptText] = useState('');
  const [narrationUploading, setNarrationUploading] = useState(false);
  const narrationInputRef = useRef(null);
  const scriptFileInputRef = useRef(null);
  // Webcam recording state
  const [showRecorder, setShowRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingAction, setUploadingAction] = useState(null);
  const [blueprintsBusy, setBlueprintsBusy] = useState(false);
  // Feb 2026: bulk auto-fill Action / Camera / Emotion across every shot
  // at Stage 13. Backed by POST /api/projects/{id}/backfill-motion-fields.
  const [autoFillingMotion, setAutoFillingMotion] = useState(false);
  // IGS split-button dropdown (2026-05-22) — anchors the "Next 3 vs All
  // remaining" picker on the Insanely Good Sketches button. Rendered via
  // ReactDOM portal because the toolbar parent has overflow-x-auto which
  // clips children on the Y axis (CSS spec: overflow-x: auto implies
  // overflow-y: hidden). Portal escapes the clipping context.
  const [igsMenuOpen, setIgsMenuOpen] = useState(false);
  const [igsMenuPos, setIgsMenuPos] = useState({ top: 0, left: 0 });
  const igsAnchorRef = useRef(null);
  const igsMenuRef = useRef(null);
  useEffect(() => {
    if (!igsMenuOpen) return;
    if (igsAnchorRef.current) {
      const r = igsAnchorRef.current.getBoundingClientRect();
      setIgsMenuPos({ top: r.bottom + 4, left: r.left });
    }
    const onDocClick = (e) => {
      if (igsMenuRef.current && igsMenuRef.current.contains(e.target)) return;
      if (igsAnchorRef.current && igsAnchorRef.current.contains(e.target)) return;
      setIgsMenuOpen(false);
    };
    const onScroll = () => setIgsMenuOpen(false);
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [igsMenuOpen]);

  // IGK split-button dropdown (2026-05-22) — same pattern as IGS for the
  // Insanely Good Keyframes button on the keyframe section toolbar.
  const [igkMenuOpen, setIgkMenuOpen] = useState(false);
  const [igkMenuPos, setIgkMenuPos] = useState({ top: 0, left: 0 });
  const igkAnchorRef = useRef(null);
  const igkMenuRef = useRef(null);
  useEffect(() => {
    if (!igkMenuOpen) return;
    if (igkAnchorRef.current) {
      const r = igkAnchorRef.current.getBoundingClientRect();
      setIgkMenuPos({ top: r.bottom + 4, left: r.left });
    }
    const onDocClick = (e) => {
      if (igkMenuRef.current && igkMenuRef.current.contains(e.target)) return;
      if (igkAnchorRef.current && igkAnchorRef.current.contains(e.target)) return;
      setIgkMenuOpen(false);
    };
    const onScroll = () => setIgkMenuOpen(false);
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [igkMenuOpen]);

  // KEYFRAME+ split-button dropdown (2026-05-27 PM) — completely
  // independent silo from IGK. Same UX pattern (Next 3 / All remaining)
  // but writes to its own keyframe_plus_* DB collections and uses
  // OpenAI-only stack (gpt-5.5 prompt composer → gpt-image-2 →
  // gpt-5.5 vision QA). Spec: /app/memory/keyframe_plus_spec.md.
  const [kfpMenuOpen, setKfpMenuOpen] = useState(false);
  const [kfpMenuPos, setKfpMenuPos] = useState({ top: 0, left: 0 });
  const kfpAnchorRef = useRef(null);
  const kfpMenuRef = useRef(null);
  const [kfpFiring, setKfpFiring] = useState(false);
  // Look-feel ref count — surfaced as a badge on the K+ buttons so the
  // operator always sees how many style refs are loaded.
  const [lookFeelRefCount, setLookFeelRefCount] = useState(0);
  // Live progress polled every 5s — shows under the button so operator
  // can see jobs fire / advance / fail in real time without opening a modal.
  const [kfpSummary, setKfpSummary] = useState({
    shots_total: 0, shots_with_keyframe_plus: 0, shots_in_flight: 0, shots_failed: 0,
  });
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/keyframe-plus/summary`);
        if (!cancelled && r.data) setKfpSummary(r.data);
      } catch (_e) { /* swallow */ }
    };
    fetchSummary();
    const handle = setInterval(fetchSummary, 5000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [projectId]);
  useEffect(() => {
    if (!kfpMenuOpen) return;
    if (kfpAnchorRef.current) {
      const r = kfpAnchorRef.current.getBoundingClientRect();
      setKfpMenuPos({ top: r.bottom + 4, left: r.left });
    }
    const onDocClick = (e) => {
      if (kfpMenuRef.current && kfpMenuRef.current.contains(e.target)) return;
      if (kfpAnchorRef.current && kfpAnchorRef.current.contains(e.target)) return;
      setKfpMenuOpen(false);
    };
    const onScroll = () => setKfpMenuOpen(false);
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [kfpMenuOpen]);

  // KEYFRAME+ live polling — when ANY job is in-flight (queued / composing /
  // generating / auditing) re-fetch the shots list every 10s so the operator
  // sees keyframe_plus_url / keyframe_plus_status flip on each shot card
  // without needing a hard refresh.
  useEffect(() => {
    if (!projectId) return;
    if (kfpSummary.shots_in_flight <= 0) return;
    let cancelled = false;
    const handle = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/shots`);
        if (!cancelled && Array.isArray(r.data)) setShots(r.data);
      } catch (_e) { /* swallow — next tick will retry */ }
    }, 10000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [projectId, kfpSummary.shots_in_flight]);

  // Fire the KEYFRAME+ batch endpoint. `limit=null` means all remaining.
  const fireKeyframePlusBatch = useCallback(async (limit) => {
    if (!projectId) return;
    setKfpFiring(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/keyframe-plus/batch`,
        { limit: limit || null },
      );
      if (r.data?.noop) {
        toast.info(r.data?.message || 'No shots need Keyframe+');
      } else {
        toast.success(`Keyframe+ queued ${r.data?.queued_count || 0} shot(s) — polling for updates`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Keyframe+ batch failed';
      toast.error(`Keyframe+ batch failed: ${String(msg).slice(0, 160)}`);
    } finally {
      setKfpFiring(false);
    }
  }, [projectId]);
  const [useProductionAgent, setUseProductionAgent] = useState(true);
  // Default OFF — enhancement is slow (~60s) and most scripts are already well-formatted.
  // User can opt-in via the toggle next to the Submit button.
  const [enhanceScript, setEnhanceScript] = useState(false);
  // P0-3 Asset Bible Bulk-Iterate modal
  const [bulkIterateOpen, setBulkIterateOpen] = useState(false);
  const [sketchProgress, setSketchProgress] = useState(null);
  const [assetProgress, setAssetProgress] = useState(null);
  const [downloading, setDownloading] = useState(null);
  
  // Global image lightbox state
  const [globalLightbox, setGlobalLightbox] = useState(null); // { url, alt } or null

  // Prompt Engine / Keyframe state
  const [promptProgress, setPromptProgress] = useState(null);
  const [keyframeProgress, setKeyframeProgress] = useState(null);

  // Stages 9-16 state
  const [vlmQaProgress, setVlmQaProgress] = useState(null);
  const [neutralProgress, setNeutralProgress] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const [selectedVideoEngine, setSelectedVideoEngine] = useState('auto');
  const [videoQaProgress, setVideoQaProgress] = useState(null);
  const [keyframeLineup, setKeyframeLineup] = useState(null);
  const [exportPackage, setExportPackage] = useState(null);

  // Premium status for HappyHorse and MJ features
  const [isPremiumMj, setIsPremiumMj] = useState(false);

  // Script processing state
  const [scriptProcessing, setScriptProcessing] = useState(false);
  const [shotsProcessing, setShotsProcessing] = useState(false);
  const [shotsProgress, setShotsProgress] = useState('');
  const [shotGenError, setShotGenError] = useState(null);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // ── Shot Breakdown — Input Method Picker state ──
  // Three equal-weight input methods: AI-generate / Upload / Paste.
  // (See `design_guidelines.md` + Shot Breakdown brief 2026-05-15.)
  // The user picks a method, stages content (file/text), then clicks the
  // single Generate CTA which dispatches based on `breakdownInputSource`.
  const [breakdownInputSource, setBreakdownInputSource] = useState('ai');
  const [pendingBreakdownFile, setPendingBreakdownFile] = useState(null);
  const [pendingBreakdownText, setPendingBreakdownText] = useState('');
  // Holds the source we're trying to switch TO when the user has unsaved
  // staged content in the current source — drives the discard-guard dialog.
  const [pendingSwitchTo, setPendingSwitchTo] = useState(null);
  // Post-generation "↻ Re-run" confirmation modal toggle.
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  
  // Script Analysis Modal
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [scriptAnalysis, setScriptAnalysis] = useState(null);
  const [analyzingScript, setAnalyzingScript] = useState(false);
  const [bindingAssets, setBindingAssets] = useState(false);
  const [analysisDocxReady, setAnalysisDocxReady] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const analysisPollRef = useRef(null);

  // Product state
  const [productName, setProductName] = useState('');
  const [productFeatures, setProductFeatures] = useState('');
  const [productFile, setProductFile] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productImgError, setProductImgError] = useState(false);
  const [productSaved, setProductSaved] = useState(false);

  // All sections collapsed by default on page load
  const [expandedSections, setExpandedSections] = useState({
    script: false, productionPackage: false, shots: false, crewPreprod: false, sketches: false, sketchAngles: false, assets: false, prompts: false, keyframes: false, keyframeAngles: false,
    vlmQa: false, keyframeLock: false, neutralMaster: false, lineup: false, videoGen: false, videoQa: false, videoVlmFrames: false, videoLock: false, export: false, postproduction: false, faceConsistency: false,
    // 2026-06-24 — Sections that default to EXPANDED need an explicit
    // `true` here, otherwise `expandedSections.<key>` starts as
    // `undefined`. The render uses `!== false` (so undefined ≈ expanded)
    // but the toggle does `!prev[key]` → `!undefined` = `true`, leaving
    // the section still expanded after the first click. Users see "I
    // have to click 2-3 times to collapse" — fixed by seeding `true`.
    mockShoot: true, sceneGeography: true, validate: true,
  });

  // ── Stage 2.5 Crew Pre-Production state (2026-05-25) ─────────────────
  // Three editable HOD briefs (Casting / Costume / Art Director) layered
  // on top of Asset Bible image prompts. Polled at the standard 4s
  // cadence. Brief counts come from the status endpoint; the briefs
  // themselves live on the asset_bibles rows so they re-render on
  // loadProjectData() with no separate fetch.
  const [crewPreprodProcessing, setCrewPreprodProcessing] = useState(false);
  const [crewBriefCounts, setCrewBriefCounts] = useState({ cast: 0, costume: 0, location: 0 });
  const crewPollRef = useRef(null);
  // ── Bulk Brief Preview state (2026-06-25) ─────────────────────────
  // Runs the per-card "Generate Preview" endpoint across every brief
  // card in parallel (throttled to 3 concurrent OpenAI calls). Shows
  // a progress counter so the operator knows when the batch is done.
  const [bulkPreviewing, setBulkPreviewing] = useState(false);
  const [bulkPreviewProgress, setBulkPreviewProgress] = useState({ done: 0, total: 0, failed: 0 });

  // Crew Pre-Production polling — fires when current_stage >= 3.
  useEffect(() => {
    if (!projectId || (project?.current_stage || 0) < 3) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/crew-preprod/status`);
        if (!alive) return;
        const s = r.data || {};
        setCrewPreprodProcessing(!!s.processing);
        if (s.brief_counts) setCrewBriefCounts(s.brief_counts);
        // When the worker just finished, refresh project data so the
        // briefs on asset_bibles rows are visible immediately.
        if (s.completed_at && !s.processing && (project?.crew_preprod_completed_at !== s.completed_at)) {
          loadProjectData();
        }
        // Slow down poll cadence when not processing.
        if (!s.processing && crewPollRef.current) {
          clearInterval(crewPollRef.current);
          crewPollRef.current = setInterval(tick, 12000);
        }
      } catch (e) { /* swallow — endpoint may not exist for old projects */ }
    };
    tick();
    crewPollRef.current = setInterval(tick, crewPreprodProcessing ? 4000 : 12000);
    return () => {
      alive = false;
      if (crewPollRef.current) {
        clearInterval(crewPollRef.current);
        crewPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project?.current_stage, crewPreprodProcessing]);

  // ── Bulk Brief Preview handler (2026-06-25) ──────────────────────
  // Iterates over every cast / costume / corner candidate that has a
  // brief AND no preview yet, then fires gpt-image-2 LOW with a
  // concurrency cap of 3 (matches OpenAI's tier-1 RPM safely). Updates
  // local assetBible optimistically as each preview lands so the
  // operator sees thumbnails appear progressively without a full
  // re-fetch storm.
  const generateAllBriefPreviews = async () => {
    if (bulkPreviewing) return;
    if (!assetBible) {
      toast.error('Asset bible not loaded yet');
      return;
    }
    const targets = [];
    const cfg = [
      { kind: 'cast',    arr: 'cast_candidates',    briefField: 'casting_brief' },
      { kind: 'costume', arr: 'costume_candidates', briefField: 'styling_brief' },
      { kind: 'corner',  arr: 'corner_candidates',  briefField: 'art_direction_brief' },
    ];
    for (const c of cfg) {
      for (const cand of (assetBible[c.arr] || [])) {
        const hasBrief = (cand[c.briefField] || '').trim().length > 0;
        const hasPreview = !!cand.brief_preview_url;
        if (hasBrief && !hasPreview) {
          targets.push({ kind: c.kind, arr: c.arr, id: cand.id });
        }
      }
    }
    if (targets.length === 0) {
      toast.info('Every brief already has a preview. Use per-card Regenerate to refresh individual ones.');
      return;
    }
    setBulkPreviewing(true);
    setBulkPreviewProgress({ done: 0, total: targets.length, failed: 0 });
    toast.info(`Generating ${targets.length} previews — gpt-image-2 · low quality`);
    const CONCURRENCY = 3;
    let cursor = 0;
    let failed = 0;
    const runOne = async () => {
      while (cursor < targets.length) {
        const i = cursor++;
        const t = targets[i];
        try {
          const { data } = await axios.post(
            `${API}/projects/${projectId}/crew-preprod/${t.kind}/${t.id}/generate-preview`,
          );
          // Optimistic patch — splice the preview_url into the matching
          // candidate so the thumbnail renders as soon as it lands.
          setAssetBible(prev => {
            if (!prev) return prev;
            const next = { ...prev };
            next[t.arr] = (prev[t.arr] || []).map(c =>
              c.id === t.id
                ? { ...c, brief_preview_url: data.preview_url, brief_preview_at: data.generated_at }
                : c);
            return next;
          });
        } catch (e) {
          failed++;
          const detail = e.response?.data?.detail;
          if (typeof detail === 'object' && detail?.code === 'OPENAI_QUOTA') {
            // Abort the whole batch on quota — no point hammering.
            toast.error('OpenAI quota / billing limit reached', { description: detail.message, duration: 8000 });
            cursor = targets.length; // break all workers
          }
        }
        setBulkPreviewProgress(p => ({ done: p.done + 1, total: targets.length, failed }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, runOne));
    if (failed === 0) {
      toast.success(`Generated ${targets.length} preview${targets.length === 1 ? '' : 's'}`);
    } else if (failed < targets.length) {
      toast.warning(`Done — ${targets.length - failed} succeeded, ${failed} failed. Click failed cards individually to retry.`);
    } else {
      toast.error(`All ${targets.length} previews failed. Check OpenAI billing / network.`);
    }
    setBulkPreviewing(false);
    // Quiet refresh so any other server-side fields stay in sync.
    loadProjectData();
  };

  // Cost tracking
  const [costData, setCostData] = useState({ total: 0, total_inr: 0, usd_to_inr: 83, breakdown: {} });
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [currencyMode, setCurrencyMode] = useState('INR'); // 'INR' or 'USD'
  const costPollRef = useRef(null);

  // Add/Delete shot state
  const [showAddShotModal, setShowAddShotModal] = useState(false);
  const [addShotDescription, setAddShotDescription] = useState('');
  const [addShotAfter, setAddShotAfter] = useState(''); // shot_id to insert after ('' = auto)
  const [addingShotLoading, setAddingShotLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // shot object to confirm delete
  const [deletingShot, setDeletingShot] = useState(false);

  // ── Multi-select for bulk delete (Sketches + Keyframes) ──
  // `bulkSelectMode` is a string: 'sketches' | 'keyframes' | null.
  // We keep it single-section so toggling Select on Sketches doesn't leak
  // into Keyframes (or vice versa) and selection state stays intuitive.
  const [bulkSelectMode, setBulkSelectMode] = useState(null);
  const [selectedShotIds, setSelectedShotIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const toggleBulkSelect = (mode) => {
    setBulkSelectMode(prev => {
      const next = prev === mode ? null : mode;
      setSelectedShotIds(new Set()); // always reset selection on mode change
      return next;
    });
  };
  const toggleShotSelected = (shotId) => {
    setSelectedShotIds(prev => {
      const next = new Set(prev);
      if (next.has(shotId)) next.delete(shotId); else next.add(shotId);
      return next;
    });
  };
  const performBulkDelete = async () => {
    const ids = Array.from(selectedShotIds);
    if (ids.length === 0) return;
    const snap = _captureNavState();
    setBulkDeleting(true);
    try {
      // 2026-05-25 — UX safety fix. The Sketches and Keyframes panels'
      // "Delete (N)" buttons previously called the full-shot DELETE
      // endpoint, wiping every shot row when the operator intended to
      // just remove rendered images. Now: when invoked from the
      // sketches/keyframes select-modes, clear only the image fields
      // (shot data is preserved). The shot-grid mode (if/when added)
      // can still do destructive shot deletion via this same function
      // by passing bulkSelectMode='shots'.
      const mode = bulkSelectMode;
      if (mode === 'sketches' || mode === 'keyframes') {
        const kind = mode === 'sketches' ? 'sketch' : 'keyframe';
        await axios.post(`${API}/projects/${projectId}/shots/clear-sketches`, {
          shot_ids: ids, kind,
        });
        // Optimistic local clear so the UI updates immediately.
        const stripKeys = mode === 'sketches'
          ? ['sketch_url', 'sketch_s3_url', 'sketch_status', 'sketch_engine', 'sketch_error', 'sketch_versions', 'sketch_sam_iterations', 'sketch_generated_at', 'sketch_locked', 'sketch_version', 'sketch_grade', 'sketch_provider']
          : ['keyframe_url', 'keyframe_s3_url', 'keyframe_status', 'keyframe_error', 'keyframe_engine', 'keyframe_stale', 'keyframe_locked', 'keyframe_version', 'keyframe_generated_at', 'keyframe_hyperreal_url', 'keyframe_hyperreal_stale', 'hyperreal_iterations', 'gemini_iterations'];
        setShots(prev => prev.map(s => {
          if (!selectedShotIds.has(s.id)) return s;
          const out = { ...s };
          for (const k of stripKeys) delete out[k];
          return out;
        }));
        toast.success(`${ids.length} ${mode === 'sketches' ? 'sketch' : 'keyframe'}${ids.length === 1 ? '' : 's'} cleared — shot data preserved`);
      } else {
        // True shot deletion path (destructive). Delete sequentially so
        // the backend's auto-renumbering stays consistent.
        for (const id of ids) {
          // eslint-disable-next-line no-await-in-loop
          await axios.delete(`${API}/shots/${id}`);
        }
        setShots(prev => prev
          .filter(s => !selectedShotIds.has(s.id))
          .map((s, idx) => ({
            ...s,
            order_index: idx,
            shot_number: `SH${String(idx + 1).padStart(2, '0')}`,
          }))
        );
        toast.success(`${ids.length} shot${ids.length === 1 ? '' : 's'} deleted`);
      }
      setSelectedShotIds(new Set());
      setBulkSelectMode(null);
      setConfirmBulkDelete(false);
      _restoreNavState(snap);
    } catch (e) {
      toast.error('Bulk action failed: ' + (e.response?.data?.detail || e.message));
    }
    setBulkDeleting(false);
  };

  // Sketch & Keyframe Angles
  const [sketchAnglesProgress, setSketchAnglesProgress] = useState(null);
  const [keyframeAnglesProgress, setKeyframeAnglesProgress] = useState(null);
  const sketchAnglesPollRef = useRef(null);
  const keyframeAnglesPollRef = useRef(null);
  // Track which individual shots are generating angles (local optimistic state)
  const [generatingAngleShots, setGeneratingAngleShots] = useState(new Set());
  const anglePollRef = useRef(null);

  // Video VLM Frame Analysis
  const [videoVlmFramesProgress, setVideoVlmFramesProgress] = useState(null);
  const [vlmFramesSampleRate, setVlmFramesSampleRate] = useState(6);
  const videoVlmFramesPollRef = useRef(null);

  // Dynamic aspect ratio based on project orientation
  const projectOrientation = project?.orientation || '16:9';
  const aspectClass = projectOrientation === '9:16' ? 'aspect-[9/16]' : projectOrientation === '1:1' ? 'aspect-square' : 'aspect-video';
  // Grid columns: fewer for taller tiles
  const tileGridClass = projectOrientation === '9:16' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : projectOrientation === '1:1' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  // Notification sound — uses Web Audio with mobile fallback
  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 800; osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
    } catch(e) {}
    // Also try vibration on mobile
    try { navigator.vibrate?.(200); } catch(e) {}
  };

  // Stage-level dedup — each stage can fire its completion toast / sound
  // at most ONCE per page session. Without this guard, intermittent polling
  // can re-fire the toast as the backend's `processing` flag oscillates
  // (e.g., between IGS pipeline phases) — the user reported seeing
  // "Storyboard Sketches Complete" before generation actually finished.
  const notifiedStagesRef = useRef(new Set());

  const notifyStageComplete = (stageName) => {
    // Idempotent: only the FIRST true-complete signal per stage actually fires.
    if (notifiedStagesRef.current.has(stageName)) {
      return;
    }
    notifiedStagesRef.current.add(stageName);
    toast.success(`${stageName} complete`, { duration: 4000 });
    playNotificationSound();
    // Auto-advance tab to show the next relevant stage
    loadProjectData().then(() => {
      const cs = project?.current_stage || 1;
      if (cs >= 16) { setActiveStageGroup('postprod'); setActiveSidebarItem('postprod'); }
      else if (cs >= 10) { setActiveStageGroup('production'); setActiveSidebarItem('video'); }
      else if (cs >= 7) { setActiveStageGroup('production'); setActiveSidebarItem('production'); }
      else if (cs >= 3) { setActiveStageGroup('preproduction'); setActiveSidebarItem('preproduction'); }
      else { setActiveStageGroup('script'); setActiveSidebarItem('script'); }
    });
  };

  // Format stage duration
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  };

  const getStageDuration = (stageNum) => {
    const st = project?.stage_timestamps?.[`stage_${stageNum}`];
    return st?.duration_seconds ? formatDuration(st.duration_seconds) : null;
  };

  const getStageTime = (stageNum) => {
    const st = project?.stage_timestamps?.[`stage_${stageNum}`];
    if (!st) return null;
    const started = st.started_at ? new Date(st.started_at.endsWith('Z') || st.started_at.includes('+') ? st.started_at : st.started_at + 'Z').toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : null;
    const completed = st.completed_at ? new Date(st.completed_at.endsWith('Z') || st.completed_at.includes('+') ? st.completed_at : st.completed_at + 'Z').toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : null;
    if (started && completed) return `${started} — ${completed}`;
    if (started) return `Started ${started}`;
    return null;
  };

  const initialLoadDoneRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const assetPollRef = useRef(null);
  const promptPollRef = useRef(null);
  const keyframePollRef = useRef(null);
  const vlmQaPollRef = useRef(null);
  const neutralPollRef = useRef(null);
  const videoPollRef = useRef(null);
  const videoQaPollRef = useRef(null);
  const scriptPollRef = useRef(null);
  const shotsPollRef = useRef(null);
  const sketchPollRef = useRef(null);
  const igkPollRef = useRef(null);
  const shootingScriptPollRef = useRef(null);
  const [shootingScriptStartedAt, setShootingScriptStartedAt] = useState(null);
  const [shootingScriptElapsed, setShootingScriptElapsed] = useState(0);

  useEffect(() => {
    loadProjectData();
    // Poll costs every 10 seconds
    costPollRef.current = setInterval(fetchCosts, 10000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (assetPollRef.current) clearInterval(assetPollRef.current);
      if (promptPollRef.current) clearInterval(promptPollRef.current);
      if (keyframePollRef.current) clearInterval(keyframePollRef.current);
      if (vlmQaPollRef.current) clearInterval(vlmQaPollRef.current);
      if (neutralPollRef.current) clearInterval(neutralPollRef.current);
      if (videoPollRef.current) clearInterval(videoPollRef.current);
      if (videoQaPollRef.current) clearInterval(videoQaPollRef.current);
      if (scriptPollRef.current) clearInterval(scriptPollRef.current);
      if (shotsPollRef.current) clearInterval(shotsPollRef.current);
      if (sketchPollRef.current) clearInterval(sketchPollRef.current);
      if (igkPollRef.current) clearInterval(igkPollRef.current);
      if (costPollRef.current) clearInterval(costPollRef.current);
      if (sketchAnglesPollRef.current) clearInterval(sketchAnglesPollRef.current);
      if (keyframeAnglesPollRef.current) clearInterval(keyframeAnglesPollRef.current);
      if (anglePollRef.current) clearInterval(anglePollRef.current);
      if (videoVlmFramesPollRef.current) clearInterval(videoVlmFramesPollRef.current);
      if (analysisPollRef.current) clearInterval(analysisPollRef.current);
      if (shootingScriptPollRef.current) clearInterval(shootingScriptPollRef.current);
    };
  }, [projectId]); // eslint-disable-line

  // Detect user location for currency display
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Use a free IP geolocation API
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data.country_code && data.country_code !== 'IN') {
          setCurrencyMode('USD');
        }
      } catch (e) {
        // Default to INR if detection fails
        console.log('Currency detection failed, defaulting to INR');
      }
    };
    detectCurrency();
  }, []);

  // Fetch premium status for HappyHorse and MJ features
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/premium/status`).then(res => {
      if (!cancelled) {
        setIsPremiumMj(res.data.is_premium || false);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const fetchCosts = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/costs`);
      setCostData(res.data);
    } catch (e) { /* silent */ }
  };

  // Sketch Angles
  const generateSketchAngles = async () => {
    try {
      setUploading(true); setUploadingAction('sketchAngles');
      await axios.post(`${API}/projects/${projectId}/generate-sketch-angles`);
      pollSketchAngles();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false); setUploadingAction('');
  };

  const pollSketchAngles = () => {
    if (sketchAnglesPollRef.current) clearInterval(sketchAnglesPollRef.current);
    notifiedStagesRef.current.delete('Sketch Angles');
    sketchAnglesPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/sketch-angles-status`);
        setSketchAnglesProgress(res.data);
        if (!res.data.processing) {
          clearInterval(sketchAnglesPollRef.current);
          await loadProjectData();
          notifyStageComplete('Sketch Angles');
        }
      } catch (e) { clearInterval(sketchAnglesPollRef.current); }
    }, 3000);
  };

  const selectSketchAngle = async (shotId, angleIndex, isMultiSelect = false) => {
    try {
      if (isMultiSelect) {
        // Multi-select: toggle the angle in the array
        const shot = shots.find(s => s.id === shotId);
        const currentSelected = shot?.selected_sketch_angles || [shot?.selected_sketch_angle ?? 0];
        let newSelected;
        if (currentSelected.includes(angleIndex)) {
          // Remove (but keep at least one)
          newSelected = currentSelected.filter(a => a !== angleIndex);
          if (newSelected.length === 0) newSelected = [0]; // Default to original
        } else {
          // Add
          newSelected = [...currentSelected, angleIndex].sort((a, b) => a - b);
        }
        await axios.post(`${API}/shots/${shotId}/select-sketch-angle`, { selected_angles: newSelected });
      } else {
        // Single select (click)
        await axios.post(`${API}/shots/${shotId}/select-sketch-angle`, { angle_index: angleIndex });
      }
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // Keyframe Angles
  const generateKeyframeAngles = async () => {
    try {
      setUploading(true); setUploadingAction('keyframeAngles');
      await axios.post(`${API}/projects/${projectId}/generate-keyframe-angles`);
      pollKeyframeAngles();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false); setUploadingAction('');
  };

  const pollKeyframeAngles = () => {
    if (keyframeAnglesPollRef.current) clearInterval(keyframeAnglesPollRef.current);
    keyframeAnglesPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/keyframe-angles-status`);
        setKeyframeAnglesProgress(res.data);
        if (!res.data.processing) {
          clearInterval(keyframeAnglesPollRef.current);
          await loadProjectData();
        }
      } catch (e) { clearInterval(keyframeAnglesPollRef.current); }
    }, 3000);
  };

  const selectKeyframeAngle = async (shotId, angleIndex, isMultiSelect = false) => {
    try {
      if (isMultiSelect) {
        // Multi-select: toggle the angle in the array
        const shot = shots.find(s => s.id === shotId);
        const currentSelected = shot?.selected_keyframe_angles || [shot?.selected_keyframe_angle ?? 0];
        let newSelected;
        if (currentSelected.includes(angleIndex)) {
          // Remove (but keep at least one)
          newSelected = currentSelected.filter(a => a !== angleIndex);
          if (newSelected.length === 0) newSelected = [0]; // Default to original
        } else {
          // Add
          newSelected = [...currentSelected, angleIndex].sort((a, b) => a - b);
        }
        await axios.post(`${API}/shots/${shotId}/select-keyframe-angle`, { selected_angles: newSelected });
      } else {
        // Single select (click)
        await axios.post(`${API}/shots/${shotId}/select-keyframe-angle`, { angle_index: angleIndex });
      }
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // Video VLM Frame Analysis
  const runVideoVlmFrames = async () => {
    try {
      setUploading(true); setUploadingAction('videoVlmFrames');
      await axios.post(`${API}/projects/${projectId}/video-vlm-frames`, { fps_sample: vlmFramesSampleRate });
      pollVideoVlmFrames();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false); setUploadingAction('');
  };

  const pollVideoVlmFrames = () => {
    if (videoVlmFramesPollRef.current) clearInterval(videoVlmFramesPollRef.current);
    videoVlmFramesPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/video-vlm-frames-status`);
        setVideoVlmFramesProgress(res.data);
        if (!res.data.processing) {
          clearInterval(videoVlmFramesPollRef.current);
          await loadProjectData();
        }
      } catch (e) { clearInterval(videoVlmFramesPollRef.current); }
    }, 5000);
  };

  // Stop Generation function
  const stopGeneration = async (taskType) => {
    try {
      const confirmed = true; // non-blocking
      if (!confirmed) return;
      
      await axios.post(`${API}/projects/${projectId}/stop-generation`, { task_type: taskType });
      
      // Clear local state based on task type
      switch(taskType) {
        case 'sketches':
          setSketchProgress(null);
          break;
        case 'keyframes':
          setPipelineProgress(null);
          break;
        case 'videos':
          setVideoProgress(null);
          break;
        case 'vlm':
          setVlmQaProgress(null);
          break;
        case 'sketch_angles':
          setSketchAnglesProgress(null);
          break;
        case 'keyframe_angles':
          setKeyframeAnglesProgress(null);
          break;
        default:
          break;
      }
      
      await loadProjectData();
    } catch (e) { 
      alert('Failed to stop: ' + (e.response?.data?.detail || e.message)); 
    }
  };

  // Shot breakdown upload
  const handleBreakdownUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setUploadingAction('breakdown');
      const res = await axios.post(`${API}/projects/${projectId}/upload-shot-breakdown`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const shotCount = res.data?.shots_estimated || res.data?.shots_count;
      toast.success(
        shotCount
          ? `"${file.name}" uploaded — parsing ~${shotCount} shots…`
          : `"${file.name}" uploaded — parsing in progress…`
      );
      // Optimistically reflect the uploaded filename so the green "Uploaded" badge
      // appears immediately, before the next loadProjectData round.
      setProject(prev => prev ? { ...prev, uploaded_breakdown_filename: file.name, breakdown_processing: true } : prev);
      pollShotsStatus();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      setUploadingAction('');
      // Clear the input so re-selecting the same file fires onChange again
      if (e?.target) e.target.value = '';
    }
  };

  // Handle pasted shot breakdown
  const handlePasteBreakdown = async () => {
    if (!pasteText.trim() || pasteText.trim().length < 10) {
      toast.error('Please paste your shot breakdown text (at least 10 characters).');
      return;
    }
    try {
      setUploadingAction('paste');
      setUploading(true);
      setShowPasteModal(false);
      const res = await axios.post(`${API}/projects/${project.id}/paste-shot-breakdown`, {
        text: pasteText
      });
      const charsReceived = res.data?.chars_received || pasteText.length;
      toast.success(`Pasted ${charsReceived.toLocaleString()} chars — parsing breakdown…`);
      setProject(prev => prev ? { ...prev, uploaded_breakdown_filename: 'pasted_text', breakdown_processing: true } : prev);
      setPasteText('');
      pollShotsStatus();
    } catch (e) {
      toast.error('Paste failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      setUploadingAction('');
    }
  };

  // Delete a shot
  // Capture the user's vertical scroll position and active stage group, then
  // restore them after a delete (single or bulk). Re-fetching project data
  // re-renders the whole tree, and if the page height shrinks the browser can
  // visually "jump" — this keeps the user planted on the same row + same stage
  // they were working in. Stage group is also explicitly re-asserted in case
  // some downstream effect tries to drag them back to the dashboard.
  const _captureNavState = useCallback(() => {
    return {
      scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
      stageGroup: activeStageGroup,
      sidebarItem: activeSidebarItem,
    };
  }, [activeStageGroup, activeSidebarItem]);

  const _restoreNavState = useCallback((snap) => {
    if (!snap) return;
    // Force the stage/sidebar back if anything bumped them.
    if (snap.stageGroup && snap.stageGroup !== activeStageGroup) setActiveStageGroup(snap.stageGroup);
    if (snap.sidebarItem && snap.sidebarItem !== activeSidebarItem) setActiveSidebarItem(snap.sidebarItem);
    // Defer scroll until after React commits the new layout.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: snap.scrollY, behavior: 'auto' });
        }
      });
    });
  }, [activeStageGroup, activeSidebarItem]);

  const deleteShot = async (shotId) => {
    const snap = _captureNavState();
    try {
      await axios.delete(`${API}/shots/${shotId}`);
      // Optimistic local update — same trick as bulk delete — so we don't
      // refetch the whole project and lose the user's place. Numbers shift
      // by one row above the deleted card; that's expected.
      setShots(prev => prev
        .filter(s => s.id !== shotId)
        .map((s, idx) => ({
          ...s,
          order_index: idx,
          shot_number: `SH${String(idx + 1).padStart(2, '0')}`,
        }))
      );
      _restoreNavState(snap);
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Reorder a shot (move up or down)
  const reorderShot = async (shotId, direction) => {
    const currentIndex = shots.findIndex(s => s.id === shotId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= shots.length) return;
    
    // Create new order
    const newShots = [...shots];
    const [moved] = newShots.splice(currentIndex, 1);
    newShots.splice(newIndex, 0, moved);
    
    // Send new order to backend
    try {
      await axios.post(`${API}/projects/${projectId}/reorder-shots`, {
        shot_ids: newShots.map(s => s.id)
      });
      await loadProjectData();
    } catch (e) {
      alert('Reorder failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Add a new shot
  const addNewShot = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/add-shot`, {});
      await loadProjectData();
    } catch (e) {
      alert('Add shot failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Subscribe to the unified /activity poller. Whenever the project's
  // signature changes (any post-processing field updated, any task started/
  // finished, any shot mutated) we refetch project data so the UI stays
  // in sync without the user needing to hard-refresh.
  const projectActivity = useProjectActivity(projectId, () => {
    // Fire-and-forget — loadProjectData is robust to overlapping calls.
    loadProjectData();
  });

  const loadProjectData = async () => {
    try {
      const [projectRes, shotsRes, assetBibleRes, derivedCCCRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/shots`),
        axios.get(`${API}/projects/${projectId}/asset-bible`).catch(() => ({ data: null })),
        axios.get(`${API}/projects/${projectId}/derived-ccc`).catch(() => ({ data: null }))
      ]);
      setProject(projectRes.data);
      // Stage 0 redirect — when a project is still in the Idea-to-Script
      // workbench, route there instead of dumping the user into Stage 1
      // with a blank script. Triad per 2026-05-15 brief:
      //   - entry_mode === "idea"       (user opted into the workbench)
      //   - current_stage === 0         (haven't progressed yet)
      //   - canonical_script_source is falsy (haven't accepted any script yet)
      // After accept (use_generated OR user_provided), canonical_script_source
      // is set → this check stops firing → Stage 1 renders. Existing
      // non-idea projects (entry_mode=script_ready or undefined) are
      // untouched.
      const _p = projectRes.data || {};
      const _entry = (_p.entry_mode || '').toLowerCase();
      const _stage = Number(_p.current_stage);
      const _accepted = !!_p.canonical_script_source;
      if (_entry === 'idea' && _stage === 0 && !_accepted) {
        navigate(`/project/${projectId}/stage-0`, { replace: true });
        return;
      }
      setShots(sortShots(shotsRes.data));
      setAssetBible(assetBibleRes.data);
      if (derivedCCCRes.data && derivedCCCRes.data.cast) setDerivedCCC(derivedCCCRes.data);
      // Cast tiering — non-blocking, fire after the rest of the load
      loadCastTiering();
      setScriptText(projectRes.data.script_text || '');
      setProductName(projectRes.data.product_name || '');
      setProductFeatures(projectRes.data.product_features || '');
      setLoading(false);
      fetchCosts();

      // Seed section expansion defaults ONLY on the first load. After
      // that, the user's manual toggles must persist across refreshes —
      // otherwise every IGK poller tick (3s) would re-evaluate and snap
      // the keyframes section shut if the criteria flip.
      // Bug fixed 2026-05-22: previously this ran on every loadProjectData()
      // call AND only checked `keyframe_url` (legacy Gemini/MJ field), so
      // IGK-only shots would not satisfy the predicate and the section
      // would collapse mid-run.
      if (!initialLoadDoneRef.current) {
        setExpandedSections(prev => ({
          ...prev,
          script: !projectRes.data.script_text,
          productionPackage: !!projectRes.data.production_package,
          shots: shotsRes.data.length > 0,
          sketches: shotsRes.data.some(s => s.sketch_url),
          assets: !!(assetBibleRes.data && (assetBibleRes.data.cast_candidates?.length > 0 || assetBibleRes.data.costume_candidates?.length > 0 || assetBibleRes.data.corner_candidates?.length > 0)),
          prompts: projectRes.data.current_stage >= 7 && shotsRes.data.some(s => s.keyframe_prompt),
          keyframes: shotsRes.data.some(s => s.keyframe_url || s.keyframe_hyperreal_url),
        }));
      }

      // Set initial active stage group based on project progress — ONLY on first load
      // Subsequent data refreshes must not override user's manual navigation
      if (!initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;
        const cs = projectRes.data.current_stage;
        if (cs >= 14) { setActiveStageGroup('postprod'); setActiveSidebarItem('postprod'); }
        else if (cs >= 7) { setActiveStageGroup('production'); setActiveSidebarItem('video'); }
        else if (cs >= 2) { setActiveStageGroup('preproduction'); setActiveSidebarItem('preproduction'); }
        else { setActiveStageGroup('script'); setActiveSidebarItem('script'); }
      }

      if (projectRes.data.sketches_processing && !sketchPollRef.current) {
        // 2026-05-18 — detect which sketch pipeline was running so the right
        // button keeps spinning on page refresh. Reads engine from any shot
        // currently generating (sketch_status='generating') — falls back
        // to 'consistent' (the default Consistent Sketches path) when no
        // shots have started yet.
        const activeShot = (shotsRes?.data || []).find(s => s.sketch_status === 'generating');
        const engine = activeShot?.sketch_engine || '';
        let tag = 'sketches:consistent';
        if (engine.startsWith('gpt-image-2')) tag = 'sketches:igs';
        else if (engine.startsWith('nano-banana') || engine.startsWith('gemini')) tag = 'sketches:consistent';
        setUploading(true); setUploadingAction(tag);
        pollSketchProgress();
      }
      if (projectRes.data.assets_processing && !assetPollRef.current) {
        // SAFETY NET (2026-06-16): auto-heal a zombie `assets_processing`
        // flag. If the backend says gen is running but the project has
        // ZERO `assets_total` (the counters were never initialised), the
        // flag is stale — a prior crash left it set without writing
        // anything else. Clear it silently so the operator never sees a
        // permanently-disabled "Generate Asset Bible" button.
        if (!projectRes.data.assets_total || projectRes.data.assets_total === 0) {
          try {
            await axios.post(
              `${API}/projects/${projectId}/reset-processing`,
              { flag: 'assets_processing' },
            );
            // Don't toast — silent recovery. Refresh state and bail.
            const refreshed = await axios.get(`${API}/projects/${projectId}`);
            setProject(refreshed.data);
          } catch (resetErr) {
            // If silent reset fails, fall through to the existing poll
            // — operator can still hit the manual "Stuck? Reset gen"
            // link surfaced under the disabled button.
            console.warn('[AutoHeal] assets_processing reset failed', resetErr);
            setUploading(true); setUploadingAction('assets');
            pollAssetProgress();
          }
        } else {
          setUploading(true); setUploadingAction('assets');
          pollAssetProgress();
        }
      }
      if (projectRes.data.prompts_processing && !promptPollRef.current) {
        setUploading(true); setUploadingAction('prompts');
        pollPromptProgress();
      }
      if (projectRes.data.keyframes_processing && !keyframePollRef.current) {
        setUploading(true); setUploadingAction('keyframes');
        pollKeyframeProgress();
      }
      // IGK auto-resume on page refresh — same pattern as sketches.
      if (projectRes.data.hyperreal_keyframes_processing && !igkPollRef.current) {
        setUploading(true); setUploadingAction('hyperrealKeyframes');
        pollIgkProgress();
      }
      // Auto-resume shooting-script polling if a generation is already in
      // flight (e.g. user hard-refreshed during the 60–120s Opus run).
      if (projectRes.data.shooting_script_processing && !shootingScriptPollRef.current) {
        if (!shootingScriptStartedAt) setShootingScriptStartedAt(Date.now());
        pollShootingScriptStatus();
      }
      // Auto-resume Stage-1 script processing poll if backend is still working
      // (refresh-during-processing case). Without this the banner would never
      // clear on its own — the user would have to refresh again to see the
      // post-processing state, which is exactly the bug Rohit reported.
      if (projectRes.data.script_processing && !scriptPollRef.current) {
        setScriptProcessing(true);
        pollScriptStatus();
      }
      // Auto pipeline polling
      if (projectRes.data.auto_pipeline_running && !autoPollRef.current) {
        pollAutoPipeline();
      }
    } catch (e) {
      console.error('Failed to load project:', e);
      setLoading(false);
    }
  };

  /* ── ACTIONS ── */

  const pollAutoPipeline = () => {
    if (autoPollRef.current) clearInterval(autoPollRef.current);
    notifiedStagesRef.current.delete('Auto Pipeline');
    autoPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/auto-pipeline-status`);
        setAutoPipelineStatus(res.data);
        
        // Auto-switch tabs to follow the current stage
        const cs = res.data.current_stage;
        if (cs && res.data.running) {
          const projectRes = await axios.get(`${API}/projects/${projectId}`);
          setProject(projectRes.data);
          
          if (cs >= 16) { setActiveStageGroup('postprod'); setActiveSidebarItem('postprod'); setExpandedSections(prev => ({...prev, postproduction: true, export: true})); }
          else if (cs >= 13) { setActiveStageGroup('production'); setActiveSidebarItem('video'); setExpandedSections(prev => ({...prev, videoGen: true})); }
          else if (cs >= 10) { setActiveStageGroup('production'); setActiveSidebarItem('video'); }
          else if (cs >= 7) { setActiveStageGroup('production'); setActiveSidebarItem('production'); setExpandedSections(prev => ({...prev, pipeline: true})); }
          else if (cs >= 4) { setActiveStageGroup('preproduction'); setActiveSidebarItem('preproduction'); setExpandedSections(prev => ({...prev, sketches: true})); }
          else if (cs >= 3) { setActiveStageGroup('preproduction'); setActiveSidebarItem('preproduction'); setExpandedSections(prev => ({...prev, assets: true})); }
          else { setActiveStageGroup('script'); setActiveSidebarItem('script'); }
        }
        
        // Also refresh shot data to update the stage views
        const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
        setShots(sortShots(shotsRes.data));
        
        if (!res.data.running) {
          clearInterval(autoPollRef.current);
          autoPollRef.current = null;
          if (res.data.stage?.startsWith('Complete')) {
            notifyStageComplete('Auto Pipeline');
          }
          await loadProjectData();
        }
      } catch (e) { console.error('Auto poll error:', e); }
    }, 3000);
  };

  const saveProduct = async () => {
    setSavingProduct(true);
    try {
      const form = new FormData();
      form.append('product_name', productName);
      form.append('product_features', productFeatures);
      if (productFile) form.append('file', productFile);
      await axios.post(`${API}/projects/${projectId}/product`, form);
      setProductSaved(true);
      setProductFile(null);
      setProductImgError(false);
      setTimeout(() => setProductSaved(false), 3000);
      // Reload project data without collapsing current section
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 503) {
        toast.error(`Image not saved: ${detail}`);
      } else {
        toast.error(`Failed to save product: ${detail}`);
      }
    }
    setSavingProduct(false);
  };

  const uploadScript = async () => {
    if (!scriptText.trim()) return;
    setUploading(true); setUploadingAction('script');
    setScriptProcessing(true);
    try {
      await axios.post(`${API}/projects/${projectId}/script`, {
        project_id: projectId, script_text: scriptText,
        enhance: enhanceScript, use_production_agent: useProductionAgent
      });
      // Poll for script processing completion
      pollScriptStatus();
    } catch (e) { 
      alert('Failed to upload script: ' + (e.response?.data?.detail || e.message)); 
      setUploading(false); setUploadingAction(null);
      setScriptProcessing(false);
    }
  };

  const handleNarrationUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File too large. Maximum 25MB for transcription.');
      return;
    }
    setNarrationUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/projects/${projectId}/transcribe-narration`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      if (res.data.success && res.data.transcript) {
        setScriptText(prev => prev ? prev + '\n\n' + res.data.transcript : res.data.transcript);
        toast.success(`Transcribed "${file.name}" (${res.data.segments?.length || 0} segments)`);
      } else {
        alert('Transcription returned empty result.');
      }
    } catch (err) {
      alert('Transcription failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setNarrationUploading(false);
      if (narrationInputRef.current) narrationInputRef.current.value = '';
    }
  };

  // Upload a script as .txt/.pdf/.docx — backend extracts text and runs the
  // same processing pipeline as the paste-script flow.
  const handleScriptFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 10 * 1024 * 1024; // 10 MB ceiling for script docs
    if (file.size > maxSize) {
      alert('File too large. Maximum 10MB for script files.');
      if (scriptFileInputRef.current) scriptFileInputRef.current.value = '';
      return;
    }
    const allowed = ['.txt', '.pdf', '.docx'];
    const lower = file.name.toLowerCase();
    if (!allowed.some(ext => lower.endsWith(ext))) {
      alert('Unsupported file type. Use .txt, .pdf, or .docx');
      if (scriptFileInputRef.current) scriptFileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    setUploadingAction('script');
    setScriptProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('enhance', enhanceScript ? 'true' : 'false');
      formData.append('use_production_agent', String(useProductionAgent));
      const res = await axios.post(`${API}/projects/${projectId}/upload-script-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`Script "${file.name}" uploaded (${res.data.chars_extracted?.toLocaleString?.() || 0} chars). Processing...`);
      pollScriptStatus();
    } catch (err) {
      alert('Script upload failed: ' + (err.response?.data?.detail || err.message));
      setUploading(false);
      setUploadingAction(null);
      setScriptProcessing(false);
    } finally {
      if (scriptFileInputRef.current) scriptFileInputRef.current.value = '';
    }
  };


  const startRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 }, audio: true });
      streamRef.current = stream;
      setShowRecorder(true);
      // Attach stream after video element mounts
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (webcamRef.current) {
            webcamRef.current.srcObject = stream;
            webcamRef.current.play().catch(() => {});
          }
        }, 100);
      });
    } catch (err) {
      alert('Camera access denied. Please allow camera and microphone permissions.');
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm';
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
    recorder.start(1000);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    setRecordingTime(0);
    recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  };

  const discardRecording = () => {
    stopRecording();
    recordedChunksRef.current = [];
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setShowRecorder(false);
    setRecordingTime(0);
  };

  const submitRecording = async () => {
    stopRecording();
    // Wait briefly for last data chunk
    await new Promise(r => setTimeout(r, 500));
    const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
    if (blob.size < 1000) { alert('Recording too short. Please try again.'); return; }
    // Clean up camera
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setShowRecorder(false);
    setRecordingTime(0);
    // Upload to transcribe
    setNarrationUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, `narration_${Date.now()}.webm`);
      const res = await axios.post(`${API}/projects/${projectId}/transcribe-narration`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      if (res.data.success && res.data.transcript) {
        setScriptText(prev => prev ? prev + '\n\n' + res.data.transcript : res.data.transcript);
        toast.success(`Narration transcribed (${res.data.segments?.length || 0} segments)`);
      } else {
        alert('Transcription returned empty result.');
      }
    } catch (err) {
      alert('Transcription failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setNarrationUploading(false);
    }
  };

  const formatRecTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;


  const pollScriptStatus = () => {
    if (scriptPollRef.current) clearInterval(scriptPollRef.current);
    notifiedStagesRef.current.delete('Script Processing');
    scriptPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/script-status`);
        if (!res.data.processing) {
          clearInterval(scriptPollRef.current);
          scriptPollRef.current = null;
          setScriptProcessing(false);
          setUploading(false);
          setUploadingAction(null);
          if (res.data.error) {
            alert('Script processing failed: ' + res.data.error);
          } else {
            notifyStageComplete('Script Processing');
            // If auto mode, start polling for auto pipeline progress
            if (project?.production_mode === 'auto' || project?.production_mode === 'telegram') {
              pollAutoPipeline();
            } else {
              // Human mode — guide user to next step
              setExpandedSections(prev => ({ ...prev, shots: true }));
            }
          }
          await loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 2000);
  };

  // Deep pre-production script analysis (Opus 4.7).
  // Produces a structured breakdown (cast, costume, environment, scene
  // nuances, shot-language hints) the crew reads BEFORE the Asset Bible.
  // Idempotent on the backend via script content hash — re-clicks are free.
  //
  // forGeneration=true (default) → behaviour for the "Generate Shots" flow:
  //   waits for analysis, shows modal, lets user proceed to generation.
  //   On hard failure, falls back to generating shots with the user's
  //   current count so the CTA never dead-ends.
  // forGeneration=false → pure preview triggered by the "Analyze Script"
  //   button; on failure we toast, we do NOT auto-generate shots.
  const analyzeScriptBeats = async (forGeneration = true) => {
    setAnalyzingScript(true);
    setAnalysisError('');
    setAnalysisDocxReady(false);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/analyze-script`);
      const body = res.data || {};
      // Cached hit — data is available immediately, no polling needed.
      if (body.cached && body.analysis) {
        setScriptAnalysis(body.analysis);
        setAnalysisDocxReady(!!body.has_docx);
        setShowAnalysisModal(true);
        setAnalyzingScript(false);
        return;
      }
      // Kicked a background run; poll until ready or error. Show the modal
      // immediately so the user sees "Opus is analyzing…" state.
      setScriptAnalysis(null);
      setShowAnalysisModal(true);
      pollScriptAnalysisStatus(forGeneration);
    } catch (e) {
      console.error('Analysis failed:', e);
      setAnalyzingScript(false);
      if (forGeneration) {
        proceedWithShots(project?.max_shots || 30);
      } else {
        toast.error('Could not start script analysis — try again in a moment.');
      }
    }
  };

  // Poll /script-analysis-status every 4s until ready or error. 12-min cap.
  // The backend's Anthropic SDK timeout is 600s; we sit 2 min above it so
  // Opus 4.7 has runway to finish legitimately-long calls (long scripts +
  // adaptive thinking can take 5-8 min) without the FE prematurely
  // surfacing "timed out" while the backend is still completing.
  const pollScriptAnalysisStatus = (forGeneration = false) => {
    if (analysisPollRef.current) clearInterval(analysisPollRef.current);
    const startedAt = Date.now();
    const POLL_TIMEOUT_MS = 12 * 60 * 1000;
    analysisPollRef.current = setInterval(async () => {
      try {
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          clearInterval(analysisPollRef.current);
          analysisPollRef.current = null;
          setAnalyzingScript(false);
          setAnalysisError('Analysis timed out — please try again.');
          return;
        }
        const r = await axios.get(`${API}/projects/${projectId}/script-analysis-status`);
        const body = r.data || {};
        if (body.error) {
          clearInterval(analysisPollRef.current);
          analysisPollRef.current = null;
          setAnalyzingScript(false);
          setAnalysisError(body.error);
          if (forGeneration) {
            // Don't block the Generate-Shots CTA on an analysis failure.
            setShowAnalysisModal(false);
            proceedWithShots(project?.max_shots || 30);
          }
          return;
        }
        if (body.ready && body.analysis) {
          clearInterval(analysisPollRef.current);
          analysisPollRef.current = null;
          setScriptAnalysis(body.analysis);
          setAnalysisDocxReady(!!body.has_docx);
          setAnalyzingScript(false);
        }
      } catch (e) {
        console.error('Analysis poll error:', e);
      }
    }, 4000);
  };

  // Trigger a browser download of the generated Script Analysis .docx.
  const downloadScriptAnalysisDocx = async () => {
    try {
      const res = await axios.get(
        `${API}/projects/${projectId}/script-analysis.docx`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (project?.name || 'script_analysis').replace(/[^A-Za-z0-9_\-]+/g, '_');
      a.download = `${safeName}_script_analysis.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
      toast.error('Could not download the analysis doc — try again in a bit.');
    }
  };

  // Proceed with shot generation using selected count
  const proceedWithShots = async (shotCount) => {
    setShowAnalysisModal(false);
    // #70 — destruction confirmation. If shots already exist, generate-shots
    // wipes them + their prompt/round history + downstream artifacts. State
    // exactly what dies; a restorable snapshot is taken server-side first.
    if ((shots?.length || 0) > 0) {
      const n = shots.length;
      const ok = window.confirm(
        `⚠️ This will DELETE all ${n} existing shot${n === 1 ? '' : 's'}, their director prompt & round history, ` +
        `and every downstream artifact (sketches, keyframes, segmentation, enhancement jobs), then regenerate the breakdown.\n\n` +
        `✓ A restorable snapshot is taken automatically before deletion.\n\nContinue?`
      );
      if (!ok) return;
    }
    // Warn if selected count exceeds project max_shots
    const currentMax = project?.max_shots || 15;
    if (shotCount > currentMax) {
      toast.info(`Shot count updated from ${currentMax} to ${shotCount} to match your selection.`);
    }
    setUploading(true); setUploadingAction('shots');
    setShotsProcessing(true);
    try {
      // Update project with selected shot count
      await axios.post(`${API}/projects/${projectId}/settings`, { max_shots: shotCount });
      await axios.post(`${API}/projects/${projectId}/generate-shots`);
      setExpandedSections(prev => ({ ...prev, shots: true }));
      pollShotsStatus();
    } catch (e) { 
      alert('Failed: ' + (e.response?.data?.detail || e.message)); 
      setUploading(false); setUploadingAction(null);
      setShotsProcessing(false);
    }
  };

  const generateShots = async () => {
    // First analyze the script to recommend shot count
    await analyzeScriptBeats();
  };

  const pollShotsStatus = () => {
    if (shotsPollRef.current) clearInterval(shotsPollRef.current);
    setShotGenError(null);
    const pollStartTime = Date.now();
    const POLL_TIMEOUT = 12 * 60 * 1000; // 12 minutes max
    shotsPollRef.current = setInterval(async () => {
      try {
        // Timeout guard — stop polling after 12 minutes
        if (Date.now() - pollStartTime > POLL_TIMEOUT) {
          clearInterval(shotsPollRef.current);
          shotsPollRef.current = null;
          setShotsProcessing(false);
          setUploading(false);
          setUploadingAction(null);
          setShotGenError({
            title: 'Shot generation timed out',
            detail: 'The job ran past 12 minutes without finishing. The AI service may be slow or the job was interrupted. You can retry safely.',
          });
          return;
        }
        const res = await axios.get(`${API}/projects/${projectId}/shots-status`);
        // Progressive display — show shots as they arrive
        if (res.data.progress) {
          setShotsProgress(res.data.progress);
        }
        if (res.data.count > 0 && res.data.processing) {
          // Shots are arriving — reload to show them progressively
          await loadProjectData();
        }
        // May 2026 — keep polling while the AB bindings auto-fire OR the
        // 3-LLM CCC consensus OR the DOP spec auto-fire is still running.
        // All three kick off after shot generation completes and take
        // 1-3 min each. Without this, the UI would freeze on "not bound" /
        // "not set" until manual reload.
        const aux_busy = !!(res.data.bindings_processing || res.data.derived_ccc_processing || res.data.dop_specs_processing || res.data.director_prompts_processing);
        if (!res.data.processing && !aux_busy) {
          clearInterval(shotsPollRef.current);
          shotsPollRef.current = null;
          setShotsProcessing(false);
          setShotsProgress('');
          setUploading(false);
          setUploadingAction(null);
          if (res.data.error) {
            setShotGenError({
              title: 'Shot generation failed',
              detail: res.data.error,
            });
          } else if ((res.data.count || 0) === 0) {
            // P1 #10 / CD-3 (2026-07-15) — in-page named error state (no
            // blocking browser alert). The shots-status endpoint mirrors the
            // just-written shots_error into the response, so a populated
            // res.data.error is handled above; this covers the rare case where
            // the job ended with zero shots and no explicit error string
            // (killed job or an empty AI breakdown).
            setShotGenError({
              title: 'No shots were saved',
              detail: 'The breakdown finished but produced zero shots. This usually means the background job was interrupted or the AI returned an empty breakdown. Retry — if it repeats, check that the script has visual content the AI can break into shots.',
            });
          }
          await loadProjectData();
        } else if (!res.data.processing && aux_busy) {
          // Shot gen done; bindings/consensus still firing — reload to
          // reflect the freshly-saved shots and show the spinner.
          await loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 2000);
  };

  const generateSketches = async (quickMode = false, onlyFailed = false) => {
    if (shots.length === 0) { alert('Generate shots first'); return; }
    // 2026-06-22 — confirm overwrite when sketches already exist. Skip
    // the prompt for `onlyFailed` (operator only fills the gaps) and
    // for a virgin project (no work to lose). Without this guard a
    // single misclick wipes the entire storyboard.
    if (!onlyFailed && hasSketchedShots) {
      const existing = shots.filter((s) => s.sketch_url).length;
      const msg =
        `This will OVERWRITE ${existing} of ${shots.length} existing sketches ` +
        `with a fresh ${quickMode ? 'Quick' : 'Production'} run.\n\n` +
        `Generated images will be replaced and cannot be recovered.\n\n` +
        `Continue?`;
      if (!window.confirm(msg)) return;
    }
    // 2026-05-18 — distinguish per pipeline so spinner shows only on the
    // button actually clicked (Quick / Production / Consistent / IGS).
    setUploading(true); setUploadingAction(quickMode ? 'sketches:quick' : 'sketches:production');
    // Optimistically clear sketch URLs (only target failed shots if onlyFailed)
    setShots(prev => prev.map(s => {
      if (onlyFailed && !(s.sketch_status === 'failed' || s.sketch_status === 'timed_out')) return s;
      return { ...s, sketch_url: '', sketch_s3_url: '', sketch_status: 'generating', sketch_error: null };
    }));
    try {
      const url = `${API}/projects/${projectId}/generate-sketches?quick_mode=${quickMode}${onlyFailed ? '&only_failed=true' : ''}`;
      await axios.post(url);
      setExpandedSections(prev => ({ ...prev, sketches: true }));
      pollSketchProgress();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('A sketch generation is already running — please wait for it to finish.');
      } else {
        // 2026-06-22 — instead of a one-line alert, ping the readiness
        // probe so the operator sees WHICH gate is shut and the
        // remediation hint, not just "Failed: <detail>".
        try {
          const probe = await axios.get(`${API}/projects/${projectId}/sketch-readiness`);
          const b = probe.data?.blockers || [];
          const lr = probe.data?.last_run || {};
          if (b.length) {
            const lines = b.map((x) => `• ${x.message}\n   ↳ ${x.fix}`).join('\n\n');
            const stuck = b.find((x) => x.stuck);
            const head = stuck
              ? '⚠️ Sketch generation is BLOCKED (a flag looks stuck — see fix below):'
              : '⚠️ Sketch generation is BLOCKED:';
            alert(`${head}\n\n${lines}`);
          } else if ((lr.failed_shots_total || 0) > 0 || lr.project_error) {
            // No gates apply (e.g. global admin) but the LAST run still
            // failed — surface the per-shot errors so the operator sees
            // "every shot died with: MJ 429 rate limit" instead of
            // staring at an empty Generate button.
            const errs = (lr.top_errors || [])
              .map((e) => `• ×${e.count} · ${e.error}` +
                          (e.shot_numbers?.length
                            ? `\n   shots: ${e.shot_numbers.join(', ')}` : ''))
              .join('\n\n');
            const projErr = lr.project_error ? `\n\nProject error: ${lr.project_error}` : '';
            alert(
              `⚠️ Sketch generation isn't proceeding even though no gates apply.\n\n` +
              `Last run: ${lr.completed || 0} ok · ${lr.failed || 0} failed` +
              `${projErr}\n\nTop per-shot errors:\n\n${errs || '(none captured)'}` +
              `\n\nTry: 1) Hit Retry Failed to re-fire just the failed shots, ` +
              `2) Check your Universal Key balance if you see budget/429 errors, ` +
              `3) POST /api/projects/${projectId}/reset-processing with ` +
              `{flag:"sketches_processing"} if a previous run is stuck.`
            );
          } else {
            alert('Failed: ' + (e.response?.data?.detail || e.message));
          }
        } catch {
          alert('Failed: ' + (e.response?.data?.detail || e.message));
        }
      }
      setUploading(false); setUploadingAction(null);
      await loadProjectData();
    }
  };

  const generateHyperrealKeyframes = async (onlyFailed = false, limit = null) => {
    if (shots.length === 0) { alert('Generate shots first'); return; }
    setUploading(true); setUploadingAction('hyperrealKeyframes');
    // Optimistically mark target shots as generating so the cards show a spinner.
    setShots(prev => prev.map(s => {
      if (onlyFailed && !(s.keyframe_hyperreal_status === 'failed' || s.keyframe_hyperreal_status === 'timed_out')) return s;
      return { ...s, keyframe_hyperreal_url: '', keyframe_hyperreal_s3_url: '', keyframe_hyperreal_status: 'generating', keyframe_hyperreal_error: null };
    }));
    try {
      const url = `${API}/projects/${projectId}/generate-hyperreal-keyframes`;
      // limit=N → IGK chunked mode: walk shotlist in scene order N at a time.
      const payload = { only_failed: !!onlyFailed };
      if (limit && Number.isInteger(limit) && limit > 0) payload.limit = limit;
      const res = await axios.post(url, payload);
      // Backend returns noop:true when chunked / all_remaining finds zero non-IGK shots.
      if (res.data?.noop) {
        toast.success('All shots already have hyperreal keyframes — nothing to do.');
        setUploading(false); setUploadingAction(null);
        await loadProjectData();
        return;
      }
      const chunkLabel = limit ? ` · next ${res.data?.total_shots || limit} shot(s)` : '';
      toast.success(
        onlyFailed
          ? 'Retrying failed hyperreal keyframes — Photo engine'
          : `Insanely Good Keyframes started — Photo engine${chunkLabel} · VLM audit enabled`,
      );
      await loadProjectData();
      pollIgkProgress();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('A hyperreal keyframe run is already in progress.');
      } else {
        toast.error('Failed: ' + (e.response?.data?.detail || e.message));
      }
      setUploading(false); setUploadingAction(null);
      await loadProjectData();
    }
  };

  const generateConsistentSketches = async (onlyFailed = false, engine = 'nano-banana', limit = null) => {
    if (shots.length === 0) { alert('Generate shots first'); return; }
    // 2026-06-22 — confirm overwrite. Suppress the prompt for:
    //   (a) onlyFailed retries — operator is filling gaps, nothing lost
    //   (b) IGS chunked mode (limit > 0) — server walks non-IGS shots
    //       in scene order so existing sketches are never touched, AND
    //       the IGS chevron already shows its own pre-confirm
    //   (c) virgin project — no existing sketches to lose
    const isChunkedIgs = engine === 'gpt-image-2' && Number.isInteger(limit) && limit > 0;
    if (!onlyFailed && !isChunkedIgs && hasSketchedShots) {
      const existing = shots.filter((s) => s.sketch_url).length;
      const label = engine === 'gpt-image-2'
        ? 'Insanely Good Sketches (Photo engine, full run)'
        : 'Consistent Sketches';
      const msg =
        `This will OVERWRITE ${existing} of ${shots.length} existing sketches ` +
        `with a fresh ${label} pass.\n\n` +
        `Generated images will be replaced and cannot be recovered.\n\n` +
        `Continue?`;
      if (!window.confirm(msg)) return;
    }
    // 2026-05-18 — IGS (gpt-image-2) and Consistent (nano-banana) are
    // separate spinners on the action bar.
    setUploading(true); setUploadingAction(engine === 'gpt-image-2' ? 'sketches:igs' : 'sketches:consistent');
    // Optimistically clear sketch URLs (only target failed shots if onlyFailed)
    setShots(prev => prev.map(s => {
      if (onlyFailed && !(s.sketch_status === 'failed' || s.sketch_status === 'timed_out')) return s;
      return { ...s, sketch_url: '', sketch_s3_url: '', sketch_status: 'generating', sketch_error: null };
    }));
    try {
      const url = `${API}/projects/${projectId}/generate-consistent-sketches`;
      // engine is sent in the body — backend accepts only_failed + engine + limit there.
      // limit=N (e.g. 3) → IGS chunked mode: walk shotlist in scene order, N at a time.
      const payload = { only_failed: !!onlyFailed, engine };
      if (limit && Number.isInteger(limit) && limit > 0) payload.limit = limit;
      const res = await axios.post(url, payload);
      setExpandedSections(prev => ({ ...prev, sketches: true }));
      const engineLabel = engine === 'gpt-image-2' ? 'Photo engine (Insanely Good)' : 'Sketch engine';
      // Backend returns noop:true when chunked mode finds zero non-IGS shots.
      if (res.data?.noop) {
        toast.success('All shots are already IGS-tagged — nothing to do.');
        setUploading(false); setUploadingAction(null);
        await loadProjectData();
        return;
      }
      const chunkLabel = limit ? ` · next ${res.data?.total_shots || limit} shot(s)` : '';
      toast.success(
        onlyFailed
          ? `Retrying failed sketches — ${engineLabel}`
          : `Sketch generation started — ${engineLabel}${chunkLabel} · VLM audit enabled`,
      );
      pollSketchProgress();
    } catch (e) {
      if (e.response?.status === 409) {
        const detail = e.response?.data?.detail || {};
        const code = typeof detail === 'object' ? detail.code : '';
        if (code === 'generation_in_progress') {
          toast.error('A sketch generation is already running — please wait for it to finish.');
        } else {
          toast.error('Conflict: ' + (typeof detail === 'object' ? detail.error || JSON.stringify(detail) : detail));
        }
      } else {
        // 2026-06-24 — surface the readiness probe so the operator sees
        // the actual blocker (mock-shoot gate, asset bible missing, etc.)
        // instead of a silent revert to "Pending" after the optimistic
        // update. Mirrors the legacy /generate-sketches handler above.
        try {
          const probe = await axios.get(`${API}/projects/${projectId}/sketch-readiness`);
          const b = probe.data?.blockers || [];
          if (b.length) {
            const lines = b.map((x) => `• ${x.message}\n   ↳ ${x.fix}`).join('\n\n');
            const stuck = b.find((x) => x.stuck);
            const head = stuck
              ? 'IGS BLOCKED (a flag looks stuck — see fix below):'
              : 'IGS BLOCKED:';
            alert(`${head}\n\n${lines}`);
          } else {
            toast.error('IGS failed: ' + (e.response?.data?.detail || e.message));
          }
        } catch {
          toast.error('IGS failed: ' + (e.response?.data?.detail || e.message));
        }
      }
      setUploading(false); setUploadingAction(null);
      await loadProjectData();
    }
  };

  const regenerateSketch = async (shotId) => {
    // Optimistically clear this shot's sketch so container shows loading state
    setShots(prev => prev.map(s => s.id === shotId ? { ...s, sketch_url: '', sketch_s3_url: '', sketch_status: 'generating', sketch_error: null } : s));
    try {
      await axios.post(`${API}/shots/${shotId}/regenerate-sketch`);
      await loadProjectData();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      await loadProjectData(); // Restore actual state on failure
    }
  };

  const sendSketchFeedback = async (shotId, feedback, refImageBase64) => {
    try {
      // Optimistically mark shot as iterating for immediate loading feedback
      setShots(prev => prev.map(s => s.id === shotId ? { ...s, sketch_iterating: true } : s));
      const res = await axios.post(`${API}/shots/${shotId}/sketch-feedback`, { feedback, reference_image: refImageBase64 });
      if (res.data.processing) {
        // Start polling for completion with 2-minute timeout
        let pollCount = 0;
        const maxPolls = 60;
        const pollInterval = setInterval(async () => {
          pollCount++;
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            alert('Sketch iteration timed out. Refresh the page to check.');
            await loadProjectData();
            return;
          }
          try {
            const statusRes = await axios.get(`${API}/shots/${shotId}/iteration-status`);
            if (!statusRes.data.sketch.iterating) {
              clearInterval(pollInterval);
              if (statusRes.data.sketch.error) {
                alert('Sketch iteration failed: ' + statusRes.data.sketch.error);
              }
              await loadProjectData();
            }
          } catch (e) {
            clearInterval(pollInterval);
            console.error('Poll error:', e);
            await loadProjectData();
          }
        }, 2000);
      } else {
        await loadProjectData();
      }
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };


  const pollValidation = () => {
    setValidationPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/validation-status`);
        const d = res.data;
        setValidationStatus(d.status || '');
        if (!d.processing) {
          clearInterval(interval);
          setValidationPolling(false);
          if (d.has_report) {
            toast.success('Simulation complete!');
            loadProjectData();
          } else if (d.error) {
            toast.error('Simulation failed: ' + d.error);
          }
        }
      } catch(e) { /* ignore poll errors */ }
    }, 4000);
    return () => clearInterval(interval);
  };


  // IGK auto-poll (2026-05-22) — keeps the toolbar counter & card statuses
  // live during chunked IGK runs. Hits the lightweight status endpoint
  // every 3s while processing=true; stops cleanly when the run finishes.
  const pollIgkProgress = () => {
    if (igkPollRef.current) clearInterval(igkPollRef.current);
    igkPollRef.current = setInterval(async () => {
      try {
        const st = await axios.get(`${API}/projects/${projectId}/hyperreal-keyframes-status`);
        const data = st.data || {};
        // Refresh shots so cards pick up newly-completed IGK URLs / status flips
        const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
        setShots(sortShots(shotsRes.data));
        // Mirror the live counters onto the project state for the toolbar label
        setProject(prev => prev ? {
          ...prev,
          hyperreal_keyframes_processing: data.processing,
          hyperreal_keyframes_completed: data.completed,
          hyperreal_keyframes_failed: data.failed,
          hyperreal_keyframes_total: data.total,
        } : prev);
        if (!data.processing) {
          clearInterval(igkPollRef.current);
          igkPollRef.current = null;
          setUploading(false);
          setUploadingAction((prev) => prev === 'hyperrealKeyframes' ? null : prev);
          // Final reconciliation
          loadProjectData();
        }
      } catch (e) { /* swallow transient errors, keep polling */ }
    }, 3000);
  };

  const pollSketchProgress = () => {
    if (sketchPollRef.current) clearInterval(sketchPollRef.current);
    // Fresh run — let the completion toast fire again for this stage.
    notifiedStagesRef.current.delete('Storyboard Sketches');
    sketchPollRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}/sketch-status`);
        const status = response.data;
        setSketchProgress(status);
        const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
        setShots(sortShots(shotsRes.data));
        // STRICT completion check — every shot must be either completed or
        // explicitly failed. Previous check (`!processing && completed > 0`)
        // fired the moment the first shot finished, leading to premature
        // "Storyboard Sketches Complete" notifications while later shots
        // were still rendering (user-reported, 2026-05-15).
        //
        // 2026-05-25 — Also require every shot ROW to be in a terminal
        // state (has sketch_url OR sketch_status==='failed'). The project
        // counters (`completed`/`failed`) drift out of sync when an
        // orchestrator task is hot-reload-killed mid-batch: the counter
        // reads `failed: 1` while the shot row stays at `status:
        // generating`. The previous check passed `(completed+failed)>=total`
        // and stopped polling — the user had to manually refresh to see
        // the missing shot. The shot-level check below catches that drift.
        const total = Number(status?.total || 0);
        const completed = Number(status?.completed || 0);
        const failed = Number(status?.failed || 0);
        const shotsList = Array.isArray(shotsRes.data) ? shotsRes.data : [];
        const allShotsTerminal = shotsList.length > 0 && shotsList.every(s => {
          const url = (s.sketch_url || '').trim();
          const st = (s.sketch_status || '').trim();
          return url || st === 'failed';
        });
        const trulyDone = !status?.processing && total > 0 && (completed + failed) >= total && allShotsTerminal;
        if (trulyDone) {
          clearInterval(sketchPollRef.current);
          sketchPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setSketchProgress(null);
          notifyStageComplete('Storyboard Sketches');
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  // ── Add / Delete Shot Handlers ──
  const handleAddShot = async () => {
    if (!addShotDescription.trim()) return;
    setAddingShotLoading(true);
    try {
      const payload = { description: addShotDescription.trim() };
      if (addShotAfter) payload.insert_after_shot_id = addShotAfter;
      const res = await axios.post(`${API}/projects/${projectId}/add-shot`, payload);
      toast.success(`Shot ${res.data.shot_number} added — sketch generating...`);
      setShowAddShotModal(false);
      setAddShotDescription('');
      setAddShotAfter('');
      await loadProjectData();
    } catch (e) {
      toast.error('Failed to add shot: ' + (e.response?.data?.detail || e.message));
    }
    setAddingShotLoading(false);
  };

  const handleDeleteShot = async () => {
    if (!deleteConfirm) return;
    const deletedId = deleteConfirm.id;
    const label = `${deleteConfirm.scene_number}-${deleteConfirm.shot_number}`;
    const snap = _captureNavState();
    setDeletingShot(true);
    try {
      await axios.delete(`${API}/shots/${deletedId}`);
      // Optimistic local re-number — skip `loadProjectData()` which reruns
      // `setExpandedSections(...)` and derived-state toggles, collapsing
      // whatever section the user was scrolled to. Keeping state local
      // preserves scroll position + stage focus.
      setShots(prev => prev
        .filter(s => s.id !== deletedId)
        .map((s, idx) => ({
          ...s,
          order_index: idx,
          shot_number: `SH${String(idx + 1).padStart(2, '0')}`,
        }))
      );
      toast.success(`Shot ${label} deleted`);
      setDeleteConfirm(null);
      _restoreNavState(snap);
    } catch (e) {
      toast.error('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
    setDeletingShot(false);
  };

  const handleReorderShots = async (newOrderIds) => {
    // Optimistic update: reorder locally first
    const reordered = newOrderIds.map((id, idx) => {
      const s = shots.find(sh => sh.id === id);
      return s ? { ...s, order_index: idx, shot_number: `SH${String(idx + 1).padStart(2, '0')}` } : null;
    }).filter(Boolean);
    setShots(reordered);

    try {
      await axios.post(`${API}/projects/${projectId}/reorder-shots`, { shot_ids: newOrderIds });
      toast.success('Shots reordered & renumbered');
    } catch (e) {
      toast.error('Reorder failed: ' + (e.response?.data?.detail || e.message));
      await loadProjectData(); // rollback
    }
  };

  const handleInsertAfter = (afterShotId) => {
    setAddShotAfter(afterShotId || '');
    setShowAddShotModal(true);
  };

  const generateAssets = async (onlyFailed = false, source = 'basic') => {
    // 2026-06-16: `source` decouples gen path from Crew Pre-Production.
    //   "basic"  (DEFAULT) — pre-Crew-PP behaviour, ignores Crew PP refs.
    //                        Always available; escape hatch when Crew PP
    //                        misbehaves.
    //   "crewpp" — uses Crew PP reference uploads as identity anchors.
    //              Gated on briefs_approved_at + refs present.
    setUploading(true); setUploadingAction('assets');
    try {
      // Flush any pending debounced CCC edits so the backend sees the latest
      // user-edited list (deletes/renames/additions) BEFORE asset generation
      // begins. Without this, a user who deletes a character and immediately
      // hits "Generate" will hit the stale derived_ccc on the server.
      if (cccSaveTimerRef.current) {
        clearTimeout(cccSaveTimerRef.current);
        cccSaveTimerRef.current = null;
      }
      if (!onlyFailed && derivedCCC && (derivedCCC.cast?.length || derivedCCC.costumes?.length || derivedCCC.locations?.length)) {
        try {
          await axios.post(`${API}/projects/${projectId}/derived-ccc`, derivedCCC);
          // Auto-confirm the current CCC so the backend uses the user-edited
          // list instead of falling back to script-LLM re-extraction (which
          // would re-hallucinate the characters the user just deleted).
          if (!derivedCCC.confirmed) {
            await axios.post(`${API}/projects/${projectId}/confirm-ccc`);
            setDerivedCCC(prev => prev ? { ...prev, confirmed: true } : prev);
          }
        } catch (syncErr) {
          console.warn('[CCC] pre-asset sync failed (continuing anyway)', syncErr);
        }
      }
      const url = `${API}/projects/${projectId}/generate-assets${onlyFailed ? '?only_failed=true' : ''}`;
      await axios.post(url, { source });
      setExpandedSections(prev => ({ ...prev, assets: true }));
      pollAssetProgress();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('An asset generation is already running — please wait for it to finish.');
      } else {
        alert('Failed: ' + (e.response?.data?.detail || e.message));
      }
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollAssetProgress = () => {
    if (assetPollRef.current) clearInterval(assetPollRef.current);
    notifiedStagesRef.current.delete('Asset Bible');
    const assetPollStart = Date.now();
    assetPollRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}/asset-status`);
        const status = response.data;
        // 2026-07-15 — terminal guard for the "stuck at 0/0" state (prod report):
        // if the job queued ZERO candidates, the old ab_done check (which needs
        // total>0) never fires and the bar spins forever. Stop and surface an
        // honest message instead — either it finished with nothing to do, or the
        // job died before queuing (flag stuck true).
        const nowTotal = Number(status?.total || 0);
        if (nowTotal === 0) {
          const stalledOut = (Date.now() - assetPollStart) > 90000;
          if (!status?.processing || stalledOut) {
            clearInterval(assetPollRef.current);
            assetPollRef.current = null;
            setUploading(false); setUploadingAction(null);
            setAssetProgress(null);
            toast.error(
              status?.processing
                ? 'Asset generation is stuck with 0 items queued — the job likely died before queuing. Hit Generate again; if it repeats, rebuild the Asset Bible spec / CCC first.'
                : 'No assets were queued to generate. Build/repair the Asset Bible spec (CCC) first, then try again.'
            );
            return;
          }
        }
        setAssetProgress(status);
        const abRes = await axios.get(`${API}/projects/${projectId}/asset-bible`).catch(() => ({ data: null }));
        if (abRes.data) setAssetBible(abRes.data);
        // STRICT completion check — every candidate must be resolved (ok or
        // failed). Previous `!processing && completed > 0` fired on first
        // candidate completion (user-reported, 2026-05-15).
        const ab_total = Number(status?.total || 0);
        const ab_completed = Number(status?.completed || 0);
        const ab_failed = Number(status?.failed || 0);
        const ab_done = !status?.processing && ab_total > 0 && (ab_completed + ab_failed) >= ab_total;
        if (ab_done) {
          clearInterval(assetPollRef.current);
          assetPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setAssetProgress(null);
          // P1-4: Count failed candidates and delay the "complete" toast
          // when any slot actually failed. Fires a warning toast instead so
          // users don't get misled into thinking everything worked.
          //
          // 2026-05-16: Skip items flagged `stub: true` — those are
          // materialized from `derived_ccc` by the auto-hydrate endpoint
          // BEFORE physical image generation, so a missing `url` on a
          // stub is by design, not a failure.
          const bible = abRes.data || {};
          const isRealFailure = c => !c?.stub && (c?.status === 'failed' || (!c?.url && c?.prompt));
          const failedCount =
            (bible.cast_candidates || []).filter(isRealFailure).length +
            (bible.costume_candidates || []).filter(isRealFailure).length +
            (bible.corner_candidates || []).filter(isRealFailure).length;
          if (failedCount > 0) {
            toast.warning(
              `Asset Bible finished — but ${failedCount} ${failedCount === 1 ? 'item' : 'items'} failed. Click "Retry failed only" to re-run them.`,
              { duration: 9000 }
            );
          } else {
            notifyStageComplete('Asset Bible');
          }
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 4000);
  };

  const advanceStage = async () => {
    try {
      const currentStage = project?.current_stage || 0;
      // ── Implicit keyframe lock on Stage 5.5/8 → 9 transition (2026-05-28) ──
      // The standalone "5b. CD Keyframe Lock" section is removed (folded
      // into advance). When the operator moves past the audit phase, mark
      // every shot with an active keyframe_url as `keyframe_locked: true`.
      // Best-effort — failure here doesn't block the stage advance.
      if (currentStage === 8 && shots.some(s => s.keyframe_url && !s.keyframe_locked)) {
        try {
          await axios.post(`${API}/projects/${projectId}/lock-all-keyframes`);
        } catch (_) { /* best-effort */ }
      }
      const res = await axios.post(`${API}/projects/${projectId}/advance-stage`);
      const newStage = res.data.current_stage;
      toast.success(`Advanced to Stage ${newStage}`);
      await loadProjectData();
      // Auto-switch tab to the new stage's group
      const stageToGroup = { 1:'script', 2:'script', 3:'preproduction', 4:'preproduction', 5:'preproduction', 6:'preproduction', 7:'production', 8:'production', 9:'production', 10:'production', 11:'production', 12:'production', 13:'production', 14:'production', 15:'production', 16:'postprod' };
      const group = stageToGroup[newStage] || 'script';
      setActiveStageGroup(group);
      setActiveSidebarItem(group === 'production' ? 'video' : group);
      // Expand the relevant section for the new stage
      const stageToSection = { 3:'assets', 4:'sketches', 5:'sketchAngles', 6:'assets', 7:'prompts', 8:'keyframes', 9:'vlmQa', 10:'keyframeLock', 11:'neutralMaster', 12:'videoGen', 13:'videoQa', 14:'videoLock', 15:'export', 16:'postproduction' };
      const section = stageToSection[newStage];
      if (section) {
        setExpandedSections(prev => ({ ...prev, [section]: true }));
      }
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const skipToStage = async (targetStage, stageName) => {
    try {
      await axios.post(`${API}/projects/${projectId}/skip-to-stage`, { stage: targetStage });
      toast.info(`Skipped to Stage ${targetStage}`);
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // ── Stage 2 footer trigger: "Generate all pending" ────────────────
  // Single-click batch fan-out replacing the per-row inline "Generate"
  // Zap buttons (removed 2026-02 · Rohit feedback). Fires the same
  // idempotent chain that auto-runs after a fresh shot breakdown:
  //   Director's Prompts → DOP Spec ∥ Asset bindings
  // Skip-cached at every phase, so re-clicks are safe and cheap.
  const [generatingPending, setGeneratingPending] = useState(false);
  // STAGE-GATE readiness (2026-07-19): the honest source for the disabled
  // "Approved, Move to Next Stage" button + its outstanding-list tooltip.
  // Blockers = unresolved ambers; secondary text-wardrobe advisories never block.
  const [bindingReadiness, setBindingReadiness] = useState(null);
  // OPERATOR OVERRIDE (2026-07-20): binding warnings are honest but NOT a hard
  // lock — the CD can review and approve anyway. Persisted per-project in
  // localStorage so it survives refresh without a backend round-trip.
  const [bindingOverride, setBindingOverride] = useState(false);
  useEffect(() => {
    if (!projectId) { setBindingOverride(false); return; }
    try { setBindingOverride(localStorage.getItem(`bindingOverride:${projectId}`) === '1'); } catch { setBindingOverride(false); }
  }, [projectId]);
  const toggleBindingOverride = React.useCallback(() => {
    setBindingOverride(prev => {
      const next = !prev;
      try { localStorage.setItem(`bindingOverride:${projectId}`, next ? '1' : '0'); } catch {}
      return next;
    });
  }, [projectId]);
  const fetchBindingReadiness = React.useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/binding-readiness`);
      setBindingReadiness(res.data || null);
    } catch (e) {
      setBindingReadiness(null);
    }
  }, [projectId]);
  useEffect(() => {
    if (!projectId || !shots || shots.length === 0) { setBindingReadiness(null); return; }
    fetchBindingReadiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, shots.length, JSON.stringify(shots.map(s => s?.asset_bible_spec?.amber_flags || []))]);

  const generateAllPending = async () => {
    if (generatingPending) return;
    setGeneratingPending(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/stage2/generate-pending`);
      const d = res.data || {};
      if (d.queued === false) {
        toast.info(d.message || 'Nothing pending — all shots already have prompts, DOP, and bindings.');
      } else {
        toast.success(
          `Queued: ${d.missing_director_prompt || 0} Director's Prompt(s), ${d.missing_dop_spec || 0} DOP Spec(s), ${d.missing_asset_bindings || 0} Asset binding(s). Refreshing as they complete…`
        );
        // Refresh project doc so processing flags + per-shot status flips
        // surface immediately in the UI.
        await loadProjectData();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message || 'Failed to start generation';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start generation');
    } finally {
      setGeneratingPending(false);
    }
  };

  const confirmPreProduction = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/confirm-pre-production`);
      await loadProjectData();
      // Auto-switch to keyframes tab
      setActiveStageGroup('production');
      setActiveSidebarItem('production');
      setExpandedSections(prev => ({ ...prev, pipeline: true, keyframes: true }));
      toast.success('Pre-production confirmed — moved to Keyframes');
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // Combined pipeline state
  const [pipelineProgress, setPipelineProgress] = useState(null);
  const pipelinePollRef = useRef(null);

  // Auto pipeline state
  const [autoPipelineStatus, setAutoPipelineStatus] = useState(null);
  const autoPollRef = useRef(null);

  const generatePromptsAndKeyframes = async () => {
    setUploading(true); setUploadingAction('pipeline');
    try {
      const isRegenerate = shots.some(s => s.keyframe_url);
      const url = `${API}/projects/${projectId}/generate-prompts-and-keyframes${isRegenerate ? '?force=true' : ''}`;
      const res = await axios.post(url);
      setExpandedSections(prev => ({ ...prev, keyframes: true }));
      if (res.data.pre_generated === true) {
        // All keyframes already pre-generated — just refresh to show them
        setUploading(false); setUploadingAction(null);
        await loadProjectData();
      } else {
        pollPipelineProgress();
      }
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollPipelineProgress = () => {
    if (pipelinePollRef.current) clearInterval(pipelinePollRef.current);
    pipelinePollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/pipeline-status`);
        setPipelineProgress(res.data);
        // Also update shots to show keyframes as they appear
        const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
        setShots(sortShots(shotsRes.data));
        if (!res.data.processing && res.data.keyframes_completed > 0) {
          clearInterval(pipelinePollRef.current);
          pipelinePollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setPipelineProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  const generatePrompts = async () => {
    setUploading(true); setUploadingAction('prompts');
    try {
      await axios.post(`${API}/projects/${projectId}/generate-prompts`);
      setExpandedSections(prev => ({ ...prev, prompts: true }));
      pollPromptProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollPromptProgress = () => {
    if (promptPollRef.current) clearInterval(promptPollRef.current);
    promptPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/prompt-status`);
        setPromptProgress(res.data);
        if (!res.data.processing) {
          clearInterval(promptPollRef.current);
          promptPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setPromptProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 2000);
  };

  const generateKeyframes = async () => {
    setUploading(true); setUploadingAction('keyframes');
    try {
      await axios.post(`${API}/projects/${projectId}/generate-keyframes`);
      setExpandedSections(prev => ({ ...prev, keyframes: true }));
      pollKeyframeProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollKeyframeProgress = () => {
    if (keyframePollRef.current) clearInterval(keyframePollRef.current);
    keyframePollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/keyframe-status`);
        setKeyframeProgress(res.data);
        const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
        setShots(sortShots(shotsRes.data));
        if (!res.data.processing) {
          clearInterval(keyframePollRef.current);
          keyframePollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setKeyframeProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  const regenerateKeyframe = async (shotId, customPrompt) => {
    // 2026-05-23 (Rohit) — CORRECTED FLOW: don't clear the tile. The card
    // must keep displaying the current keyframe + video during regen. The
    // new keyframe lands as an iteration entry in gemini_iterations /
    // hyperreal_iterations and the SAM workbench auto-opens on completion
    // for A/B review. Only Accept in the workbench swaps the card.
    //
    // Detect IGK-sourced shots so we route regen to the hyperreal endpoint
    // instead of the legacy MJ/Gemini one (which would produce the wrong
    // style of frame for an Insanely Good keyframe card).
    // Route regen to the IGK/hyperreal endpoint whenever the shot has
    // been IGK'd, regardless of whether the legacy `keyframe_url` is
    // still lying around from the initial MJ pass. Previous check was
    // `!keyframe_url || keyframe_hyperreal_stale` which routed IGK'd
    // shots through the plain MJ regen (no cast bible / no `--cref` /
    // no LLG refs) and produced totally different characters every
    // time — reported by Rohit 2026-05-23.
    const currentShot = shots.find(s => s.id === shotId);
    const isIGK = !!(currentShot && currentShot.keyframe_hyperreal_url);
    // Light-touch optimistic marker — just flip a "regen in progress"
    // flag so the SAM icon on the card can pulse. Do NOT clear
    // keyframe_url / keyframe_hyperreal_url / video_url.
    setShots(prev => prev.map(s => s.id === shotId
      ? { ...s, keyframe_regen_in_progress: true, keyframe_error: '' }
      : s));
    try {
      if (isIGK) {
        await axios.post(`${API}/shots/${shotId}/regenerate-hyperreal-keyframe`, {});
      } else {
        await axios.post(`${API}/shots/${shotId}/regenerate-keyframe`, customPrompt ? { prompt: customPrompt } : {});
      }
    } catch (e) {
      setShots(prev => prev.map(s => s.id === shotId ? { ...s, keyframe_regen_in_progress: false } : s));
      toast.error('Failed to start regen: ' + (e.response?.data?.detail || e.message));
      return;
    }
    // Poll the lightweight iteration-status endpoint every 4s until done.
    // Caps at 5 min (75 × 4s) — typical regen is 30-90s but MJ + VLM +
    // Gemini fallback worst-case ~3 min. After cap, do a full reload so
    // the user at least sees the latest DB state.
    const maxPolls = 75;
    let pollCount = 0;
    const pollInterval = setInterval(async () => {
      pollCount++;
      try {
        const res = await axios.get(`${API}/shots/${shotId}/iteration-status`);
        const kf = res.data?.keyframe || {};
        const kfHy = res.data?.keyframe_hyperreal || {};
        const stillWorking = kf.generating || kf.iterating || (isIGK && kfHy.generating);
        if (!stillWorking) {
          clearInterval(pollInterval);
          await loadProjectData();
          const finalErr = kf.error || (isIGK ? kfHy.error : '');
          // For legacy MJ/Gemini regen, `keyframe_url` is intentionally
          // NOT overwritten in the corrected flow — the new frame lands on
          // `keyframe_regen_last_url` + as a `regen_current` entry in
          // `gemini_iterations`. For IGK the hyperreal pipeline still
          // updates `keyframe_hyperreal_url` in place.
          const finalUrl = isIGK ? kfHy.url : (kf.regen_last_url || '');
          if (finalErr) {
            toast.error('Keyframe regen failed: ' + finalErr);
          } else if (finalUrl) {
            toast.success('New keyframe ready — opening workbench for A/B compare');
            // Auto-open the SAM workbench so the operator can compare the
            // new keyframe against the `pre_regen` iteration entry stashed
            // by the backend. Dispatched via a window event since
            // KeyframeCard owns the `showGeminiIterModal` state.
            try {
              window.dispatchEvent(new CustomEvent('keyframe-regen-done', { detail: { shotId, isIGK } }));
            } catch (_) { /* older browsers */ }
          }
          return;
        }
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          await loadProjectData();
          toast.warning('Keyframe regen taking longer than expected — check back in a minute');
        }
      } catch (e) {
        // Keep polling on transient errors; bail only if we hit the cap.
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          await loadProjectData();
        }
      }
    }, 4000);
  };

  const sendKeyframeFeedback = async (shotId, feedback, refImageBase64) => {
    try {
      // Optimistically mark shot as iterating for immediate loading feedback
      setShots(prev => prev.map(s => s.id === shotId ? { ...s, keyframe_iterating: true } : s));
      const res = await axios.post(`${API}/shots/${shotId}/keyframe-feedback`, { feedback, reference_image: refImageBase64 });
      if (res.data.processing) {
        // Start polling for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/shots/${shotId}/iteration-status`);
            if (!statusRes.data.keyframe.iterating) {
              clearInterval(pollInterval);
              if (statusRes.data.keyframe.error) {
                alert('Keyframe iteration failed: ' + statusRes.data.keyframe.error);
              }
              await loadProjectData();
            }
          } catch (e) {
            clearInterval(pollInterval);
            console.error('Poll error:', e);
            await loadProjectData();
          }
        }, 2000);
      } else {
        await loadProjectData();
      }
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // Animate keyframe (Image to Video) from keyframe stage
  const animateKeyframe = async (shotId, prompt) => {
    try {
      await axios.post(`${API}/shots/${shotId}/animate-keyframe`, { prompt });
      toast.success('Video generation started');
      // Start polling for video progress
      pollVideoProgress();
    } catch (e) { 
      alert('Failed: ' + (e.response?.data?.detail || e.message)); 
      throw e;
    }
  };

  const generateSingleAngles = async (shotId, mode = 'sketch') => {
    try {
      // Optimistically mark this shot as generating in local state
      setGeneratingAngleShots(prev => new Set(prev).add(`${shotId}_${mode}`));
      await axios.post(`${API}/shots/${shotId}/generate-angles`, { mode });
      toast.success(`Generating ${mode} angles...`);
      // Start a shared polling interval if not already running
      if (!anglePollRef.current) {
        anglePollRef.current = setInterval(async () => {
          try {
            const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
            setShots(sortShots(shotsRes.data));
            // Check if any shots still have angles generating
            const stillGenerating = shotsRes.data.some(s => s.sketch_angles_generating || s.keyframe_angles_generating);
            if (!stillGenerating) {
              clearInterval(anglePollRef.current);
              anglePollRef.current = null;
              setGeneratingAngleShots(new Set());
            }
          } catch {}
        }, 4000);
        // Safety: stop after 3 min
        setTimeout(() => { 
          if (anglePollRef.current) { clearInterval(anglePollRef.current); anglePollRef.current = null; }
          setGeneratingAngleShots(new Set());
        }, 180000);
      }
    } catch (e) {
      setGeneratingAngleShots(prev => { const n = new Set(prev); n.delete(`${shotId}_${mode}`); return n; });
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const stopVideoGeneration = async (shotId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/stop-generation`, { task_type: 'videos' });
      toast.success('Stopping video generation...');
      await loadProjectData();
    } catch (e) {
      alert('Failed to stop: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Convert keyframe to RAW or GRADE the RAW
  const convertKeyframeRaw = async (shotId, mode) => {
    try {
      await axios.post(`${API}/shots/${shotId}/convert-raw`, { mode });
      toast.success(mode === 'raw' ? 'Converting to S-Log3 RAW...' : 'Grading to Rec709...');
      await loadProjectData();
    } catch (e) { 
      alert('Failed: ' + (e.response?.data?.detail || e.message)); 
      throw e;
    }
  };

  // Generate pending keyframes (only those without keyframe_url)
  const generatePendingKeyframes = async () => {
    setUploading(true); setUploadingAction('pendingKeyframes');
    try {
      const url = `${API}/projects/${projectId}/generate-pending-keyframes`;
      await axios.post(url);
      setExpandedSections(prev => ({ ...prev, keyframes: true }));
      pollPipelineProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  // Batch convert all keyframes to RAW (S-Log3)
  const convertAllToRaw = async () => {
    
    setUploading(true); setUploadingAction('convertAllRaw');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/batch-convert-raw`);
      toast.success(`Converting ${res.data.total} keyframes to RAW...`);
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/projects/${projectId}/batch-raw-status`);
          if (statusRes.data.completed >= statusRes.data.total) {
            clearInterval(pollInterval);
            await loadProjectData();
            setUploading(false); setUploadingAction(null);
            toast.success('All keyframes converted to RAW!');
          }
        } catch (e) {
          clearInterval(pollInterval);
          setUploading(false); setUploadingAction(null);
        }
      }, 3000);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  // Batch grade all keyframes to Rec709
  const gradeAllKeyframes = async () => {
    
    setUploading(true); setUploadingAction('gradeAll');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/batch-grade-all`);
      toast.success(`Grading ${res.data.total} keyframes to Rec709...`);
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/projects/${projectId}/batch-grade-status`);
          if (statusRes.data.completed >= statusRes.data.total) {
            clearInterval(pollInterval);
            await loadProjectData();
            setUploading(false); setUploadingAction(null);
            toast.success('All keyframes graded!');
          }
        } catch (e) {
          clearInterval(pollInterval);
          setUploading(false); setUploadingAction(null);
        }
      }, 3000);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const approvePPM = async () => {
    
    setUploading(true); setUploadingAction('ppm');
    try {
      await axios.post(`${API}/projects/${projectId}/ppm-approval`, { project_id: projectId, approved: true });
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false); setUploadingAction(null);
  };

  const downloadFile = async (type) => {
    setDownloading(type);
    // P1-5: Human-readable labels for the completion toast below. Users were
    // previously left guessing whether a storyboard/PPM/shot PDF actually saved.
    const labelMap = {
      shots: 'Shot list (.txt)',
      'mj-prompts': 'Midjourney prompts (.txt)',
      storyboard: 'Storyboard (.pdf)',
      ppm: 'PPM (.pdf)',
    };
    const friendlyLabel = labelMap[type] || `${type}.pdf`;
    try {
      const response = await axios.get(`${API}/projects/${projectId}/download/${type}`, {
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const ext = (type === 'shots' || type === 'mj-prompts') ? 'txt' : 'pdf';
      let filename = `${project?.name || 'download'}_${type}.${ext}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      // P1-5: confirm success with a toast + size line; users report the
      // storyboard .pdf is the main one they miss visibility on.
      const sizeMb = (blob.size / (1024 * 1024));
      const sizeStr = sizeMb >= 1 ? `${sizeMb.toFixed(1)} MB` : `${Math.max(1, Math.round(blob.size / 1024))} KB`;
      toast.success(`${friendlyLabel} downloaded · ${filename} (${sizeStr})`, { duration: 5000 });
    } catch (e) {
      console.error('Download failed:', e);
      toast.error(`${friendlyLabel} download failed: ${e.response?.data?.detail || e.message}`);
    }
    setTimeout(() => setDownloading(null), 1500);
  };

  /* ══════════════════════════════════════════════════════════════════════════
     STAGE 9-16 FUNCTIONS
     ══════════════════════════════════════════════════════════════════════════ */

  // Stage 9: VLM QA
  const runVlmQa = async () => {
    setUploading(true); setUploadingAction('vlmqa');
    try {
      await axios.post(`${API}/projects/${projectId}/vlm-qa`);
      setExpandedSections(prev => ({ ...prev, vlmQa: true }));
      pollVlmQaProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollVlmQaProgress = () => {
    if (vlmQaPollRef.current) clearInterval(vlmQaPollRef.current);
    vlmQaPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/vlm-qa-status`);
        setVlmQaProgress(res.data);
        if (!res.data.processing && res.data.completed > 0) {
          clearInterval(vlmQaPollRef.current);
          vlmQaPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setVlmQaProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  // Auto-improve states
  const [autoImproving, setAutoImproving] = useState({});
  const [autoImproveAllProgress, setAutoImproveAllProgress] = useState(null);
  const autoImproveAllPollRef = useRef(null);

  // Auto-improve single keyframe
  const autoImproveSingle = async (shotId) => {
    setAutoImproving(prev => ({ ...prev, [shotId]: true }));
    try {
      await axios.post(`${API}/shots/${shotId}/auto-improve-keyframe`);
      // Poll for completion
      const pollSingle = setInterval(async () => {
        try {
          const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
          const updatedShot = shotsRes.data.find(s => s.id === shotId);
          if (updatedShot && !updatedShot.auto_improving) {
            clearInterval(pollSingle);
            setAutoImproving(prev => ({ ...prev, [shotId]: false }));
            loadProjectData();
          }
        } catch (e) { console.error('Poll error:', e); }
      }, 3000);
    } catch (e) {
      alert('Auto-improve failed: ' + (e.response?.data?.detail || e.message));
      setAutoImproving(prev => ({ ...prev, [shotId]: false }));
    }
  };

  // Auto-improve all low-scoring keyframes
  const autoImproveAll = async () => {
    const lowScoreCount = shots.filter(s => s.vlm_qa_score && s.vlm_qa_score < 90).length;
    if (lowScoreCount === 0) {
      alert('All keyframes already have 90+ scores!');
      return;
    }
    
    setUploading(true); setUploadingAction('autoImproveAll');
    try {
      await axios.post(`${API}/projects/${projectId}/auto-improve-all-keyframes`);
      pollAutoImproveAll();
    } catch (e) {
      alert('Auto-improve failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollAutoImproveAll = () => {
    if (autoImproveAllPollRef.current) clearInterval(autoImproveAllPollRef.current);
    autoImproveAllPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/auto-improve-status`);
        setAutoImproveAllProgress(res.data);
        if (!res.data.processing && res.data.completed > 0) {
          clearInterval(autoImproveAllPollRef.current);
          autoImproveAllPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setAutoImproveAllProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  // Stage 10: Lock All Keyframes
  const lockAllKeyframes = async () => {
    
    setUploading(true); setUploadingAction('lockKeyframes');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/lock-all-keyframes`);
      alert(`Locked ${res.data.locked_count} keyframes!`);
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false); setUploadingAction(null);
  };

  // Stage 11: Neutral Master
  const generateNeutralMasters = async () => {
    setUploading(true); setUploadingAction('neutral');
    try {
      await axios.post(`${API}/projects/${projectId}/generate-neutral-masters`);
      setExpandedSections(prev => ({ ...prev, neutralMaster: true }));
      pollNeutralProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollNeutralProgress = () => {
    if (neutralPollRef.current) clearInterval(neutralPollRef.current);
    neutralPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/neutral-status`);
        setNeutralProgress(res.data);
        if (!res.data.processing && res.data.completed > 0) {
          clearInterval(neutralPollRef.current);
          neutralPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setNeutralProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 4000);
  };

  // Stage 12: Keyframe Lineup
  const loadKeyframeLineup = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/keyframe-lineup`);
      setKeyframeLineup(res.data);
      setExpandedSections(prev => ({ ...prev, lineup: true }));
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const confirmKeyframeLineup = async () => {
    
    try {
      await axios.post(`${API}/projects/${projectId}/confirm-keyframe-lineup`);
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // Stage 13: Video Generation
  // ── Auto-engine routing (2026-05-28) ────────────────────────────────
  // For shots produced by Keyframe+ (`gpt-image-2-keyframe-plus`) or IGK
  // (`gpt-image-2-hyperreal`), prefer the Hyperreal cascade
  // (Seedance primary → Kling fallback). For mixed/other engines, fall
  // through to the default Vertex→Seedance→Veo→Kling cascade.
  const computePreferredEngineChain = (shotList) => {
    const eligible = (shotList || []).filter(s => s.keyframe_url);
    if (!eligible.length) return { mode: 'vertex', chain: 'default', autoRouted: false };
    const hyperrealEngines = new Set(['gpt-image-2-keyframe-plus', 'gpt-image-2-hyperreal']);
    const hyperrealShots = eligible.filter(s => hyperrealEngines.has(s.keyframe_engine || ''));
    // If >=80% of eligible shots are K+/IGK, route the whole batch via
    // hyperreal cascade. Otherwise use the platform default.
    const ratio = hyperrealShots.length / eligible.length;
    if (ratio >= 0.8) {
      return { mode: 'seedance', chain: 'hyperreal_cascade', autoRouted: true, hyperrealCount: hyperrealShots.length, totalCount: eligible.length };
    }
    return { mode: 'vertex', chain: 'default', autoRouted: false, hyperrealCount: hyperrealShots.length, totalCount: eligible.length };
  };

  const generateVideos = async (mode = 'vertex', strategy = 'sequential', extraOpts = {}) => {
    // If admin selected a specific engine, use it
    const engine = selectedVideoEngine !== 'auto' ? selectedVideoEngine : mode;
    const strategyLabel = strategy === 'parallel' ? 'all at once' : 'one by one';
    setUploading(true); setUploadingAction(`videos_${engine}_${strategy}`);
    try {
      // Append engine_chain so the backend can use Seedance→Kling
      // hyperreal cascade for K+/IGK shots.
      const chainQ = extraOpts.engine_chain ? `&engine_chain=${extraOpts.engine_chain}` : '';
      const batchQ = extraOpts.batch_size ? `&batch_size=${extraOpts.batch_size}` : '';
      await axios.post(`${API}/projects/${projectId}/generate-videos?mode=${engine}&strategy=${strategy}${chainQ}${batchQ}`);
      setExpandedSections(prev => ({ ...prev, videoGen: true }));
      pollVideoProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollVideoProgress = () => {
    if (videoPollRef.current) clearInterval(videoPollRef.current);
    videoPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/video-status`);
        setVideoProgress(res.data);
        const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
        setShots(sortShots(shotsRes.data));
        // Stop polling only when: project not processing AND no shots are in-progress
        const isDone = !res.data.processing && res.data.in_progress === 0;
        if (isDone) {
          clearInterval(videoPollRef.current);
          videoPollRef.current = null;
          // Keep progress bar visible briefly so user sees final state
          setTimeout(() => setVideoProgress(null), 2000);
          setUploading(false); setUploadingAction(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  const generateSingleVideo = async (shotId) => {
    try {
      // Engine routing for individual shot — K+/IGK go through
      // Seedance→Kling hyperreal cascade; everything else falls back to
      // the platform default. Reads shot.keyframe_engine.
      const s = shots.find(x => x.id === shotId);
      const isHyperreal = ['gpt-image-2-keyframe-plus', 'gpt-image-2-hyperreal'].includes(s?.keyframe_engine || '');
      const chainQ = isHyperreal ? '?engine_chain=hyperreal_cascade' : '';
      await axios.post(`${API}/shots/${shotId}/generate-video${chainQ}`);
      pollVideoProgress();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const stopSingleVideo = async (shotId) => {
    try {
      await axios.post(`${API}/shots/${shotId}/stop-video`);
      // Refresh shots data
      const shotsRes = await axios.get(`${API}/projects/${projectId}/shots`);
      setShots(sortShots(shotsRes.data));
    } catch (e) { alert('Failed to stop: ' + (e.response?.data?.detail || e.message)); }
  };

  const sendVideoFeedback = async (shotId, feedback, refImageBase64) => {
    try {
      await axios.post(`${API}/shots/${shotId}/video-feedback`, { feedback, reference_image: refImageBase64 });
      pollVideoProgress();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const lockSingleVideo = async (shotId) => {
    try {
      await axios.post(`${API}/shots/${shotId}/lock-video`);
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const submitMotionControl = async (shotId, videoFile, prompt, orientation) => {
    try {
      const form = new FormData();
      form.append('file', videoFile);
      form.append('prompt', prompt || '');
      form.append('character_orientation', orientation || 'video');
      form.append('mode', 'pro');
      await axios.post(`${API}/shots/${shotId}/motion-control`, form);
      // Start polling video progress
      pollVideoProgress();
    } catch (e) { alert('Motion control failed: ' + (e.response?.data?.detail || e.message)); }
  };


  // Stage 14: Video QA
  const runVideoQa = async () => {
    setUploading(true); setUploadingAction('videoqa');
    try {
      await axios.post(`${API}/projects/${projectId}/video-qa`);
      setExpandedSections(prev => ({ ...prev, videoQa: true }));
      pollVideoQaProgress();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
      setUploading(false); setUploadingAction(null);
    }
  };

  const pollVideoQaProgress = () => {
    if (videoQaPollRef.current) clearInterval(videoQaPollRef.current);
    videoQaPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/video-qa-status`);
        setVideoQaProgress(res.data);
        if (!res.data.processing && res.data.completed > 0) {
          clearInterval(videoQaPollRef.current);
          videoQaPollRef.current = null;
          setUploading(false); setUploadingAction(null);
          setVideoQaProgress(null);
          loadProjectData();
        }
      } catch (e) { console.error('Poll error:', e); }
    }, 3000);
  };

  // Stage 15: Lock All Videos
  const lockAllVideos = async () => {
    
    setUploading(true); setUploadingAction('lockVideos');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/lock-all-videos`);
      alert(`Locked ${res.data.locked_count} videos!`);
      await loadProjectData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setUploading(false); setUploadingAction(null);
  };

  // Stage 16: Export
  const loadExportPackage = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/export`);
      setExportPackage(res.data);
      setExpandedSections(prev => ({ ...prev, export: true }));
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const finalizeProject = async () => {
    
    try {
      await axios.post(`${API}/projects/${projectId}/finalize`);
      await loadProjectData();
      alert('Project finalized successfully!');
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // AI Chat functions
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        project_id: projectId,
        session_id: `chat_${projectId}`,
        message: chatInput,
        context: {
          project_name: project?.name,
          current_stage: project?.current_stage,
          production_mode: project?.production_mode,
          total_shots: shots?.length || 0,
          sketched: shots?.filter(s => s.sketch_url)?.length || 0,
          keyframed: shots?.filter(s => s.keyframe_url)?.length || 0,
          kf_locked: shots?.filter(s => s.keyframe_locked)?.length || 0,
          videos_done: shots?.filter(s => s.video_status === 'completed')?.length || 0,
          videos_failed: shots?.filter(s => s.video_status === 'error')?.length || 0,
          videos_processing: shots?.filter(s => s.video_status === 'processing')?.length || 0,
          script_summary: project?.script_text?.substring(0, 500),
          product_name: project?.product_name,
          has_assets: !!assetBible?.cast_candidates?.length,
          ppm_approved: project?.ppm_approved,
          // System health
          auto_pipeline_running: project?.auto_pipeline_running,
          auto_pipeline_stage: project?.auto_pipeline_stage,
          auto_pipeline_error: project?.auto_pipeline_error,
          pipeline_processing: project?.pipeline_processing,
          video_processing: project?.video_processing,
          sketches_processing: project?.sketches_processing,
          assets_processing: project?.assets_processing,
          stage_timestamps: project?.stage_timestamps,
          cost_total: project?.cost_tracker?.total,
        }
      });

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        suggestions: response.data.suggestions
      }]);
    } catch (e) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble right now. Please try again.",
        isError: true
      }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const scrollToStage = (stageNum) => {
    const stageToGroup = {
      1: 'script', 2: 'script',
      3: 'preproduction', 4: 'preproduction', 5: 'preproduction', 6: 'preproduction',
      7: 'production', 8: 'production', 9: 'production',
      10: 'production', 11: 'production', 12: 'production',
      13: 'production', 14: 'production', 15: 'production',
      16: 'postprod', 17: 'postprod',
    };
    const group = stageToGroup[stageNum] || 'script';
    setActiveStageGroup(group);
    // Sync sidebar with the group
    const groupToSidebar = {
      'script': 'script', 'preproduction': 'preproduction', 'production': 'production',
      'production': 'video', 'postprod': 'postprod',
    };
    setActiveSidebarItem(groupToSidebar[group] || 'script');
    // Also expand the relevant section
    const stageMap = {
      1:  'script', 2: 'shots', 3: 'assets', 5: 'sketches',
      7:  'pipeline', 9: 'vlmQa', 13: 'videoGen',
      16: 'export', 17: 'postproduction',
    };
    if (stageMap[stageNum]) {
      setExpandedSections(prev => ({ ...prev, [stageMap[stageNum]]: true }));
      // Scroll to the section after a brief delay for React to render
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${stageMap[stageNum]}-section"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  };

  /* ── RENDER ── */

  if (loading) return (
    <div className="flex items-center justify-center h-screen cosmic-bg"><Loader2 className="w-8 h-8 animate-spin text-ai-accent" /></div>
  );
  if (!project) return (
    <div className="flex items-center justify-center h-screen cosmic-bg">
      <div className="text-center"><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><p className="text-white/60">Project not found</p></div>
    </div>
  );

  const hasSketchedShots = shots.some(s => s.sketch_url);
  // Counts used by the "Download all" icon buttons on Sketch + Keyframe stages.
  // Button greys out unless every shot has finished rendering — clicking
  // streams a ZIP archive of the raw images straight to disk/phone.
  const sketchUrlCount = shots.filter(s => s.sketch_url || s.sketch_s3_url).length;
  const keyframeUrlCount = shots.filter(
    s => s.keyframe_url || s.keyframe_s3_url
       || s.keyframe_hyperreal_url || s.keyframe_hyperreal_s3_url
  ).length;
  const allSketchesDone = shots.length > 0 && sketchUrlCount === shots.length;
  const allKeyframesDone = shots.length > 0 && keyframeUrlCount === shots.length;
  // `hasAssets` means "has at least one ACTUALLY RENDERED asset image"
  // (i.e., a candidate with a non-empty `url`). Mere stubs — names without
  // a rendered image, the state right after the deterministic resolver
  // populates the bible from script — do NOT count, otherwise the
  // "Generated" badge fires and the button label flips to "Regenerate"
  // before any image has actually rendered. Fixes the post-wipe state
  // confusion reported 2026-05-19.
  const _hasRenderedUrl = (c) => !!(c && (c.url || c.normalized_url));
  const hasAssets = assetBible && (
    (assetBible.cast_candidates || []).some(_hasRenderedUrl)
    || (assetBible.costume_candidates || []).some(_hasRenderedUrl)
    || (assetBible.corner_candidates || []).some(_hasRenderedUrl)
  );

  const toggleShowAllStages = () => {
    setShowAllStages(v => {
      const nv = !v;
      try { localStorage.setItem('showAllStages', nv ? '1' : '0'); } catch { /* ignore */ }
      return nv;
    });
  };
  const effectiveStage = showAllStages ? 17 : (project?.current_stage || 0);
  const isPpmApproved = () => showAllStages || !!project?.ppm_approved;

  return (
    <div className="h-screen w-full max-w-full cosmic-bg text-white flex overflow-hidden relative" data-testid="project-view">
      {/* Global Image Lightbox (supports single-image {url,alt} AND gallery {items,index}) */}
      {globalLightbox && (() => {
        const items = globalLightbox.items
          || [{ url: globalLightbox.url, type: globalLightbox.type || 'image', label: globalLightbox.alt || '' }];
        const index = globalLightbox.index ?? 0;
        return (
          <NavigableLightbox
            items={items}
            index={index}
            onIndexChange={(i) => setGlobalLightbox((prev) => prev ? { ...prev, index: i } : null)}
            onClose={() => setGlobalLightbox(null)}
            testid="global-lightbox"
          />
        );
      })()}
      {/* P0-3 Asset Bible Bulk-Iterate modal */}
      <BulkIteratePanel
        projectId={projectId}
        assetBible={assetBible}
        open={bulkIterateOpen}
        onClose={() => setBulkIterateOpen(false)}
        onDone={() => loadProjectData()}
      />
      {/* Public share-link modal */}
      <ShareLinkModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        projectId={projectId}
        authToken={localStorage.getItem('token')}
        projectName={project?.name || 'Project'}
      />
      {/* Script Templatizer modal */}
      <ScriptTemplatizerModal
        open={showTemplatizerModal}
        onClose={() => { setShowTemplatizerModal(false); loadProjectData(); }}
        projectId={projectId}
        authToken={localStorage.getItem('token')}
        onLocked={() => loadProjectData()}
      />
      {/* Shooting Script modal — mirrors Templatizer flow, mutually exclusive lock */}
      <ShootingScriptModal
        open={showShootingScriptModal}
        onClose={() => { setShowShootingScriptModal(false); loadProjectData(); }}
        projectId={projectId}
        authToken={localStorage.getItem('token')}
        onLocked={() => loadProjectData()}
      />
      {/* Image Board modal — gpt-image-2 3×3 contact sheets for composition / framing */}
      {showImageBoardModal && (
        <Suspense fallback={null}>
          <ImageBoardModal
            projectId={projectId}
            shotCount={(shots || []).length}
            onClose={() => setShowImageBoardModal(false)}
          />
        </Suspense>
      )}
      {/* Add Shot Modal */}
      {showAddShotModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowAddShotModal(false)} data-testid="add-shot-modal-overlay">
          <div className="bg-[#1A1033] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#22C55E]" /> Add New Shot
              </h3>
              <button onClick={() => setShowAddShotModal(false)} className="p-1 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5 text-white/50" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">Shot Description</label>
                <textarea
                  value={addShotDescription}
                  onChange={(e) => setAddShotDescription(e.target.value)}
                  placeholder="Describe the shot you want to add. AI will contextually align it with your script and existing breakdown..."
                  className="w-full h-28 px-3 py-2 text-sm text-white bg-white/5 border border-white/10 rounded-xl outline-none focus:border-ai-accent resize-none placeholder:text-white/30"
                  data-testid="add-shot-description-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">Insert After (optional)</label>
                <select
                  value={addShotAfter}
                  onChange={(e) => setAddShotAfter(e.target.value)}
                  className="w-full px-3 py-2 text-sm text-white bg-white/5 border border-white/10 rounded-xl outline-none focus:border-ai-accent cursor-pointer"
                  data-testid="add-shot-position-select"
                >
                  <option value="" className="bg-[#1A1033]">Auto (AI decides best position)</option>
                  {shots.map((s) => (
                    <option key={s.id} value={s.id} className="bg-[#1A1033]">
                      After {s.scene_number}-{s.shot_number}: {s.description?.slice(0, 50)}...
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[10px] text-white/40">
                AI will analyze your script and existing shots to generate camera angle, movement, duration, and other metadata.
                A sketch will be auto-generated after the shot is added.
              </p>
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setShowAddShotModal(false)} data-testid="add-shot-cancel-btn"
                className="px-4 py-2 text-sm font-medium text-white/60 hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={handleAddShot} disabled={addingShotLoading || !addShotDescription.trim()} data-testid="add-shot-submit-btn"
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 bg-[#22C55E] text-white hover:bg-[#16A34A] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                {addingShotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {addingShotLoading ? 'Adding...' : 'Add Shot'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Shot Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)} data-testid="delete-shot-confirm-overlay">
          <div className="bg-[#1A1033] border border-[#FF3B30]/30 rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Delete Shot?</h3>
                <p className="text-xs text-white/50">{deleteConfirm.scene_number} — {deleteConfirm.shot_number}</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-white/70">
                This will permanently delete <span className="font-semibold text-white">{deleteConfirm.scene_number}-{deleteConfirm.shot_number}</span> and
                its sketch, keyframe, and video. Remaining shots will be re-numbered.
              </p>
              <p className="text-xs text-white/40 mt-2 line-clamp-2">{deleteConfirm.description}</p>
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} data-testid="delete-shot-cancel-btn"
                className="px-4 py-2 text-sm font-medium text-white/60 hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteShot} disabled={deletingShot} data-testid="delete-shot-confirm-btn"
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 bg-[#FF3B30] text-white hover:bg-[#D32F2F] disabled:opacity-40 transition-all flex items-center gap-2">
                {deletingShot ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deletingShot ? 'Deleting...' : 'Delete Shot'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Delete Confirm Modal */}
      {confirmBulkDelete && (() => {
        // 2026-05-25 — Modal text adapts to the action's actual scope.
        // In sketches/keyframes mode we CLEAR images (preserves shots);
        // in any other mode we DELETE shot rows.
        const mode = bulkSelectMode;
        const isClear = (mode === 'sketches' || mode === 'keyframes');
        const noun = mode === 'keyframes' ? 'Keyframe' : (mode === 'sketches' ? 'Sketch' : 'Shot');
        const verb = isClear ? 'Clear' : 'Delete';
        return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => !bulkDeleting && setConfirmBulkDelete(false)} data-testid="bulk-delete-confirm-overlay">
          <div className={`bg-[#1A1033] border ${isClear ? 'border-amber-500/30' : 'border-[#FF3B30]/30'} rounded-2xl w-full max-w-md shadow-2xl`}
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${isClear ? 'bg-amber-500/15' : 'bg-[#FF3B30]/10'} flex items-center justify-center`}>
                <Trash2 className={`w-5 h-5 ${isClear ? 'text-amber-400' : 'text-[#FF3B30]'}`} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">{verb} {selectedShotIds.size} {noun}{selectedShotIds.size === 1 ? '' : 's'}?</h3>
                <p className="text-xs text-white/50">{isClear ? 'Images only — shot data is preserved.' : 'This action cannot be undone.'}</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-white/70">
                {isClear
                  ? <>Remove the rendered <span className="font-semibold text-white">{noun.toLowerCase()} image{selectedShotIds.size === 1 ? '' : 's'}</span> on {selectedShotIds.size} shot{selectedShotIds.size === 1 ? '' : 's'}. The shot descriptions, prompts, DOP specs, bindings, and any other artifacts remain intact — you can regenerate the {noun.toLowerCase()}s anytime.</>
                  : <>Permanently delete <span className="font-semibold text-white">{selectedShotIds.size} shot{selectedShotIds.size === 1 ? '' : 's'}</span>, along with every sketch, keyframe, and video tied to them. Remaining shots will be re-numbered.</>}
              </p>
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setConfirmBulkDelete(false)} disabled={bulkDeleting} data-testid="bulk-delete-cancel-btn"
                className="px-4 py-2 text-sm font-medium text-white/60 hover:text-white transition-colors disabled:opacity-40">
                Cancel
              </button>
              <button onClick={performBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-confirm-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 ${isClear ? 'bg-amber-500/80 hover:bg-amber-500 text-white' : 'bg-[#FF3B30] hover:bg-[#D32F2F] text-white'} disabled:opacity-40 transition-all flex items-center gap-2`}>
                {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {bulkDeleting ? `${isClear ? 'Clearing' : 'Deleting'} ${selectedShotIds.size}…` : `${verb} ${selectedShotIds.size}`}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {/* ── Shot Breakdown: Re-run confirmation modal ──
       *  Triggered by the ↻ icon in the section header (post-gen). Confirms
       *  destructive replacement of all current shots, then returns to the
       *  InputMethodPicker preserving the last-used `breakdownInputSource`. */}
      {showRerunConfirm && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowRerunConfirm(false)}
          data-testid="shot-breakdown-rerun-confirm-modal"
        >
          <div
            className="bg-[#1A1033] border border-white/10 rounded-2xl w-full max-w-md p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-base font-semibold mb-2">Replace all shots?</h3>
            <p className="text-white/70 text-sm mb-2" data-testid="shot-breakdown-rerun-what-dies">
              This will <span className="text-red-400 font-semibold">permanently delete all {shots.length} current shot{shots.length === 1 ? '' : 's'}</span>, their director prompt &amp; round history, and every downstream artifact (sketches, keyframes, segmentation masks, enhancement jobs).
            </p>
            <p className="text-emerald-400/80 text-xs mb-5">
              ✓ A restorable snapshot is taken automatically before anything is deleted — you can roll back from Project → Snapshots.
            </p>
            <div className="flex justify-end gap-2">
              <ActionButton
                variant="neutral"
                onClick={() => setShowRerunConfirm(false)}
                data-testid="shot-breakdown-rerun-cancel"
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="primary"
                icon={RefreshCw}
                onClick={async () => {
                  setShowRerunConfirm(false);
                  // Wipe all current shots so the InputMethodPicker UI
                  // surfaces. The user's chosen `breakdownInputSource` is
                  // preserved across the wipe (component state).
                  try {
                    await axios.post(`${API}/projects/${projectId}/clear-shots`);
                    toast.info('Shots cleared — pick an input method to re-run.');
                    await loadProjectData();
                  } catch (e) {
                    // Fallback: deleting individually if no bulk endpoint.
                    try {
                      for (const s of shots) {
                        // eslint-disable-next-line no-await-in-loop
                        await axios.delete(`${API}/shots/${s.id}`);
                      }
                      toast.info('Shots cleared — pick an input method to re-run.');
                      await loadProjectData();
                    } catch (e2) {
                      toast.error('Failed to clear shots: ' + (e2.response?.data?.detail || e2.message));
                    }
                  }
                }}
                data-testid="shot-breakdown-rerun-confirm"
              >
                Yes, replace all
              </ActionButton>
            </div>
          </div>
        </div>
      )}
      {/* ── Shot Breakdown: discard-guard dialog when switching method ──
       *  Triggered when the user attempts to switch input method while a
       *  file or pasted text is staged in the current method. Cancel keeps
       *  the staged content; Discard wipes it and moves to the new method. */}
      {pendingSwitchTo && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setPendingSwitchTo(null)}
          data-testid="shot-breakdown-discard-modal"
        >
          <div
            className="bg-[#1A1033] border border-white/10 rounded-2xl w-full max-w-md p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-base font-semibold mb-2">
              Discard your {breakdownInputSource === 'upload' ? 'uploaded file' : 'pasted text'}?
            </h3>
            <p className="text-white/60 text-sm mb-5">
              Switching input method will clear the content you have staged in the current method. This can't be undone.
            </p>
            <div className="flex justify-end gap-2">
              <ActionButton
                variant="neutral"
                onClick={() => setPendingSwitchTo(null)}
                data-testid="shot-breakdown-discard-cancel"
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={() => {
                  // Wipe staged content from the source we're leaving.
                  if (breakdownInputSource === 'upload') setPendingBreakdownFile(null);
                  if (breakdownInputSource === 'paste') setPendingBreakdownText('');
                  setBreakdownInputSource(pendingSwitchTo);
                  setPendingSwitchTo(null);
                }}
                data-testid="shot-breakdown-discard-confirm"
              >
                Discard & switch
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Paste Shot Breakdown Modal */}
      {showPasteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowPasteModal(false)} data-testid="paste-modal-overlay">
          <div className="bg-[#1A1033] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                  <ClipboardPaste className="w-5 h-5 text-[#34C759]" />
                  Paste Shot Breakdown
                </h3>
                <p className="text-white/40 text-xs mt-1">Paste your shot breakdown in any format. AI will structure it while keeping your descriptions intact.</p>
              </div>
              <button onClick={() => setShowPasteModal(false)} className="p-1.5 text-white/40 hover:text-white transition-colors" data-testid="paste-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 flex-1 overflow-auto">
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`Paste your shot breakdown here...\n\nExamples:\n• SH01 - Wide establishing shot of the city skyline at dawn\n• SH02 - Close-up of protagonist's face, looking worried\n• SH03 - Over-the-shoulder shot of two characters talking\n\nOr any format — numbered lists, paragraphs, bullet points, copied from Google Docs, etc.`}
                className="w-full h-64 bg-black/30 border border-white/10 rounded-xl p-4 text-white text-sm font-mono resize-none focus:outline-none focus:border-[#34C759]/50 placeholder-white/20"
                autoFocus
                data-testid="paste-breakdown-textarea"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-white/30 text-xs">{pasteText.length} characters</span>
                <div className="flex gap-2">
                  <button onClick={() => setShowPasteModal(false)} className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white transition-colors">
                    Cancel
                  </button>
                  <button onClick={handlePasteBreakdown} disabled={pasteText.trim().length < 10}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      pasteText.trim().length < 10 ? 'bg-white/5 text-white/30 cursor-not-allowed' : 'bg-[#34C759] text-white hover:bg-[#34C759]/90 active:scale-95'
                    }`}
                    data-testid="paste-breakdown-submit">
                    <ClipboardPaste className="w-4 h-4" />
                    Parse & Create Shots
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Mobile sidebar overlay */}
      {showSidebar && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setShowSidebar(false)} />
      )}

      {/* Left Sidebar — HUMAN/WARM Style (The Einstein) */}
      <aside className={`
        fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50
        w-56 flex flex-col overflow-hidden
        transform transition-transform duration-300
        ${showSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ background: 'linear-gradient(180deg, #0B1120 0%, #101B2E 50%, #0D1526 100%)', borderRight: '1px solid rgba(56, 189, 248, 0.08)' }}>
        <BrandLogo />

        {/* New Project Button — matches pipeline tabs h-12 (48px) */}
        <div className="h-12 px-3 flex items-center border-b" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <button onClick={() => navigate('/')} className="w-full px-4 py-2 rounded-lg font-ai font-semibold text-xs uppercase tracking-wider transition-all hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #162D4A 100%)', color: '#94C4F7', boxShadow: '0 4px 15px rgba(30, 58, 95, 0.4)', border: '1px solid rgba(56, 189, 248, 0.12)' }}>
            <Plus className="w-4 h-4 inline mr-2" />New Project
          </button>
        </div>

        {/* Navigation — Warm serif style */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-hidden">
          {SIDEBAR_NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.path) navigate(item.path);
                else if (item.stage) scrollToStage(item.stage);
                setActiveSidebarItem(item.id);
                setShowSidebar(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-ai font-semibold uppercase tracking-wider transition-all duration-200 ${
                activeSidebarItem === item.id 
                  ? 'text-human-text' 
                  : 'hover:bg-[#26201B]/50'
              }`}
              style={activeSidebarItem === item.id ? {
                background: 'rgba(56, 189, 248, 0.08)',
                color: '#E2E8F0',
                boxShadow: '0 0 12px rgba(56, 189, 248, 0.06)'
              } : { color: '#64748B' }}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{item.label}</span>
            </button>
          ))}

          {/* Creative Tools — inside scrollable nav */}
          <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(245, 158, 11, 0.12)' }}>
            <p className="px-3 mb-2 text-[9px] font-ai font-bold uppercase tracking-[0.2em]" style={{ color: 'rgba(245, 158, 11, 0.5)' }}>Creative Tools</p>
            <button onClick={() => navigate('/recipes')} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-ai uppercase tracking-wider rounded-lg transition-all hover:bg-[#3D2A0A]/40 group"
              style={{ color: '#F59E0B' }}
              data-testid="sidebar-recipes-pv">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <Beaker className="w-3.5 h-3.5 transition-transform group-hover:scale-110" style={{ color: '#F59E0B' }} />
              </div>
              <span>Recipes</span>
              <span className="ml-auto text-[9px] font-bold" style={{ color: 'rgba(245, 158, 11, 0.4)' }} data-testid="sidebar-recipe-count-pv">{recipeCount ?? ''}</span>
            </button>
            <button onClick={() => navigate('/llm-studio')} className="w-full mt-1 flex items-center gap-3 px-3 py-2.5 text-xs font-ai uppercase tracking-wider rounded-lg transition-all hover:bg-[#3D2A0A]/40 group"
              style={{ color: '#F59E0B' }}
              data-testid="sidebar-llms-pv">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <Bot className="w-3.5 h-3.5 transition-transform group-hover:scale-110" style={{ color: '#F59E0B' }} />
              </div>
              <span>LLMs</span>
              <span className="ml-auto text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.20)', color: '#c7d2fe' }}>NEW</span>
            </button>
          </div>
        </nav>

        <div className="p-3 border-t relative z-[70]" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#162D4A]/50 transition-colors">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(56, 189, 248, 0.1)' }}>
              <span className="text-xs font-ai" style={{ color: '#7DD3FC' }}>{user?.name?.[0] || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-ai truncate" style={{ color: '#CBD5E1' }}>{user?.name || 'User'}</p>
            </div>
            <button onClick={() => { localStorage.clear(); window.location.href = '/login'; }} className="p-1.5 hover:bg-[#162D4A]/50 rounded transition-colors" title="Sign out">
              <LogOut className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
            </button>
          </div>
          {user?.is_admin && (
            <button onClick={() => navigate('/admin')} className="w-full flex items-center gap-3 px-3 py-2 mt-1 text-xs font-ai uppercase tracking-wider rounded-lg transition-colors hover:bg-[#162D4A]/50" style={{ color: '#7DD3FC' }}>
              <Shield className="w-3.5 h-3.5" />
              <span>Admin Panel</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content — AI/COOL Style (The Picasso) */}
      <main className="flex-1 flex flex-col min-w-0 relative z-10">
        {/* Duality of Creation background — centered to main panel */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          backgroundImage: 'url(/assets/duality-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.06
        }} />
        {/* Header */}
        <header className="h-14 px-4 flex items-center justify-between border-b border-ai-border -ml-px" style={{ background: 'rgba(11, 15, 25, 0.8)', backdropFilter: 'blur(20px)' }}>
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 overflow-hidden">
            <button onClick={() => setShowSidebar(true)} className="p-2 hover:bg-white/5 rounded-lg lg:hidden flex-shrink-0">
              <Menu className="w-5 h-5 text-white/60" />
            </button>
            {/* EiPi logo — visible on mobile when sidebar is hidden, click → home */}
            <img src="/assets/eipi-logo.png" alt="EiPi Media — back to home" className="h-6 w-auto brightness-0 invert opacity-80 lg:hidden flex-shrink-0 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => navigate('/')} title="Back to home" data-testid="project-mobile-logo-home" />
            <button onClick={() => navigate('/')} className="p-2 hover:bg-white/5 rounded-lg transition-colors hidden sm:block flex-shrink-0">
              <ArrowLeft className="w-4 h-4 text-white/60" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              {project.product_sketch_url && !project.product_image_missing && (
                <img src={resolveUrl(project.product_sketch_url)} alt="" className="h-7 w-7 rounded object-cover border border-ai-border hidden sm:block flex-shrink-0" onError={() => setProductImgError(true)} />
              )}
              <span className="text-sm text-white/50 hidden sm:inline flex-shrink-0">Project:</span>
              <span className="text-sm font-semibold text-white truncate max-w-[90px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-none">{project.name}</span>
              <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold hidden sm:inline ${project.uploaded_breakdown_filename ? 'bg-[#FF9500]/20 text-[#FF9500]' : 'bg-ai-accent/15 text-ai-accent'}`}
                data-testid="project-header-mode-badge">
                {project.production_mode === 'human' ? 'HUMAN' : project.production_mode === 'telegram' ? 'TELEGRAM' : 'AUTO'}
              </span>
              <ChevronDown className="w-4 h-4 text-white/40 hidden sm:block" />
              {/* C4 (#73) — always-visible degraded-consensus pill on the
                  project header (banner lives in the Stage-2 panel; this
                  makes the DEGRADED state visible on every tab). */}
              {(derivedCCC?.consensus_degraded || project?.consensus_degraded) && (
                <span
                  className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-500/20 text-red-300 border border-red-500/50 animate-pulse flex-shrink-0"
                  data-testid="consensus-degraded-header-pill"
                  title="Consensus DEGRADED — a single-model draft is in use (Opus reconciliation failed). Review cast / costume / location data before generating assets."
                  role="alert"
                >
                  <AlertTriangle className="w-3 h-3" /> DEGRADED
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {/* Cost Tracker */}
            <div className="relative" data-testid="cost-tracker">
              <button onClick={() => setShowCostBreakdown(!showCostBreakdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors"
                style={{ background: 'rgba(14, 165, 233, 0.15)', border: '1px solid rgba(14, 165, 233, 0.3)' }}
                data-testid="cost-tracker-toggle">
                <span className="text-sm font-semibold text-white">
                  {currencyMode === 'USD' 
                    ? `$${costData.total.toFixed(2)}` 
                    : `₹${costData.total_inr?.toFixed(0) || Math.round(costData.total * 83)}`}
                </span>
              </button>
              {showCostBreakdown && (
                <div className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-xl z-50 p-4"
                  style={{ background: 'rgba(19, 27, 44, 0.95)', border: '1px solid rgba(14, 165, 233, 0.3)', backdropFilter: 'blur(20px)' }}
                  data-testid="cost-breakdown-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-white">Cost Breakdown</span>
                      {/* Currency Toggle */}
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: 'rgba(14, 165, 233, 0.2)' }}>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setCurrencyMode('INR'); }}
                          className={`text-[9px] px-1.5 py-0.5 rounded-lg transition-all ${currencyMode === 'INR' ? 'bg-ai-accent text-white' : 'text-white/50 hover:text-white/70'}`}
                        >INR</button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setCurrencyMode('USD'); }}
                          className={`text-[9px] px-1.5 py-0.5 rounded-lg transition-all ${currencyMode === 'USD' ? 'bg-ai-accent text-white' : 'text-white/50 hover:text-white/70'}`}
                        >USD</button>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-400">
                        {currencyMode === 'USD' 
                          ? `$${costData.total.toFixed(2)}` 
                          : `₹${costData.total_inr?.toFixed(0) || Math.round(costData.total * 83)}`}
                      </div>
                      <div className="text-[10px] text-white/50">
                        {currencyMode === 'USD' 
                          ? `₹${costData.total_inr?.toFixed(0) || Math.round(costData.total * 83)} INR`
                          : `$${costData.total.toFixed(2)} USD`}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(costData.breakdown || {}).filter(([,v]) => v.count > 0).sort((a,b) => b[1].total - a[1].total).map(([op, data]) => (
                      <div key={op} className="flex items-center justify-between text-[10px]">
                        <span className="text-white/60 capitalize">{op.replace(/_/g, ' ')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50">x{data.count}</span>
                          <span className="font-medium text-white w-16 text-right">
                            {currencyMode === 'USD' 
                              ? `$${data.total.toFixed(2)}` 
                              : `₹${Math.round(data.total * (costData.usd_to_inr || 83))}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-2 border-t border-ai-border">
                    <div className="flex items-center justify-between text-[9px] text-white/50">
                      <span>1 USD = ₹{costData.usd_to_inr?.toFixed(2) || '83.00'}</span>
                      <span>Live rate</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid rgba(14, 165, 233, 0.2)' }} data-testid="view-mode-toggle">
              <button onClick={() => setViewMode('stages')}
                className="px-2 sm:px-3 py-1.5 text-[10px] sm:text-[11px] font-mono font-semibold tracking-wide transition-all"
                style={{ background: viewMode === 'stages' ? 'rgba(14,165,233,0.15)' : 'transparent', color: viewMode === 'stages' ? '#0EA5E9' : '#64748B' }}>
                Stages
              </button>
              <button onClick={() => setViewMode('pipeline')}
                className="px-2 sm:px-3 py-1.5 text-[10px] sm:text-[11px] font-mono font-semibold tracking-wide transition-all flex items-center gap-1"
                style={{ background: viewMode === 'pipeline' ? 'rgba(14,165,233,0.15)' : 'transparent', color: viewMode === 'pipeline' ? '#0EA5E9' : '#64748B' }}>
                <Layers className="w-3 h-3" /> Canvas
              </button>
            </div>
            <button className="px-4 py-2 rounded-lg text-sm font-medium hidden sm:flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)', boxShadow: '0 4px 15px rgba(14, 165, 233, 0.4)' }}>
              <Sparkles className="w-4 h-4" /><span>Generate</span>
            </button>
            <button onClick={() => setShowSettings(!showSettings)} className="p-2 hover:bg-white/5 rounded-lg transition-colors hidden sm:block relative" data-testid="settings-btn">
              <Settings className="w-4 h-4 text-white/60" />
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              title="Share storyboard"
              className="p-2 hover:bg-white/5 rounded-lg transition-colors hidden sm:flex items-center gap-1.5 text-cyan-300/80 hover:text-cyan-300"
              data-testid="open-share-link-btn"
            >
              <Link2 className="w-4 h-4" />
              <span className="text-[11px] font-medium hidden lg:inline">Share</span>
            </button>
            {showSettings && (
              <div className="absolute right-20 top-12 w-80 rounded-xl shadow-xl z-50 p-4"
                style={{ background: 'rgba(19, 27, 44, 0.95)', border: '1px solid rgba(14, 165, 233, 0.3)', backdropFilter: 'blur(20px)' }}
                data-testid="settings-panel">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-white">Project Settings</span>
                  <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4 text-white/60" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Project Name</label>
                    <div className="text-sm text-white font-medium">{project.name}</div>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Current Stage</label>
                    <div className="text-sm text-ai-text font-mono">{project.current_stage}/17</div>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Video Duration (target)</label>
                    <div className="text-sm text-white">{project.video_duration || 60}s</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-white/60">Auto-Improve (VLM QA)</label>
                    <input type="checkbox" checked={project.auto_improve_enabled || false}
                      onChange={async (e) => {
                        try {
                          await axios.post(`${API}/projects/${projectId}/settings`, { auto_improve_enabled: e.target.checked });
                          loadProjectData();
                        } catch (err) { console.error(err); }
                      }}
                      className="w-4 h-4 accent-[#0EA5E9]" data-testid="setting-auto-improve" />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Status</label>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${project.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-ai-accent/10 text-ai-accent'}`}>
                      {project.status || 'active'}
                    </span>
                  </div>
                  {user?.role === 'admin' && (
                    <button
                      onClick={async () => {
                        
                        try {
                          await axios.post(`${API}/projects/${projectId}/force-reset`);
                          toast.success('Processing flags reset');
                          loadProjectData();
                          setShowSettings(false);
                        } catch (err) { toast.error('Reset failed'); }
                      }}
                      className="w-full px-3 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                      data-testid="force-reset-btn"
                    >
                      <AlertCircle className="w-3.5 h-3.5" />
                      Force Reset Stuck Tasks
                    </button>
                  )}
                  <SnapshotHistory projectId={projectId} onRollbackComplete={() => { loadProjectData(); setShowSettings(false); }} />
                </div>
              </div>
            )}
            <button className="p-2 hover:bg-white/5 rounded-lg transition-colors hidden sm:block">
              <Bell className="w-4 h-4 text-white/60" />
            </button>
            {/* Mobile chat toggle */}
            <button onClick={() => setShowChat(!showChat)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden">
              <MessageSquare className="w-5 h-5 text-ai-accent" />
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-ai-border">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(14, 165, 233, 0.3)' }}>
                <span className="text-xs font-medium">{user?.name?.[0] || 'U'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* ── Pipeline View (Node Workflow) ── */}
        {viewMode === 'pipeline' ? (
          <div className="flex-1 overflow-hidden" data-testid="pipeline-view-container">
            <Suspense fallback={
              <div className="flex items-center justify-center h-full" style={{ background: '#0B0F19' }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0EA5E9' }} />
              </div>
            }>
              <NodeWorkflow projectId={projectId} />
            </Suspense>
          </div>
        ) : (
        <>
        {/* Pipeline Navigation */}
        <div className="h-12 px-2 sm:px-4 flex items-center gap-1 border-b border-ai-border overflow-x-auto custom-scrollbar -ml-px scrollbar-none"
          style={{ background: 'rgba(11, 15, 25, 0.4)', WebkitOverflowScrolling: 'touch' }}>
          {[
            { id: 'script', label: 'Script', icon: FileText, stage: 0 },
            { id: 'preproduction', label: 'Pre-Production', icon: Layers, stage: 2 },
            { id: 'production', label: 'Production', icon: Video, stage: 7 },
            { id: 'postprod', label: 'Post-Prod', icon: Wand2, stage: 14 },
          ].map((stage, idx, arr) => (
            <React.Fragment key={stage.id}>
              <button
                onClick={() => { setActiveStageGroup(stage.id); setActiveSidebarItem(
                  stage.id === 'production' ? 'production' : stage.id
                ); }}
                data-testid={`pipeline-tab-${stage.id}`}
                className={`flex items-center gap-1.5 px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  activeStageGroup === stage.id ? 'text-white' : 'text-white/50 hover:text-white/70'
                }`}
                style={activeStageGroup === stage.id ? {
                  background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.4) 0%, rgba(20, 184, 166, 0.2) 100%)',
                  boxShadow: '0 0 20px rgba(14, 165, 233, 0.3)'
                } : {}}
              >
                <stage.icon className="w-4 h-4" />
                <span>{stage.label}</span>
              </button>
              {idx < arr.length - 1 && <ChevronRight className="w-4 h-4 text-white/20 flex-shrink-0" />}
            </React.Fragment>
          ))}
          <div className="flex-1" />
          {/* Test-mode toggle: show every section regardless of stage
              gate. Persisted in localStorage. Small pill so it doesn't
              distract from the pipeline UI when off. */}
          <button
            type="button"
            onClick={toggleShowAllStages}
            data-testid="show-all-stages-toggle"
            title={showAllStages
              ? 'Test mode ON — every section is rendered regardless of stage gate. Click to return to single-stage view.'
              : 'Test mode: force all sections to render regardless of stage gate. Useful for review / QA.'}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium whitespace-nowrap transition-all ${
              showAllStages
                ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40 hover:bg-amber-500/30'
                : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/70'
            }`}
          >
            <Eye className="w-3 h-3" />
            <span>{showAllStages ? 'Showing all' : 'Show all sections'}</span>
          </button>
          <span className="text-xs px-2 py-1 rounded-lg whitespace-nowrap" style={{ background: 'rgba(14, 165, 233, 0.2)' }}>
            Stage {project.current_stage}/17
          </span>
        </div>

        {/* Stage Progression Sidebar — collapsible 17-stage overview */}
        <StageProgressionSidebar
          projectId={projectId}
          currentStage={project.current_stage}
          authToken={localStorage.getItem('token')}
          onStageClick={(s) => {
            // Jump to the right pipeline tab when user clicks a stage row.
            if (s.num <= 1) { setActiveStageGroup('script'); setActiveSidebarItem('script'); }
            else if (s.num < 7) { setActiveStageGroup('preproduction'); setActiveSidebarItem('preproduction'); }
            else if (s.num < 14) { setActiveStageGroup('production'); setActiveSidebarItem('production'); }
            else { setActiveStageGroup('postprod'); setActiveSidebarItem('postprod'); }
          }}
        />

        {/* Elapsed Timer Bar — starts from script upload time */}
        {effectiveStage >= 2 && project.stage_timestamps?.stage_1?.started_at && (
          <ElapsedTimerBar project={project} />
        )}

        {/* Content Area - Single Stage View */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar p-3 sm:p-4 md:p-6 space-y-4 pb-28 sm:pb-6">

        {/* Auto Pipeline Progress Panel */}
        {(autoPipelineStatus?.running || (['auto', 'telegram'].includes(project.production_mode) && project.current_stage < 16 && autoPipelineStatus)) && (
          <div className="rounded-xl overflow-hidden mb-2" style={{ background: 'linear-gradient(135deg, rgba(14,165,233,0.08), rgba(236,72,153,0.03))' }} data-testid="auto-pipeline-banner">
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-ai-border">
              <div className="flex items-center gap-3">
                {autoPipelineStatus?.running ? (
                  <div className="w-2.5 h-2.5 rounded-lg bg-green-400 animate-pulse" />
                ) : autoPipelineStatus?.possibly_stuck ? (
                  <div className="w-2.5 h-2.5 rounded-lg bg-yellow-400" />
                ) : (
                  <div className="w-2.5 h-2.5 rounded-lg bg-white/30" />
                )}
                <span className="text-sm font-semibold text-white">Auto Mode</span>
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-ai-accent/15 text-ai-text">Stage {project.current_stage}/17</span>
                {autoPipelineStatus?.possibly_stuck && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-500/20 text-yellow-400">Possibly stuck — no update in 5min</span>
                )}
              </div>
              {autoPipelineStatus?.running && (
                <button onClick={async () => {
                  await axios.post(`${API}/projects/${projectId}/reset-pipeline`);
                  toast.info('Pipeline reset');
                  loadProjectData();
                }} className="px-2 py-1 rounded text-[10px] text-red-400 border border-red-500/20 hover:bg-red-500/10">
                  Force Reset
                </button>
              )}
            </div>
            
            {/* Stage label + clock */}
            <div className="px-4 py-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-white/80 font-medium">{autoPipelineStatus?.stage || 'Initializing...'}</p>
                <span className="text-xs text-ai-text font-mono">{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              </div>
              {autoPipelineStatus?.detail?.note && (
                <p className="text-[10px] text-white/40 mt-0.5">{autoPipelineStatus.detail.note}</p>
              )}
              {/* Stage timestamps from DB */}
              {autoPipelineStatus?.timestamps && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 px-1 py-1.5 rounded-lg" style={{ background: 'rgba(14,165,233,0.08)' }}>
                  {Object.entries(autoPipelineStatus.timestamps).sort().map(([key, val]) => {
                    const stageNum = key.replace('stage_', '');
                    const started = val.started_at ? new Date(val.started_at.endsWith('Z') || val.started_at.includes('+') ? val.started_at : val.started_at + 'Z').toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', timeZone: 'Asia/Kolkata' }) : null;
                    const completed = val.completed_at ? new Date(val.completed_at.endsWith('Z') || val.completed_at.includes('+') ? val.completed_at : val.completed_at + 'Z').toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', timeZone: 'Asia/Kolkata' }) : null;
                    const dur = val.duration_seconds ? (val.duration_seconds < 60 ? `${Math.round(val.duration_seconds)}s` : `${Math.floor(val.duration_seconds/60)}m${Math.round(val.duration_seconds%60)}s`) : null;
                    const isActive = started && !completed;
                    return (
                      <span key={key} className={`text-[11px] font-mono ${isActive ? 'text-green-400' : 'text-white/50'}`}>
                        S{stageNum}: {started || '—'}{completed ? ` → ${completed}` : ' ...'}{dur ? ` (${dur})` : ''}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Overall progress bar */}
            <div className="px-4 pb-2">
              <div className="h-2 rounded-lg overflow-hidden" style={{ background: 'rgba(14,165,233,0.15)' }}>
                <div className="h-full rounded-lg transition-all duration-1000" style={{ 
                  width: `${Math.round(((autoPipelineStatus?.current_stage || project.current_stage) / 17) * 100)}%`, 
                  background: 'linear-gradient(90deg, #0EA5E9, #14B8A6)' 
                }} />
              </div>
            </div>
            
            {/* Shot stats grid */}
            {autoPipelineStatus?.shots && (
              <div className="px-4 pb-3 grid grid-cols-4 sm:grid-cols-7 gap-2">
                {[
                  { label: 'Shots', val: autoPipelineStatus.shots.total, color: '#0EA5E9' },
                  { label: 'Sketches', val: autoPipelineStatus.shots.sketched, color: '#38BDF8' },
                  { label: 'Keyframes', val: autoPipelineStatus.shots.keyframed, color: '#FF9500' },
                  { label: 'KF Locked', val: autoPipelineStatus.shots.kf_locked, color: '#22C55E' },
                  { label: 'Videos', val: autoPipelineStatus.shots.vid_done, color: '#FF2D55' },
                  { label: 'Vid Fail', val: autoPipelineStatus.shots.vid_failed, color: autoPipelineStatus.shots.vid_failed > 0 ? '#EF4444' : '#374151' },
                  { label: 'Processing', val: autoPipelineStatus.shots.vid_processing, color: autoPipelineStatus.shots.vid_processing > 0 ? '#F59E0B' : '#374151' },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="text-sm font-bold" style={{ color: s.color }}>{s.val}</div>
                    <div className="text-[8px] text-white/40">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Detail: pass info for keyframes/videos */}
            {autoPipelineStatus?.detail?.pass && (
              <div className="px-4 pb-3 flex items-center gap-4 text-[10px]">
                <span className="text-white/50">Pass {autoPipelineStatus.detail.pass}/{autoPipelineStatus.detail.max_passes}</span>
                {autoPipelineStatus.detail.engine && (
                  <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-medium">
                    {autoPipelineStatus.detail.engine === 'veo' ? 'Veo' : 'Kling'}
                  </span>
                )}
                <span className="text-white/50">{autoPipelineStatus.detail.done}/{autoPipelineStatus.detail.total} done</span>
                {autoPipelineStatus.detail.remaining > 0 && (
                  <span className="text-yellow-400">{autoPipelineStatus.detail.remaining} remaining</span>
                )}
              </div>
            )}
            
            {/* Error */}
            {autoPipelineStatus?.error && (
              <div className="px-4 pb-3">
                <p className="text-xs text-red-400">{autoPipelineStatus.error}</p>
              </div>
            )}
            
            {/* Last updated */}
            {autoPipelineStatus?.stale_seconds > 0 && (
              <div className="px-4 pb-2 text-[9px] text-white/20">
                Last updated {autoPipelineStatus.stale_seconds < 60 ? `${autoPipelineStatus.stale_seconds}s ago` : `${Math.round(autoPipelineStatus.stale_seconds / 60)}m ago`}
              </div>
            )}
          </div>
        )}

        {/* ═══ SCRIPT GROUP (Stages 1-2) ═══ */}
        {activeStageGroup === 'script' && (<>
        {/* ── 1. SCRIPT INGEST ── */}
        <div className="rounded-xl overflow-hidden bg-ai-bg-secondary" data-testid="script-section">
          <button onClick={() => toggleSection('script')}
            className="w-full flex items-center justify-between px-5 py-4 hover:glass-panel transition-colors text-left">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-ai-accent/15 flex items-center justify-center"><FileText className="w-3.5 h-3.5 text-ai-accent" /></div>
              <span className="font-semibold text-sm">1. Script Ingest</span>
              {project.script_text && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">Uploaded</span>}
            </div>
            {expandedSections.script ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
          </button>
          {expandedSections.script && (
            <div className="px-5 pb-5 border-t border-ai-border pt-4">
              {/* Product/SKU Section */}
              <div className="glass-panel rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-ai-accent" />
                  <span className="text-xs font-semibold text-white">Product / SKU Details</span>
                  <span className="text-[10px] text-white/50">(optional — incorporated into shots & sketches)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <input value={productName} onChange={e => setProductName(e.target.value)} placeholder="Product name (e.g. Max Fashion Summer Collection)"
                      className="w-full text-xs text-white px-3 py-2 border border-ai-border rounded-lg outline-none focus:border-ai-accent mb-2"
                      style={{ background: 'rgba(8, 12, 22, 0.9)' }}
                      data-testid="product-name-input" />
                    <textarea value={productFeatures} onChange={e => setProductFeatures(e.target.value)} placeholder="Key features / description..."
                      rows={2} className="w-full text-xs text-white px-3 py-2 border border-ai-border rounded-lg outline-none focus:border-ai-accent resize-none"
                      style={{ background: 'rgba(8, 12, 22, 0.9)' }}
                      data-testid="product-features-input" />
                  </div>
                  <div>
                    <label className="flex flex-col items-center justify-center h-full border-2 border-dashed border-ai-border rounded-xl cursor-pointer hover:border-ai-accent transition-colors p-4" data-testid="product-image-upload">
                      {productFile ? (
                        <img src={URL.createObjectURL(productFile)} alt="Product preview" className="h-16 object-contain mb-1" />
                      ) : (project.product_sketch_url && !project.product_image_missing && !productImgError) ? (
                        <img src={resolveUrl(project.product_sketch_url)} alt="Product" className="h-16 object-contain mb-1" onError={() => setProductImgError(true)} data-testid="product-image-preview" />
                      ) : (project.product_sketch_url && (project.product_image_missing || productImgError)) ? (
                        <div className="text-center" data-testid="product-image-failed-state">
                          <AlertCircle className="w-6 h-6 text-amber-400 mx-auto mb-1" />
                          <span className="text-[10px] text-amber-300 font-medium">Upload failed — re-upload</span>
                          <span className="block text-[9px] text-white/40">previous image is no longer available</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <UploadCloud className="w-6 h-6 text-white/40 mx-auto mb-1" />
                          <span className="text-[10px] text-white/50">Upload product image</span>
                        </div>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={e => setProductFile(e.target.files?.[0])} />
                    </label>
                  </div>
                </div>
                <button onClick={saveProduct} disabled={savingProduct || (!productName && !productFile)} data-testid="save-product-btn"
                  className={`mt-3 px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    productSaved ? 'bg-[#22C55E] text-white' :
                    savingProduct || (!productName && !productFile) ? 'bg-white/10 text-white/40' : 'bg-ai-accent text-white hover:bg-[#4B49B7] active:scale-95'
                  }`}>
                  {savingProduct ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  {productSaved ? <><Check className="w-3 h-3 inline mr-1" /> Saved!</> : 'Save Product Details'}
                </button>
              </div>

              {/* Script viewer/editor — tabbed */}
              {(project.script_text || project.templatized_script_text) ? (
                <div className="mb-3" data-testid="script-tabs-container">
                  <div className="flex items-center gap-1 mb-2 border-b border-white/10 overflow-x-auto -mx-1 px-1 scrollbar-thin">
                    <button
                      onClick={() => setScriptViewTab('edit')}
                      className={`px-2.5 sm:px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${
                        scriptViewTab === 'edit'
                          ? 'border-cyan-400 text-cyan-300 bg-cyan-500/5'
                          : 'border-transparent text-white/50 hover:text-white/80'
                      }`}
                      data-testid="script-tab-edit"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    {project.script_text && (
                      <button
                        onClick={() => setScriptViewTab('original')}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${
                          scriptViewTab === 'original'
                            ? 'border-cyan-400 text-cyan-300 bg-cyan-500/5'
                            : 'border-transparent text-white/50 hover:text-white/80'
                        }`}
                        data-testid="script-tab-original"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Original
                      </button>
                    )}
                    {project.templatized_script_text && (
                      <button
                        onClick={() => setScriptViewTab('templatized')}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${
                          scriptViewTab === 'templatized'
                            ? 'border-cyan-400 text-cyan-300 bg-cyan-500/5'
                            : 'border-transparent text-white/50 hover:text-white/80'
                        }`}
                        data-testid="script-tab-templatized"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Templatized
                        {project.templatized_locked && (
                          <Lock className="w-3 h-3 text-emerald-300" />
                        )}
                      </button>
                    )}
                    {project.shooting_script_text && (
                      <button
                        onClick={() => setScriptViewTab('shooting_script')}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${
                          scriptViewTab === 'shooting_script'
                            ? 'border-purple-400 text-purple-200 bg-purple-500/5'
                            : 'border-transparent text-white/50 hover:text-white/80'
                        }`}
                        data-testid="script-tab-shooting-script"
                      >
                        <Film className="w-3.5 h-3.5" />
                        Shooting Script
                        {project.shooting_script_locked && (
                          <Lock className="w-3 h-3 text-emerald-300" />
                        )}
                      </button>
                    )}
                  </div>

                  {scriptViewTab === 'edit' && (
                    <textarea
                      value={scriptText}
                      onChange={(e) => setScriptText(e.target.value)}
                      placeholder="Paste your script here..."
                      rows={10}
                      data-testid="script-textarea"
                      className="w-full px-4 py-3 rounded-xl border border-ai-border focus:border-ai-accent focus:ring-2 focus:ring-ai-accent/10 outline-none resize-none text-sm text-white leading-relaxed font-mono"
                      style={{ background: 'rgba(8, 12, 22, 0.9)' }}
                    />
                  )}

                  {scriptViewTab === 'original' && (
                    <div className="relative">
                      {project.script_text && (
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(project.script_text || '');
                              toast.success('Original script copied to clipboard');
                            } catch { toast.error('Copy failed'); }
                          }}
                          className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/60 hover:bg-black/80 text-white/60 hover:text-white border border-white/10 transition-all"
                          data-testid="copy-original-script-btn"
                          title="Copy original script to clipboard"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <div
                        className="w-full px-4 py-3 rounded-xl border border-white/10 bg-black/40 text-sm text-white/85 leading-relaxed font-mono whitespace-pre-wrap overflow-y-auto"
                        style={{ minHeight: '260px', maxHeight: '60vh' }}
                        data-testid="script-original-view"
                      >
                        {project.script_text || (
                          <span className="text-white/40 italic">No saved upload yet.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {scriptViewTab === 'templatized' && (
                    <div className="relative">
                      {project.templatized_script_text && (
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(project.templatized_script_text || '');
                              toast.success('Templatized script copied to clipboard');
                            } catch { toast.error('Copy failed'); }
                          }}
                          className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/60 hover:bg-black/80 text-white/60 hover:text-white border border-cyan-500/30 transition-all"
                          data-testid="copy-templatized-script-btn"
                          title="Copy templatized script to clipboard"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <div
                        className="w-full px-4 py-3 rounded-xl border border-cyan-500/20 bg-black/40 text-sm text-white/90 leading-relaxed font-mono whitespace-pre-wrap overflow-y-auto"
                        style={{ minHeight: '260px', maxHeight: '60vh' }}
                        data-testid="script-templatized-view"
                      >
                        {project.templatized_script_text || (
                          <span className="text-white/40 italic">Click "Templatize Script" to generate the canonical form.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {scriptViewTab === 'shooting_script' && (
                    <div className="relative">
                      {project.shooting_script_text && !project.shooting_script_processing && (
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(project.shooting_script_text || '');
                              toast.success('Shooting script copied to clipboard');
                            } catch { toast.error('Copy failed'); }
                          }}
                          className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/60 hover:bg-black/80 text-white/60 hover:text-white border border-purple-500/30 transition-all"
                          data-testid="copy-shooting-script-btn"
                          title="Copy shooting script to clipboard"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <div
                        className="w-full px-4 py-3 rounded-xl border border-purple-500/20 bg-black/40 text-sm text-white/90 leading-relaxed font-mono whitespace-pre-wrap overflow-y-auto"
                        style={{ minHeight: '260px', maxHeight: '60vh' }}
                        data-testid="script-shooting-script-view"
                      >
                        {project.shooting_script_processing ? (
                          <span className="text-white/40 italic flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Director AI is writing your shooting script… (~60–120s)
                          </span>
                        ) : project.shooting_script_text || (
                          <span className="text-white/40 italic">Click "Generate Shooting Script" to create the two-column production form.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {scriptViewTab === 'templatized' && project.templatized_locked && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-[11px] text-emerald-300/85 flex items-center gap-2">
                      <Lock className="w-3 h-3 flex-shrink-0" />
                      <span>This is the canonical version. Shot Breakdown, Asset Bible, and Script Analysis use this script — not the Original Upload.</span>
                    </div>
                  )}
                  {scriptViewTab === 'templatized' && !project.templatized_locked && project.templatized_script_text && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-300/85 flex items-center gap-2">
                      <Unlock className="w-3 h-3 flex-shrink-0" />
                      <span>
                        Templatized but not locked.
                        {project.shooting_script_locked
                          ? ' The Shooting Script is currently locked as canonical.'
                          : ' Downstream stages still use the Original Upload. Open the Templatizer modal to lock.'}
                      </span>
                    </div>
                  )}
                  {scriptViewTab === 'templatized' && project.templatized_script_json && (
                    <TemplatizedDraftShotsPanel project={project} onPromoted={loadProjectData} />
                  )}
                  {scriptViewTab === 'shooting_script' && project.shooting_script_warnings && project.shooting_script_warnings.length > 0 && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-300/90 flex items-start gap-2" data-testid="shooting-script-warning">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        {project.shooting_script_warnings.map((w, i) => (<div key={i}>{w}</div>))}
                      </div>
                    </div>
                  )}
                  {scriptViewTab === 'shooting_script' && project.shooting_script_locked && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-[11px] text-emerald-300/85 flex items-center gap-2">
                      <Lock className="w-3 h-3 flex-shrink-0" />
                      <span>This is the canonical version. Shot Breakdown, Asset Bible, and Script Analysis use this shooting script.</span>
                    </div>
                  )}
                  {scriptViewTab === 'shooting_script' && !project.shooting_script_locked && project.shooting_script_text && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-300/85 flex items-center gap-2">
                      <Unlock className="w-3 h-3 flex-shrink-0" />
                      <span>
                        Shooting Script ready but not locked.
                        {project.templatized_locked
                          ? ' The Templatized version is currently locked as canonical.'
                          : ' Open the Shooting Script modal to lock as canonical.'}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <textarea value={scriptText} onChange={(e) => setScriptText(e.target.value)} placeholder="Paste your script here..."
                  rows={8} data-testid="script-textarea"
                  className="w-full px-4 py-3 rounded-xl border border-ai-border focus:border-ai-accent focus:ring-2 focus:ring-ai-accent/10 outline-none resize-none mb-3 text-sm text-white leading-relaxed"
                  style={{ background: 'rgba(8, 12, 22, 0.9)' }} />
              )}

              {/* Narration Options */}
              <div className="mb-3">
                <div className="flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
                  <input ref={narrationInputRef} type="file" accept=".mp3,.mp4,.m4a,.wav,.webm,.mov,.ogg,.aac,.mpeg,.mpga" onChange={handleNarrationUpload} className="hidden" id="narration-upload" data-testid="narration-upload-input" />
                  <input ref={scriptFileInputRef} type="file" accept=".txt,.pdf,.docx" onChange={handleScriptFileUpload} className="hidden" id="script-file-upload" data-testid="script-file-upload-input" />

                  {/* Upload Script File (.txt/.pdf/.docx) */}
                  <button
                    type="button"
                    onClick={() => scriptFileInputRef.current?.click()}
                    disabled={uploadingAction === 'script'}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all flex items-center gap-2 border border-ai-accent/40 bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20 disabled:opacity-40"
                    data-testid="upload-script-file-btn"
                    title="Upload a script as .txt, .pdf, or .docx"
                  >
                    {uploadingAction === 'script' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                    {uploadingAction === 'script' ? 'Uploading…' : 'Upload Script File'}
                  </button>

                  {/* Record Narration */}
                  <button
                    type="button"
                    onClick={startRecorder}
                    disabled={narrationUploading || showRecorder}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all flex items-center gap-2 border border-[#FF2D55]/30 bg-[#FF2D55]/10 text-[#FF2D55] hover:bg-[#FF2D55]/20 disabled:opacity-40"
                    data-testid="record-narration-btn"
                  >
                    <Video className="w-3.5 h-3.5" />
                    Record Narration
                  </button>

                  {/* Upload Narration */}
                  <button
                    type="button"
                    onClick={() => narrationInputRef.current?.click()}
                    disabled={narrationUploading}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all flex items-center gap-2 border border-ai-border hover:border-ai-accent/50 text-ai-text-secondary hover:text-ai-accent disabled:opacity-40"
                    style={{ background: 'rgba(14, 165, 233, 0.06)' }}
                    data-testid="narration-upload-btn"
                  >
                    {narrationUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
                    {narrationUploading ? 'Transcribing...' : 'Upload Audio/Video'}
                  </button>
                </div>

                {/* Hint text — moved below the scrollable button row so it doesn't squeeze */}
                <div className="text-[10px] text-white/30 font-ai mt-1.5 px-1">
                  Paste, upload a script doc (.txt/.pdf/.docx), or transcribe a narration
                </div>

                {/* Webcam Recording Modal */}
                {showRecorder && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-[#FF2D55]/30" style={{ background: 'rgba(8, 12, 22, 0.95)' }}>
                    <div className="relative">
                      <video ref={(el) => { webcamRef.current = el; if (el && streamRef.current && !el.srcObject) { el.srcObject = streamRef.current; el.play().catch(() => {}); } }} autoPlay playsInline muted className="w-full max-h-[300px] object-cover" style={{ transform: 'scaleX(-1)' }} />
                      {/* Recording indicator */}
                      {isRecording && (
                        <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255, 45, 85, 0.9)' }}>
                          <span className="w-2.5 h-2.5 rounded-lg bg-white animate-pulse" />
                          <span className="text-xs font-ai font-medium text-white">{formatRecTime(recordingTime)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-3 p-3">
                      {!isRecording ? (
                        <button onClick={startRecording} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#FF2D55] text-white hover:bg-[#E0264A] flex items-center gap-2 transition-all active:scale-95" data-testid="start-recording-btn">
                          <span className="w-3 h-3 rounded-full bg-white" />
                          Start Recording
                        </button>
                      ) : (
                        <button onClick={submitRecording} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 flex items-center gap-2 transition-all active:scale-95" data-testid="stop-submit-btn">
                          <Check className="w-3.5 h-3.5" />
                          Stop & Transcribe
                        </button>
                      )}
                      <button onClick={discardRecording} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/20 text-white/60 hover:text-white hover:border-white/40 flex items-center gap-2 transition-all" data-testid="discard-recording-btn">
                        <X className="w-3.5 h-3.5" />
                        Discard
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-nowrap overflow-x-auto pb-1 -mx-1 px-1 gap-2 mt-2 items-center scrollbar-thin">
                <label className="flex items-center gap-1.5 text-[11px] text-white/60 hover:text-white/80 cursor-pointer select-none px-2 whitespace-nowrap flex-shrink-0" title="Rewrite your script for better pacing, clearer beats and tighter dialogue before entity extraction. Adds ~60s.">
                  <input
                    type="checkbox"
                    checked={enhanceScript}
                    onChange={(e) => setEnhanceScript(e.target.checked)}
                    className="accent-ai-accent w-3 h-3"
                    data-testid="enhance-script-toggle"
                  />
                  Enhance script <span className="text-white/40">(+~60s)</span>
                </label>
                <button onClick={uploadScript} disabled={uploadingAction === 'script' || !scriptText.trim()} data-testid="upload-script-btn"
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center justify-center gap-2 ${
                    uploadingAction === 'script' || !scriptText.trim() ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-ai-accent text-white hover:bg-ai-accent/80 active:scale-95'
                  }`}>
                  {uploadingAction === 'script' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Save Script
                </button>
                {/* Templatize Script — Director AI scene-by-scene canonical rewrite */}
                {project.script_text && (
                  <button
                    onClick={() => setShowTemplatizerModal(true)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 transition-all border ${
                      project.templatized_locked
                        ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20'
                        : project.templatized_status === 'ready'
                          ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/20'
                          : project.templatized_status === 'processing'
                            ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                            : 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30 hover:bg-fuchsia-500/20'
                    }`}
                    title={project.templatized_locked
                      ? 'Templatized & locked — downstream stages (Shot Breakdown, Asset Bible, Script Analysis) will use this version'
                      : 'Rewrite the script into a strict scene-by-scene canonical form (Cast/Costume/Corner per scene). No inference — blanks left for you to fill.'}
                    data-testid="templatize-script-btn"
                  >
                    {project.templatized_status === 'processing' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Wand2 className="w-3.5 h-3.5" />
                    )}
                    {project.templatized_locked
                      ? 'Bible · Canonical ✓'
                      : project.templatized_status === 'ready'
                        ? 'Review Production Bible'
                        : project.templatized_status === 'processing'
                          ? 'Generating Bible…'
                          : 'Generate Production Bible'}
                  </button>
                )}
                {/* Generate Shooting Script — Director AI two-column production script
                 *   Mirror flow to Templatize Script. Locks are mutually exclusive
                 *   on the backend. Button label/state reflects backend status. */}
                {project.script_text && (() => {
                  const ssLocked = !!project.shooting_script_locked;
                  const ssProcessing = !!project.shooting_script_processing;
                  const ssReady = !!project.shooting_script_text && !ssProcessing;
                  return (
                    <button
                      onClick={() => setShowShootingScriptModal(true)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 transition-all border ${
                        ssLocked
                          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20'
                          : ssReady
                            ? 'bg-purple-500/10 text-purple-200 border-purple-400/30 hover:bg-purple-500/20'
                            : ssProcessing
                              ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                              : 'bg-purple-500/10 text-purple-200 border-purple-400/30 hover:bg-purple-500/20'
                      }`}
                      title={ssLocked
                        ? 'Shooting Script locked as canonical — downstream stages use this version'
                        : 'Generate a detailed two-column shooting script (Visuals / Audio) ready for the crew. You can edit and lock it as the canonical source instead of the Templatized form.'}
                      data-testid="generate-shooting-script-btn"
                    >
                      {ssProcessing ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Film className="w-3.5 h-3.5" />
                      )}
                      {ssLocked
                        ? 'Shooting Script · Canonical ✓'
                        : ssReady
                          ? 'Review Shooting Script'
                          : ssProcessing
                            ? 'Writing…'
                            : 'Generate Shooting Script'}
                    </button>
                  );
                })()}
                {project.script_text && !project.thumbnail_url && (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/projects/${projectId}/generate-thumbnail`);
                        toast.success('Generating poster...');
                        loadProjectData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to generate poster');
                      }
                    }}
                    className="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium bg-white/[0.06] text-white hover:bg-white/10 transition-all flex items-center justify-center gap-2 active:scale-95"
                    data-testid="generate-poster-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Generate Poster
                  </button>
                )}
                {/* (Removed) dedicated "Download Shooting Script (.docx)" button —
                 *  the Generate / Review Shooting Script modal now exposes the
                 *  same .docx download via its toolbar download icon, plus full
                 *  edit/lock/regenerate flow. */}
              </div>

              {/* Shooting Script live status panel — gives the user visibility
                  into what's happening with the Opus background job:
                    • processing → live spinner + elapsed seconds + hint text
                    • error      → red error card with full error message + retry CTA
                    • ready      → green confirmation with timestamp + download CTA
                  Hidden when the script field is empty (nothing to generate yet). */}
              {project.script_text && project.shooting_script_processing && (
                <div className="mt-3 p-3 bg-purple-500/10 border border-purple-400/30 rounded-xl" data-testid="shooting-script-status-processing">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-medium text-purple-100">
                        Director AI is writing your shooting script
                        {shootingScriptElapsed > 0 && (
                          <span className="text-purple-300/70 font-normal"> · {shootingScriptElapsed}s elapsed</span>
                        )}
                      </div>
                      <div className="text-[10px] sm:text-xs text-purple-200/70 mt-0.5">
                        Expanding your script into a detailed two-column scene-by-scene breakdown. Typical run: 60–120s. You can keep working — the download button will appear here when it's done.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {project.script_text && !project.shooting_script_processing && project.shooting_script_error && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-400/30 rounded-xl" data-testid="shooting-script-status-error">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-medium text-red-100">Shooting script generation failed</div>
                      <div className="text-[10px] sm:text-xs text-red-200/80 mt-1 break-words">
                        {project.shooting_script_error}
                      </div>
                      <button
                        onClick={regenerateShootingScript}
                        className="mt-2 px-3 py-1 rounded-lg text-[10px] sm:text-xs font-medium bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-400/40 transition-all"
                      >
                        Retry now
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {project.script_text && !project.shooting_script_processing && !project.shooting_script_error && project.shooting_script_text && (
                <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-400/30 rounded-xl" data-testid="shooting-script-status-ready">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-medium text-emerald-100">Shooting Script ready</div>
                      <div className="text-[10px] sm:text-xs text-emerald-200/70 mt-0.5">
                        Two-column VISUAL | AUDIO breakdown · click <span className="font-medium text-emerald-100">Shooting Script (.docx)</span> above to download.
                      </div>
                    </div>
                    <button
                      onClick={downloadShootingScript}
                      disabled={downloadingShootingScript}
                      className="shrink-0 px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-medium bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-400/40 transition-all flex items-center gap-1.5 disabled:opacity-60"
                      data-testid="download-shooting-script-status-btn"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                  </div>
                </div>
              )}

              {(scriptProcessing || project.script_processing) && (
                <div className="mt-3 p-3 bg-ai-accent/5 border border-ai-accent/20 rounded-xl animate-pulse">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin text-ai-accent" />
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-ai-accent">Processing script with AI...</div>
                      <div className="text-[10px] sm:text-xs text-white/50">Enhancing script & extracting entities. This usually takes 60–120s — feel free to keep working in another tab.</div>
                    </div>
                  </div>
                </div>
              )}
              
              {project.thumbnail_url && (
                <div className="mt-3 p-2.5 border border-green-500/20 rounded-xl flex items-center gap-3" style={{ background: 'rgba(34,197,94,0.08)' }}>
                  <img src={resolveUrl(project.thumbnail_url)} alt="Poster" className="w-12 h-16 object-cover rounded-lg" />
                  <div>
                    <div className="text-[10px] sm:text-xs font-medium text-green-400">Poster Generated</div>
                    <div className="text-[9px] sm:text-[10px] text-green-400/70">Visible on dashboard</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── 1b. PRODUCTION PACKAGE (hidden — data available internally but not shown to user) ── */}
        {false && project.production_package && (
          <CollapsibleSection
            title="Production Package"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/15 flex items-center justify-center"><FileText className="w-3.5 h-3.5 text-ai-accent" /></div>}
            expanded={expandedSections.productionPackage}
            onToggle={() => toggleSection('productionPackage')}
            testId="production-package"
          >
            <div className="space-y-3">
              {[
                { key: 'concept', title: 'Concept & Tonality' },
                { key: 'screenplay', title: 'Screenplay' },
                { key: 'character_bible', title: 'Character Bible' },
                { key: 'shot_breakdown', title: 'Shot Breakdown' },
                { key: 'midjourney_prompts', title: 'MidJourney Prompts' }
              ].map(({ key, title }) => {
                const content = project.production_package[key];
                if (!content) return null;
                return (
                  <details key={key} className="group">
                    <summary className="cursor-pointer font-medium text-xs text-ai-accent hover:text-[#7C3AED] py-1.5 select-none">{title}</summary>
                    <div className="p-3 glass-panel rounded-xl text-xs text-white/70 whitespace-pre-wrap max-h-64 overflow-y-auto mt-1 leading-relaxed">
                      {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
                    </div>
                  </details>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* ── Validate or Skip — after script is processed ── */}
        {project.script_text && effectiveStage >= 1 && !project.validation_report && (
          <div className="mt-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-sm font-semibold text-white mb-2">Next Step</h4>
            <p className="text-xs text-white/40 mb-4">Validate your script with AI audience simulation, or skip to Pre-Production.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={async () => {
                if (!project.script_text) { await uploadScript(); }
                setShowValidateForm(true);
              }} data-testid="validate-script-btn"
                className={`flex-1 p-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex items-center justify-center gap-2 active:scale-[0.98] ${
                  showValidateForm ? 'bg-purple-600 text-white ring-2 ring-purple-400/40 shadow-lg shadow-purple-500/20' : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}>
                <Beaker className="w-4 h-4" /> Validate Script
              </button>
              <button onClick={async () => {
                if (!project.script_text) { await uploadScript(); }
                setActiveStageGroup('preproduction');
              }} data-testid="skip-validation-btn"
                className="flex-1 p-3 rounded-xl bg-white/[0.06] text-white text-sm font-medium whitespace-nowrap hover:bg-white/10 transition-all flex items-center justify-center gap-2 active:scale-[0.98]">
                Skip to Pre-Production <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── Validate Form (shown when user clicks Validate Script) ── */}
        {(showValidateForm || project.validation_report) && project.script_text && (
        <CollapsibleSection
          title="Script Validation"
          subtitle="Simulate audience reactions before production"
          icon={<div className="w-7 h-7 rounded-lg bg-purple-500/15 flex items-center justify-center"><Beaker className="w-3.5 h-3.5 text-purple-400" /></div>}
          expanded={expandedSections.validate !== false}
          onToggle={() => setExpandedSections(prev => ({...prev, validate: prev.validate === false ? true : false}))}
          testId="stage-0-validate"
        >
          {!project.script_text ? (
            <div className="p-6 text-center text-white/40 text-sm">Upload a script first to enable validation.</div>
          ) : (
            <div className="space-y-6">
              {/* Audience Brief */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Audience Brief</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-white/40 mb-1 block">Age Range</label>
                    <select defaultValue="18-35" data-testid="validate-age" className="w-full bg-white/[0.06] text-white text-xs rounded-lg px-3 py-2 border border-white/10 outline-none">
                      <option value="13-17">13-17 (Teen)</option><option value="18-24">18-24 (Gen Z)</option><option value="18-35">18-35 (Young Adult)</option><option value="25-34">25-34 (Millennial)</option><option value="25-44">25-44 (Core Working)</option><option value="35-55">35-55 (Mature)</option><option value="45+">45+ (Senior)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-white/40 mb-1 block">Gender Split</label>
                    <select defaultValue="60M/40F" data-testid="validate-gender" className="w-full bg-white/[0.06] text-white text-xs rounded-lg px-3 py-2 border border-white/10 outline-none">
                      <option value="50M/50F">50M / 50F</option><option value="60M/40F">60M / 40F</option><option value="70M/30F">70M / 30F</option><option value="80M/20F">80M / 20F</option><option value="40M/60F">40M / 60F</option><option value="30M/70F">30M / 70F</option><option value="20M/80F">20M / 80F</option><option value="100M">100% Male</option><option value="100F">100% Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-white/40 mb-1 block">Location</label>
                    <select defaultValue="metro-tier1" data-testid="validate-location" className="w-full bg-white/[0.06] text-white text-xs rounded-lg px-3 py-2 border border-white/10 outline-none">
                      <option value="metro">Metro cities only</option>
                      <option value="metro-tier1">Metro + Tier 1</option>
                      <option value="tier1-tier2">Tier 1 + Tier 2</option>
                      <option value="metro-tier1-tier2">Metro + Tier 1 + Tier 2</option>
                      <option value="all-urban">All Urban (Metro to Tier 3)</option>
                      <option value="rural">Rural only</option>
                      <option value="pan-india">Pan India (all tiers)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-white/40 mb-1 block">Income Demographic</label>
                    <select defaultValue="mid-premium" data-testid="validate-income" className="w-full bg-white/[0.06] text-white text-xs rounded-lg px-3 py-2 border border-white/10 outline-none">
                      <option value="budget">Budget (under 3L/yr)</option>
                      <option value="lower-mid">Lower Middle (3-6L/yr)</option>
                      <option value="mid">Middle Class (6-12L/yr)</option>
                      <option value="mid-premium">Mid-Premium (12-25L/yr)</option>
                      <option value="premium">Premium (25-50L/yr)</option>
                      <option value="luxury">Luxury (50L+/yr)</option>
                      <option value="mixed">Mixed (all income levels)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-white/40 mb-1 block">Interests (comma separated)</label>
                    <input defaultValue="cricket, fitness, premium lifestyle" data-testid="validate-interests" className="w-full bg-white/[0.06] text-white text-xs rounded-lg px-3 py-2 border border-white/10 outline-none" placeholder="cricket, fashion, gaming..." />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/40 mb-1 block">Campaign Goal</label>
                    <select defaultValue="brand_awareness" data-testid="validate-goal" className="w-full bg-white/[0.06] text-white text-xs rounded-lg px-3 py-2 border border-white/10 outline-none">
                      <option value="brand_awareness">Brand Awareness</option><option value="brand_love">Brand Love / Affinity</option><option value="conversion">Purchase Conversion</option><option value="engagement">Social Engagement</option><option value="recall">Ad Recall</option><option value="consideration">Brand Consideration</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Mode Selection */}
              <div className="flex gap-3">
                <button data-testid="validate-quick" onClick={() => {
                  const brief = {
                    age_range: document.querySelector('[data-testid="validate-age"]')?.value || '18-35',
                    gender_split: document.querySelector('[data-testid="validate-gender"]')?.value || '60M/40F',
                    location: document.querySelector('[data-testid="validate-location"]')?.value || 'metro-tier1',
                    income: document.querySelector('[data-testid="validate-income"]')?.value || 'mid-premium',
                    interests: (document.querySelector('[data-testid="validate-interests"]')?.value || '').split(',').map(s => s.trim()),
                  };
                  const goal = document.querySelector('[data-testid="validate-goal"]')?.value || 'brand_awareness';
                  toast.info('Quick Check simulation starting...');
                  setSelectedValidateMode('quick');
                  axios.post(`${API}/projects/${projectId}/validate-script`, { mode: 'quick', audience_profile: brief, campaign_objective: goal })
                    .then(() => { toast.success('Simulation started — results in ~2-3 min'); pollValidation(); loadProjectData(); })
                    .catch(e => toast.error('Failed: ' + (e.response?.data?.detail || e.message)));
                }}
                  className={`flex-1 p-4 rounded-xl border transition-all cursor-pointer text-left ${
                    selectedValidateMode === 'quick' ? 'border-purple-500 bg-purple-500/[0.15] ring-2 ring-purple-500/40 shadow-lg shadow-purple-500/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-semibold text-white">Quick Check</span>
                    {selectedValidateMode === 'quick' && validationPolling && <Loader2 className="w-3 h-3 text-purple-400 animate-spin ml-auto" />}
                  </div>
                  <div className="text-[10px] text-white/40">250 agents · 1 debate round · ~2-3 min</div>
                  <div className="text-[10px] text-white/30 mt-1">Best for: early drafts, quick directional reads</div>
                </button>
                <button data-testid="validate-deep" onClick={() => {
                  const brief = {
                    age_range: document.querySelector('[data-testid="validate-age"]')?.value || '18-35',
                    gender_split: document.querySelector('[data-testid="validate-gender"]')?.value || '60M/40F',
                    location: document.querySelector('[data-testid="validate-location"]')?.value || 'metro-tier1',
                    income: document.querySelector('[data-testid="validate-income"]')?.value || 'mid-premium',
                    interests: (document.querySelector('[data-testid="validate-interests"]')?.value || '').split(',').map(s => s.trim()),
                  };
                  const goal = document.querySelector('[data-testid="validate-goal"]')?.value || 'brand_awareness';
                  toast.info('Deep Dive simulation starting...');
                  setSelectedValidateMode('deep');
                  axios.post(`${API}/projects/${projectId}/validate-script`, { mode: 'deep', audience_profile: brief, campaign_objective: goal })
                    .then(() => { toast.success('Deep Dive started — results in ~5-7 min'); pollValidation(); loadProjectData(); })
                    .catch(e => toast.error('Failed: ' + (e.response?.data?.detail || e.message)));
                }}
                  className={`flex-1 p-4 rounded-xl border transition-all cursor-pointer text-left ${
                    selectedValidateMode === 'deep' ? 'border-purple-500 bg-purple-500/[0.15] ring-2 ring-purple-500/40 shadow-lg shadow-purple-500/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Beaker className="w-4 h-4 text-white/60" />
                    <span className="text-sm font-semibold text-white">Deep Dive</span>
                    {selectedValidateMode === 'deep' && validationPolling && <Loader2 className="w-3 h-3 text-purple-400 animate-spin ml-auto" />}
                  </div>
                  <div className="text-[10px] text-white/40">1,000 agents · 2 debate rounds · ~5-7 min</div>
                  <div className="text-[10px] text-white/30 mt-1">Best for: final go/no-go before production</div>
                </button>
              </div>

              {/* Progress — 5-Phase Stepper */}
              {validationPolling && (
                <div className="p-4 rounded-xl bg-purple-500/[0.04] border border-purple-500/15">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="w-5 h-5 text-purple-400 animate-spin flex-shrink-0" />
                    <div>
                      <div className="text-sm text-white font-medium">Simulation in progress</div>
                      <div className="text-[10px] text-white/40">{validationStatus || 'Initializing...'}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {['Profiles', 'Reactions', 'Debate', 'Synthesis', 'Improve'].map((phase, i) => {
                      const statusText = validationStatus || '';
                      const current = statusText.includes('profile') || statusText.includes('Generating') ? 0 :
                        statusText.includes('watching') || statusText.includes('Collecting') ? 1 :
                        statusText.includes('Debate') || statusText.includes('shifting') ? 2 :
                        statusText.includes('Synthesiz') || statusText.includes('consensus') ? 3 :
                        statusText.includes('Improv') || statusText.includes('script') ? 4 : 0;
                      const active = i <= current;
                      return (
                        <div key={i} className="flex-1">
                          <div className={`h-1 rounded-lg mb-1 transition-all ${active ? 'bg-purple-500' : 'bg-white/[0.06]'}`} />
                          <div className={`text-[8px] text-center transition-all ${active ? 'text-purple-400' : 'text-white/20'}`}>{phase}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div className="text-[10px] text-white/25 text-center italic">
                Simulation shows directional trends, not exact predictions. AI agents approximate audience behavior.
              </div>


              {/* ═══ REDESIGNED VALIDATE RESULTS ═══ */}
              {project.validation_report && (() => {
                const report = project.validation_report;
                const reactions = project.validation_reactions || [];
                const score = report.overall_score || 0;
                const verdict = report.verdict?.replace(/_/g, ' ') || '';
                const strengths = report.strengths || [];
                const weaknesses = report.weaknesses || [];
                const riskFlags = report.risk_flags || [];
                const debateMoments = report.debate_insights || [];
                const agentQuotes = report.agent_quotes || [];
                
                // Calculate sentiment breakdown from reactions
                let posCount = 0, neuCount = 0, negCount = 0, totalPeople = 0;
                reactions.forEach(r => {
                  const s = r.final_score || r.score || 5;
                  const c = r.archetype?.represents_count || 100;
                  totalPeople += c;
                  if (s >= 7) posCount += c;
                  else if (s >= 5) neuCount += c;
                  else negCount += c;
                });
                const pctPos = totalPeople ? Math.round(posCount/totalPeople*100) : 0;
                const pctNeu = totalPeople ? Math.round(neuCount/totalPeople*100) : 0;
                const pctNeg = totalPeople ? Math.round(negCount/totalPeople*100) : 0;
                
                // Top quotes: best advocate, worst critic, first neutral
                const sortedReactions = [...reactions].filter(r => r.first_impression || r.quotable_reaction);
                const bestAdvocate = sortedReactions.find(r => (r.final_score || r.score || 5) >= 7);
                const worstCritic = [...sortedReactions].reverse().find(r => (r.final_score || r.score || 5) < 5);
                const firstNeutral = sortedReactions.find(r => { const s = r.final_score || r.score || 5; return s >= 5 && s < 7; });
                const topQuotes = [bestAdvocate, firstNeutral, worstCritic].filter(Boolean);

                return (
                <div className="space-y-5 pb-24">
                  {/* A. Score Header — Circular Ring */}
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] flex items-center gap-6">
                    <div className="relative flex-shrink-0" style={{width:'90px',height:'90px'}}>
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#9333ea" strokeWidth="5"
                          strokeDasharray={`${(score/10)*264} 264`} strokeLinecap="round"
                          className="transition-all duration-1000" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-white">{score}</span>
                        <span className="text-[9px] text-white/40">/10</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-white capitalize mb-1">{verdict}</div>
                      <div className="text-[10px] text-white/40 mb-3">{report.executive_summary || `Based on ${totalPeople} simulated audience members`}</div>
                      <div className="flex gap-2">
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-medium" style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.25)',color:'#4ade80'}}>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 mr-1" />{pctPos}% Positive
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-medium" style={{background:'rgba(234,179,8,0.1)',border:'1px solid rgba(234,179,8,0.25)',color:'#facc15'}}>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 mr-1" />{pctNeu}% Neutral
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-medium" style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',color:'#f87171'}}>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 mr-1" />{pctNeg}% Negative
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* B. Sentiment Scatter Plot — Canvas */}
                  {reactions.length > 0 && (
                    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-semibold text-white/50 uppercase">Sentiment Map ({totalPeople} agents)</div>
                      </div>
                      <canvas ref={el => {
                        if (!el || el.dataset.drawn) return;
                        el.dataset.drawn = '1';
                        const ctx = el.getContext('2d');
                        const w = el.width = el.offsetWidth * 2;
                        const h = el.height = 700;
                        el.style.height = '350px';
                        ctx.scale(2, 2);
                        const cw = w/2, ch = h/2;
                        const sr = (s) => { let x = Math.sin(s+1)*10000; return x-Math.floor(x); };
                        
                        // Store dot positions for click detection
                        const dotPositions = [];
                        
                        // Grid
                        ctx.strokeStyle = 'rgba(255,255,255,0.03)';
                        ctx.lineWidth = 0.5;
                        for (let i = 1; i < 5; i++) {
                          ctx.beginPath(); ctx.moveTo(cw*i/5, 0); ctx.lineTo(cw*i/5, ch); ctx.stroke();
                          ctx.beginPath(); ctx.moveTo(0, ch*i/5); ctx.lineTo(cw, ch*i/5); ctx.stroke();
                        }
                        
                        // Draw dots and store positions
                        reactions.forEach((r, ai) => {
                          const score = r.final_score || r.score || 5;
                          const count = r.archetype?.represents_count || 100;
                          const col = score >= 7 ? '#22c55e' : score >= 5 ? '#eab308' : '#ef4444';
                          const bx = 10 + (score/10)*80;
                          const by = 10 + (score/10)*60 + (ai*7)%20;
                          
                          for (let j = 0; j < Math.min(count, 80); j++) {
                            const s = ai*1000+j;
                            const px = Math.max(5, Math.min(95, bx + (sr(s*7+1)-0.5)*55)) * cw/100;
                            const py = (100 - Math.max(5, Math.min(95, by + (sr(s*13+3)-0.5)*50))) * ch/100;
                            const isLeader = j === 0;
                            const radius = isLeader ? 5 : 2;
                            
                            ctx.beginPath();
                            ctx.arc(px, py, radius, 0, Math.PI*2);
                            ctx.fillStyle = col;
                            ctx.globalAlpha = isLeader ? 0.9 : 0.3;
                            ctx.fill();
                            if (isLeader) {
                              ctx.strokeStyle = 'rgba(255,255,255,0.7)';
                              ctx.lineWidth = 1;
                              ctx.stroke();
                              ctx.fillStyle = '#fff';
                              ctx.globalAlpha = 1;
                              ctx.font = 'bold 7px sans-serif';
                              ctx.textAlign = 'center';
                              ctx.textBaseline = 'middle';
                              ctx.fillText((r.archetype?.name || 'A').substring(0,2).toUpperCase(), px, py);
                            }
                            
                            // Store for hit detection (leaders get bigger hit area)
                            dotPositions.push({
                              x: px, y: py, r: isLeader ? 12 : 6,
                              score, col, isLeader,
                              name: r.archetype?.name || `${r.archetype?.age||'?'}${r.archetype?.gender||'?'}`,
                              city: r.archetype?.city || r.archetype?.location || '',
                              quote: r.first_impression || r.quotable_reaction || (score >= 7 ? 'Positive reaction' : score >= 5 ? 'Mixed reaction' : 'Negative reaction'),
                              vibe: r.vibe || '',
                            });
                          }
                        });
                        ctx.globalAlpha = 1;
                        
                        // Axis labels
                        ctx.fillStyle = 'rgba(255,255,255,0.15)';
                        ctx.font = '9px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.fillText('Negative ← Sentiment → Positive', cw/2, ch-4);
                        ctx.save();
                        ctx.translate(8, ch/2);
                        ctx.rotate(-Math.PI/2);
                        ctx.fillText('Engagement →', 0, 0);
                        ctx.restore();
                        
                        // Click/tap handler — find nearest dot and show tooltip
                        el._dotPositions = dotPositions;
                      }} onClick={e => {
                        const canvas = e.currentTarget;
                        const dots = canvas._dotPositions;
                        if (!dots) return;
                        const rect = canvas.getBoundingClientRect();
                        const scaleX = (canvas.width / 2) / rect.width;
                        const scaleY = (canvas.height / 2) / rect.height;
                        const cx = (e.clientX - rect.left) * scaleX;
                        const cy = (e.clientY - rect.top) * scaleY;
                        
                        // Find nearest dot
                        let nearest = null, minDist = Infinity;
                        for (const d of dots) {
                          const dist = Math.sqrt((d.x - cx)**2 + (d.y - cy)**2);
                          if (dist < d.r && dist < minDist) {
                            nearest = d;
                            minDist = dist;
                          }
                        }
                        
                        // Show tooltip
                        const existingTooltip = document.getElementById('scatter-tooltip');
                        if (existingTooltip) existingTooltip.remove();
                        
                        if (nearest) {
                          const tooltip = document.createElement('div');
                          tooltip.id = 'scatter-tooltip';
                          tooltip.style.cssText = `position:fixed;left:${e.clientX+10}px;top:${e.clientY-80}px;z-index:9999;background:rgba(10,10,30,0.95);border:1px solid ${nearest.col}40;border-radius:8px;padding:10px;max-width:220px;backdrop-filter:blur(8px);pointer-events:none;`;
                          tooltip.innerHTML = `
                            <div style="font-size:10px;color:${nearest.col};font-weight:bold;margin-bottom:3px">${nearest.name}</div>
                            <div style="font-size:9px;color:rgba(255,255,255,0.4);margin-bottom:4px">${nearest.city} · Score: ${nearest.score}/10</div>
                            <div style="font-size:10px;color:rgba(255,255,255,0.7);font-style:italic">"${nearest.quote}"</div>
                          `;
                          document.body.appendChild(tooltip);
                          
                          // Auto-remove after 3 seconds
                          setTimeout(() => tooltip.remove(), 3000);
                        }
                      }} className="w-full rounded-lg bg-[#06061a] cursor-pointer" style={{height:'350px'}} />
                      <div className="flex items-center justify-center gap-5 mt-2">
                        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-lg bg-green-500" /><span className="text-[9px] text-white/40">Positive <b className="text-green-400">{pctPos}%</b></span></div>
                        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-lg bg-yellow-500" /><span className="text-[9px] text-white/40">Neutral <b className="text-yellow-400">{pctNeu}%</b></span></div>
                        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-lg bg-red-500" /><span className="text-[9px] text-white/40">Negative <b className="text-red-400">{pctNeg}%</b></span></div>
                      </div>
                    </div>
                  )}

                  {/* C. Audience Debate — Compact Redesign */}
                  <div className="space-y-3">
                    {/* C1: Consensus Bar */}
                    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                      <div className="flex justify-between text-[9px] text-white/40 mb-1.5">
                        <span className="text-green-400">{pctPos}% Positive</span>
                        <span className="text-yellow-400">{pctNeu}% Neutral</span>
                        <span className="text-red-400">{pctNeg}% Negative</span>
                      </div>
                      <div className="flex h-2 rounded-lg overflow-hidden gap-0.5">
                        <div style={{width:`${pctPos}%`}} className="bg-green-500 rounded-l-full" />
                        <div style={{width:`${pctNeu}%`}} className="bg-yellow-500" />
                        <div style={{width:`${pctNeg}%`}} className="bg-red-500 rounded-r-full" />
                      </div>
                    </div>

                    {/* C2: Top 3 Quote Cards */}
                    {topQuotes.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {topQuotes.map((q, i) => {
                          const s = q.final_score || q.score || 5;
                          const col = s >= 7 ? '#22c55e' : s >= 5 ? '#eab308' : '#ef4444';
                          const stance = s >= 7 ? 'Positive' : s >= 5 ? 'Neutral' : 'Negative';
                          return (
                            <div key={i} className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white" style={{background:col+'30',border:`1px solid ${col}50`}}>
                                  {(q.archetype?.name || 'A').charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] text-white font-medium truncate">{q.archetype?.name || `${q.archetype?.age||'?'}${q.archetype?.gender||'?'}`}</div>
                                  <div className="text-[9px] text-white/30">{q.archetype?.city || q.archetype?.location || ''} · <span style={{color:col}}>{stance}</span></div>
                                </div>
                                <div className="text-xs font-bold px-1.5 py-0.5 rounded" style={{background:col+'20',color:col}}>{s}</div>
                              </div>
                              <div className="text-[10px] text-white/60 italic line-clamp-3">"{q.first_impression || q.quotable_reaction || 'No comment'}"</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* C3: Key Debate Moments */}
                    {debateMoments.length > 0 && (
                      <div className="p-3 rounded-xl" style={{background:'rgba(147,51,234,0.04)',border:'1px solid rgba(147,51,234,0.1)'}}>
                        <div className="text-[10px] font-semibold text-purple-300 uppercase mb-2">Key Debate Moments</div>
                        {debateMoments.slice(0,3).map((m, i) => (
                          <div key={i} className="text-[10px] text-white/50 mb-1.5 flex gap-2">
                            <MessageSquare className="w-3 h-3 text-purple-400/40 mt-0.5 flex-shrink-0" />{m}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* C4: All Agents Accordion */}
                    {reactions.length > 0 && (
                        <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
                          <button onClick={() => setShowAllAgents(!showAllAgents)} className="w-full p-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                            <span className="text-[10px] font-semibold text-white/50 uppercase">All Agents ({reactions.length})</span>
                            <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${showAllAgents ? 'rotate-180' : ''}`} />
                          </button>
                          {showAllAgents && (
                            <div className="border-t border-white/[0.05] p-2">
                              <div className="flex gap-2 mb-2">
                                <div className="flex-1 relative">
                                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-white/20" />
                                  <input value={searchAgent} onChange={e => setSearchAgent(e.target.value)} placeholder="Search agents..."
                                    className="w-full bg-white/[0.04] text-white text-[10px] rounded-lg pl-7 pr-2 py-1.5 border border-white/10 outline-none" />
                                </div>
                                <div className="flex gap-1">
                                  {['all','advocate','neutral','critic'].map(f => (
                                    <button key={f} onClick={() => setFilterStance(f)}
                                      className={`px-2 py-1 rounded text-[9px] font-medium transition-colors ${filterStance === f ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/50'}`}>
                                      {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="max-h-60 overflow-y-auto space-y-0.5">
                                {reactions.filter(r => {
                                  const s = r.final_score || r.score || 5;
                                  const stance = s >= 7 ? 'advocate' : s >= 5 ? 'neutral' : 'critic';
                                  if (filterStance !== 'all' && stance !== filterStance) return false;
                                  if (searchAgent) {
                                    const q = searchAgent.toLowerCase();
                                    return (r.archetype?.name || '').toLowerCase().includes(q) || (r.first_impression || '').toLowerCase().includes(q);
                                  }
                                  return true;
                                }).map((r, i) => {
                                  const s = r.final_score || r.score || 5;
                                  const col = s >= 7 ? '#22c55e' : s >= 5 ? '#eab308' : '#ef4444';
                                  return (
                                    <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.02] transition-colors">
                                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{background:col}} />
                                      <span className="text-[10px] text-white/60 w-28 truncate flex-shrink-0">{r.archetype?.name || `${r.archetype?.age||'?'}${r.archetype?.gender||'?'}, ${r.archetype?.city||''}`}</span>
                                      <span className="text-[10px] text-white/40 flex-1 truncate italic">"{r.first_impression || r.quotable_reaction || ''}"</span>
                                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{background:col+'20',color:col}}>{s}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                    )}
                  </div>

                  {/* D. Strengths / Weaknesses / Risk Flags */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-green-500/[0.04] border border-green-500/15">
                      <div className="text-[10px] font-semibold text-green-400 uppercase mb-2">Strengths</div>
                      {strengths.map((s, i) => (
                        <div key={i} className="text-[10px] text-white/60 mb-1.5 flex gap-2"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />{s.point || s}</div>
                      ))}
                    </div>
                    <div className="p-3 rounded-xl bg-red-500/[0.04] border border-red-500/15">
                      <div className="text-[10px] font-semibold text-red-400 uppercase mb-2">Weaknesses</div>
                      {weaknesses.map((w, i) => (
                        <div key={i} className="text-[10px] text-white/60 mb-1.5 flex gap-2"><XCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />{w.point || w}</div>
                      ))}
                    </div>
                  </div>
                  
                  {riskFlags.length > 0 && (
                    <div className="p-3 rounded-xl" style={{background:'rgba(245,158,11,0.04)',border:'1px solid rgba(245,158,11,0.12)'}}>
                      <div className="text-[10px] font-semibold text-amber-400 uppercase mb-2 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" /> Risk Flags
                      </div>
                      {riskFlags.map((r, i) => (
                        <div key={i} className="text-[10px] text-white/50 mb-1 flex gap-2">
                          <span className="text-amber-400">•</span>{r}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* E. Script Comparison — Side-by-Side Diff */}
                  {project.improved_script && (
                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
                      <div className="p-3 border-b border-white/[0.05]">
                        <div className="text-[10px] font-semibold text-purple-400 uppercase">Script Comparison</div>
                      </div>
                      <div className="grid grid-cols-2 divide-x divide-white/[0.05]">
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[9px] text-white/30 uppercase">Original</div>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(project.script_text || '');
                                  toast.success('Original script copied');
                                } catch (err) {
                                  toast.error('Copy failed: ' + (err.message || ''));
                                }
                              }}
                              data-testid="copy-original-script-btn"
                              title="Copy full original script"
                              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                          <pre className="text-[10px] text-white/45 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono leading-relaxed">{project.script_text || ''}</pre>
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[9px] text-purple-400/60 uppercase">Improved</div>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(project.improved_script || '');
                                  toast.success('Improved script copied');
                                } catch (err) {
                                  toast.error('Copy failed: ' + (err.message || ''));
                                }
                              }}
                              data-testid="copy-improved-script-btn"
                              title="Copy full improved script"
                              className="p-1 rounded hover:bg-purple-400/10 text-purple-400/60 hover:text-purple-300 transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                          <pre className="text-[10px] text-purple-300/70 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono leading-relaxed" style={{borderLeft:'2px solid rgba(147,51,234,0.3)',paddingLeft:'8px'}}>{project.improved_script || ''}</pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}

              {/* G. Sticky Action Bar */}
              {project.validation_report && (
                <div className="fixed bottom-0 left-0 right-0 z-40" style={{background:'rgba(10,10,26,0.9)',backdropFilter:'blur(16px)',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
                  <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <button onClick={async () => {
                      await axios.post(`${API}/projects/${projectId}/settings`, { validation_report: null, improved_script: null });
                      toast.info('Ready to re-validate');
                      loadProjectData();
                    }} className="px-3 py-2 text-white/50 hover:text-white/80 text-xs font-medium flex items-center gap-1.5 transition-colors" data-testid="revalidate-btn">
                      <RotateCcw className="w-3.5 h-3.5" /> Re-validate
                    </button>
                    <div className="flex gap-2">
                      <button onClick={() => setActiveStageGroup('preproduction')} data-testid="keep-original-btn"
                        className="px-4 py-2 border border-white/15 text-white rounded-lg text-xs font-medium hover:bg-white/5 transition-all">
                        Keep Original & Continue
                      </button>
                      {project.improved_script && (
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/projects/${projectId}/use-improved-script`);
                            toast.success('Improved script adopted');
                            setActiveStageGroup('preproduction');
                            loadProjectData();
                          } catch(e) { toast.error(e.response?.data?.detail || e.message); }
                        }} data-testid="use-improved-btn"
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg text-xs font-semibold hover:from-purple-700 hover:to-purple-800 transition-all flex items-center gap-1.5">
                          Use Improved Script <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}
        </CollapsibleSection>
        )}
        {/* ═══ END SCRIPT TAB ═══ */}
        </>)}

        {/* ═══════════════════════════════════════════════ */}
        {/* PRE-PRODUCTION TAB                              */}
        {/* Shot Breakdown, CCC, Asset Bible, Sketches      */}
        {/* ═══════════════════════════════════════════════ */}
        {activeStageGroup === 'preproduction' && (<>

        {/* ── 2. SHOT BREAKDOWN ── */}
        {(effectiveStage >= 1 || shots.length > 0) && (
          <CollapsibleSection
            title="1. Shot Breakdown"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/10 flex items-center justify-center"><FileText className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={<>
              {/* Single state-aware status pill — supersedes the old
               *  "uploaded badge" + "N shots" double pill. */}
              {(() => {
                const isGenerating = project.breakdown_processing || shotsProcessing || uploadingAction === 'shots' || uploadingAction === 'breakdown' || uploadingAction === 'paste';
                if (isGenerating) {
                  return (
                    <span data-testid="shot-breakdown-state-badge"
                      className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-ai-accent/15 text-ai-accent flex items-center gap-1">
                      <Loader2 className="w-2.5 h-2.5 animate-spin" />
                      Generating…
                    </span>
                  );
                }
                if (shots.length > 0) {
                  return (
                    <span data-testid="shot-breakdown-state-badge"
                      className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-ai-accent/15 text-ai-accent flex items-center gap-1">
                      <Check className="w-2.5 h-2.5" />
                      {shots.length} shots ready
                    </span>
                  );
                }
                return (
                  <span data-testid="shot-breakdown-state-badge"
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/10 text-white/50 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                    Not generated
                  </span>
                );
              })()}
            </>}
            expanded={expandedSections.shots}
            onToggle={() => toggleSection('shots')}
            testId="shots-section"
            rightContent={shots.length > 0 && (
              <div className="flex items-center gap-1">
                {/* Re-run — opens confirm dialog, then returns the user to
                 *  the InputMethodPicker preserving last-used source. */}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowRerunConfirm(true); }}
                  data-testid="shot-breakdown-rerun-icon"
                  title="Re-run — replaces all current shots"
                  className="p-1.5 rounded-md text-white/50 hover:text-ai-accent hover:bg-white/5 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                {/* Bind Assets — re-parse Director's Prompts deterministically
                 *  and repopulate cast_bindings + corner on every shot.
                 *  Preserves user-edited specs. Zero LLM cost.
                 *  (Backend: POST /asset-bible-spec/parse-from-prompts) */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (bindingAssets) return;
                    setBindingAssets(true);
                    try {
                      const res = await axios.post(`${API}/projects/${projectId}/asset-bible-spec/parse-from-prompts`);
                      const d = res.data || {};
                      const preserved = d.preserved_user_edits || 0;
                      toast.success(
                        `Re-parsed ${d.bound}/${d.total} shots from Director's Prompts`
                        + (preserved ? ` · preserved ${preserved} user-edited` : '')
                      );
                      loadProjectData();
                    } catch (err) {
                      toast.error('Bind failed: ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setBindingAssets(false);
                    }
                  }}
                  data-testid="shot-breakdown-bind-assets-icon"
                  title="Bind Assets — re-parse Director's Prompts and repopulate cast/costume/corner on every shot"
                  disabled={bindingAssets}
                  className="p-1.5 rounded-md text-white/50 hover:text-ai-accent hover:bg-white/5 transition-colors disabled:opacity-40"
                >
                  {bindingAssets ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                </button>
                {/* View analysis — opens Script Analysis modal read-only. */}
                <button
                  onClick={(e) => { e.stopPropagation(); analyzeScriptBeats(false); }}
                  disabled={analyzingScript}
                  data-testid="shot-breakdown-view-analysis-icon"
                  title="View Script Analysis"
                  className="p-1.5 rounded-md text-white/50 hover:text-ai-accent hover:bg-white/5 transition-colors disabled:opacity-40"
                >
                  {analyzingScript ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); downloadFile('shots'); }} data-testid="download-shots-inline"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-medium text-ai-accent bg-ai-accent/5 rounded-lg hover:bg-ai-accent/10 transition-colors">
                  {downloading === 'shots' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} .txt
                </button>
                {/* 2026-06-25 (Rohit) — Surface Director's Prompts (MJ
                    prompts) download in Shot Breakdown header too; the
                    existing instance in the Keyframe Prompts panel
                    (Stage 7) is kept as-is. Only shows when at least one
                    shot has a prompt to export. */}
                {shots.some(s => s.directors_prompt || s.keyframe_prompt || s.mj_prompt) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadFile('mj-prompts'); }}
                    data-testid="download-mj-prompts-inline"
                    title="Download Director's Prompts (.txt)"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-medium text-ai-accent bg-ai-accent/5 rounded-lg hover:bg-ai-accent/10 transition-colors"
                  >
                    {downloading === 'mj-prompts'
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Download className="w-3 h-3" />}
                    Director.txt
                  </button>
                )}
              </div>
            )}
          >
            {/* 2026-07-09 — Fix 2: red incompletion banner. Shows when
                the extractor's validation gate detected a shot-count
                mismatch between the input doc and the persisted shots.
                Blocks progression semantically (operator MUST re-run or
                manually add missing shots before moving to Asset Bible).
                Docs behind `services/shot_breakdown_extractor.py`. */}
            {project.breakdown_stage_incomplete && (
              <div
                className="mb-4 p-4 rounded-xl border"
                style={{
                  background: 'rgba(220,38,38,0.10)',
                  borderColor: 'rgba(220,38,38,0.35)',
                }}
                data-testid="shot-breakdown-incomplete-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <AlertTriangle className="w-5 h-5" style={{ color: '#EF4444' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold" style={{ color: '#FCA5A5' }}>
                      Extracted {project.breakdown_output_shot_count || 0} of {project.breakdown_input_marker_count || 0} shots
                    </div>
                    <div className="text-xs mt-1" style={{ color: '#FCA5A5', opacity: 0.85 }}>
                      Missing: {(project.breakdown_missing_markers || []).slice(0, 12).join(', ')}
                      {(project.breakdown_missing_markers || []).length > 12 && ` … +${(project.breakdown_missing_markers || []).length - 12} more`}
                    </div>
                    <div className="text-[11px] mt-2" style={{ color: '#FCA5A5', opacity: 0.75 }}>
                      The parser hit truncation on {(project.breakdown_truncation_events || []).length} chunk(s).
                      Re-run the shot breakdown, or manually add the missing shots before continuing.
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowRerunConfirm(true); }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0"
                    style={{ background: 'rgba(220,38,38,0.20)', color: '#FCA5A5' }}
                    data-testid="shot-breakdown-incomplete-rerun-btn"
                  >
                    Re-run
                  </button>
                </div>
              </div>
            )}
            {/* Parse-method badge (2026-07-20) — shows whether the last breakdown
                was parsed INSTANTLY from a locked/structured doc (deterministic,
                no AI) or via the AI extractor. Only shown on a successful,
                complete parse. */}
            {!project.breakdown_stage_incomplete && project.breakdown_parse_method && (project.breakdown_output_shot_count || 0) > 0 && (
              <div className="mb-3" data-testid="shot-breakdown-parse-method-badge">
                {project.breakdown_parse_method === 'deterministic' ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: 'rgba(16,185,129,0.12)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.3)' }}
                  >
                    <Zap className="w-3 h-3" /> Parsed instantly · structured format ({project.breakdown_output_shot_count} shots, no AI)
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: 'rgba(99,102,241,0.12)', color: '#A5B4FC', border: '1px solid rgba(99,102,241,0.3)' }}
                  >
                    <Sparkles className="w-3 h-3" /> AI-parsed ({project.breakdown_output_shot_count} shots)
                  </span>
                )}
              </div>
            )}
            {/* Napkin Sketches — bulk generate + stitch (2026-07-10, E1
                ticket v2). Runs the per-shot purple pencil for every shot
                in one click, then stitches into an MP4 animatic + contact
                sheet. Independent from Stage 8 sketches. */}
            {shots.length > 0 && (
              <NapkinSketchesPanel
                projectId={projectId}
                shotCount={shots.length}
                onNapkinsChanged={loadProjectData}
              />
            )}
            {shots.length === 0 ? (
              <div>
                {project.breakdown_processing ? (
                  <div className="p-4 bg-ai-accent/5 border border-ai-accent/20 rounded-xl animate-pulse" data-testid="breakdown-processing-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-ai-accent" />
                        <div>
                          <div className="text-sm font-medium text-ai-accent">
                            {project.uploaded_breakdown_filename && project.uploaded_breakdown_filename !== 'pasted_text'
                              ? `Parsing "${project.uploaded_breakdown_filename}"…`
                              : 'Parsing breakdown…'}
                          </div>
                          <div className="text-xs text-white/50" data-testid="breakdown-parse-progress">
                            {project.breakdown_progress?.scenes_total > 0
                              ? `Parsing scene ${project.breakdown_progress.scenes_done} / ${project.breakdown_progress.scenes_total} in parallel — larger documents take longer, and this resumes if interrupted.`
                              : 'AI is parsing your shot breakdown. Large documents (30+ pages) can take a few minutes.'}
                          </div>
                          {project.breakdown_progress?.scenes_total > 0 && (
                            <div className="mt-2 w-56 max-w-full">
                              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <div
                                  className="h-full bg-ai-accent transition-all duration-500"
                                  style={{ width: `${Math.round((project.breakdown_progress.scenes_done / project.breakdown_progress.scenes_total) * 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <button 
                        onClick={async () => {
                          { // confirm removed - non-blocking
                            try {
                              await axios.post(`${API}/projects/${projectId}/reset-processing`, { flag: 'breakdown_processing' });
                              toast.success('Processing cancelled. You can try again.');
                              loadProjectData();
                            } catch (e) {
                              toast.error('Failed to cancel: ' + (e.response?.data?.detail || e.message));
                            }
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-1"
                        title="Cancel if stuck"
                      >
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : shotsProcessing ? (
                  <div className="p-4 bg-ai-accent/5 border border-ai-accent/20 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-ai-accent" />
                        <div>
                          <div className="text-sm font-medium text-ai-accent">Generating shot breakdown...</div>
                          <div className="text-xs text-white/50">
                            {shotsProgress ? `${shotsProgress} shots generated — batches processing in parallel...` : 'AI is analyzing your script. This may take 1-2 minutes.'}
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={async () => {
                          { // confirm removed - non-blocking
                            try {
                              await axios.post(`${API}/projects/${projectId}/reset-processing`, { flag: 'shots_processing' });
                              toast.success('Shot generation cancelled. You can try again.');
                              loadProjectData();
                            } catch (e) {
                              toast.error('Failed to cancel: ' + (e.response?.data?.detail || e.message));
                            }
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-1"
                        title="Cancel if stuck"
                      >
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                    {shotsProgress && (() => {
                      const parts = shotsProgress.split('/');
                      const current = parseInt(parts[0]) || 0;
                      const total = parseInt(parts[1]) || 1;
                      const pct = Math.min(100, Math.round((current / total) * 100));
                      return (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-white/40 mb-1">
                            <span>Batch progress</span>
                            <span>{current} / {total} shots</span>
                          </div>
                          <div className="w-full h-2 bg-white/5 rounded-lg overflow-hidden">
                            <div 
                              className="h-full bg-ai-accent rounded-lg transition-all duration-700 ease-out"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    {/* CD-3 (2026-07-15) — in-page named error state, replaces
                        the old blocking browser alert() for shot-gen failures.
                        The input picker below is the retry path. */}
                    {shotGenError && (
                      <div className="mb-4 p-4 rounded-xl border border-rose-500/30 bg-rose-500/10" data-testid="shot-gen-error-banner">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-rose-200" data-testid="shot-gen-error-title">{shotGenError.title}</div>
                            <div className="text-xs text-rose-200/70 mt-1" data-testid="shot-gen-error-detail">{shotGenError.detail}</div>
                            <div className="text-[11px] text-white/40 mt-1.5">Pick an input method below and generate again to retry.</div>
                          </div>
                          <button
                            onClick={() => setShotGenError(null)}
                            className="px-2 py-1 rounded-lg text-xs font-medium bg-white/5 text-white/60 hover:bg-white/10 transition-colors flex items-center gap-1 flex-shrink-0"
                            data-testid="shot-gen-error-dismiss-btn"
                          >
                            <X className="w-3 h-3" /> Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Look & Feel References panel is now hoisted to the top of
                        this CollapsibleSection (see ~L11382) so it stays visible
                        even after shots exist — 2026-07-07 fix. */}
                    {/* ── Input Method Picker (redesigned 2026-05-15) ──
                     * Three peer-weight cards. Selection drives both the
                     * inline content area (file picker / textarea / nothing)
                     * and the unified Generate CTA's dispatch logic.
                     * 4th option (`templatized`) surfaces only when the
                     * project has a templatized script with draft_shots —
                     * one-click promote, no LLM cost, has built-in 7-day undo. */}
                    {(() => {
                      const tplScenes = project?.templatized_script_json?.scenes || [];
                      const tplDraftCount = tplScenes.reduce(
                        (sum, sc) => sum + ((sc.draft_shots || []).length),
                        0,
                      );
                      const hasTemplatizedDrafts = tplDraftCount > 0;
                      const baseOpts = [
                        { key: 'ai', icon: Sparkles, title: 'AI-generate', sub: 'from canonical script', testid: 'shot-breakdown-input-ai' },
                        { key: 'upload', icon: Upload, title: 'Upload', sub: '.docx · .pdf · .txt · .md', testid: 'shot-breakdown-input-upload' },
                        { key: 'paste', icon: ClipboardPaste, title: 'Paste text', sub: 'shot list', testid: 'shot-breakdown-input-paste' },
                      ];
                      if (hasTemplatizedDrafts) {
                        baseOpts.push({
                          key: 'templatized',
                          icon: Wand2,
                          title: 'Pull from Templatized',
                          sub: `${tplDraftCount} shots · instant`,
                          testid: 'shot-breakdown-input-templatized',
                        });
                      }
                      const gridCols = hasTemplatizedDrafts ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3';
                      return (
                    <div className="mb-3" data-testid="shot-breakdown-input-picker">
                      <p className="text-xs text-white/60 mb-2">How do you want to build your shot list?</p>
                      <div className={`grid ${gridCols} gap-2`}>
                        {baseOpts.map(opt => {
                          const Icon = opt.icon;
                          const isActive = breakdownInputSource === opt.key;
                          const tryPick = () => {
                            if (opt.key === breakdownInputSource) return;
                            // Guard against losing staged content on method switch.
                            const hasStagedFile = breakdownInputSource === 'upload' && pendingBreakdownFile;
                            const hasStagedText = breakdownInputSource === 'paste' && pendingBreakdownText.trim();
                            if (hasStagedFile || hasStagedText) {
                              setPendingSwitchTo(opt.key);
                              return;
                            }
                            setBreakdownInputSource(opt.key);
                          };
                          return (
                            <button
                              key={opt.key}
                              onClick={tryPick}
                              data-testid={opt.testid}
                              className={`px-3 py-3 rounded-xl border text-left transition-all ${
                                isActive
                                  ? 'border-ai-accent bg-ai-accent/10 ring-1 ring-ai-accent/40'
                                  : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                  isActive ? 'bg-ai-accent/20 text-ai-accent' : 'bg-white/5 text-white/60'
                                }`}>
                                  <Icon className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className={`text-xs font-semibold ${isActive ? 'text-ai-accent' : 'text-white/80'}`}>{opt.title}</div>
                                  <div className="text-[10px] text-white/40 mt-0.5 truncate">{opt.sub}</div>
                                </div>
                                <div className={`w-3.5 h-3.5 rounded-full border-2 mt-1 shrink-0 ${
                                  isActive ? 'border-ai-accent bg-ai-accent' : 'border-white/30'
                                }`} />
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Conditional inline content for the picked method. */}
                      {breakdownInputSource === 'upload' && (
                        <div className="mt-3 space-y-2">
                          <label
                            className={`block w-full p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                              pendingBreakdownFile
                                ? 'border-ai-accent/50 bg-ai-accent/5'
                                : 'border-white/15 hover:border-white/30 bg-white/[0.02]'
                            }`}
                          >
                            <input
                              type="file"
                              accept=".txt,.md,.markdown,.pdf,.docx"
                              className="hidden"
                              data-testid="shot-breakdown-upload-file-input"
                              onChange={(e) => setPendingBreakdownFile(e.target.files?.[0] || null)}
                            />
                            <div className="flex items-center gap-3">
                              {pendingBreakdownFile ? <FileText className="w-5 h-5 text-ai-accent shrink-0" /> : <Upload className="w-5 h-5 text-white/40 shrink-0" />}
                              <div className="min-w-0 flex-1">
                                {pendingBreakdownFile ? (
                                  <>
                                    <div className="text-xs font-medium text-ai-accent truncate">{pendingBreakdownFile.name}</div>
                                    <div className="text-[10px] text-white/40">{(pendingBreakdownFile.size / 1024).toFixed(1)} KB · click to change</div>
                                  </>
                                ) : (
                                  <>
                                    <div className="text-xs font-medium text-white/70">Click to choose a file (or drag in)</div>
                                    <div className="text-[10px] text-white/40">Accepted: .docx · .pdf · .txt · .md</div>
                                  </>
                                )}
                              </div>
                              {pendingBreakdownFile && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setPendingBreakdownFile(null); }}
                                  className="p-1 rounded text-white/40 hover:text-white/80 hover:bg-white/10"
                                  data-testid="shot-breakdown-upload-clear"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </label>
                          {/* Recently uploaded mini-history — one-click re-use of
                           *  the last 3 files. Saves the user from re-dragging
                           *  a refreshed brief. Per Rohit's mandate: human-
                           *  prepared .docx breakdowns are a heavy-volume path. */}
                          {(() => {
                            const history = (project?.uploaded_breakdown_history || []).slice(0, 3);
                            if (!history.length) return null;
                            return (
                              <div className="px-1" data-testid="shot-breakdown-history">
                                <div className="text-[10px] uppercase tracking-wide text-white/40 font-semibold mb-1.5">Recently uploaded</div>
                                <div className="flex flex-col gap-1">
                                  {history.map((h, idx) => (
                                    <button
                                      key={`${h.filename}-${idx}`}
                                      onClick={async () => {
                                        setUploadingAction('breakdown');
                                        try {
                                          const res = await axios.post(`${API}/projects/${project.id}/reuse-uploaded-breakdown`, { filename: h.filename });
                                          toast.success(res.data?.message || `Re-using ${h.filename}`);
                                          await loadProjectData();
                                        } catch (e) {
                                          toast.error(e.response?.data?.detail || 'Re-use failed');
                                        } finally {
                                          setUploadingAction(null);
                                        }
                                      }}
                                      disabled={uploadingAction === 'breakdown'}
                                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/[0.03] hover:bg-ai-accent/10 hover:border-ai-accent/30 border border-white/5 text-left transition-all disabled:opacity-40"
                                      data-testid={`shot-breakdown-history-entry-${idx}`}
                                      title={`Re-use ${h.filename} (${h.text_length} chars, uploaded ${h.uploaded_at?.slice(0, 10)})`}
                                    >
                                      <FileText className="w-3 h-3 text-white/40 shrink-0" />
                                      <span className="text-[11px] text-white/70 truncate flex-1">{h.filename}</span>
                                      <span className="text-[9px] text-white/30 shrink-0">{(h.size_bytes / 1024).toFixed(0)} KB</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {breakdownInputSource === 'paste' && (
                        <div className="mt-3">
                          <textarea
                            value={pendingBreakdownText}
                            onChange={(e) => setPendingBreakdownText(e.target.value)}
                            placeholder="Paste your shot breakdown here…"
                            rows={6}
                            data-testid="shot-breakdown-paste-textarea"
                            className="w-full p-3 rounded-xl bg-white/[0.03] border border-white/10 text-xs text-white/90 placeholder-white/30 focus:border-ai-accent focus:ring-1 focus:ring-ai-accent/40 outline-none resize-y"
                          />
                          <div className="text-[10px] text-white/40 mt-1">Writer AI adds SC/SH numbers and metadata; your descriptions stay word-for-word.</div>
                        </div>
                      )}
                      {breakdownInputSource === 'templatized' && hasTemplatizedDrafts && (
                        <div className="mt-3 p-3 rounded-xl border border-ai-accent/20 bg-ai-accent/[0.04]" data-testid="shot-breakdown-templatized-info">
                          <div className="flex items-start gap-2">
                            <Wand2 className="w-4 h-4 text-ai-accent shrink-0 mt-0.5" />
                            <div className="text-xs text-white/80 leading-relaxed">
                              <div className="font-semibold text-ai-accent mb-0.5">Use your templatized shot list as-is</div>
                              Imports <span className="text-white font-medium">{tplDraftCount} shot{tplDraftCount === 1 ? '' : 's'}</span> across <span className="text-white font-medium">{tplScenes.length} scene{tplScenes.length === 1 ? '' : 's'}</span> exactly as approved in the Templatized Script tab. No LLM call, no credits burned. <span className="text-white/60">Existing breakdown (if any) is snapshot for a 7-day undo.</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                      );
                    })()}

                    {/* Unified Generate CTA — dispatches based on source. */}
                    <ActionButton
                      variant="primary"
                      icon={ArrowRight}
                      loading={
                        uploadingAction === 'shots'
                        || uploadingAction === 'breakdown'
                        || uploadingAction === 'paste'
                        || uploadingAction === 'templatized'
                        || analyzingScript
                      }
                      disabled={
                        (breakdownInputSource === 'upload' && !pendingBreakdownFile)
                        || (breakdownInputSource === 'paste' && !pendingBreakdownText.trim())
                        || uploadingAction === 'shots'
                        || uploadingAction === 'breakdown'
                        || uploadingAction === 'paste'
                        || uploadingAction === 'templatized'
                      }
                      data-testid="generate-shot-breakdown"
                      onClick={async () => {
                        if (breakdownInputSource === 'ai') {
                          // Existing flow — opens Script Analysis modal then
                          // generates. analyzeScriptBeats with forGeneration=true
                          // (default behavior of generateShots).
                          await generateShots();
                          return;
                        }
                        if (breakdownInputSource === 'upload' && pendingBreakdownFile) {
                          // Synthesize a change event so we can reuse the
                          // existing handler verbatim — no new code path.
                          await handleBreakdownUpload({ target: { files: [pendingBreakdownFile], value: '' } });
                          return;
                        }
                        if (breakdownInputSource === 'paste' && pendingBreakdownText.trim()) {
                          setUploadingAction('paste');
                          try {
                            const res = await axios.post(`${API}/projects/${project.id}/paste-shot-breakdown`, {
                              text: pendingBreakdownText,
                            });
                            toast.success(`Parsed ${res.data?.shots_count || ''} shots`);
                            setPendingBreakdownText('');
                            await loadProjectData();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to parse pasted text');
                          } finally {
                            setUploadingAction(null);
                          }
                          return;
                        }
                        if (breakdownInputSource === 'templatized') {
                          // Promote the templatized draft_shots into the
                          // canonical Shot Breakdown. Backend snapshots the
                          // existing rows for a 7-day undo.
                          setUploadingAction('templatized');
                          try {
                            const res = await axios.post(`${API}/projects/${project.id}/script/templatized/promote-drafts`);
                            const after = res.data?.shot_count_after ?? '';
                            const before = res.data?.shot_count_before ?? 0;
                            toast.success(
                              before > 0
                                ? `Imported ${after} shots from templatized script · ${before} previous shots backed up for 7-day undo`
                                : `Imported ${after} shots from templatized script · undoable for 7 days`,
                            );
                            await loadProjectData();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to import templatized shots');
                          } finally {
                            setUploadingAction(null);
                          }
                        }
                      }}
                    >
                      {breakdownInputSource === 'ai' ? 'Generate Shot Breakdown'
                        : breakdownInputSource === 'upload' ? 'Generate from Document'
                        : breakdownInputSource === 'templatized' ? 'Pull Templatized Shots'
                        : 'Generate from Pasted Text'}
                    </ActionButton>
                  </>
                )}
              </div>
            ) : (
              <div>
                {/* Post-generation: shot cards only. Re-run + View Analysis
                 *  live in the section header (rightContent). The pre-gen
                 *  bloat (5 buttons + duplicative + Add Shot top-level) is
                 *  intentionally gone — per Shot Breakdown brief 2026-05-15. */}

                {/* Asset Bible Spec audit banner (May 16 brief — Steps 9-10) */}
                <AssetBibleSpecAuditBanner
                  shots={shots}
                  projectId={projectId}
                  assetBible={assetBible}
                  project={project}
                  onComplete={loadProjectData}
                />

                <div className="space-y-0 max-h-[500px] overflow-y-auto">
                  {shots.map((shot, idx) => (
                    <React.Fragment key={shot.id}>
                      <ShotEditCard shot={shot} index={idx} totalShots={shots.length} onUpdate={loadProjectData} onDelete={deleteShot} onReorder={reorderShot} assetBible={assetBible} projectId={projectId} sketchGateReady={!!project?.cast_confirmed && shots.length > 0} bindingsProcessing={project?.bindings_processing} promptsProcessing={project?.prompts_processing} dopSpecsProcessing={project?.dop_specs_processing} />
                      {/* + Add Shot Button between shots — Phase 1 (2026-05-25)
                          uses the new /shots/insert-after endpoint which kicks
                          off the per-shot enrichment chain (DP → DOP → bindings
                          → entity-diff) in background and surfaces toast
                          progress. */}
                      <div className="flex items-center justify-center py-1">
                        <button onClick={async () => {
                          const desc = window.prompt('New shot description:');
                          if (!desc || !desc.trim()) return;
                          try {
                            const insertRes = await axios.post(
                              `${API}/projects/${projectId}/shots/insert-after/${shot.id}`,
                              { description: desc.trim(), duration: 5 }
                            );
                            const newShotId = insertRes.data?.shot_id;
                            const label = insertRes.data?.shot_number || 'new shot';
                            toast.success(`Shot ${label} added · enrichment chain running`);
                            loadProjectData();
                            // Poll the chain so we surface "X new entities detected" when it lands
                            if (newShotId) {
                              const pollStart = Date.now();
                              const poll = setInterval(async () => {
                                if (Date.now() - pollStart > 180000) { clearInterval(poll); return; }
                                try {
                                  const st = await axios.get(`${API}/projects/${projectId}/shots/${newShotId}/enrich/status`);
                                  const s = st.data;
                                  if (s?.status === 'completed') {
                                    clearInterval(poll);
                                    if (s?.entity_diff_has_new) {
                                      const ed = s.entity_diff || {};
                                      const counts = [
                                        (ed.new_cast || []).length && `${ed.new_cast.length} cast`,
                                        (ed.new_costumes || []).length && `${ed.new_costumes.length} costume`,
                                        (ed.new_locations || []).length && `${ed.new_locations.length} location`,
                                      ].filter(Boolean).join(' · ');
                                      toast.info(`${label}: new entities detected (${counts}) — re-confirm cast tiers`, { duration: 8000 });
                                    } else {
                                      toast.success(`${label}: enrichment complete · ready for sketch`, { duration: 4000 });
                                    }
                                    loadProjectData();
                                  } else if (s?.status === 'failed' || s?.status === 'stranded') {
                                    clearInterval(poll);
                                    toast.error(`${label}: enrichment ${s.status} — ${s.error || 'tap retry on the shot card'}`);
                                  }
                                } catch { /* keep polling on transient */ }
                              }, 3000);
                            }
                          } catch (e) {
                            const detail = e.response?.data?.detail;
                            if (typeof detail === 'object' && detail?.code === 'ENRICHMENT_IN_FLIGHT') {
                              toast.error('Another shot is still enriching — wait for it to finish');
                            } else {
                              toast.error(typeof detail === 'string' ? detail : (e.message || 'Insert failed'));
                            }
                          }
                        }} className="w-full py-1.5 rounded-lg border border-dashed border-[#22C55E]/40 flex items-center justify-center gap-1 hover:border-[#22C55E] hover:bg-[#22C55E]/10 transition-all"
                          data-testid={`insert-shot-after-${shot.id}-btn`}
                          title="Add shot here (delta-aware catch-up)">
                          <Plus className="w-3 h-3 text-[#22C55E]" />
                          <span className="text-[9px] text-[#22C55E]/70">+</span>
                        </button>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-ai-border">
                  <span className="text-xs text-white/50">{shots.length} shots • Click to edit • Drag to reorder</span>
                </div>


                {/* Derived CCC — Compact */}
                {derivedCCC && derivedCCC.cast && derivedCCC.cast.length > 0 && (
                  <div className="mt-4 p-3 rounded-xl border border-ai-accent/15 bg-ai-accent/[0.02]" data-testid="derived-ccc-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-semibold text-ai-accent">Asset Bible Bindings</h4>
                        {castTiering?.confirmed && !castTiering?.needs_reconfirm && (
                          <span data-testid="cast-tier-confirmed-badge" className="text-[8px] px-1.5 py-0.5 rounded-lg bg-green-500/20 text-green-400">Confirmed · {castTiering.primary_count} Primary</span>
                        )}
                        {castTiering?.needs_reconfirm && (
                          <span data-testid="cast-tier-reconfirm-badge" className="text-[8px] px-1.5 py-0.5 rounded-lg bg-amber-500/20 text-amber-300">Re-confirm — {castTiering.new_cast_since_confirm?.length} new</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {/* Re-detect from script — safety net for accidental deletes */}
                        <button
                          data-testid="regenerate-detected-ccc"
                          title="Re-detect cast / costumes / locations from the script (overwrites edits — no undo!)"
                          onClick={async () => {
                            const editCount = (derivedCCC.cast?.length || 0) + (derivedCCC.costumes?.length || 0) + (derivedCCC.locations?.length || 0);
                            if (!window.confirm(
                              `Re-detect Cast / Costumes / Locations from the script?\n\n` +
                              `This will wipe the current ${editCount} entries (including any edits or additions you made) ` +
                              `and rebuild from scratch using the latest shot breakdown. No undo.`
                            )) return;
                            try {
                              const res = await axios.post(`${API}/projects/${projectId}/derived-ccc/regenerate`);
                              setDerivedCCC(res.data);
                              toast.success(
                                `Re-detected: ${(res.data.cast || []).length} cast, ` +
                                `${(res.data.costumes || []).length} costumes, ` +
                                `${(res.data.locations || []).length} locations`
                              );
                            } catch (err) {
                              toast.error('Re-detect failed: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="p-1 rounded-md bg-white/[0.04] hover:bg-ai-accent/15 text-white/50 hover:text-ai-accent transition-colors"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                        <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
                          <button onClick={async () => { await axios.post(`${API}/projects/${projectId}/settings`, { cast_style: 'commercial' }); setProject(prev => ({...prev, cast_style: 'commercial'})); }}
                            className={`px-2 py-0.5 rounded-full text-[9px] font-medium transition-all ${project?.cast_style !== 'documentary' ? 'bg-ai-accent text-white' : 'text-white/30'}`}>Commercial</button>
                          <button onClick={async () => { await axios.post(`${API}/projects/${projectId}/settings`, { cast_style: 'documentary' }); setProject(prev => ({...prev, cast_style: 'documentary'})); }}
                            className={`px-2 py-0.5 rounded-full text-[9px] font-medium transition-all ${project?.cast_style === 'documentary' ? 'bg-white/20 text-white' : 'text-white/30'}`}>Documentary</button>
                        </div>
                      </div>
                    </div>

                    {/* Add-Shot Phase 1 (2026-05-25) — NEW CAST FROM ENTITY-DIFF banner. */}
                    {(() => {
                      const newFromDiff = (castTiering?.cast || []).filter(c => c.from_entity_diff);
                      if (newFromDiff.length === 0) return null;
                      return (
                        <div data-testid="new-cast-from-entity-diff-banner" className="mb-3 px-2.5 py-2 rounded-lg border border-cyan-400/30 bg-cyan-400/[0.04]">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Sparkles className="w-3 h-3 text-cyan-300 shrink-0" />
                            <span className="text-[10px] font-semibold text-cyan-200">
                              {newFromDiff.length} new cast detected in added shot{newFromDiff.length > 1 ? 's' : ''}
                            </span>
                            <span className="text-[9px] text-white/40">· pick tier, then add to Asset Bible</span>
                          </div>
                          <div className="space-y-1">
                            {newFromDiff.map(c => {
                              const inShots = (c.detected_in_shots || []).join(', ');
                              const slug = c.key.replace(/\s+/g, '-');
                              return (
                                <div key={c.key} className="flex items-center gap-2 px-2 py-1 rounded bg-white/[0.03] hover:bg-white/[0.05] transition-colors">
                                  <span className="flex-1 min-w-0 text-[10px] truncate">
                                    <span className="font-medium text-white" data-testid={`new-cast-name-${slug}`}>{c.name}</span>
                                    <span className="text-cyan-300/60 ml-1.5">· in {inShots}</span>
                                  </span>
                                  <select
                                    data-testid={`new-cast-tier-${slug}`}
                                    value={c.tier || 'secondary'}
                                    onChange={async (e) => {
                                      const next = e.target.value;
                                      setCastTiering(prev => prev ? {
                                        ...prev,
                                        cast: (prev.cast || []).map(x => x.key === c.key ? { ...x, tier: next } : x),
                                      } : prev);
                                      try {
                                        const tiers = {};
                                        (castTiering?.cast || []).forEach(x => { tiers[x.key] = x.key === c.key ? next : x.tier; });
                                        await axios.post(`${API}/projects/${projectId}/cast-tiering`, { tiers });
                                      } catch { /* swallow — optimistic UI */ }
                                    }}
                                    className={`bg-white/[0.06] outline-none text-[9px] px-1.5 py-0.5 rounded w-20 shrink-0 ${c.tier === 'primary' ? 'text-ai-accent font-semibold' : 'text-white/70'}`}
                                  >
                                    <option value="primary">Primary</option>
                                    <option value="secondary">Secondary</option>
                                  </select>
                                  <button
                                    data-testid={`new-cast-add-to-bible-${slug}`}
                                    onClick={async () => {
                                      try {
                                        // 1) Merge tier (no-op for already-set tiers)
                                        await axios.post(`${API}/projects/${projectId}/cast-tiering/confirm-delta`, {
                                          new_entity_tiers: { [c.key]: c.tier || 'secondary' }
                                        });
                                        // 2) Push to derived_ccc so the rest of the pipeline picks it up
                                        updateCCC(prev => {
                                          const u = { ...prev };
                                          const exists = (u.cast || []).some(x => (x.name || '').toLowerCase() === c.name.toLowerCase());
                                          if (!exists) {
                                            u.cast = [...(u.cast || []), { name: c.name, gender: 'unspecified', age: '', description: `Detected in ${inShots}` }];
                                          }
                                          return u;
                                        }, { immediate: true });
                                        // 3) Insert an Asset Bible stub + trigger the only-failed retry
                                        //    path so the portrait actually renders. Without this the
                                        //    card never materialises in the Asset Bible.
                                        const delta = await axios.post(`${API}/projects/${projectId}/generate-assets/delta`, {
                                          new_cast_names: [c.name],
                                        });
                                        const added = delta?.data?.added_stubs?.cast || 0;
                                        const costumeAuto = delta?.data?.auto_costume_inferred || 0;
                                        toast.success(
                                          added > 0
                                            ? `${c.name} (${c.tier === 'primary' ? 'Primary' : 'Secondary'}) added · portrait${costumeAuto ? ' + costume' : ''} rendering now`
                                            : `${c.name} already in Asset Bible · skipping`
                                        );
                                        // refresh tiering so banner row clears
                                        const fresh = await axios.get(`${API}/projects/${projectId}/cast-tiering`);
                                        setCastTiering(fresh.data);
                                        loadProjectData();
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Add failed');
                                      }
                                    }}
                                    className="px-2 py-0.5 rounded text-[9px] font-medium bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-200 border border-cyan-400/30 shrink-0 whitespace-nowrap transition-colors"
                                  >
                                    + Add to Bible
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* All 3 categories in one compact row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
                      {/* Cast */}
                      <div className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                        <div className="flex items-center gap-1 mb-1.5 text-ai-accent font-semibold"><Users className="w-3 h-3" />Cast ({derivedCCC.cast.length})</div>
                        {derivedCCC.cast.map((c, i) => (
                          <div key={i} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 py-0.5 group">
                            <div className="flex items-center gap-1.5 flex-1 basis-[55%] min-w-0">
                              <input value={c.name} onChange={e => updateCCC(prev => { const u = {...prev}; const old = u.cast[i].name; u.cast[i].name = e.target.value; const co = u.costumes?.find(x => x.character_name === old); if (co) co.character_name = e.target.value; return u; })}
                                className="bg-transparent text-white font-medium w-full outline-none text-[10px] truncate" />
                              {/* 2026-05-25 — Optional canonical name (e.g. "Maya"). When set, this becomes
                                  the identity token used everywhere downstream (Opus binder, IGS prompt,
                                  shot ref sheet) so pronouns like "she winks" resolve cleanly to this cast
                                  row. Empty = falls back to role name above. */}
                              <input
                                value={c.display_name || ''}
                                onChange={e => updateCCC(prev => {
                                  const u = {...prev};
                                  u.cast[i].display_name = e.target.value;
                                  // Mark this row as operator-set so the
                                  // backend's placeholder autofill never
                                  // overwrites it on the next CCC re-run.
                                  u.cast[i]._display_name_source = 'operator_set';
                                  return u;
                                })}
                                placeholder="e.g. Maya"
                                title="Canonical name — locks identity across all sketches & frames. Pre-filled with a unique placeholder; edit freely. Once you edit, the placeholder autofill won't overwrite your value on CCC re-runs."
                                data-testid={`cast-display-name-${i}`}
                                className="bg-white/[0.04] text-ai-accent font-medium outline-none text-[10px] px-1 py-0.5 rounded w-[64px] placeholder:text-white/25"
                              />
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                              {/* May 2026 — Tier dropdown. Default Secondary;
                                  user marks Primary cast to gate them into
                                  Asset Bible image-gen. */}
                              {(() => {
                                const norm = (s) => (s||'').trim().toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+/g, ' ');
                                const k = norm(c.name);
                                const currentTier = castTiering?.cast?.find(x => x.key === k)?.tier || 'secondary';
                                return (
                                  <select
                                    data-testid={`cast-tier-select-${k.replace(/\s+/g,'-')}`}
                                    value={currentTier}
                                    onChange={async (e) => {
                                      const next = e.target.value;
                                      // optimistic
                                      setCastTiering(prev => prev ? {
                                        ...prev,
                                        cast: (prev.cast || []).map(x => x.key === k ? { ...x, tier: next } : x),
                                        primary_count: (prev.cast || []).reduce((acc, x) => acc + ((x.key === k ? next : x.tier) === 'primary' ? 1 : 0), 0),
                                        confirmed: false,  // any edit reopens confirm
                                      } : prev);
                                      // persist debounced via a one-shot save
                                      try {
                                        const tiers = {};
                                        (castTiering?.cast || []).forEach(x => { tiers[x.key] = x.key === k ? next : x.tier; });
                                        await axios.post(`${API}/projects/${projectId}/cast-tiering`, { tiers });
                                      } catch { /* swallow — optimistic UI */ }
                                    }}
                                    className={`bg-white/[0.06] outline-none text-[9px] px-1.5 py-0.5 rounded min-w-[28px] ${currentTier === 'primary' ? 'text-ai-accent font-semibold' : 'text-white/60'}`}
                                  >
                                    <option value="primary">P</option>
                                    <option value="secondary">S</option>
                                  </select>
                                );
                              })()}
                              <select value={c.gender} onChange={e => updateCCC(prev => { const u = {...prev}; u.cast[i].gender = e.target.value; return u; }, { immediate: true })}
                                className="bg-white/[0.04] text-white/50 outline-none text-[9px] px-1 py-0.5 rounded min-w-[22px]">{['male','female'].map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase()}</option>)}</select>
                              <button onClick={() => updateCCC(prev => { const u = {...prev}; u.cast.splice(i, 1); u.costumes = u.costumes?.filter(co => co.character_name !== c.name); return u; }, { immediate: true })}
                                className="text-red-400/40 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                            </div>
                          </div>
                        ))}
                        <button onClick={() => updateCCC(prev => { const u = {...prev}; u.cast = [...(u.cast || []), { name: 'New Character', gender: 'male', age: '', description: '' }]; return u; }, { immediate: true })}
                          className="mt-1 w-full py-1 rounded text-[9px] text-cyan-400/50 hover:text-cyan-400 hover:bg-cyan-500/10 border border-dashed border-cyan-500/15 transition-colors" data-testid="add-cast-btn">+ Add Cast</button>
                      </div>
                      
                      {/* Costumes */}
                      <div className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                        <div className="flex items-center gap-1 mb-1.5 text-[#FF2D55] font-semibold"><Shirt className="w-3 h-3" />Costumes ({derivedCCC.costumes?.length || 0})</div>
                        {(derivedCCC.costumes || []).map((c, i) => (
                          <div key={i} className="flex items-center justify-between py-0.5 group">
                            <input value={c.character_name + (c.description ? ' — ' + c.description : '')} onChange={e => updateCCC(prev => {
                              const u = {...prev};
                              const parts = e.target.value.split(' — ');
                              u.costumes[i].character_name = parts[0] || '';
                              u.costumes[i].description = parts.slice(1).join(' — ') || '';
                              return u;
                            })}
                              className="bg-transparent text-white/60 w-full outline-none text-[10px] truncate" />
                            {c.brand_deck_ref_id && (
                              <span
                                title={`Brand deck linked: ${c.brand_deck_ref_caption || 'page'}`}
                                className="text-[#22C55E] flex-shrink-0 mr-1"
                                data-testid={`costume-brand-linked-${i}`}
                              >
                                <Link2 className="w-2.5 h-2.5" />
                              </span>
                            )}
                            <button onClick={() => updateCCC(prev => { const u = {...prev}; u.costumes.splice(i, 1); return u; }, { immediate: true })}
                              className="text-red-400/40 hover:text-red-400 flex-shrink-0"><X className="w-2.5 h-2.5" /></button>
                          </div>
                        ))}
                        <button onClick={() => updateCCC(prev => { const u = {...prev}; u.costumes = [...(u.costumes || []), { character_name: 'Character', description: 'New costume' }]; return u; }, { immediate: true })}
                          className="mt-1 w-full py-1 rounded text-[9px] text-pink-400/50 hover:text-pink-400 hover:bg-pink-500/10 border border-dashed border-pink-500/15 transition-colors" data-testid="add-costume-btn">+ Add Costume</button>
                      </div>
                      
                      {/* Locations */}
                      <div className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                        <div className="flex items-center gap-1 mb-1.5 text-[#FF9500] font-semibold"><MapPin className="w-3 h-3" />Locations ({derivedCCC.locations?.length || 0})</div>
                        {(derivedCCC.locations || []).map((loc, i) => (
                          <div key={i} className="flex items-center justify-between py-0.5 group">
                            <input value={loc.name} onChange={e => updateCCC(prev => { const u = {...prev}; u.locations[i].name = e.target.value; return u; })}
                              className="bg-transparent text-white/60 font-medium w-full outline-none text-[9px] truncate" />
                            <button onClick={() => updateCCC(prev => { const u = {...prev}; u.locations.splice(i, 1); return u; }, { immediate: true })}
                              className="text-red-400/40 hover:text-red-400 flex-shrink-0"><X className="w-2.5 h-2.5" /></button>
                          </div>
                        ))}
                        <button onClick={() => updateCCC(prev => { const u = {...prev}; u.locations = [...(u.locations || []), { name: 'New Location', description: '' }]; return u; }, { immediate: true })}
                          className="mt-1 w-full py-1 rounded text-[9px] text-amber-400/50 hover:text-amber-400 hover:bg-amber-500/10 border border-dashed border-amber-500/15 transition-colors" data-testid="add-location-btn">+ Add Location</button>
                      </div>
                    </div>

                    {/* #73 — LOUD persistent degraded-consensus banner. Shows
                        whenever Opus reconciliation failed and a single-model
                        draft was salvage-picked. Queryable via
                        project.consensus_degraded. */}
                    {(derivedCCC?.consensus_degraded || project?.consensus_degraded) && (
                      <div
                        className="mt-2 rounded-lg border-2 border-red-500/60 bg-red-500/10 px-3 py-2.5 flex items-start gap-2"
                        data-testid="consensus-degraded-banner"
                        data-degraded-source={derivedCCC?.consensus_salvage_source || project?.consensus_salvage_source || ''}
                        role="alert"
                      >
                        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                        <div className="text-[11px] leading-relaxed">
                          <div className="text-red-300 font-bold uppercase tracking-wide">Consensus DEGRADED</div>
                          <div className="text-red-200/80">
                            A single-model draft is in use
                            {(derivedCCC?.consensus_salvage_source || project?.consensus_salvage_source)
                              ? ` (${derivedCCC?.consensus_salvage_source || project?.consensus_salvage_source})` : ''}
                            {' '}— Opus reconciliation failed. <span className="font-semibold">Review the TIER and COSTUME columns carefully</span> before generating assets.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Shot Map expandable view (May 2026 — 3-LLM consensus) ──
                        Renders the per-shot-per-cast shotmap with rationale.
                        Collapsed by default to keep the AB panel compact;
                        operators expand it to audit WHY each cast/costume/
                        corner was chosen per shot. Only shows when the
                        consensus extraction has produced a shotmap. */}
                    {derivedCCC?.shotmap && derivedCCC.shotmap.length > 0 && (
                      <div className="mt-2 rounded-lg border border-ai-accent/15 bg-ai-accent/[0.02]" data-testid="shotmap-section">
                        <button
                          onClick={() => setShowShotMap(s => !s)}
                          className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-ai-accent hover:bg-ai-accent/5 rounded-lg transition-colors"
                          data-testid="shotmap-toggle"
                        >
                          <span className="flex items-center gap-1.5">
                            <ChevronDown className={`w-3 h-3 transition-transform ${showShotMap ? '' : '-rotate-90'}`} />
                            Shot Map ({derivedCCC.shotmap.length} rows · 3-LLM consensus)
                          </span>
                          {derivedCCC.consensus_cost_usd != null && (
                            <span className="text-[8px] font-normal text-white/40">
                              ${derivedCCC.consensus_cost_usd.toFixed(2)} · {Math.round(derivedCCC.consensus_elapsed_s || 0)}s
                            </span>
                          )}
                        </button>
                        {showShotMap && (
                          <div className="px-3 pb-3 space-y-2">
                            {/* Reconciliation notes from Opus */}
                            {derivedCCC.reconciliation_notes && (
                              <div className="p-2 rounded bg-white/[0.03] border border-white/[0.05] text-[9px] text-white/60 leading-relaxed">
                                <div className="text-white/40 font-semibold mb-1">Director AI reconciliation:</div>
                                {derivedCCC.reconciliation_notes}
                              </div>
                            )}
                            {/* Per-LLM draft cost breakdown */}
                            {derivedCCC.consensus_drafts && derivedCCC.consensus_drafts.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 text-[9px]">
                                {derivedCCC.consensus_drafts.map((d, i) => (
                                  <div key={i} className={`px-2 py-1 rounded border flex items-center gap-1 ${
                                    d.status === 'ok'
                                      ? 'bg-green-500/10 border-green-500/20 text-green-300'
                                      : 'bg-red-500/10 border-red-500/20 text-red-300'
                                  }`}>
                                    <span className="font-semibold">{d.label}</span>
                                    <span className="text-white/40">·</span>
                                    <span>{d.status === 'ok' ? `${d.rows || 0} rows` : 'failed'}</span>
                                    {d.cost_usd != null && d.status === 'ok' && (
                                      <>
                                        <span className="text-white/40">·</span>
                                        <span>${d.cost_usd.toFixed(3)}</span>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* The shotmap table */}
                            <div className="overflow-x-auto rounded border border-white/[0.05]">
                              <table className="w-full text-[9px] text-white/70" data-testid="shotmap-table">
                                <thead className="bg-white/[0.04] text-white/40 uppercase">
                                  <tr>
                                    <th className="px-2 py-1.5 text-left font-semibold w-12">Sc/Sh</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Cast</th>
                                    <th className="px-2 py-1.5 text-left font-semibold w-16">Tier</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Costume</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Corner</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {derivedCCC.shotmap.map((row, i) => (
                                    <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02]" title={
                                      row.rationale
                                        ? `Costume: ${row.rationale.costume || ''}\n\nCorner: ${row.rationale.corner || ''}\n\nTier: ${row.rationale.tier || ''}`
                                        : ''
                                    }>
                                      <td className="px-2 py-1.5 text-white/40 whitespace-nowrap">SC{row.sc}-SH{row.sh}</td>
                                      <td className="px-2 py-1.5 text-white font-medium">{row.cast}</td>
                                      <td className="px-2 py-1.5">
                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold ${
                                          row.tier === 'primary'
                                            ? 'bg-ai-accent/20 text-ai-accent'
                                            : 'bg-white/[0.06] text-white/40'
                                        }`}>
                                          {row.tier === 'primary' ? 'P' : 'S'}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1.5 text-white/60">{row.costume}</td>
                                      <td className="px-2 py-1.5 text-white/50 text-[8px]">{row.corner}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="text-[8px] text-white/30 italic">Hover any row to see the rationale (why this costume / corner / tier was chosen).</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CCC-specific action buttons — only shown when CCC is generated */}
                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/[0.05]">
                      <button onClick={async () => {
                        // May 2026 — combined Confirm: CCC + cast tiering in one click.
                        // Validates ≥1 Primary cast picked before firing.
                        setCastTieringError(null);
                        const primaryCount = (castTiering?.cast || []).filter(c => c.tier === 'primary').length;
                        if ((castTiering?.cast || []).length > 0 && primaryCount === 0) {
                          setCastTieringError('Select at least one Primary cast before confirming.');
                          return;
                        }
                        setCastTieringSaving(true);
                        try {
                          await axios.post(`${API}/projects/${projectId}/derived-ccc`, derivedCCC);
                          await axios.post(`${API}/projects/${projectId}/confirm-ccc`);
                          // Cast tiering confirm (only if there's a tier list to confirm)
                          if ((castTiering?.cast || []).length > 0) {
                            const tiers = {};
                            castTiering.cast.forEach(x => { tiers[x.key] = x.tier; });
                            await axios.post(`${API}/projects/${projectId}/cast-tiering/confirm`, { tiers });
                          }
                          setDerivedCCC(prev => ({...prev, confirmed: true}));
                          await loadCastTiering();
                          toast.success('Confirmed — Asset Bible image generation unlocked');
                          loadProjectData();
                        } catch(e) {
                          const code = e.response?.data?.detail?.code;
                          const msg = e.response?.data?.detail?.message || e.response?.data?.detail || e.message;
                          if (code === 'NO_PRIMARY_CAST') {
                            setCastTieringError(msg);
                          } else {
                            toast.error(msg);
                          }
                        } finally {
                          setCastTieringSaving(false);
                        }
                      }} data-testid="confirm-ccc-btn"
                        disabled={castTieringSaving}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-[10px] font-semibold hover:bg-green-700 active:scale-95 transition-all flex items-center gap-1 whitespace-nowrap disabled:opacity-50">
                        <CheckCircle className="w-3 h-3 shrink-0" /> {castTieringSaving ? 'Confirming…' : (derivedCCC.confirmed && castTiering?.confirmed && !castTiering?.needs_reconfirm ? 'Re-confirm' : 'Confirm')}
                      </button>
                      <button onClick={async () => {
                        try { await axios.post(`${API}/projects/${projectId}/derived-ccc`, derivedCCC); toast.success('Saved'); } catch(e) { toast.error('Save failed'); }
                      }} className="px-3 py-1.5 bg-white/10 text-white/70 rounded-lg text-[10px] font-semibold hover:bg-white/15 active:scale-95 transition-all whitespace-nowrap">Save Draft</button>
                    </div>
                    {/* Validation message for the NO_PRIMARY_CAST gate */}
                    {castTieringError && (
                      <div data-testid="cast-tier-validation-error" className="mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-300 flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        <span>{castTieringError}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Stage-advance button — gated on per-shot completeness.
                    Per Rohit (2026-05-18): the "Approved, Move to Next Stage"
                    button MUST stay disabled until every shot has all three
                    of:
                      • Director's Prompt generated
                      • DOP Spec populated
                      • Asset Bible spec bound (corner_id OR cast_bindings non-empty —
                        empty cast_bindings is valid for OBJECT_ONLY shots)
                    Hover tooltip surfaces exactly what's still missing. */}
                {(() => {
                  const isShotBound = (s) => {
                    const spec = s?.asset_bible_spec;
                    if (!spec) return false;
                    const bindings = Array.isArray(spec.cast_bindings) ? spec.cast_bindings : [];
                    // A preview-mode spec (parsed from Director's Prompt before
                    // Asset Bible exists) carries `corner_label` instead of
                    // `corner_id`. Count both as a valid corner binding.
                    // CONTRACT (2026-07-19): a product-macro / no-location shot
                    // (SH30–33) carries an explicit CD ruling — it is COMPLETE
                    // with no corner and no cast, so it is bound by definition.
                    return !!spec.no_location || !!spec.corner_id || !!spec.corner_label || bindings.length > 0;
                  };
                  const missingPrompt = shots.filter(s => !((s.keyframe_prompt || s.mj_prompt || '').trim()));
                  const missingDop = shots.filter(s => {
                    const d = s.dop_spec;
                    return !d || (typeof d === 'object' && Object.keys(d).length === 0);
                  });
                  const missingAssets = shots.filter(s => !isShotBound(s));
                  // 2026-05-25 — every cast must have a canonical display_name
                  // (e.g. "Maya"). This is the single identity token that
                  // anchors every downstream prompt (Opus binder, IGS, KGI,
                  // shot ref sheet). Without it, role-name drift returns.
                  const castRoster = derivedCCC?.cast || [];
                  const castMissingName = castRoster.filter(c => !(c.display_name || '').trim());
                  const missingNamesBlock = castRoster.length > 0 && castMissingName.length > 0;
                  // 2026-05-29 (Rohit) — Approve must ALSO require the operator
                  // to have clicked the green "Confirm / Re-confirm" cast tiering
                  // button. Until `project.cast_confirmed=true`, the tier map
                  // is provisional and Primary/Secondary filters downstream
                  // can't be trusted. The Re-confirm button exists for a reason.
                  const castConfirmed = !!project?.cast_confirmed;
                  const castTieringBlock = castRoster.length > 0 && !castConfirmed;
                  // STAGE-GATE (2026-07-19): unresolved binding blockers close the
                  // door (ambiguous costume, undecided corner). Secondary
                  // text-wardrobe advisories are NOT blockers and never appear here.
                  const bindingBlocked = !!(bindingReadiness && bindingReadiness.ready === false);
                  const bindingOutstanding = (bindingReadiness && bindingReadiness.outstanding) || [];
                  // Honest-but-overridable: a binding warning only BLOCKS while the
                  // operator hasn't acknowledged it. Once acknowledged, it stays
                  // visible as advisory but no longer locks the door.
                  const bindingBlocks = bindingBlocked && !bindingOverride;
                  const stage2Complete = shots.length > 0 && missingPrompt.length === 0 && missingDop.length === 0 && missingAssets.length === 0 && !missingNamesBlock && !castTieringBlock && !bindingBlocks;
                  const reasons = [];
                  if (shots.length === 0) reasons.push('Generate shot breakdown first');
                  if (missingPrompt.length) reasons.push(`${missingPrompt.length} shot${missingPrompt.length === 1 ? '' : 's'} missing Director's Prompt`);
                  if (missingDop.length) reasons.push(`${missingDop.length} shot${missingDop.length === 1 ? '' : 's'} missing DOP Spec`);
                  if (missingAssets.length) reasons.push(`${missingAssets.length} shot${missingAssets.length === 1 ? '' : 's'} missing Asset bindings`);
                  if (missingNamesBlock) reasons.push(`${castMissingName.length} cast member${castMissingName.length === 1 ? '' : 's'} missing canonical name (e.g. Maya) — needed to lock identity across all sketches`);
                  if (castTieringBlock) reasons.push(`Cast tiering not confirmed — click the green Confirm / Re-confirm button at the Cast list to lock Primary vs Secondary`);
                  // Per-shot binding blockers, honest and specific (SH09: Arjun costume unruled · SH31: corner undecided)
                  if (bindingBlocks) {
                    bindingOutstanding.forEach(o => {
                      (o.issues || []).forEach(iss => reasons.push(`${o.shot}: ${iss}`));
                    });
                  }
                  const tooltip = stage2Complete ? 'Approve & move to Asset Bible' : `Incomplete:\n• ${reasons.join('\n• ')}`;
                  const hasPending = (missingPrompt.length + missingDop.length + missingAssets.length) > 0;
                  const _promptsProc = !!project?.prompts_processing;
                  const _dopsProc = !!project?.dop_specs_processing;
                  const _bindingsProc = !!project?.bindings_processing;
                  const _anyProc = _promptsProc || _dopsProc || _bindingsProc;
                  return (
                    <div className="flex flex-col items-end gap-1 mt-3 pt-2 border-t border-ai-border">
                      {!stage2Complete && reasons.length > 0 && (
                        <p data-testid="approve-shots-blocker" className="text-[9px] text-amber-300/70 italic">
                          {reasons.slice(0, 3).join(' · ')}
                        </p>
                      )}
                      {/* Binding warnings are honest but OVERRIDABLE — the CD takes
                          the call, they never hard-lock the stage (2026-07-20). */}
                      {bindingBlocked && (
                        <button
                          type="button"
                          onClick={toggleBindingOverride}
                          data-testid="binding-override-toggle"
                          title={bindingOverride
                            ? 'Binding warnings acknowledged — click to re-enable the block'
                            : `${bindingReadiness?.blocked_shots || 0} shot(s) have binding warnings:\n• ${bindingOutstanding.slice(0, 8).map(o => `${o.shot}: ${(o.issues || []).join(', ')}`).join('\n• ')}\n\nClick to acknowledge and approve anyway.`}
                          className={`px-2.5 py-1 rounded-lg text-[9px] font-medium border transition-colors flex items-center gap-1.5 ${
                            bindingOverride
                              ? 'bg-emerald-500/10 text-emerald-300/80 border-emerald-500/30 hover:bg-emerald-500/20'
                              : 'bg-amber-500/10 text-amber-200 border-amber-500/40 hover:bg-amber-500/20'
                          }`}
                        >
                          {bindingOverride ? (
                            <><Check className="w-3 h-3 shrink-0" /> Binding warnings acknowledged — proceeding</>
                          ) : (
                            <><AlertTriangle className="w-3 h-3 shrink-0" /> {bindingReadiness?.blocked_shots || 0} binding warning{(bindingReadiness?.blocked_shots || 0) === 1 ? '' : 's'} — review & approve anyway</>
                          )}
                        </button>
                      )}
                      <div className="flex items-center gap-2 ml-auto">
                        {hasPending && shots.length > 0 && (
                          <button
                            type="button"
                            onClick={generateAllPending}
                            disabled={generatingPending || _anyProc}
                            title={`Fire the full Stage 2 chain (Director's Prompt → DOP Spec + Asset bindings) for every shot that's still missing one. Idempotent — already-populated shots are skipped.`}
                            data-testid="generate-all-pending-btn"
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 whitespace-nowrap border ${
                              generatingPending || _anyProc
                                ? 'bg-amber-500/10 text-amber-300/50 border-amber-500/20 cursor-wait'
                                : 'bg-amber-500/15 text-amber-200 border-amber-500/40 hover:bg-amber-500/25 hover:border-amber-400 active:scale-95 cursor-pointer'
                            }`}
                          >
                            {generatingPending || _anyProc ? (
                              <Loader2 className="w-3 h-3 shrink-0 animate-spin" />
                            ) : (
                              <Zap className="w-3 h-3 shrink-0" />
                            )}
                            <span>
                              {generatingPending
                                ? 'Queuing…'
                                : _anyProc
                                  ? 'Generating…'
                                  : 'Generate all pending'}
                            </span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowImageBoardModal(true)}
                          disabled={!stage2Complete}
                          title={stage2Complete
                            ? 'Generate a 3×3 contact-sheet image-board of every shot for framing & composition review (gpt-image-2)'
                            : 'Image Board unlocks once every shot has Director\'s Prompt + DOP Spec + Asset bindings'}
                          data-testid="image-board-open-btn"
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 whitespace-nowrap border ${
                            stage2Complete
                              ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/25 hover:border-emerald-400 active:scale-95 cursor-pointer'
                              : 'bg-white/[0.04] text-white/30 border-white/10 cursor-not-allowed'
                          }`}
                        >
                          <Camera className="w-3 h-3 shrink-0" />
                          <span className="hidden sm:inline">Image Board</span>
                          <span className="sm:hidden">Board</span>
                        </button>
                        <button
                          onClick={stage2Complete ? advanceStage : undefined}
                          disabled={!stage2Complete}
                          title={tooltip}
                          data-testid="approve-shots-btn"
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1 whitespace-nowrap ${
                            stage2Complete
                              ? 'bg-ai-accent text-white hover:bg-ai-accent/90 active:scale-95 cursor-pointer'
                              : 'bg-white/[0.06] text-white/30 border border-white/10 cursor-not-allowed'
                          }`}
                        >
                          <span className="hidden sm:inline">Approved, Move to Next Stage</span>
                          <span className="sm:hidden">Approve &amp; Move</span>
                          <ArrowRight className="w-3 h-3 shrink-0" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 1.5 LOOK & FEEL (own gated step, CD placement ruling 2026-07-15)
            Between Shot Breakdown and Crew Pre-Production. Locked until the
            breakdown is approved (scenes defined); never empty rows. */}
        {effectiveStage >= 3 && (
          <CollapsibleSection
            title="1.5 Look & Feel"
            icon={<div className="w-7 h-7 rounded-lg bg-fuchsia-500/10 flex items-center justify-center"><Palette className="w-3.5 h-3.5 text-fuchsia-400" /></div>}
            expanded={expandedSections.lookFeel}
            onToggle={() => toggleSection('lookFeel')}
            testId="look-feel-step"
          >
            <SceneLookFeel projectId={projectId} shots={shots} />
          </CollapsibleSection>
        )}

        {/* ── 2.5 CREW PRE-PRODUCTION (Stage 2.5) ────────────────────────
            Three editable HOD briefs (Casting Director, Costume Stylist,
            Art Director) generated by Opus 4.7 in parallel. Each brief
            additively layers into the Stage 3 image-gen prompts —
            empty briefs preserve the original behaviour (zero
            regression for projects that skip 2.5).
            Sequential contract: any brief edit clears
            `project.briefs_approved_at`; Stage 3 generate button stays
            disabled until briefs are approved. */}
        {effectiveStage >= 3 && (
          <CollapsibleSection
            title="2. Crew Pre-Production"
            icon={<div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-amber-400" /></div>}
            badge={(() => {
              const cnt = crewBriefCounts || { cast: 0, costume: 0, location: 0 };
              const total = cnt.cast + cnt.costume + cnt.location;
              const approved = !!project?.briefs_approved_at;
              if (approved) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E] flex items-center gap-1"><Check className="w-3 h-3" />Approved</span>;
              if (total > 0) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300">{total} briefs · unapproved</span>;
              return null;
            })()}
            expanded={expandedSections.crewPreprod}
            onToggle={() => toggleSection('crewPreprod')}
            testId="crew-preprod-section"
          >
            <p className="text-xs text-white/60 mb-3">
              Casting Director, Costume Stylist & Art Director — three Director AI specialists draft <b>lived-in, culturally-grounded briefs</b> for every cast / costume / location.
              These briefs layer into the Asset Bible image prompts to kill generic IKEA / stock-photo aesthetics.
              <br /><span className="text-amber-400/80">Optional — skip to use the default prompts.</span>
            </p>
            <BrandDNAPanel projectId={projectId} project={project} onAttached={() => { try { loadProjectData(); } catch {} }} />
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <ActionButton
                variant="primary"
                icon={Sparkles}
                loading={crewPreprodProcessing}
                disabled={crewPreprodProcessing}
                onClick={async () => {
                  try {
                    setCrewPreprodProcessing(true);
                    await axios.post(`${API}/projects/${projectId}/crew-preprod/generate`);
                    toast.info('Generating crew briefs — Casting + Costume + Art Director running in parallel…');
                  } catch (e) {
                    toast.error('Generate failed: ' + (e.response?.data?.detail || e.message));
                    setCrewPreprodProcessing(false);
                  }
                }}
                data-testid="generate-crew-briefs-btn"
              >
                {(crewBriefCounts && (crewBriefCounts.cast + crewBriefCounts.costume + crewBriefCounts.location) > 0)
                  ? 'Regenerate All Briefs' : 'Generate Crew Briefs'}
              </ActionButton>
              {project?.briefs_approved_at ? (
                <span className="text-[10px] text-emerald-400 flex items-center gap-1.5">
                  <Check className="w-3 h-3" /> Approved {new Date(project.briefs_approved_at).toLocaleString()}
                </span>
              ) : (crewBriefCounts && (crewBriefCounts.cast + crewBriefCounts.costume + crewBriefCounts.location) > 0) ? (
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/projects/${projectId}/crew-preprod/approve`);
                      toast.success('Briefs approved — you can now generate the Asset Bible');
                      await loadProjectData();
                    } catch (e) {
                      toast.error('Approve failed: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30 active:scale-95 transition-all flex items-center gap-1.5"
                  data-testid="approve-crew-briefs-btn"
                  title="Approve briefs to enable Asset Bible generation"
                >
                  <Check className="w-3.5 h-3.5" /> Approve Briefs
                </button>
              ) : null}
              {/* 2026-06-25 — Bulk Brief Preview button. Sits next to
                  Approve Briefs (or alone if already approved) and fires
                  gpt-image-2 LOW for every brief that doesn't have a
                  preview yet, throttled to 3 concurrent calls. */}
              {(crewBriefCounts && (crewBriefCounts.cast + crewBriefCounts.costume + crewBriefCounts.location) > 0) && (
                <button
                  onClick={generateAllBriefPreviews}
                  disabled={bulkPreviewing}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 border border-sky-500/30 active:scale-95 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="generate-all-previews-btn"
                  title="Render a gpt-image-2 thumbnail for every brief that doesn't have one yet (low quality · ~$0.006 each)"
                >
                  {bulkPreviewing
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Previews {bulkPreviewProgress.done}/{bulkPreviewProgress.total}{bulkPreviewProgress.failed ? ` · ${bulkPreviewProgress.failed} failed` : ''}</>
                    : <><Sparkles className="w-3.5 h-3.5" /> Generate All Previews</>}
                </button>
              )}
            </div>

            {crewBriefCounts && (crewBriefCounts.cast + crewBriefCounts.costume + crewBriefCounts.location) > 0 && (() => {
              // 2026-05-26 — Render the three Opus specialists side-by-side
              // as a 3-column table. Each column scrolls independently so
              // long brief lists in one role don't push the others off-screen.
              const cols = [
                { kind: 'cast',    label: 'Casting Director',  briefField: 'casting_brief',        arrField: 'cast_candidates',    accent: 'amber',   testId: 'crew-col-cast' },
                { kind: 'costume', label: 'Costume Stylist',   briefField: 'styling_brief',        arrField: 'costume_candidates', accent: 'rose',    testId: 'crew-col-costume' },
                { kind: 'corner',  label: 'Art Director',      briefField: 'art_direction_brief',  arrField: 'corner_candidates',  testId: 'crew-col-corner', accent: 'sky' },
              ];
              const accentClasses = {
                amber: { text: 'text-amber-300', bg: 'bg-amber-500/5',  border: 'border-amber-500/20', headerBg: 'bg-amber-500/10' },
                rose:  { text: 'text-rose-300',  bg: 'bg-rose-500/5',   border: 'border-rose-500/20',  headerBg: 'bg-rose-500/10' },
                sky:   { text: 'text-sky-300',   bg: 'bg-sky-500/5',    border: 'border-sky-500/20',   headerBg: 'bg-sky-500/10' },
              };
              return (
                <div
                  className="grid grid-cols-1 lg:grid-cols-3 gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3"
                  data-testid="crew-briefs-table"
                >
                  {cols.map(col => {
                    const items = (assetBible?.[col.arrField] || []).filter(it => (it?.[col.briefField] || '').trim());
                    const a = accentClasses[col.accent];
                    return (
                      <div
                        key={col.kind}
                        className={`flex flex-col rounded-lg border ${a.border} ${a.bg} overflow-hidden`}
                        data-testid={col.testId}
                      >
                        <div className={`flex items-center justify-between px-3 py-2 ${a.headerBg} border-b ${a.border}`}>
                          <h4 className={`text-[11px] font-semibold ${a.text} uppercase tracking-wider flex items-center gap-1.5`}>
                            <Sparkles className="w-3 h-3" /> {col.label}
                          </h4>
                          <span className={`text-[10px] font-mono ${a.text} px-1.5 py-0.5 rounded bg-black/30`}>
                            {items.length}
                          </span>
                        </div>
                        <div className="flex-1 overflow-y-auto max-h-[640px] p-2 space-y-2">
                          {items.length === 0 ? (
                            <div className="text-[11px] text-white/30 italic px-2 py-4 text-center">
                              No {col.label.toLowerCase()} briefs yet.
                            </div>
                          ) : (
                            items.map(item => (
                              <CrewBriefEditor
                                key={item.id}
                                projectId={projectId}
                                kind={col.kind}
                                asset={item}
                                briefField={col.briefField}
                                onSaved={loadProjectData}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CollapsibleSection>
        )}

        {/* ── 3. ASSET BIBLE — gated on explicit Stage 2 approval ──
            Per Rohit (2026-05-18): Asset Bible section only unlocks AFTER
            the user clicks "Approved, Move to Next Stage" in Stage 2
            (current_stage flips from 2 → 3). Mere existence of shots is
            no longer enough — the director's explicit hand-off gesture
            is required. */}
        {effectiveStage >= 3 && (
          <CollapsibleSection
            title="3. Asset Bible"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/10 flex items-center justify-center"><Users className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={(() => {
              // 2026-06-17 — Stale Asset Bible detection (EasyBuy bug).
              // The bible is "Generated" only when its shot-map fingerprint
              // still matches the live shot map AND no shots are missing
              // Director's Prompts. Otherwise it's "stale" — the operator
              // grew/changed the breakdown after the bible rendered and the
              // cast/costume/location cards no longer cover every shot.
              if (!hasAssets) return null;
              const stampedCount = assetBible?.generated_at_shot_count;
              const liveCount = shots.length;
              const missingPrompts = shots.filter(
                (s) => !((s.keyframe_prompt || s.mj_prompt || '').trim())
              ).length;
              const isStale = (
                (typeof stampedCount === 'number' && stampedCount !== liveCount)
                || missingPrompts > 0
              );
              const lockedBadge = assetBible?.locked && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E] flex items-center gap-1">
                  <Check className="w-3 h-3" />Locked
                </span>
              );
              if (isStale) {
                const reasonParts = [];
                if (typeof stampedCount === 'number' && stampedCount !== liveCount) {
                  reasonParts.push(`shot map ${stampedCount} → ${liveCount}`);
                }
                if (missingPrompts > 0) {
                  reasonParts.push(`${missingPrompts} prompts missing`);
                }
                return (
                  <>
                    <span
                      data-testid="asset-bible-stale-badge"
                      title={`Asset Bible was generated against an older shot map. Reason: ${reasonParts.join(' · ')}. Regenerate once all Director's Prompts are filled.`}
                      className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1 cursor-help"
                    >
                      <AlertTriangle className="w-3 h-3" />Stale · {reasonParts[0]}
                    </span>
                    {lockedBadge}
                  </>
                );
              }
              return (
                <>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">Generated</span>
                  {lockedBadge}
                </>
              );
            })()}
            expanded={expandedSections.assets}
            onToggle={() => toggleSection('assets')}
            testId="assets-section"
            rightContent={hasAssets && (
              <button onClick={() => downloadFile('ppm')} data-testid="download-ppm-inline"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ai-accent bg-ai-accent/5 rounded-lg hover:bg-ai-accent/10 transition-colors">
                {downloading === 'ppm' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PPM .pdf
              </button>
            )}
          >
            <p className="text-xs text-white/60 mb-3">Generate cast, costume, and location references from your script.</p>
            <div className="flex items-center gap-2 mb-4 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
              <ActionButton
                variant="primary"
                icon={Users}
                loading={uploadingAction === 'assets'}
                disabled={uploadingAction === 'assets'}
                onClick={() => generateAssets(false, 'basic')}
                data-testid="generate-assets-btn"
                title={hasAssets
                  ? "Regenerate Asset Bible from script only (ignores Crew Pre-Production refs)."
                  : "Generate Asset Bible from script only. Always available — your safe path even when Crew PP is incomplete."}
              >
                {hasAssets ? 'Regenerate Assets' : 'Generate Asset Bible'}
              </ActionButton>
              {/* 2026-06-16: PREMIUM path — uses Crew Pre-Production
                  reference uploads as identity anchors (cast face,
                  costume garment, location plate). Lights up blue only
                  when Crew PP has approved briefs (the same gate that
                  used to block the SINGLE button — now isolated to
                  this premium path only). */}
              {(crewBriefCounts && (crewBriefCounts.cast + crewBriefCounts.costume + crewBriefCounts.location) > 0) && (() => {
                const crewppReady = !!project?.briefs_approved_at;
                return (
                  <ActionButton
                    variant={crewppReady ? "primary" : "outline"}
                    icon={Sparkles}
                    loading={false}
                    disabled={uploadingAction === 'assets' || !crewppReady}
                    onClick={() => generateAssets(false, 'crewpp')}
                    data-testid="generate-assets-from-crewpp-btn"
                    title={crewppReady
                      ? "Generate Asset Bible using your Crew Pre-Production briefs AND uploaded reference photos as identity anchors. Produces identity-locked outputs (real faces, real garments, real locations)."
                      : "Complete Crew Pre-Production above — generate briefs, then Approve Briefs — to unlock this enhanced path. The basic button on the left always works in the meantime."}
                    className={crewppReady
                      ? "!bg-violet-600 hover:!bg-violet-500 !border-violet-500"
                      : ""}
                  >
                    {hasAssets ? 'Regen from Crew PP' : 'Generate from Crew PP'}
                  </ActionButton>
                );
              })()}
              {/* SAFETY NET (2026-06-16): when EITHER button is in
                  loading state but no progress events have arrived, show
                  the "Stuck? Reset gen" link. */}
              {uploadingAction === 'assets' && !assetProgress && (
                <button
                  onClick={async () => {
                    const ok = window.confirm(
                      'No progress detected. Reset the stuck Asset Bible generation flag? '
                      + 'This is safe — it just clears the "in progress" marker so you can start a fresh run.'
                    );
                    if (!ok) return;
                    try {
                      await axios.post(
                        `${API}/projects/${projectId}/reset-processing`,
                        { flag: 'assets_processing' },
                      );
                      toast.success('Asset gen flag reset — button unlocked.');
                      if (assetPollRef.current) {
                        clearInterval(assetPollRef.current);
                        assetPollRef.current = null;
                      }
                      setUploadingAction('');
                      setUploading(false);
                      setAssetProgress(null);
                      await loadProjectData();
                    } catch (e) {
                      toast.error('Reset failed: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="text-[11px] text-amber-400/80 hover:text-amber-300 underline underline-offset-2 px-2 py-1"
                  title="If the generation looks frozen — no progress for more than a minute — click to clear the stuck flag and try again."
                  data-testid="reset-stuck-asset-gen-btn"
                >
                  ⚠ Stuck? Reset gen
                </button>
              )}
              {(() => {
                const failedAssetCount =
                  (assetBible?.cast_candidates || []).filter(c => c?.status === 'failed').length +
                  (assetBible?.costume_candidates || []).filter(c => c?.status === 'failed').length +
                  (assetBible?.corner_candidates || []).filter(c => c?.status === 'failed').length;
                return failedAssetCount > 0 && !assetProgress ? (
                  <ActionButton
                    variant="secondary"
                    icon={RefreshCw}
                    loading={uploadingAction === 'assets'}
                    disabled={uploadingAction === 'assets'}
                    onClick={() => generateAssets(true)}
                    data-testid="retry-failed-assets-btn"
                  >
                    Retry failed ({failedAssetCount})
                  </ActionButton>
                ) : null;
              })()}
              {hasAssets && !assetProgress && (
                <ActionButton
                  variant="secondary"
                  icon={Wand2}
                  onClick={() => setBulkIterateOpen(true)}
                  data-testid="open-bulk-iterate-btn"
                >
                  Bulk Iterate
                </ActionButton>
              )}
            </div>
            {assetProgress && <ProgressBar label="Generating asset bible..." progress={assetProgress} color="#0EA5E9" onStop={stopGeneration} taskType="assets" />}
            {/* Placeholder tiles — render ONLY after cast tiering is confirmed.
                Per Rohit (2026-05-18): "DO NOT Load placeholder tile even until
                the user confirms detection from script." Stubs cost real $$ on
                generation and confuse the asset count. Locations are
                un-tiered so they render for every detected setup; cast +
                costume tiles are filtered to Primary-only (matches backend
                generation filter at server.py L14894). */}
            {!hasAssets && shots.length > 0 && castTiering?.confirmed && !castTiering?.needs_reconfirm && (() => {
              // Build the Primary-only cast key set from confirmed tiering.
              // Normalization mirrors backend `_norm_cast_key`: lowercase,
              // strip trailing parentheticals, collapse whitespace.
              const _norm = (s) => (s || '').toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+/g, ' ').trim();
              const primaryKeys = new Set(
                (castTiering?.cast || [])
                  .filter(c => c.tier === 'primary')
                  .map(c => c.key || _norm(c.name))
              );
              const isPrimary = (name) => primaryKeys.has(_norm(name));
              // Setup names from shots (locations are NOT tier-filtered)
              const setupNames = new Set();
              shots.forEach(s => {
                if (s.setup_name) setupNames.add(s.setup_name);
                else if (s.setup_id) setupNames.add(`Setup ${s.setup_id}`);
              });
              if (setupNames.size === 0) setupNames.add('Primary Location');
              // Filter cast + costumes to Primary only
              const castList = (derivedCCC?.cast || []).filter(c => isPrimary(c.name)).map(c => c.name);
              const costumeList = (derivedCCC?.costumes || []).filter(c => isPrimary(c.character_name)).map(c => c.character_name);
              const locationList = derivedCCC?.locations?.length > 0 ? derivedCCC.locations.map(l => l.name) : [...setupNames];
              // Get any already-generated assets from polling
              const genCast = assetBible?.cast_candidates || [];
              const genCostume = assetBible?.costume_candidates || [];
              const genCorner = assetBible?.corner_candidates || [];
              const isGenerating = !!assetProgress;
              return (
                <div className="space-y-4 mt-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-ai-accent" /><h4 className="font-medium text-xs">Cast ({genCast.length}/{castList.length})</h4></div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {castList.map((name, i) => {
                        const generated = genCast.find(c => c.name?.toLowerCase() === name.toLowerCase()) || genCast[i];
                        if (generated?.url) return (
                          <div key={i} className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-white/[0.02] relative">
                            <img src={generated.url.startsWith('http') ? generated.url : `${window.__API_BASE}${generated.url}`} alt={name} className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5"><span className="text-[9px] text-white truncate block">{generated.name || name}</span></div>
                          </div>
                        );
                        return (
                          <div key={i} className="aspect-square rounded-lg border border-dashed border-white/20 bg-white/[0.02] flex flex-col items-center justify-center p-2 opacity-50">
                            {isGenerating ? <Loader2 className="w-5 h-5 text-ai-accent animate-spin mb-1" /> : <Users className="w-6 h-6 text-white/20 mb-1" />}
                            <span className="text-[10px] text-white/40 text-center truncate w-full">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2"><Shirt className="w-4 h-4 text-[#FF2D55]" /><h4 className="font-medium text-xs">Costumes ({genCostume.length}/{costumeList.length})</h4></div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {costumeList.map((name, i) => {
                        const generated = genCostume.find(c => c.name?.toLowerCase() === name.toLowerCase()) || genCostume[i];
                        if (generated?.url) return (
                          <div key={i} className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-white/[0.02] relative">
                            <img src={generated.url.startsWith('http') ? generated.url : `${window.__API_BASE}${generated.url}`} alt={name} className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5"><span className="text-[9px] text-white truncate block">{generated.name || name}</span></div>
                          </div>
                        );
                        return (
                          <div key={i} className="aspect-square rounded-lg border border-dashed border-white/20 bg-white/[0.02] flex flex-col items-center justify-center p-2 opacity-50">
                            {isGenerating ? <Loader2 className="w-5 h-5 text-[#FF2D55] animate-spin mb-1" /> : <Shirt className="w-6 h-6 text-white/20 mb-1" />}
                            <span className="text-[10px] text-white/40 text-center truncate w-full">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2"><MapPin className="w-4 h-4 text-[#FF9500]" /><h4 className="font-medium text-xs">Locations ({genCorner.length}/{locationList.length})</h4></div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {locationList.map((name, i) => {
                        const generated = genCorner.find(c => c.name?.toLowerCase() === name.toLowerCase()) || genCorner[i];
                        if (generated?.url) return (
                          <div key={i} className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-white/[0.02] relative">
                            <img src={generated.url.startsWith('http') ? generated.url : `${window.__API_BASE}${generated.url}`} alt={name} className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5"><span className="text-[9px] text-white truncate block">{generated.name || name}</span></div>
                          </div>
                        );
                        return (
                          <div key={i} className="aspect-square rounded-lg border border-dashed border-white/20 bg-white/[0.02] flex flex-col items-center justify-center p-2 opacity-50">
                            {isGenerating ? <Loader2 className="w-5 h-5 text-[#FF9500] animate-spin mb-1" /> : <MapPin className="w-6 h-6 text-white/20 mb-1" />}
                            <span className="text-[10px] text-white/40 text-center truncate w-full">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {!isGenerating && <p className="text-[10px] text-white/30 text-center">Click "Generate Asset Bible" to populate these tiles</p>}
                </div>
              );
            })()}
            {/* Pre-confirm CTA — replaces placeholder grid until cast tiering
                is confirmed. Per Rohit (2026-05-18): no placeholder tiles
                until user confirms cast detection. */}
            {!hasAssets && shots.length > 0 && (!castTiering?.confirmed || castTiering?.needs_reconfirm) && (
              <div className="mt-4 p-4 rounded-xl border border-dashed border-ai-border bg-white/[0.02] text-center space-y-1.5" data-testid="asset-bible-precommit-cta">
                <Users className="w-5 h-5 text-white/30 mx-auto" />
                <p className="text-[11px] text-white/60 font-medium">
                  {castTiering?.needs_reconfirm ? 'Re-confirm cast tiering above' : 'Confirm cast tiering above'}
                </p>
                <p className="text-[10px] text-white/40">
                  Asset Bible placeholders appear here once you confirm Primary vs Secondary cast. Cast + costume flat-lays render only for <span className="text-ai-accent">Primary</span>; locations render for every detected setup.
                </p>
              </div>
            )}
            {hasAssets && (
              <div className="space-y-6 mt-4">
                {[
                  { label: 'Cast', field: 'cast_candidates', icon: <Users className="w-4 h-4 text-ai-accent" /> },
                  { label: 'Costumes', field: 'costume_candidates', icon: <Shirt className="w-4 h-4 text-[#FF2D55]" /> },
                  { label: 'Location', field: 'corner_candidates', icon: <MapPin className="w-4 h-4 text-[#FF9500]" /> },
                ].map(({ label, field, icon }) => {
                  const candidates = assetBible?.[field] || [];
                  
                  // For empty categories, show generate + upload options
                  if (candidates.length === 0) {
                    return (
                      <div key={field}>
                        <div className="flex items-center gap-2 mb-2">{icon}<h4 className="font-medium text-xs">{label} (0)</h4></div>
                        <div className="p-4 rounded-xl border border-dashed border-ai-border bg-white/[0.02] space-y-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => generateAssets(false)}
                              disabled={uploadingAction === 'assets'}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-xs font-medium bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20 border border-ai-accent/20 transition-all disabled:opacity-50"
                              data-testid={`generate-empty-${field}`}
                            >
                              {uploadingAction === 'assets' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                              Generate with AI
                            </button>
                            <label className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-xs font-medium bg-white/[0.03] text-white/60 hover:bg-white/[0.06] border border-white/10 cursor-pointer transition-all" data-testid={`upload-empty-${field}`}>
                              <UploadCloud className="w-3.5 h-3.5" /> Upload Reference
                              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const form = new FormData();
                                form.append('category', field);
                                form.append('file', file);
                                try {
                                  await axios.post(`${API}/projects/${projectId}/asset-upload-direct`, form);
                                  toast.success(`${label} asset uploaded`);
                                  loadProjectData();
                                } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
                              }} />
                            </label>
                          </div>
                          <p className="text-[10px] text-white/30 text-center">Click "Generate with AI" to create {label.toLowerCase()} or upload your own reference images</p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Costumes — single unified grid (costume flat-lays only).
                  // 2026-05-18 (Rohit): removed the "Lifestyle References" split
                  // entirely. The flat-lay/lifestyle heuristic (prompt-based)
                  // was bucketing stubs with empty prompts into a "Lifestyle"
                  // sub-section, surfacing as ghost "Anya · Anya · Anya · Hari"
                  // duplicates while the real flat-lays were still rendering.
                  // Costume flat-lays are the canonical wardrobe artefact —
                  // lifestyle composites belong in the Lookbook panel below.
                  if (field === 'costume_candidates') {
                    return (
                      <div key={field}>
                        <div className="flex items-center gap-2 mb-2">{icon}<h4 className="font-medium text-xs">{label} ({candidates.length})</h4></div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {candidates.map((c, idx) => (
                            <CastAssetWithTpose key={c.id || idx} candidate={c} label={`${label} ${idx+1}`}
                              field={field} idx={idx} projectId={projectId} onUpdate={loadProjectData}
                              onImageClick={(url, alt) => setGlobalLightbox({ url, alt })} brandDeck={project?.brand_deck || []}
                              assetBible={assetBible} />
                          ))}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={field}>
                      <div className="flex items-center gap-2 mb-2">{icon}<h4 className="font-medium text-xs">{label} ({candidates.length})</h4></div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {candidates.map((c, idx) => (
                          <div key={c.id || idx} className="relative">
                            <CastAssetWithTpose candidate={c} label={`${label} ${idx+1}`}
                              field={field} idx={idx} projectId={projectId} onUpdate={loadProjectData}
                              onImageClick={(url, alt) => setGlobalLightbox({ url, alt })} brandDeck={project?.brand_deck || []}
                              assetBible={assetBible} />
                            {/* 3D Button — temporarily hidden (Rohit, 2026-05-13).
                                See SketchCard rationale. Re-enable when fixed. */}
                            {false && field === 'corner_candidates' && c.url && (
                              <button
                                onClick={async () => {
                                  if (c.world_3d_id) {
                                    // Already has 3D — open viewer
                                    if (window.confirm('This will open the 3D viewer on World Labs.\n\nA free World Labs account is needed to explore.\n\nContinue?')) {
                                      window.open(`https://marble.worldlabs.ai/world/${c.world_3d_id}`, '_blank');
                                    }
                                    return;
                                  }
                                  toast.info('Generating 3D environment — this takes 15-30 seconds...');
                                  try {
                                    // Use a virtual shot ID based on the asset index
                                    const virtualShotId = `corner_${idx}_${projectId.substring(0,8)}`;
                                    await axios.post(`${API}/projects/${projectId}/generate-corner-3d`, {
                                      corner_index: idx,
                                      image_url: c.url,
                                      name: c.name || `Location ${idx+1}`,
                                    });
                                    // Poll for completion
                                    const poll = setInterval(async () => {
                                      try {
                                        const res = await axios.get(`${API}/projects/${projectId}/corner-3d-status/${idx}`);
                                        if (res.data.has_3d) {
                                          clearInterval(poll);
                                          toast.success('3D environment ready!');
                                          loadProjectData();
                                        } else if (!res.data.processing && res.data.error) {
                                          clearInterval(poll);
                                          toast.error('3D failed: ' + res.data.error);
                                        }
                                      } catch(e) {}
                                    }, 4000);
                                    setTimeout(() => clearInterval(poll), 300000);
                                  } catch(e) {
                                    toast.error('3D generation failed: ' + (e.response?.data?.detail || e.message));
                                  }
                                }}
                                className={`absolute bottom-2 right-2 z-20 px-2 py-1 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 shadow-md ${
                                  c.world_3d_id ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-black/60 text-white/80 hover:bg-black/80 border border-white/20'
                                }`}
                                data-testid={`corner-3d-${idx}`}
                              >
                                <Box className="w-3 h-3" /> 3D
                                {c.world_3d_id && <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-0.5" />}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {/* ── Product card (single, full-width, two-column).
                 *  Surfaces project.product_name / product_features /
                 *  product_sketch_url as a first-class asset bible entry
                 *  so the verbose anchor + SKU image flow downstream is
                 *  user-visible. Auditable in one glance: "what I uploaded
                 *  vs what the prompt will send." */}
                {(() => {
                  const pname = project?.product_name || '';
                  const pfeatures = project?.product_features || '';
                  const pimg = project?.product_sketch_url || '';
                  if (!pname && !pfeatures && !pimg) return null;
                  const isSynthesized = !!project?.product_image_synthesized;
                  return (
                    <div data-testid="asset-bible-product-card">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-ai-accent" />
                        <h4 className="font-medium text-xs">Product (1)</h4>
                      </div>
                      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] flex flex-col sm:flex-row gap-4">
                        {/* LEFT — image preview ~40% */}
                        <div className="sm:w-2/5 shrink-0">
                          <div className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-white/[0.03] relative">
                            {pimg ? (
                              <>
                                <img
                                  src={pimg}
                                  alt={pname || 'Product'}
                                  className="w-full h-full object-cover cursor-pointer"
                                  onClick={() => setGlobalLightbox({ url: pimg, alt: pname || 'Product' })}
                                  data-testid="product-image-preview"
                                />
                                {isSynthesized && (
                                  <div className="absolute top-2 left-2 px-1 py-px rounded-sm text-[7px] font-bold uppercase shadow-sm bg-white/15 text-white/80 backdrop-blur-sm">
                                    AI · synthetic
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center p-3 opacity-60">
                                <Package className="w-10 h-10 text-white/20 mb-2" />
                                <span className="text-[10px] text-white/40 text-center">No reference image — upload or auto-generate from the description.</span>
                              </div>
                            )}
                          </div>
                          {isSynthesized && (
                            <p className="text-[9px] text-white/40 mt-1.5 text-center">AI-generated from description — Upload Ref or Iterate to refine.</p>
                          )}
                        </div>
                        {/* RIGHT — name + description + actions ~60% */}
                        <div className="flex-1 min-w-0 flex flex-col">
                          <div className="flex-1 min-h-0">
                            <h5 className="text-base font-semibold text-white truncate" data-testid="product-name">{pname || <span className="text-white/40 italic">Untitled product</span>}</h5>
                            {pfeatures ? (
                              <div className="mt-2 text-xs text-white/70 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto pr-1" data-testid="product-description">{pfeatures}</div>
                            ) : (
                              <p className="mt-2 text-xs text-white/40 italic">No description yet — add features at Script stage to drive the downstream visual register.</p>
                            )}
                            {/* Reserved space for future token chips (N-2). */}
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                            <ActionButton
                              variant="secondary"
                              icon={Wand2}
                              onClick={() => {
                                // Minimal iterate-via-prompt — opens a window prompt
                                // and posts to /product/iterate. Endpoint stub will
                                // be added in next session per brief F.
                                const txt = window.prompt('Describe how the product reference should change (e.g., "make the label crisper, increase blue saturation by 10%").');
                                if (!txt || !txt.trim()) return;
                                toast.info('Iteration endpoint ships in session 2 (brief F) — text saved: ' + txt.slice(0, 60));
                              }}
                              data-testid="product-iterate"
                              title="Refine the product image with a text prompt"
                            >
                              Iterate
                            </ActionButton>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid="product-upload-ref"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const form = new FormData();
                                  form.append('file', file);
                                  // Reuse the existing /product upload
                                  // endpoint — same one used at Script
                                  // stage. Doesn't touch product_name /
                                  // product_features (those stay as-is).
                                  try {
                                    await axios.post(`${API}/projects/${projectId}/product`, form);
                                    toast.success('Product reference updated');
                                    await loadProjectData();
                                    e.target.value = '';
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Upload failed');
                                  }
                                }}
                              />
                              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-white/70 border border-white/15 hover:bg-white/10 hover:text-white/90 transition-all whitespace-nowrap active:scale-95">
                                <UploadCloud className="w-3.5 h-3.5" />
                                Upload Ref
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}
            {/* Brand Deck — multi-page reference asset uploader.
                Lets the director attach a brand outfit deck (PDF or images) and
                link individual pages to costumes so the Asset Bible costume
                generator produces on-brand wardrobe references instead of
                generic LLM guesses. Sits below the Locations grid and above the
                "Approved" CTA so directors can finalize references before
                advancing to sketches. */}
            <div className="mt-4">
              <BrandDeckPanel
                projectId={projectId}
                costumes={derivedCCC?.costumes || []}
                onLinked={loadProjectData}
              />
            </div>

            {/* Lookbook — "cast in costume on location" cinematic stills.
                Auto-triggered after Asset Bible completes. Sits between
                BrandDeck and the "Approved" CTA so directors can preview
                composited references before advancing to sketches. */}
            <LookbookPanel
              projectId={projectId}
              assetBible={assetBible}
              onChange={loadProjectData}
            />

            {hasAssets && !assetBible?.locked && (() => {
              // 2026-05-25 — Stage 3 commit gate. Operator must explicitly
              // click "Save Assets" before "Approved" turns blue. Any
              // edit (delete tile, regen, derived_ccc save) clears
              // `asset_bible_saved_at` so the operator must re-save.
              // This enforces the sequential-contract principle the
              // operator articulated: Stage 3 state is FROZEN at save
              // time and downstream stages read only that frozen state.
              //
              // 2026-05-29 (Rohit) — Tighten the gate further: if ANY
              // generation flag is in-flight (assets_processing for the
              // Asset Bible itself, lookbook in render, or auto-fired
              // bindings still running), the bible CANNOT be saved nor
              // approved. Bible state is provisional until every async
              // gen settles. The backend already rejects with 409 — this
              // gate prevents the operator from ever needing to see the
              // "Asset generation still in progress" toast.
              const savedAt = project?.asset_bible_saved_at || '';
              const isSaved = !!(savedAt && savedAt.trim());
              const assetsInFlight = !!project?.assets_processing;
              const bindingsInFlight = !!project?.bindings_processing;
              const lookbookInFlight = project?.lookbook_status === 'processing';
              const anyGenInFlight = assetsInFlight || bindingsInFlight || lookbookInFlight;
              const inFlightReasons = [];
              if (assetsInFlight) inFlightReasons.push('Cast / costume / location image generation');
              if (bindingsInFlight) inFlightReasons.push('Asset bindings (linking shots to cast/costume)');
              if (lookbookInFlight) inFlightReasons.push('Lookbook composites');
              const canSave = !anyGenInFlight;
              const canApprove = isSaved && !anyGenInFlight;
              const onSaveAssets = async () => {
                if (!canSave) {
                  toast.info('Wait for asset generation to finish before saving.');
                  return;
                }
                try {
                  await axios.post(`${API}/projects/${projectId}/asset-bible/save`);
                  toast.success('Asset Bible saved — you can now move to sketches');
                  await loadProjectData();
                } catch (e) {
                  toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
                }
              };
              return (
                <div className="flex items-center justify-end gap-3 mt-4 flex-wrap">
                  {anyGenInFlight ? (
                    <span
                      className="text-[10px] text-amber-300 flex items-center gap-1.5"
                      data-testid="asset-bible-gen-in-flight-nudge"
                      title={inFlightReasons.join(' · ')}
                    >
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Asset generation in progress — Save &amp; Approve will unlock when done
                    </span>
                  ) : isSaved ? (
                    <span className="text-[10px] text-emerald-400 flex items-center gap-1.5">
                      <Check className="w-3 h-3" /> Saved {new Date(savedAt).toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-[10px] text-amber-400 flex items-center gap-1.5" data-testid="asset-bible-unsaved-nudge">
                      Unsaved edits — click <b>Save Assets</b> to commit before moving on.
                    </span>
                  )}
                  <button
                    onClick={onSaveAssets}
                    disabled={!canSave}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                      !canSave
                        ? 'bg-white/[0.04] text-white/30 border border-white/10 cursor-not-allowed'
                        : isSaved
                          ? 'bg-white/[0.04] text-white/50 hover:bg-white/[0.08] border border-white/10 active:scale-95'
                          : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30 active:scale-95'
                    }`}
                    data-testid="save-assets-btn"
                    title={
                      !canSave
                        ? `Generation still in-flight: ${inFlightReasons.join(' · ')}. Save unlocks when done.`
                        : 'Commit the current Asset Bible state. Required before moving to Sketches.'
                    }
                  >
                    <Save className="w-3.5 h-3.5" /> {isSaved ? 'Re-save' : 'Save Assets'}
                  </button>
                  <button
                    onClick={canApprove ? advanceStage : undefined}
                    disabled={!canApprove}
                    data-testid="approve-assets-btn"
                    title={
                      anyGenInFlight
                        ? `Generation still in-flight: ${inFlightReasons.join(' · ')}. Approve unlocks when done.`
                        : !isSaved
                          ? 'Click "Save Assets" first to enable this'
                          : 'Proceed to Sketches with the saved Asset Bible'
                    }
                    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                      canApprove
                        ? 'bg-ai-accent/100 text-white hover:bg-ai-accent active:scale-95'
                        : 'bg-ai-accent/20 text-white/30 cursor-not-allowed'
                    }`}
                  >
                    Approved, Move to Next Stage <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })()}
          </CollapsibleSection>
        )}

        {/* ── 3.5 SCENE GEOGRAPHY (Asst CD) — HIDDEN 2026-05-19 per Rohit ──
            "Not a great tool in its current shape and form. We will come
            back to it later." Component + endpoint kept in the codebase
            for the eventual revival; only the section render is gated. */}
        {false && effectiveStage >= 3 && (
          <CollapsibleSection
            title="3.5 Scene Geography"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/15 flex items-center justify-center"><Compass className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={<span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-ai-accent/10 text-ai-accent">Asst CD · Director AI</span>}
            expanded={expandedSections.sceneGeography !== false}
            onToggle={() => toggleSection('sceneGeography')}
            testId="scene-geography-section"
          >
            <SceneGeographyPanel projectId={projectId} currentStage={project.current_stage} />
          </CollapsibleSection>
        )}

        {/* ── 3.5 MOCK SHOOT (Gate for Stage 4+) ──
            Hidden for the owner account (rohit@eipimedia.com) per request —
            the sketch gate is already open for the owner (sketchGateOpen). */}
        {effectiveStage >= 3 && !isOwner && (
          <CollapsibleSection
            title="3.5 Mock Shoot"
            icon={<div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center"><Camera className="w-3.5 h-3.5 text-amber-400" /></div>}
            badge={<span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-300">Gate · Required</span>}
            expanded={expandedSections.mockShoot !== false}
            onToggle={() => toggleSection('mockShoot')}
            testId="mock-shoot-section"
          >
            <MockShootPanel
              projectId={projectId}
              shots={shots}
              currentUser={user}
              orientation={project?.orientation}
              onUnlockChange={(unlocked) => setMockShootUnlocked(!!unlocked)}
            />
          </CollapsibleSection>
        )}

        {/* ── 4. STORYBOARD SKETCHES ── */}
        {effectiveStage >= 2 && (
          <CollapsibleSection
            title="4. Storyboard Sketches"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/15 flex items-center justify-center"><Image className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={hasSketchedShots && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.sketch_url).length}/{shots.length} sketches</span>}
            expanded={expandedSections.sketches}
            onToggle={() => toggleSection('sketches')}
            testId="sketches-section"
            rightContent={hasSketchedShots && (
              <button onClick={() => downloadFile('sketches')} data-testid="download-sketches-inline"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ai-accent bg-ai-accent/5 rounded-lg hover:bg-ai-accent/10 transition-colors">
                {downloading === 'sketches' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} .pdf
              </button>
            )}
          >
            <MockShootLockBanner
              projectId={projectId}
              currentUser={user}
              onGoToMockShoot={() => toggleSection('mockShoot')}
              hidden={isOwner}
            />
            <div className="flex gap-2 mb-4 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
              {!hasAssets ? (
                <div className="w-full p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-sm text-amber-400" data-testid="sketches-need-assets">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Before creating sketches, complete: <b>Asset Bible</b> (Cast, Costume, Environment) <b>and</b> the <b>Mock Shoot</b> stage (upload one image + one video clip per shot).
                </div>
              ) : (
              <>
              {/* Quick Sketches */}
              <ActionButton
                variant="secondary"
                icon={Zap}
                loading={uploadingAction === 'sketches:quick'}
                disabled={uploadingAction?.startsWith('sketches:') || shots.length === 0 || !sketchGateOpen}
                onClick={() => generateSketches(true)}
                size="sm"
                data-testid="generate-sketches-quick-btn"
                title={!sketchGateOpen ? 'Locked — upload mock shoot image + video for every shot first' : 'Generate sketches using shot descriptions (faster)'}
              >
                Quick Sketches
              </ActionButton>
              {/* Diagnose — runs /sketch-readiness on demand so the
                  operator can see "no gates apply but every shot failed
                  with MJ 429" without having to click Generate twice
                  and read the error path. Added 2026-06-22 after a
                  prod report where the user saw nothing happening on
                  click and had no surface to investigate. */}
              <button
                type="button"
                data-testid="diagnose-sketches-btn"
                disabled={uploadingAction?.startsWith('sketches:')}
                onClick={async () => {
                  try {
                    const probe = await axios.get(
                      `${API}/projects/${projectId}/sketch-readiness`);
                    const b = probe.data?.blockers || [];
                    const lr = probe.data?.last_run || {};
                    const head = b.length
                      ? `⚠️ ${b.length} blocker${b.length > 1 ? 's' : ''}:`
                      : '✓ No gates blocking sketch generation.';
                    const blockerLines = b.length
                      ? '\n\n' + b.map(
                          (x) => `• ${x.message}\n   ↳ ${x.fix}`).join('\n\n')
                      : '';
                    const lastRunLine = lr.completed != null
                      ? `\n\nLast run: ${lr.completed || 0} ok · ${lr.failed || 0} failed`
                      : '';
                    const projErrLine = lr.project_error
                      ? `\nProject error: ${lr.project_error}` : '';
                    const errLines = (lr.top_errors || []).length
                      ? '\n\nTop per-shot errors:\n\n' +
                        lr.top_errors.map(
                          (e) => `• ×${e.count} · ${e.error}` +
                                  (e.shot_numbers?.length
                                    ? `\n   shots: ${e.shot_numbers.join(', ')}`
                                    : '')).join('\n\n')
                      : '';
                    alert(`${head}${blockerLines}${lastRunLine}${projErrLine}${errLines}`);
                  } catch (e) {
                    alert(
                      'Diagnose failed: ' +
                      (e.response?.data?.detail || e.message));
                  }
                }}
                title="Probe why sketches won't generate"
                className="px-2 py-1 text-[10px] font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg hover:bg-amber-500/20 transition-colors whitespace-nowrap"
              >
                Diagnose
              </button>
              {/* Production Sketches - with asset bible (consistent) */}
              <ActionButton
                variant="primary"
                loading={uploadingAction === 'sketches:production'}
                disabled={uploadingAction?.startsWith('sketches:') || shots.length === 0 || !sketchGateOpen}
                onClick={() => generateSketches(false)}
                size="sm"
                data-testid="generate-sketches-btn"
                title={!sketchGateOpen ? 'Locked — upload mock shoot image + video for every shot first' : 'Generate sketches with Asset Bible for character/costume/location consistency'}
              >
                {hasSketchedShots ? 'Regenerate Sketches' : 'Production Sketches'}
              </ActionButton>
              {/* Consistent Sketches - VLM audit + correction loop */}
              <ActionButton
                variant="secondary"
                icon={CheckCircle}
                loading={uploadingAction === 'sketches:consistent'}
                disabled={uploadingAction?.startsWith('sketches:') || shots.length === 0 || !sketchGateOpen}
                onClick={() => generateConsistentSketches(false, 'nano-banana')}
                size="sm"
                data-testid="generate-consistent-sketches-btn"
                title={!sketchGateOpen ? 'Locked — upload mock shoot image + video for every shot first' : 'Generate sketches via Sketch engine with VLM audit — fast + cheap. ~$0.05/shot, ~8–15s/shot. Verifies each sketch matches cast, costume, and environment from asset bible.'}
              >
                Consistent Sketches
              </ActionButton>
              {/* Insanely Good Sketches — GPT Image 2 path. Split button:
                 *   • Main click  = Next 3 shots (chunked walk in scene order)
                 *   • Chevron     = dropdown to pick "Next 3" or "All remaining"
                 * The chunked default (3) lets operators review/delete bad
                 * shots without burning $6-12 on a full 28-shot run. Re-clicks
                 * walk forward through the shotlist; non-IGS shots are picked
                 * first by the backend filter at server.py:9302+.
                 */}
              <div className="relative inline-flex" ref={igsAnchorRef} data-testid="igs-split-button">
                <ActionButton
                  variant="primary"
                  icon={Sparkles}
                  loading={uploadingAction === 'sketches:igs'}
                  disabled={uploadingAction?.startsWith('sketches:') || shots.length === 0 || !sketchGateOpen}
                  onClick={() => {
                    setIgsMenuOpen(false);
                    if (window.confirm(
                      `"Insanely Good Sketches — Next 3" renders the next 3 shots (in scene order) ` +
                      `that haven't been IGS-tagged yet, using the Photo engine in Grade-3 hyperreal graphite mode.\n\n` +
                      `Cost: ~$0.60–1.20 for 3 shots · ~1–2 min.\n\n` +
                      `You can review them, delete any that look wrong, then click again to render the next 3.\n\n` +
                      `Continue?`,
                    )) {
                      generateConsistentSketches(false, 'gpt-image-2', 3);
                    }
                  }}
                  size="sm"
                  data-testid="generate-insanely-good-sketches-btn"
                  title={!sketchGateOpen ? 'Locked — upload mock shoot image + video for every shot first' : 'Photo engine · Grade-3 hyperreal graphite. Renders the next 3 non-IGS shots in scene order so you can review before committing the whole run.'}
                  className="rounded-r-none"
                >
                  Insanely Good Sketches
                </ActionButton>
                <button
                  type="button"
                  disabled={uploadingAction?.startsWith('sketches:') || shots.length === 0 || !sketchGateOpen}
                  onClick={() => setIgsMenuOpen((v) => !v)}
                  data-testid="igs-dropdown-chevron"
                  title={!sketchGateOpen ? 'Locked — complete Mock Shoot first' : 'Choose IGS run size'}
                  className="px-2 py-1.5 rounded-l-none border-l border-white/20 bg-ai-accent/90 hover:bg-ai-accent text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-md"
                  style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${igsMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {igsMenuOpen && ReactDOM.createPortal(
                  <div
                    ref={igsMenuRef}
                    className="fixed z-[9999] w-72 rounded-md border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden"
                    style={{ top: `${igsMenuPos.top}px`, left: `${igsMenuPos.left}px` }}
                    data-testid="igs-dropdown-menu"
                  >
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex flex-col gap-0.5"
                      data-testid="igs-next-3-btn"
                      onClick={() => {
                        setIgsMenuOpen(false);
                        if (window.confirm(
                          `Render the NEXT 3 non-IGS shots in scene order (~$0.60–1.20, ~1–2 min). ` +
                          `Skips shots already IGS-tagged. Continue?`,
                        )) {
                          generateConsistentSketches(false, 'gpt-image-2', 3);
                        }
                      }}
                    >
                      <span className="text-sm font-medium text-white">IGS — Next 3 shots</span>
                      <span className="text-[11px] text-white/50">~$0.60–1.20 · ~1–2 min · scene order, skips IGS-tagged</span>
                    </button>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-white/5 flex flex-col gap-0.5"
                      data-testid="igs-all-remaining-btn"
                      onClick={() => {
                        setIgsMenuOpen(false);
                        const remaining = shots.filter(s => (s.sketch_engine || '').toLowerCase() !== 'gpt-image-2-grade3' || !s.sketch_url).length;
                        const cost = (remaining * 0.30).toFixed(2);
                        if (window.confirm(
                          `Render ALL ${remaining} remaining non-IGS shots in one batch.\n\n` +
                          `Cost: ~$${cost} · ~${Math.max(1, Math.ceil(remaining * 0.5))} min.\n\n` +
                          `Already-IGS shots are left untouched.\n\nContinue?`,
                        )) {
                          generateConsistentSketches(false, 'gpt-image-2', null);
                        }
                      }}
                    >
                      <span className="text-sm font-medium text-white">IGS — All remaining</span>
                      <span className="text-[11px] text-white/50">Full batch · ~$0.20–0.40/shot · ~25–40s/shot · skips IGS-tagged</span>
                    </button>
                  </div>,
                  document.body,
                )}
              </div>
              {(() => {
                const failedCount = shots.filter(s => s.sketch_status === 'failed' || s.sketch_status === 'timed_out').length;
                const lastRunEngine = project?.sketches_engine;
                const hasGptGrade3Shot = shots.some(s => s.sketch_engine === 'gpt-image-2-grade3');
                const retryEngine = lastRunEngine === 'gpt-image-2'
                  ? 'gpt-image-2'
                  : (lastRunEngine === 'nano-banana'
                      ? 'nano-banana'
                      : (hasGptGrade3Shot ? 'gpt-image-2' : 'nano-banana'));
                const retryLabel = retryEngine === 'gpt-image-2' ? 'Insanely Good' : 'Sketch';
                return hasSketchedShots && failedCount > 0 && !sketchProgress ? (
                  <ActionButton
                    variant="secondary"
                    icon={RefreshCw}
                    loading={uploadingAction === 'sketches'}
                    disabled={uploadingAction === 'sketches'}
                    onClick={() => generateConsistentSketches(true, retryEngine)}
                    size="sm"
                    data-testid="retry-failed-sketches-btn"
                    title={`Retries the ${failedCount} failed shot(s) using the same engine as the original run (${retryLabel}).`}
                  >
                    Retry failed ({failedCount})
                  </ActionButton>
                ) : null;
              })()}
              {user?.is_admin && (
                <select
                  value={project.orientation || '16:9'}
                  onChange={async (e) => {
                    // P1-2: changing orientation no longer auto-triggers regen.
                    // User must now click "Regenerate Sketches" explicitly — avoids
                    // surprise API spend when someone toggles just to preview.
                    const newOrientation = e.target.value;
                    try {
                      await axios.post(`${API}/projects/${projectId}/settings`, { orientation: newOrientation });
                      setProject(p => ({ ...p, orientation: newOrientation }));
                      if (hasSketchedShots) {
                        toast.info('Orientation updated. Click "Regenerate Sketches" to apply it to existing sketches.');
                      }
                    } catch (err) { console.error('Failed to update orientation', err); }
                  }}
                  data-testid="sketch-orientation-selector"
                  className="px-2 py-1 rounded-lg text-[10px] font-medium bg-white/5 border border-white/10 text-white/80 outline-none cursor-pointer hover:border-white/20 transition-all"
                >
                  <option value="16:9" className="bg-[#0a0f1a]">16:9 Landscape</option>
                  <option value="9:16" className="bg-[#0a0f1a]">9:16 Portrait</option>
                  <option value="1:1" className="bg-[#0a0f1a]">1:1 Square</option>
                </select>
              )}
              </>
              )}
              {/* Generate All Blueprints (lineart) — batch button, added on the
                  Storyboard Sketches toolbar. Enables only when every bindable
                  (non-secondary) cast across all shots is resolved, so blueprints
                  never fire with missing identity anchors. */}
              {IGK2P_ON && shots.length > 0 && (() => {
                const _norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s*\(.*?\)\s*/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
                const tier = project?.cast_tier_map || {};
                const tierOf = (c) => { const k = Object.keys(tier).find((kk) => _norm(kk) === _norm(c)); return k ? (tier[k] || '').toLowerCase() : ''; };
                let unresolved = 0;
                for (const s of shots) {
                  const rp = (s.ref_plan || {}).cast_names || [];
                  const vb = ((s.asset_bible_spec?.cast_bindings) || []).filter((b) => b && b.cast_id).flatMap((b) => [b.cast_name, b.cast_role]).filter(Boolean);
                  const resolved = new Set([...rp, ...vb].map(_norm));
                  for (const c of (s.cast_visible || [])) {
                    if (tierOf(c) === 'secondary') continue;
                    if (!resolved.has(_norm(c))) unresolved++;
                  }
                }
                const ready = unresolved === 0;
                return (
                  <button
                    type="button"
                    data-testid="generate-all-blueprints-btn"
                    disabled={blueprintsBusy || !ready}
                    onClick={async () => {
                      setBlueprintsBusy(true);
                      try {
                        const res = await genBlueprintsBatch(projectId);
                        toast.success(res?.message || 'Blueprint batch queued for all shots');
                        loadProjectData?.();
                      } catch (e) {
                        toast.error('Blueprint batch failed: ' + (e.response?.data?.detail || e.message));
                      } finally {
                        setBlueprintsBusy(false);
                      }
                    }}
                    title={ready ? 'Generate lineart blueprints for all shots in one go' : `Resolve ${unresolved} unbound cast first — pick a card on the yellow chips`}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg border transition-colors whitespace-nowrap ${ready ? 'text-cyan-200 bg-cyan-500/15 border-cyan-500/30 hover:bg-cyan-500/25 cursor-pointer' : 'text-white/30 bg-white/5 border-white/10 cursor-not-allowed'}`}
                  >
                    {blueprintsBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
                    {ready ? 'Generate All Blueprints' : `Generate All Blueprints (${unresolved} unbound)`}
                  </button>
                );
              })()}
              {hasSketchedShots && (
                <ActionButton
                  variant={bulkSelectMode === 'sketches' ? 'primary' : 'neutral'}
                  icon={CheckCircle}
                  onClick={() => toggleBulkSelect('sketches')}
                  size="sm"
                  data-testid="bulk-select-sketches-btn"
                  title="Select multiple sketches to delete in one go"
                >
                  {bulkSelectMode === 'sketches' ? 'Exit Select' : 'Select'}
                </ActionButton>
              )}
              {/* Download-all sketches as ZIP — greys out until every shot has rendered. */}
              <button
                type="button"
                disabled={!allSketchesDone || downloading === 'sketches.zip'}
                onClick={() => downloadFile('sketches.zip')}
                data-testid="download-all-sketches-zip-btn"
                title={
                  allSketchesDone
                    ? `Download all ${sketchUrlCount} sketches as a ZIP`
                    : `Download all (waiting · ${sketchUrlCount}/${shots.length} sketches rendered)`
                }
                className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${
                  allSketchesDone
                    ? 'border-ai-accent/30 bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20 hover:border-ai-accent/50 cursor-pointer'
                    : 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                }`}
              >
                {downloading === 'sketches.zip'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Download className="w-4 h-4" />
                }
              </button>
              </div>
            {/* Bulk action bar — shown only while selecting in this section */}
            {bulkSelectMode === 'sketches' && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-ai-accent/5 border border-ai-accent/20 flex items-center justify-between gap-2" data-testid="bulk-action-bar-sketches">
                <div className="text-xs text-ai-accent/90">
                  <span className="font-bold">{selectedShotIds.size}</span> selected
                  {selectedShotIds.size === 0 && <span className="text-white/40 ml-2">· tap cards to select</span>}
                </div>
                <div className="flex items-center gap-2">
                  {selectedShotIds.size > 0 && (
                    <ActionButton
                      variant="neutral"
                      size="sm"
                      onClick={() => {
                        const allIds = new Set(shots.map(s => s.id));
                        setSelectedShotIds(allIds);
                      }}
                      data-testid="bulk-select-all-sketches"
                    >
                      Select all ({shots.length})
                    </ActionButton>
                  )}
                  <ActionButton
                    variant="neutral"
                    icon={Trash2}
                    loading={bulkDeleting}
                    disabled={selectedShotIds.size === 0 || bulkDeleting}
                    onClick={() => setConfirmBulkDelete(true)}
                    data-testid="bulk-delete-sketches"
                    title="Remove rendered sketch images on the selected shots — shot data and bindings are preserved"
                  >
                    Clear sketches {selectedShotIds.size > 0 ? `(${selectedShotIds.size})` : ''}
                  </ActionButton>
                </div>
              </div>
            )}
            {sketchProgress && <ProgressBar label="Generating sketches with EiPiMedia AI Pro..." progress={sketchProgress} color="#0EA5E9" onStop={stopGeneration} taskType="sketches" />}
            {sketchProgress?.error && (
              <div className="mt-2 p-3 bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-xl text-xs text-[#FF3B30]" data-testid="sketch-error">
                <AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />
                {sketchProgress.error} — Use the refresh button on each pending sketch to regenerate individually.
              </div>
            )}
            {shots.length > 0 && (
              <DraggableFrameGrid
                items={shots}
                gridClass={tileGridClass}
                onReorder={handleReorderShots}
                onInsertAfter={handleInsertAfter}
                renderCard={(shot) => (
                  <SketchCard key={shot.id} shot={shot} onRefresh={regenerateSketch} onReload={loadProjectData} onFeedback={sendSketchFeedback} isGenerating={!!sketchProgress} aspectClass={aspectClass} projectId={projectId}
                    castTierMap={project?.cast_tier_map}
                    selectable={bulkSelectMode === 'sketches'}
                    selected={selectedShotIds.has(shot.id)}
                    onToggleSelect={toggleShotSelected}
                    onDelete={(s) => setDeleteConfirm(s)}
                    onUndo={async (shotId) => {
                      try {
                        await axios.post(`${API}/shots/${shotId}/sketch-undo`);
                        toast.success('Reverted to pre-iteration sketch');
                        await loadProjectData();
                      } catch (e) { alert(e.response?.data?.detail || 'Undo failed'); }
                    }}
                    onImageClick={() => {
                      const gallery = shots
                        .filter(s => s.sketch_url)
                        .map(s => ({
                          url: resolveUrl(s.sketch_s3_url || s.sketch_url),
                          type: 'image',
                          label: `Sketch ${s.scene_number}-${s.shot_number}`,
                          shot_id: s.id,
                        }));
                      const idx = gallery.findIndex(g => g.shot_id === shot.id);
                      setGlobalLightbox({ items: gallery, index: Math.max(0, idx) });
                    }} />
                )}
              />
            )}
            {hasSketchedShots && (
              <div className="flex justify-end mt-4">
                <button onClick={advanceStage} data-testid="approve-sketches-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Approved, Move to Next Stage <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 4.5 ANIMATIC REEL (Sketch-stage bulk concat) ─────────────────────────────
         * Visible whenever any shot has a sketch OR keyframe, regardless of stage.
         * "Generate Animatics" auto-fires Veo 3.1 Fast on any not-yet-animated
         * shots, then stitches the reel — one click, no per-card needed.
         * Same backend route — `POST /api/projects/:id/animatic-reel/generate`. */}
        {shots.some(s => s.sketch_url || s.sketch_s3_url || s.keyframe_url || s.keyframe_s3_url) && (() => {
          const total = shots.filter(s => s.sketch_url || s.sketch_s3_url || s.keyframe_url || s.keyframe_s3_url).length;
          const animated = shots.filter(s => s.animatic_url).length;
          const missing = total - animated;
          const isProcessing = reelStatus.status === 'processing';
          const isDone = reelStatus.status === 'done' && reelStatus.mp4_url;
          return (
            <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/[0.04] p-3 mb-4" data-testid="animatic-reel-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-cyan-200 flex items-center gap-2">
                    <Play className="w-3.5 h-3.5" />
                    Animatic Reel
                  </div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    {isProcessing
                      ? `${reelStatus.last_message || 'Working…'} · ${reelStatus.progress_pct || 0}%`
                      : isDone
                      ? 'Reel ready — works on phone, laptop, desktop'
                      : reelStatus.status === 'failed'
                      ? `Failed: ${reelStatus.error}`
                      : missing > 0
                      ? `One click — auto-animates the ${missing} missing of ${total} shots via Veo, then stitches into one MP4 (~${Math.ceil(missing/4) * 90}s).`
                      : `All ${total} shots animated — ready to stitch into a single MP4 (Audio: native per-clip).`}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isDone ? (
                    <a
                      href={reelStatus.mp4_url.startsWith('http') ? reelStatus.mp4_url : `${process.env.REACT_APP_BACKEND_URL}${reelStatus.mp4_url}`}
                      target="_blank" rel="noopener noreferrer"
                      download
                      className="px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#031018] text-xs font-semibold flex items-center gap-2 whitespace-nowrap active:scale-95"
                      data-testid="animatic-reel-download"
                    >
                      ⬇ Download Reel
                    </a>
                  ) : null}
                  <button
                    onClick={generateAnimaticsReel}
                    disabled={total === 0 || isProcessing || reelKicking}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 whitespace-nowrap transition-all ${
                      total === 0 || isProcessing
                        ? 'bg-white/5 text-white/30 cursor-not-allowed border border-white/10'
                        : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-100 border border-cyan-400/40 active:scale-95'
                    }`}
                    data-testid="generate-animatics-btn"
                    title={
                      total === 0
                        ? 'No sketches yet — generate sketches first'
                        : isProcessing
                        ? 'Working — please wait'
                        : isDone
                        ? 'Re-stitch the reel'
                        : missing > 0
                        ? `Animate ${missing} missing shot${missing===1?'':'s'} + stitch the full reel`
                        : 'Stitch all animatics into a single MP4 with crossfades'
                    }
                  >
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    {isProcessing
                      ? `${reelStatus.progress_pct || 0}%`
                      : isDone
                      ? 'Re-stitch'
                      : 'Generate Animatics'}
                  </button>
                </div>
              </div>
              {isProcessing && (
                <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden mt-3">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-teal-400 transition-all duration-500"
                    style={{ width: `${Math.max(reelStatus.progress_pct || 0, 4)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })()}

        {/* ── 4a-music. SAMPLE MUSIC (Suno) — under the Animatic Reel ── */}
        {shots.some(s => s.sketch_url || s.sketch_s3_url || s.keyframe_url || s.keyframe_s3_url) && (() => {
          const m = musicStatus || {};
          const isProcessing = m.status === 'processing';
          const isDone = m.status === 'done' && m.audio_url;
          const isFailed = m.status === 'failed';
          const audioHref = isDone
            ? (m.audio_url.startsWith('http') ? m.audio_url : `${process.env.REACT_APP_BACKEND_URL}${m.audio_url}`)
            : null;
          return (
            <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3 mb-4" data-testid="animatic-music-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-fuchsia-200 flex items-center gap-2">
                    <Music className="w-3.5 h-3.5" />
                    Sample Music
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-fuchsia-400/15 text-fuchsia-200/80 uppercase tracking-wide">beta</span>
                  </div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    {isProcessing
                      ? `Composing a full-length track from your script + shots… (~1–2 min)`
                      : isDone
                      ? `${m.instrumental ? 'Instrumental' : 'With vocals'}${m.duration ? ` · ${Math.round(m.duration)}s` : ''} — preview & download below`
                      : isFailed
                      ? `Failed: ${m.error || 'generation error'}`
                      : `One click — an AI music supervisor reads your script + shot list, writes a track brief, and generates a full-length sample score.`}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={generateAnimaticMusic}
                    disabled={isProcessing || musicKicking}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 whitespace-nowrap transition-all ${
                      isProcessing
                        ? 'bg-white/5 text-white/30 cursor-not-allowed border border-white/10'
                        : 'bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-100 border border-fuchsia-400/40 active:scale-95'
                    }`}
                    data-testid="generate-music-btn"
                    title={isProcessing ? 'Composing — please wait' : isDone ? 'Regenerate a new track' : 'Generate a sample music track'}
                  >
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music className="w-4 h-4" />}
                    {isProcessing ? 'Composing…' : isDone ? 'Regenerate' : 'Generate Music'}
                  </button>
                </div>
              </div>

              {(m.title || m.style) && !isProcessing && (
                <div className="mt-2 text-[11px] text-white/60">
                  {m.title && <span className="font-semibold text-fuchsia-100" data-testid="music-title">{m.title}</span>}
                  {m.style && <span className="text-white/45"> · {m.style}</span>}
                </div>
              )}

              {isProcessing && (
                <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden mt-3">
                  <div className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-400 animate-pulse" style={{ width: '60%' }} />
                </div>
              )}

              {isDone && (
                <div className="mt-3 flex flex-col gap-2" data-testid="music-player-wrap">
                  <audio controls src={audioHref} className="w-full h-9" data-testid="music-audio" />
                  <div className="flex items-center gap-2">
                    <a
                      href={audioHref}
                      target="_blank" rel="noopener noreferrer"
                      download
                      className="px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-[#150015] text-xs font-semibold flex items-center gap-2 whitespace-nowrap active:scale-95"
                      data-testid="music-download"
                    >
                      ⬇ Download Track
                    </a>
                    {m.lyrics && !m.instrumental && (
                      <details className="text-[11px] text-white/60">
                        <summary className="cursor-pointer hover:text-white/80" data-testid="music-lyrics-toggle">View lyrics</summary>
                        <pre className="mt-2 whitespace-pre-wrap font-sans text-white/55 max-h-40 overflow-auto" data-testid="music-lyrics">{m.lyrics}</pre>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── 4b. SKETCH ANGLES (Hidden for now) ── */}
        {false && effectiveStage >= 2 && hasSketchedShots && (
          <CollapsibleSection
            title="5. Sketch Angles"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/10 flex items-center justify-center"><Film className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={shots.some(s => s.sketch_angles?.length > 0) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-ai-accent/10 text-ai-accent">{shots.filter(s => s.sketch_angles?.length > 0).length} with angles</span>}
            expanded={expandedSections.sketchAngles}
            onToggle={() => toggleSection('sketchAngles')}
            testId="sketch-angles-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Generate alternative camera angles. Use checkboxes to select multiple angles as sub-shots (e.g., Shot 1.1, 1.2).
            </p>
            <button onClick={generateSketchAngles} disabled={uploadingAction === "sketchAngles"} data-testid="generate-sketch-angles-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 mb-4 ${
                uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-ai-accent text-white hover:bg-ai-accent/80 active:scale-95'
              }`}>
              {uploadingAction === 'sketchAngles' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              {shots.some(s => s.sketch_angles?.length > 0) ? 'Regenerate Angles' : 'Generate Angles'}
            </button>
            {sketchAnglesProgress?.processing && (
              <ProgressBar label="Generating sketch angles..." progress={{completed: sketchAnglesProgress.completed, total: sketchAnglesProgress.total, percentage: Math.round((sketchAnglesProgress.completed / Math.max(sketchAnglesProgress.total, 1)) * 100)}} color="#0EA5E9" onStop={stopGeneration} taskType="sketch_angles" />
            )}
            <div className="space-y-4 mt-3">
              {shots.filter(s => s.sketch_url).map((shot) => {
                const selectedAngles = shot.selected_sketch_angles || [shot.selected_sketch_angle ?? 0];
                const isMultiSelected = selectedAngles.length > 1;
                return (
                <div key={shot.id} className="rounded-xl p-3 bg-white/[0.02]" data-testid={`sketch-angle-row-${shot.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white">{shot.scene_number} — {shot.shot_number}</span>
                    <div className="flex items-center gap-2">
                      {(shot.sketch_angles_generating || generatingAngleShots.has(`${shot.id}_sketch`)) && <Loader2 className="w-3.5 h-3.5 animate-spin text-ai-accent" />}
                      <button 
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); generateSingleAngles(shot.id, 'sketch'); }}
                        disabled={shot.sketch_angles_generating || generatingAngleShots.has(`${shot.id}_sketch`)}
                        className="px-2 py-1 rounded text-[10px] font-medium bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20 transition-colors disabled:opacity-30 flex items-center gap-1 whitespace-nowrap"
                        title="Generate angles for this sketch"
                        data-testid={`gen-angles-sketch-${shot.id}`}
                      >
                        {(shot.sketch_angles_generating || generatingAngleShots.has(`${shot.id}_sketch`)) ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Angles
                      </button>
                      {isMultiSelected && (
                        <span className="text-[10px] px-2 py-0.5 bg-ai-accent/10 text-ai-accent rounded-full font-medium">
                          {selectedAngles.length} sub-shots
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {/* Original */}
                    <div className={`relative rounded-lg overflow-hidden border-2 transition-all ${selectedAngles.includes(0) ? 'border-ai-accent shadow-md' : 'border-transparent hover:border-ai-border'}`}>
                      <div className="cursor-pointer" onClick={() => selectSketchAngle(shot.id, 0)} data-testid={`select-sketch-original-${shot.id}`}>
                        {shot.sketch_url && <img src={resolveUrl(shot.sketch_url)} alt="Original" className={`w-full ${aspectClass} object-cover`} />}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] sm:text-[9px] text-center py-0.5 font-medium flex items-center justify-center gap-1">
                        <input type="checkbox" checked={selectedAngles.includes(0)} onChange={() => selectSketchAngle(shot.id, 0, true)}
                          className="w-3 h-3 accent-[#0EA5E9]" data-testid={`checkbox-sketch-original-${shot.id}`} onClick={e => e.stopPropagation()} />
                        Original
                      </div>
                    </div>
                    {/* Alternative angles */}
                    {(shot.sketch_angles || []).map((angle, ai) => (
                      <div key={ai} className={`relative rounded-lg overflow-hidden border-2 transition-all ${selectedAngles.includes(ai + 1) ? 'border-ai-accent shadow-md' : 'border-transparent hover:border-ai-border'}`}>
                        <div className="cursor-pointer" onClick={() => selectSketchAngle(shot.id, ai + 1)} data-testid={`select-sketch-angle-${shot.id}-${ai}`}>
                          <img src={resolveUrl(angle.url)} alt={angle.angle_name} className={`w-full ${aspectClass} object-cover`} />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 font-medium flex items-center justify-center gap-1">
                          <input type="checkbox" checked={selectedAngles.includes(ai + 1)} onChange={() => selectSketchAngle(shot.id, ai + 1, true)}
                            className="w-3 h-3 accent-[#0EA5E9]" data-testid={`checkbox-sketch-angle-${shot.id}-${ai}`} onClick={e => e.stopPropagation()} />
                          {angle.angle_name}
                        </div>
                      </div>
                    ))}
                    {(!shot.sketch_angles || shot.sketch_angles.length === 0) && (
                      <>
                        {(shot.sketch_angles_generating || generatingAngleShots.has(`${shot.id}_sketch`)) ? (
                          <>
                            <div className={`${aspectClass} rounded-lg flex flex-col items-center justify-center overflow-hidden relative`} style={{ background: 'rgba(14,165,233,0.05)' }}>
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-shimmer" />
                              <Loader2 className="w-5 h-5 animate-spin text-ai-accent mb-1" />
                              <span className="text-[9px] text-ai-accent/70">Generating...</span>
                            </div>
                            <div className={`${aspectClass} rounded-lg flex flex-col items-center justify-center overflow-hidden relative`} style={{ background: 'rgba(14,165,233,0.05)' }}>
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-shimmer" style={{ animationDelay: '0.5s' }} />
                              <Loader2 className="w-5 h-5 animate-spin text-ai-accent mb-1" style={{ animationDelay: '0.3s' }} />
                              <span className="text-[9px] text-ai-accent/70">Generating...</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`${aspectClass} glass-panel rounded-lg flex items-center justify-center`}><span className="text-[10px] text-white/40">Low Angle</span></div>
                            <div className={`${aspectClass} glass-panel rounded-lg flex items-center justify-center`}><span className="text-[10px] text-white/40">High Angle</span></div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )})}
            </div>
          </CollapsibleSection>
        )}

        {/* ── 6. PRE-PRODUCTION CONFIRMATION ── */}
        {!isPpmApproved() && hasAssets && shots.length > 0 && (
          <div className="border-2 border-dashed border-ai-accent/30 rounded-xl bg-ai-accent/[0.02] p-6 text-center" data-testid="confirm-pre-production">
            <button onClick={confirmPreProduction} data-testid="confirm-stages-btn"
              className="px-6 py-3 bg-ai-accent text-white rounded-lg text-sm font-semibold hover:bg-[#4B49B7] active:scale-95 transition-all inline-flex items-center gap-2">
              Confirm & Move to Production <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {isPpmApproved() && (
          <div className="border border-[#22C55E]/20 rounded-xl p-4 flex items-center gap-3" style={{ background: 'rgba(34,197,94,0.05)' }} data-testid="stage-6-confirmed">
            <div className="w-7 h-7 rounded-lg bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-[#22C55E]" />
            </div>
            <div>
              <span className="font-semibold text-sm text-white">6. Pre-Production Confirmed</span>
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">Locked</span>
            </div>
          </div>
        )}

        {/* ── 7. PROMPT ENGINE ── */}
        {/* ═══ END PRE-PRODUCTION GROUP ═══ */}
        </>)}

        {/* ═══════════════════════════════════════════════ */}
        {/* PRODUCTION TAB                                  */}
        {/* Keyframes, VLM QA, SAM, Video Generation        */}
        {/* ═══════════════════════════════════════════════ */}
        {activeStageGroup === 'production' && (<>
        {/* ═══ PRODUCTION GROUP ═══ */}
        {(shots.some(s => s.keyframe_url) || effectiveStage >= 7) && (
          <CollapsibleSection
            title="1. Keyframe Gallery"
            icon={<div className="w-7 h-7 rounded-lg bg-[#FF9500]/10 flex items-center justify-center"><Image className="w-3.5 h-3.5 text-[#FF9500]" /></div>}
            badge={<>
              {shots.every(s => s.keyframe_url) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E] mr-1">Ready</span>}
              {shots.some(s => s.keyframe_url) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF9500]/10 text-[#FF9500]">{shots.filter(s => s.keyframe_url).length}/{shots.length} keyframes</span>}
            </>}
            expanded={expandedSections.keyframes}
            onToggle={() => toggleSection('keyframes')}
            testId="pipeline-section"
            rightContent={shots.some(s => s.keyframe_url) && (
              <div className="flex items-center gap-2 mr-2" onClick={(e) => e.stopPropagation()}>
                <FaceLockAllButton projectId={projectId} media="keyframe" onReload={loadProjectData} />
                <a href={`${BASE_URL}/api/projects/${projectId}/download/keyframes`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-[#FF9500] hover:text-[#E08600] font-medium"
                  data-testid="download-keyframes-pdf">
                  <Download className="w-3.5 h-3.5" /> .pdf
                </a>
              </div>
            )}
          >
            {/* Look-Feel References — project-scoped style/mood refs that
             *  the KEYFRAME+ composer auto-merges into every run. Sticky
             *  per project. Identity / brand / wardrobe stay locked. */}
            <div className="mb-3">
              <LookFeelPanel
                projectId={projectId}
                onChange={setLookFeelRefCount}
              />
            </div>

            {shots.every(s => s.keyframe_url) && !pipelineProgress && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#22C55E]/5 border border-[#22C55E]/20 rounded-xl" data-testid="keyframes-ready-banner">
                <CheckCircle className="w-4 h-4 text-[#22C55E]" />
                <span className="text-xs text-[#22C55E] font-medium">All keyframes pre-generated and ready for review</span>
              </div>
            )}
            
            {/* Midjourney Toggle — hidden until integration is complete
            <div className="flex items-center gap-3 mb-4 px-3 py-2 rounded-xl" style={{ background: 'rgba(14, 165, 233, 0.06)' }}>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="midjourney-toggle">
                <input 
                  type="checkbox" 
                  checked={project.use_midjourney || false}
                  onChange={async (e) => {
                    try {
                      await axios.post(`${API}/projects/${projectId}/settings`, { use_midjourney: e.target.checked });
                      loadProject();
                    } catch (err) {
                      alert('Failed to update setting: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                  className="w-4 h-4 rounded border-ai-border text-ai-accent focus:ring-ai-accent/20"
                />
                <span className="text-sm font-medium text-white">Use Midjourney</span>
              </label>
              <span className="text-[10px] text-white/60">
                {project.use_midjourney ? 'Higher quality, slower (~60s/image)' : 'Using Sketch engine (fast, ~5s/image)'}
              </span>
            </div>
            */}

            {/* Auto-Improve Toggle */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 px-3 py-2 rounded-xl" style={{ background: 'rgba(14, 165, 233, 0.06)' }}>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-improve-toggle">
                <input 
                  type="checkbox" 
                  checked={project.auto_improve_enabled || false}
                  onChange={async (e) => {
                    try {
                      await axios.post(`${API}/projects/${projectId}/settings`, { auto_improve_enabled: e.target.checked });
                      loadProject();
                    } catch (err) {
                      alert('Failed to update setting: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                  className="w-4 h-4 rounded border-ai-border text-ai-accent focus:ring-ai-accent/20"
                />
                <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5 whitespace-nowrap">
                  <Sparkles className="w-4 h-4 text-ai-accent" />
                  Auto-Improve
                </span>
                {/* Help tooltip */}
                <div className="relative group">
                  <div className="w-5 h-5 rounded-lg bg-ai-accent/40 flex items-center justify-center cursor-help text-[11px] font-bold text-white">?</div>
                  <div className="absolute left-0 top-full mt-2 w-72 p-4 rounded-xl text-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100] shadow-2xl"
                    style={{ background: '#0B0F19', border: '2px solid rgba(14, 165, 233, 0.6)' }}>
                    <div className="font-bold text-ai-text mb-2 text-sm">VLM Quality Assurance</div>
                    <p className="text-white leading-relaxed">When enabled, keyframes with VLM QA scores below 90 will automatically be regenerated with improved prompts. The AI analyzes composition, lighting, and accuracy to ensure highest quality outputs.</p>
                    <div className="absolute left-4 -top-2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-transparent border-b-ai-accent/60"></div>
                  </div>
                </div>
              </label>
              <span className="text-[10px] text-ai-text">
                {project.auto_improve_enabled ? 'VLM QA will auto-fix low scores (<90)' : 'Manual improvement only'}
              </span>
            </div>

            {/* Generate Buttons (engine toggle removed — backend canonical cascade handles routing) */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
                {/* Step 1: Generate MJ Prompts (batch, all at once) */}
                <ActionButton
                  variant="secondary"
                  icon={FileText}
                  loading={uploadingAction === 'prompts'}
                  disabled={uploadingAction === 'prompts' || uploadingAction === 'pipeline'}
                  onClick={() => generatePrompts()}
                  data-testid="generate-prompts-btn"
                >
                  {shots.every(s => s.keyframe_prompt || s.mj_prompt) ? 'Regenerate MJ Prompts' : 'Generate MJ Prompts'}
                </ActionButton>

                {/* Step 2: Generate Keyframe Images (uses existing prompts) */}
                <ActionButton
                  variant="primary"
                  loading={uploadingAction === 'pipeline'}
                  disabled={uploadingAction === 'pipeline' || uploadingAction === 'prompts'}
                  onClick={() => generatePromptsAndKeyframes()}
                  data-testid="generate-pipeline-btn"
                >
                  {shots.every(s => s.keyframe_url) ? 'Regenerate All' : shots.some(s => s.keyframe_url) ? 'Regenerate All' : 'Generate Keyframes'}
                </ActionButton>

                {/* Generate Pending Keyframes — only show if some are missing */}
                {shots.some(s => s.keyframe_url) && shots.some(s => !s.keyframe_url) && (
                  <ActionButton
                    variant="secondary"
                    icon={RefreshCw}
                    loading={uploadingAction === 'pendingKeyframes'}
                    disabled={uploadingAction === 'pendingKeyframes'}
                    onClick={() => generatePendingKeyframes()}
                    data-testid="generate-pending-keyframes-btn"
                  >
                    Generate Pending ({shots.filter(s => !s.keyframe_url).length})
                  </ActionButton>
                )}

                {/* Download-all keyframes as ZIP — greys out until every shot has rendered. */}
                <button
                  type="button"
                  disabled={!allKeyframesDone || downloading === 'keyframes.zip'}
                  onClick={() => downloadFile('keyframes.zip')}
                  data-testid="download-all-keyframes-zip-btn"
                  title={
                    allKeyframesDone
                      ? `Download all ${keyframeUrlCount} keyframes as a ZIP`
                      : `Download all (waiting · ${keyframeUrlCount}/${shots.length} keyframes rendered)`
                  }
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors shrink-0 ${
                    allKeyframesDone
                      ? 'border-ai-accent/30 bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20 hover:border-ai-accent/50 cursor-pointer'
                      : 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                  }`}
                >
                  {downloading === 'keyframes.zip'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Download className="w-4 h-4" />
                  }
                </button>
                {/* Topaz Batch Upscale */}
                {shots.some(s => s.keyframe_url) && (
                  <ActionButton
                    variant="secondary"
                    icon={ArrowUpCircle}
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/projects/${project.id}/batch-upscale`, { target: 'keyframe', scale: 2 });
                        toast.success(res.data.message || 'Batch upscale started');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Batch upscale failed');
                      }
                    }}
                    data-testid="batch-upscale-keyframes-btn"
                  >
                    Topaz Upscale All
                  </ActionButton>
                )}

                {/* Insanely Good Keyframes — GPT Image 2 hyperreal photographic
                    keyframes with VLM audit + correction. Separate field
                    (keyframe_hyperreal_url) so the existing Gemini/MJ keyframe
                    pipeline above is NOT touched. */}
                {(() => {
                  const failedHRCount = shots.filter(s => s.keyframe_hyperreal_status === 'failed' || s.keyframe_hyperreal_status === 'timed_out').length;
                  const hasAnyHR = shots.some(s => s.keyframe_hyperreal_url);
                  const processing = project?.hyperreal_keyframes_processing;
                  const plannedCount = shots.filter(s => s.ref_plan && s.ref_plan.shot_type).length;
                  return (
                    <>
                      <ActionButton
                        variant={plannedCount === shots.length && shots.length > 0 ? 'primary' : 'secondary'}
                        icon={Compass}
                        loading={uploadingAction === 'castingPlan'}
                        disabled={uploadingAction === 'castingPlan' || processing}
                        onClick={async () => {
                          setUploadingAction('castingPlan');
                          try {
                            const res = await axios.post(`${API}/projects/${projectId}/build-ref-plan?force=true`);
                            // Background mode — endpoint returns 202-style immediately, work continues server-side
                            if (res.data?.processing) {
                              toast.info(res.data?.message || 'Casting plan started in the background', {
                                description: `Polling for completion · ${res.data.total || 0} shots queued`,
                                duration: 8000,
                              });
                              // Poll status until completion (max ~3 min)
                              const startedAt = Date.now();
                              const pollHandle = setInterval(async () => {
                                if (Date.now() - startedAt > 180000) {
                                  clearInterval(pollHandle);
                                  setUploadingAction(null);
                                  toast.warning('Casting plan still running — check back in a minute and refresh the project.');
                                  return;
                                }
                                try {
                                  const st = await axios.get(`${API}/projects/${projectId}/ref-plan-status`);
                                  if (st.data?.status === 'completed') {
                                    clearInterval(pollHandle);
                                    setUploadingAction(null);
                                    toast.success(st.data?.summary || 'Casting plan built', { duration: 8000 });
                                    onLockToggle?.();
                                  } else if (st.data?.status === 'failed') {
                                    clearInterval(pollHandle);
                                    setUploadingAction(null);
                                    toast.error('Casting plan failed', {
                                      description: (st.data?.summary || st.data?.error || 'unknown').slice(0, 400),
                                      duration: 12000,
                                    });
                                    onLockToggle?.();
                                  }
                                } catch {/* keep polling on transient errors */}
                              }, 5000);
                            } else {
                              // Legacy synchronous path (kept for back-compat)
                              toast.success(res.data?.message || 'Casting plan built');
                              onLockToggle?.();
                              setUploadingAction(null);
                            }
                          } catch (err) {
                            const status = err.response?.status;
                            const detail = err.response?.data?.detail
                              || err.response?.data?.message
                              || err.message
                              || 'Unknown error';
                            const banner = status ? `Casting plan failed (${status})` : 'Casting plan failed';
                            toast.error(banner, {
                              description: String(detail).slice(0, 400),
                              duration: 10000,
                            });
                            // eslint-disable-next-line no-console
                            console.error('[BuildCastingPlan] error', { status, detail, full: err.response?.data || err });
                            setUploadingAction(null);
                          }
                        }}
                        data-testid="build-casting-plan-btn"
                        title={`Per-shot Casting & Reference Plan via Director AI. For each shot, decides shot type and which refs belong in the frame. ${plannedCount}/${shots.length} shots planned.`}
                      >
                        {plannedCount === shots.length && shots.length > 0
                          ? `Casting Plan ✓ (${plannedCount}/${shots.length})`
                          : `Build Casting Plan (${plannedCount}/${shots.length})`}
                      </ActionButton>
                      <div className="relative inline-flex" ref={igkAnchorRef} data-testid="igk-split-button">
                        <ActionButton
                          variant="primary"
                          icon={Sparkles}
                          loading={uploadingAction === 'hyperrealKeyframes' || processing}
                          disabled={uploadingAction === 'hyperrealKeyframes' || processing}
                          onClick={() => {
                            setIgkMenuOpen(false);
                            if (window.confirm(
                              `"Insanely Good Keyframes — Next 3" renders the next 3 shots (in scene order) ` +
                              `that don't yet have a hyperreal keyframe, using the Photo engine.\n\n` +
                              `Cost: ~$0.60–1.20 for 3 shots · ~1–2 min.\n\n` +
                              `You can review, delete any wrong ones, then click again for the next 3.\n\nContinue?`,
                            )) {
                              generateHyperrealKeyframes(false, 3);
                            }
                          }}
                          data-testid="insanely-good-keyframes-btn"
                          title="GPT Image 2 photoreal cinematic keyframe. Renders next 3 non-IGK shots in scene order so you can review before committing the full run."
                          className="rounded-r-none"
                        >
                          {processing
                            ? `IGK… ${project?.hyperreal_keyframes_completed || 0}/${project?.hyperreal_keyframes_total || shots.length}`
                            : hasAnyHR ? 'Regenerate IGK' : 'Insanely Good Keyframes'}
                        </ActionButton>
                        <button
                          type="button"
                          disabled={uploadingAction === 'hyperrealKeyframes' || processing || shots.length === 0}
                          onClick={() => setIgkMenuOpen((v) => !v)}
                          data-testid="igk-dropdown-chevron"
                          title="Choose IGK run size"
                          className="px-2 py-1.5 rounded-l-none border-l border-white/20 bg-ai-accent/90 hover:bg-ai-accent text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-md"
                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${igkMenuOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {igkMenuOpen && ReactDOM.createPortal(
                          <div
                            ref={igkMenuRef}
                            className="fixed z-[9999] w-72 rounded-md border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden"
                            style={{ top: `${igkMenuPos.top}px`, left: `${igkMenuPos.left}px` }}
                            data-testid="igk-dropdown-menu"
                          >
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex flex-col gap-0.5"
                              data-testid="igk-next-3-btn"
                              onClick={() => {
                                setIgkMenuOpen(false);
                                if (window.confirm(
                                  `Render the NEXT 3 non-IGK shots in scene order (~$0.60–1.20, ~1–2 min). ` +
                                  `Skips shots that already have a hyperreal keyframe. Continue?`,
                                )) {
                                  generateHyperrealKeyframes(false, 3);
                                }
                              }}
                            >
                              <span className="text-sm font-medium text-white">IGK — Next 3 shots</span>
                              <span className="text-[11px] text-white/50">~$0.60–1.20 · ~1–2 min · scene order, skips IGK-tagged</span>
                            </button>
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-white/5 flex flex-col gap-0.5"
                              data-testid="igk-all-remaining-btn"
                              onClick={() => {
                                setIgkMenuOpen(false);
                                // 2026-08-17: mirror backend's not_yet_igk filter — also skip shots
                                // that already have a done Keyframe+ result, so the cost/count shown
                                // here matches what generate_hyperreal_keyframes will actually process.
                                const remaining = shots.filter(s =>
                                  (!s.keyframe_hyperreal_url || ['failed','timed_out'].includes((s.keyframe_hyperreal_status || '').toLowerCase()))
                                  && !(s.keyframe_plus_status === 'done' && s.keyframe_plus_url)
                                ).length;
                                const cost = (remaining * 0.30).toFixed(2);
                                if (window.confirm(
                                  `Render ALL ${remaining} remaining non-IGK shots in one batch.\n\n` +
                                  `Cost: ~$${cost} · ~${Math.max(1, Math.ceil(remaining * 0.5))} min.\n\n` +
                                  `Already-IGK shots are left untouched.\n\nContinue?`,
                                )) {
                                  generateHyperrealKeyframes(false, null);
                                }
                              }}
                            >
                              <span className="text-sm font-medium text-white">IGK — All remaining</span>
                              <span className="text-[11px] text-white/50">Full batch · ~$0.20–0.40/shot · ~25–40s/shot · skips IGK-tagged</span>
                            </button>
                          </div>,
                          document.body,
                        )}
                      </div>

                      {/* ── KEYFRAME+ split-button ─────────────────────────
                          Operator-locked parallel silo. Photo-only stack
                          (Writer AI composer → Photo engine → Writer AI vision
                          QA). Writes to keyframe_plus_* DB collections,
                          NEVER touches keyframe_url / IGK / iterations.
                          Phase 1 SCAFFOLD — pipeline returns FAILED with
                          "not_implemented" until Phase 2 wires the LLM
                          stack. Button + endpoints are wired so operator
                          flow is testable end-to-end on day 1. */}
                      <div className="relative inline-flex" ref={kfpAnchorRef} data-testid="keyframe-plus-split-button">
                        <ActionButton
                          variant="primary"
                          icon={Sparkles}
                          loading={kfpFiring}
                          disabled={kfpFiring}
                          onClick={() => {
                            setKfpMenuOpen(false);
                            if (window.confirm(
                              `"KEYFRAME+ — Next 3" runs the new Photo-only keyframe pipeline ` +
                              `(Writer AI composer → Photo engine → Writer AI vision QA) on the ` +
                              `next 3 shots that don't yet have a Keyframe+ output.\n\n` +
                              `This is a parallel silo — it does NOT touch IGK or any other ` +
                              `keyframe.\n\nContinue?`,
                            )) {
                              fireKeyframePlusBatch(3);
                            }
                          }}
                          data-testid="keyframe-plus-btn"
                          title={`Parallel silo — Writer AI composer + Photo engine + Writer AI vision QA. Writes to keyframe_plus_* — never touches IGK.${lookFeelRefCount > 0 ? `\n\n✨ ${lookFeelRefCount} look-feel reference${lookFeelRefCount === 1 ? '' : 's'} loaded.` : ''}`}
                          className="rounded-r-none"
                          style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #06B6D4 100%)', border: 'none' }}
                        >
                          {kfpFiring
                            ? 'Keyframe+…'
                            : (lookFeelRefCount > 0 ? `Keyframe+ ✨${lookFeelRefCount}` : 'Keyframe+')}
                        </ActionButton>
                        <button
                          type="button"
                          disabled={kfpFiring || shots.length === 0}
                          onClick={() => setKfpMenuOpen((v) => !v)}
                          data-testid="keyframe-plus-dropdown-chevron"
                          title="Choose Keyframe+ run size"
                          className="px-2 py-1.5 rounded-l-none border-l border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-md"
                          style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #06B6D4 100%)', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${kfpMenuOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {kfpMenuOpen && ReactDOM.createPortal(
                          <div
                            ref={kfpMenuRef}
                            className="fixed z-[9999] w-80 rounded-md border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden"
                            style={{ top: `${kfpMenuPos.top}px`, left: `${kfpMenuPos.left}px` }}
                            data-testid="keyframe-plus-dropdown-menu"
                          >
                            <div className="px-3 py-2 border-b border-white/10 bg-gradient-to-r from-violet-900/40 to-cyan-900/30">
                              <div className="text-[11px] font-bold text-violet-300 uppercase tracking-wider">Keyframe+ (parallel silo)</div>
                              <div className="text-[10px] text-white/50 mt-0.5">Writer AI + Photo engine + VLM QA</div>
                              <div className="text-[10px] text-white/60 mt-1" data-testid="keyframe-plus-progress-detail">
                                {kfpSummary.shots_with_keyframe_plus}/{kfpSummary.shots_total} done
                                {kfpSummary.shots_in_flight > 0 && <span className="text-violet-300"> · {kfpSummary.shots_in_flight} in-flight</span>}
                                {kfpSummary.shots_failed > 0 && <span className="text-rose-300"> · {kfpSummary.shots_failed} failed</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex flex-col gap-0.5"
                              data-testid="keyframe-plus-next-3-btn"
                              onClick={() => {
                                setKfpMenuOpen(false);
                                if (window.confirm(
                                  `Render the NEXT 3 shots without a Keyframe+ output using the new ` +
                                  `Photo-only pipeline (Writer AI composer → Photo engine → Writer AI vision QA).\n\n` +
                                  `This is a parallel silo — does NOT touch IGK / hyperreal / any other keyframe.\n\nContinue?`,
                                )) {
                                  fireKeyframePlusBatch(3);
                                }
                              }}
                            >
                              <span className="text-sm font-medium text-white">Keyframe+ — Next 3 shots</span>
                              <span className="text-[11px] text-white/50">Scene order · skips shots with existing Keyframe+ output</span>
                            </button>
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-white/5 flex flex-col gap-0.5"
                              data-testid="keyframe-plus-all-remaining-btn"
                              onClick={() => {
                                setKfpMenuOpen(false);
                                const remaining = shots.filter(s => !s.keyframe_plus_url || s.keyframe_plus_status === 'failed').length;
                                if (window.confirm(
                                  `Render ALL ${remaining} remaining shots without a Keyframe+ output in one batch.\n\n` +
                                  `Parallel silo — does NOT touch IGK / hyperreal / any other keyframe.\n\nContinue?`,
                                )) {
                                  fireKeyframePlusBatch(null);
                                }
                              }}
                            >
                              <span className="text-sm font-medium text-white">Keyframe+ — All remaining</span>
                              <span className="text-[11px] text-white/50">Full batch · skips shots with existing Keyframe+ output</span>
                            </button>
                          </div>,
                          document.body,
                        )}
                      </div>
                      {/* Live progress chip — polls /keyframe-plus/summary every 5s
                          so operator can see jobs fire/advance/fail without
                          opening a modal. Hides when no shots have started. */}
                      {(kfpSummary.shots_with_keyframe_plus > 0 || kfpSummary.shots_in_flight > 0 || kfpSummary.shots_failed > 0) && (
                        <div
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono"
                          style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.35)', color: '#C4B5FD' }}
                          data-testid="keyframe-plus-progress-chip"
                          title={`Keyframe+: ${kfpSummary.shots_with_keyframe_plus} done · ${kfpSummary.shots_in_flight} in-flight · ${kfpSummary.shots_failed} failed (of ${kfpSummary.shots_total})`}
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>K+ {kfpSummary.shots_with_keyframe_plus}/{kfpSummary.shots_total}</span>
                          {kfpSummary.shots_in_flight > 0 && (
                            <>
                              <span className="text-white/30">·</span>
                              <Loader2 className="w-3 h-3 animate-spin text-violet-300" />
                              <span className="text-violet-300">{kfpSummary.shots_in_flight}</span>
                            </>
                          )}
                          {kfpSummary.shots_failed > 0 && (
                            <>
                              <span className="text-white/30">·</span>
                              <span className="text-rose-300">⚠ {kfpSummary.shots_failed}</span>
                            </>
                          )}
                        </div>
                      )}
                      {hasAnyHR && failedHRCount > 0 && !processing && (
                        <ActionButton
                          variant="secondary"
                          loading={uploadingAction === 'hyperrealKeyframes'}
                          disabled={uploadingAction === 'hyperrealKeyframes'}
                          onClick={() => generateHyperrealKeyframes(true)}
                          data-testid="retry-failed-hyperreal-keyframes-btn"
                          title={`Retries the ${failedHRCount} failed hyperreal keyframe(s) via the Photo engine.`}
                        >
                          Retry failed ({failedHRCount})
                        </ActionButton>
                      )}
                    </>
                  );
                })()}
              </div>
              {user?.is_admin && shots.some(s => s.keyframe_prompt || s.mj_prompt) && (
                <ActionButton
                  variant="neutral"
                  icon={Download}
                  loading={downloading === 'mj-prompts'}
                  disabled={downloading === 'mj-prompts'}
                  onClick={() => downloadFile('mj-prompts')}
                  data-testid="download-mj-prompts-btn"
                >
                  Download MJ Prompts
                </ActionButton>
              )}
            </div>

            {/* Batch RAW/Grade buttons — only show when keyframes exist */}
            {shots.some(s => s.keyframe_url) && !pipelineProgress && (
              <div className="flex items-center gap-2 mb-4 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1 px-3 py-2 rounded-xl" style={{ background: 'rgba(100, 100, 100, 0.04)' }}>
                <span className="text-xs text-white/50 whitespace-nowrap">Batch:</span>
                <ActionButton
                  variant="neutral"
                  size="sm"
                  loading={uploadingAction === 'convertAllRaw'}
                  disabled={uploadingAction === 'convertAllRaw'}
                  onClick={() => convertAllToRaw()}
                  data-testid="convert-all-raw-btn"
                >
                  RAW
                </ActionButton>
                <ActionButton
                  variant="secondary"
                  size="sm"
                  loading={uploadingAction === 'gradeAll'}
                  disabled={uploadingAction === 'gradeAll'}
                  onClick={() => gradeAllKeyframes()}
                  data-testid="grade-all-btn"
                >
                  Grade
                </ActionButton>
                <span className="text-[9px] text-white/30 whitespace-nowrap">S-Log3 → Rec709</span>
              </div>
            )}
            {/* Reset stuck pipeline — shows when uploading OR when no keyframes generated despite being at stage 7+ */}
            {((uploading && uploadingAction === 'pipeline') || (effectiveStage >= 7 && !shots.some(s => s.keyframe_url) && !pipelineProgress)) && (
              <ActionButton
                variant="neutral"
                icon={RefreshCw}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/projects/${projectId}/reset-pipeline`);
                    setUploading(false); setUploadingAction(null); setPipelineProgress(null);
                    toast.info('Pipeline reset. You can retry now.');
                    loadProjectData();
                  } catch (err) { alert(err.response?.data?.detail || 'Reset failed'); }
                }}
                data-testid="reset-pipeline-btn"
                className="mb-4 ml-2"
              >
                Reset Stuck Pipeline
              </ActionButton>
            )}
            {/* S-Log3 backfill — see comment above. */}
            {shots.some(s => s.keyframe_hyperreal_url) && (
              <ActionButton
                variant="secondary"
                icon={Sparkles}
                loading={uploading && uploadingAction === 'slog3-backfill'}
                disabled={uploading}
                onClick={async () => {
                  if (!window.confirm('Regenerate S-Log3 files for every hyperreal keyframe in this project? Skips keyframes that already have a v2 S-Log3 file. Use Alt+click to force re-roll all.')) return;
                  const force = window.event?.altKey || false;
                  setUploading(true); setUploadingAction('slog3-backfill');
                  try {
                    const res = await axios.post(`${API}/projects/${projectId}/backfill-hyperreal-slog3-v2?bit_depth=8&force=${force}`);
                    toast.success(
                      `S-Log3 backfill complete — converted ${res.data?.converted || 0}, skipped ${res.data?.skipped || 0}.`,
                      { description: res.data?.errors?.length ? `${res.data.errors.length} shot(s) failed — see logs.` : undefined, duration: 8000 }
                    );
                    await loadProjectData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'S-Log3 backfill failed', {
                      description: err.response?.status ? `HTTP ${err.response.status}` : undefined,
                    });
                  } finally {
                    setUploading(false); setUploadingAction(null);
                  }
                }}
                data-testid="slog3-backfill-btn"
                title="Generate the S-Log3 sibling (corrected v2 transform) for any hyperreal keyframes missing it. Alt+click to force-regenerate all."
                className="mb-4 ml-2"
              >
                Regenerate S-Log3 (missing)
              </ActionButton>
            )}
            {pipelineProgress && (
              <div className="mb-4">
                <ProgressBar label={`Generating keyframes... (${pipelineProgress.keyframes_completed}/${pipelineProgress.total})`} progress={{completed: pipelineProgress.keyframes_completed, total: pipelineProgress.total, percentage: pipelineProgress.keyframes_percentage}} color="#FF9500" onStop={stopGeneration} taskType="keyframes" />
                {pipelineProgress.current_shot && (
                  <div className="mt-2 px-3 py-2 rounded-lg border border-ai-border" style={{ background: 'rgba(255,149,0,0.05)' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-white/70 font-medium">
                        Current: <span className="text-white font-semibold">{pipelineProgress.current_shot}</span>
                      </span>
                      <span className="text-[10px] font-mono text-[#FF9500]">
                        {pipelineProgress.current_step === 'prompt' ? 'Writing prompt...' : 'Generating image...'}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-lg overflow-hidden" style={{ background: 'rgba(255,149,0,0.15)' }}>
                      <div className="h-full rounded-lg transition-all duration-500"
                        style={{ width: `${pipelineProgress.current_progress || 0}%`, background: 'linear-gradient(90deg, #FF9500, #FF6B35)' }} />
                    </div>
                    <div className="text-[9px] text-white/30 mt-1 text-right">{pipelineProgress.current_progress || 0}%</div>
                  </div>
                )}
              </div>
            )}

            {/* Bulk-select toggle for Keyframes — mirrors the sketches section. */}
            {shots.some(s => s.keyframe_url) && (
              <div className="flex items-center justify-end gap-2 mb-2">
                <ActionButton
                  variant={bulkSelectMode === 'keyframes' ? 'primary' : 'neutral'}
                  icon={CheckCircle}
                  onClick={() => toggleBulkSelect('keyframes')}
                  data-testid="bulk-select-keyframes-btn"
                  title="Select multiple keyframes to delete"
                >
                  {bulkSelectMode === 'keyframes' ? 'Exit Select' : 'Select'}
                </ActionButton>
              </div>
            )}
            {bulkSelectMode === 'keyframes' && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-ai-accent/5 border border-ai-accent/20 flex items-center justify-between gap-2" data-testid="bulk-action-bar-keyframes">
                <div className="text-xs text-ai-accent/90">
                  <span className="font-bold">{selectedShotIds.size}</span> selected
                  {selectedShotIds.size === 0 && <span className="text-white/40 ml-2">· tap cards to select</span>}
                </div>
                <div className="flex items-center gap-2">
                  {selectedShotIds.size > 0 && (
                    <ActionButton
                      variant="neutral"
                      size="sm"
                      onClick={() => setSelectedShotIds(new Set(shots.map(s => s.id)))}
                      data-testid="bulk-select-all-keyframes"
                    >
                      Select all ({shots.length})
                    </ActionButton>
                  )}
                  <ActionButton
                    variant="neutral"
                    icon={Trash2}
                    loading={bulkDeleting}
                    disabled={selectedShotIds.size === 0 || bulkDeleting}
                    onClick={() => setConfirmBulkDelete(true)}
                    data-testid="bulk-delete-keyframes"
                  >
                    Delete {selectedShotIds.size > 0 ? `(${selectedShotIds.size})` : ''}
                  </ActionButton>
                </div>
              </div>
            )}

            {IGK2P_ON && shots.length > 0 && (
              <Igk2pBlueprintBoard projectId={projectId} onReload={loadProjectData} />
            )}

            {shots.length > 0 && (
              <DraggableFrameGrid
                items={shots}
                gridClass={tileGridClass}
                onReorder={handleReorderShots}
                onInsertAfter={handleInsertAfter}
                renderCard={(shot) => {
                  // Smart isGenerating: only show spinner for the next 2 shots without keyframes
                  const pendingShots = shots.filter(s => !s.keyframe_url);
                  const isInCurrentBatch = !!pipelineProgress && pendingShots.slice(0, 2).some(ps => ps.id === shot.id);
                  return (
                  <KeyframeCard key={shot.id} shot={shot} onRefresh={regenerateKeyframe} onReload={loadProjectData} onFeedback={sendKeyframeFeedback} isGenerating={isInCurrentBatch} projectId={projectId}
                    aspectClass={aspectClass}
                    userEmail={user?.email}
                    selectable={bulkSelectMode === 'keyframes'}
                    selected={selectedShotIds.has(shot.id)}
                    onToggleSelect={toggleShotSelected}
                    onDelete={(s) => setDeleteConfirm(s)}
                    onLockToggle={loadProjectData}
                    onAnimate={animateKeyframe}
                    onConvertRaw={convertKeyframeRaw}
                    onStop={stopVideoGeneration}
                    onImageClick={() => {
                      const gallery = shots
                        .filter(s => s.keyframe_url)
                        .map(s => ({
                          url: resolveUrl(s.keyframe_s3_url || s.keyframe_url),
                          type: 'image',
                          label: `Keyframe ${s.scene_number}-${s.shot_number}`,
                          shot_id: s.id,
                        }));
                      const idx = gallery.findIndex(g => g.shot_id === shot.id);
                      setGlobalLightbox({ items: gallery, index: Math.max(0, idx) });
                    }}
                    onSelectMjOption={async (shotId, index) => {
                      const res = await axios.post(`${API}/shots/${shotId}/select-mj-option`, { index });
                      toast.success(`Selected option ${index + 1}`);
                      loadProjectData();
                    }}
                    onUndo={async (shotId) => {
                      try {
                        await axios.post(`${API}/shots/${shotId}/keyframe-undo`);
                        toast.success('Reverted to pre-iteration keyframe');
                        loadProjectData();
                      } catch (err) { alert(err.response?.data?.detail || 'Undo failed'); }
                    }}
                    onRegenerate={(shotId, newPrompt) => regenerateKeyframe(shotId, newPrompt)}
                    onRevert={async (shotId) => {
                      try {
                        await axios.post(`${API}/shots/${shotId}/revert-keyframe`);
                        toast.success('Reverted to original keyframe');
                        loadProjectData();
                      } catch (err) { alert(err.response?.data?.detail || 'Revert failed'); }
                    }} />
                )}}
              />
            )}

            {shots.some(s => s.keyframe_url) && !pipelineProgress && (
              <div className="flex justify-end mt-4 gap-2">
                <button onClick={advanceStage} data-testid="approve-pipeline-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Approved, Move to VLM QA <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 5.5 AI AUDITOR (Opus 4.7 narrative-quality gate + auto-fix) ──
            ENGINE-AGNOSTIC (2026-05-28): renders for any shot with an
            active `keyframe_url`, regardless of producing engine (MJ,
            Gemini IGK, Keyframe+/gpt-image-2, or any future pipeline). */}
        {(shots.some(s => s.keyframe_url)) && (
          <CollapsibleSection
            title="2. AI Auditor (Optional)"
            icon={<div className="w-7 h-7 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-[#0EA5E9]" /></div>}
            badge={<>
              {project?.stage_5_5_processing && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#0EA5E9]/10 text-[#0EA5E9] mr-1">Auditing…</span>}
              {(() => {
                // Engine-agnostic eligibility: any shot with an active
                // keyframe_url qualifies (mirrors the backend filter).
                const eligible = shots.filter(s => s.keyframe_url);
                const audited = eligible.filter(s => s.stage_5_5_audit_latest && !s.stage_5_5_audit_latest?.error).length;
                if (audited === 0) return null;
                return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#0EA5E9]/10 text-[#0EA5E9]">{audited}/{eligible.length} audited</span>;
              })()}
            </>}
            expanded={expandedSections.stage55 ?? false}
            onToggle={() => toggleSection('stage55')}
            testId="stage-5-5-collapsible"
          >
            <Stage55Section
              projectId={projectId}
              project={project}
              shots={shots}
              onShotsRefresh={loadProjectData}
              onSkip={async () => {
                // Skip the AI audit step entirely. Collapse the 5.5
                // section + auto-approve PPM (since reaching Stage 5.5
                // means pre-production is effectively committed)
                // + advance the project + give a clear toast pointing
                // at the new stage so the operator knows where they
                // landed.
                const prevStage = project?.current_stage || 0;
                setExpandedSections(prev => ({ ...prev, stage55: false }));
                try {
                  // Auto-flip ppm_approved if it isn't already set. Every
                  // section past 5.5 (CD Keyframe Lock / Neutral Master /
                  // Video Gen / Video Lock) gates on `ppm_approved`. If the
                  // operator skipped the Pre-Production "Confirm & Move to
                  // Production" button and walked into the audit anyway,
                  // flipping it here lets the downstream sections render.
                  if (!project?.ppm_approved) {
                    try {
                      await axios.post(`${API}/projects/${projectId}/ppm-approval`, {
                        project_id: projectId, approved: true,
                      });
                    } catch (_) { /* best-effort */ }
                  }
                  // Auto-lock all unlocked keyframes — Skip Audit implies
                  // operator-approved keyframes. Implicit lock replaces the
                  // removed standalone "CD Keyframe Lock" stage.
                  if (shots.some(s => s.keyframe_url && !s.keyframe_locked)) {
                    try {
                      await axios.post(`${API}/projects/${projectId}/lock-all-keyframes`);
                    } catch (_) { /* best-effort */ }
                  }
                  const res = await axios.post(`${API}/projects/${projectId}/advance-stage`);
                  const newStage = res.data.current_stage;
                  await loadProjectData();
                  const stageNames = { 8:'Keyframe Lock', 9:'VLM QA', 10:'Keyframe Lock', 11:'Neutral Master', 12:'Video Gen', 13:'Video QA', 14:'Video Lock', 15:'Export', 16:'Post-production' };
                  toast.success(
                    `Skipped Stage 5.5 audit → advanced to Stage ${newStage}`,
                    { description: `Now: ${stageNames[newStage] || 'next stage'}. Scroll down to see the new section.`, duration: 5000 },
                  );
                  const stageToGroup = { 8:'production', 9:'production', 10:'production', 11:'production', 12:'production', 13:'production', 14:'production', 15:'production', 16:'postprod' };
                  const stageToSection = { 8:'keyframes', 9:'vlmQa', 10:'keyframeLock', 11:'neutralMaster', 12:'videoGen', 13:'videoQa', 14:'videoLock', 15:'export', 16:'postproduction' };
                  setActiveStageGroup(stageToGroup[newStage] || 'production');
                  setActiveSidebarItem(stageToGroup[newStage] === 'postprod' ? 'postprod' : 'video');
                  const section = stageToSection[newStage];
                  if (section) {
                    setExpandedSections(prev => ({ ...prev, [section]: true }));
                    setTimeout(() => {
                      const el = document.querySelector(`[data-testid="${section}-collapsible"]`)
                              || document.querySelector(`[data-testid="${section}-section"]`)
                              || document.querySelector(`[data-section="${section}"]`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 300);
                  }
                  console.info(`[Stage 5.5] operator skipped audit: ${prevStage} → ${newStage}`);
                } catch (e) {
                  toast.error('Skip failed: ' + (e.response?.data?.detail || e.message));
                }
              }}
            />
          </CollapsibleSection>
        )}

        {/* ── 8b. KEYFRAME ANGLES (Hidden for now) ── */}
        {false && effectiveStage >= 7 && shots.some(s => s.keyframe_url) && (
          <CollapsibleSection
            title="8a. Keyframe Angles"
            icon={<div className="w-7 h-7 rounded-lg bg-[#FF9500]/10 flex items-center justify-center"><Film className="w-3.5 h-3.5 text-[#FF9500]" /></div>}
            badge={shots.some(s => s.keyframe_angles?.length > 0) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF9500]/10 text-[#FF9500]">{shots.filter(s => s.keyframe_angles?.length > 0).length} with angles</span>}
            expanded={expandedSections.keyframeAngles}
            onToggle={() => toggleSection('keyframeAngles')}
            testId="keyframe-angles-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Generate alternative angles. Use checkboxes to select multiple angles as sub-shots for video generation.
            </p>
            <button onClick={generateKeyframeAngles} disabled={uploadingAction === "keyframeAngles"} data-testid="generate-keyframe-angles-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 mb-4 ${
                uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-ai-accent text-white hover:bg-ai-accent/80 active:scale-95'
              }`}>
              {uploadingAction === 'keyframeAngles' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              {shots.some(s => s.keyframe_angles?.length > 0) ? 'Regenerate Angles' : 'Generate Angles'}
            </button>
            {keyframeAnglesProgress?.processing && (
              <ProgressBar label="Generating keyframe angles with EiPiMedia AI Pro..." progress={{completed: keyframeAnglesProgress.completed, total: keyframeAnglesProgress.total, percentage: Math.round((keyframeAnglesProgress.completed / Math.max(keyframeAnglesProgress.total, 1)) * 100)}} color="#FF9500" onStop={stopGeneration} taskType="keyframe_angles" />
            )}
            <div className="space-y-4 mt-3">
              {shots.filter(s => s.keyframe_url).map((shot) => {
                const selectedAngles = shot.selected_keyframe_angles || [shot.selected_keyframe_angle ?? 0];
                const isMultiSelected = selectedAngles.length > 1;
                return (
                <div key={shot.id} className="rounded-xl p-3 bg-white/[0.02]" data-testid={`keyframe-angle-row-${shot.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white">{shot.scene_number} — {shot.shot_number}</span>
                    <div className="flex items-center gap-2">
                      {(shot.keyframe_angles_generating || generatingAngleShots.has(`${shot.id}_keyframe`)) && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF9500]" />}
                      <button 
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); generateSingleAngles(shot.id, 'keyframe'); }}
                        disabled={shot.keyframe_angles_generating || generatingAngleShots.has(`${shot.id}_keyframe`)}
                        className="px-2 py-1 rounded text-[10px] font-medium bg-[#FF9500]/10 text-[#FF9500] hover:bg-[#FF9500]/20 transition-colors disabled:opacity-30 flex items-center gap-1 whitespace-nowrap"
                        title="Generate angles for this keyframe"
                        data-testid={`gen-angles-kf-${shot.id}`}
                      >
                        {(shot.keyframe_angles_generating || generatingAngleShots.has(`${shot.id}_keyframe`)) ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Angles
                      </button>
                      {isMultiSelected && (
                        <span className="text-[10px] px-2 py-0.5 bg-[#FF9500]/10 text-[#FF9500] rounded-full font-medium">
                          {selectedAngles.length} sub-shots
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {/* Original */}
                    <div className={`relative rounded-lg overflow-hidden border-2 transition-all ${selectedAngles.includes(0) ? 'border-[#FF9500] shadow-md' : 'border-transparent hover:border-ai-border'}`}>
                      <div className="cursor-pointer" onClick={() => selectKeyframeAngle(shot.id, 0)} data-testid={`select-kf-original-${shot.id}`}>
                        {shot.keyframe_url && <img src={resolveUrl(shot.keyframe_url)} alt="Original" className={`w-full ${aspectClass} object-cover`} />}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 font-medium flex items-center justify-center gap-1">
                        <input type="checkbox" checked={selectedAngles.includes(0)} onChange={() => selectKeyframeAngle(shot.id, 0, true)}
                          className="w-3 h-3 accent-[#FF9500]" data-testid={`checkbox-kf-original-${shot.id}`} onClick={e => e.stopPropagation()} />
                        Original
                      </div>
                    </div>
                    {/* Alternative angles */}
                    {(shot.keyframe_angles || []).map((angle, ai) => (
                      <div key={ai} className={`relative rounded-lg overflow-hidden border-2 transition-all ${selectedAngles.includes(ai + 1) ? 'border-[#FF9500] shadow-md' : 'border-transparent hover:border-ai-border'}`}>
                        <div className="cursor-pointer" onClick={() => selectKeyframeAngle(shot.id, ai + 1)} data-testid={`select-kf-angle-${shot.id}-${ai}`}>
                          <img src={resolveUrl(angle.url)} alt={angle.angle_name} className={`w-full ${aspectClass} object-cover`} />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 font-medium flex items-center justify-center gap-1">
                          <input type="checkbox" checked={selectedAngles.includes(ai + 1)} onChange={() => selectKeyframeAngle(shot.id, ai + 1, true)}
                            className="w-3 h-3 accent-[#FF9500]" data-testid={`checkbox-kf-angle-${shot.id}-${ai}`} onClick={e => e.stopPropagation()} />
                          {angle.angle_name}
                        </div>
                      </div>
                    ))}
                    {(!shot.keyframe_angles || shot.keyframe_angles.length === 0) && (
                      <>
                        {(shot.keyframe_angles_generating || generatingAngleShots.has(`${shot.id}_keyframe`)) ? (
                          <>
                            <div className={`${aspectClass} rounded-lg flex flex-col items-center justify-center overflow-hidden relative`} style={{ background: 'rgba(255,149,0,0.05)' }}>
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-shimmer" />
                              <Loader2 className="w-5 h-5 animate-spin text-[#FF9500] mb-1" />
                              <span className="text-[9px] text-[#FF9500]/70">Generating...</span>
                            </div>
                            <div className={`${aspectClass} rounded-lg flex flex-col items-center justify-center overflow-hidden relative`} style={{ background: 'rgba(255,149,0,0.05)' }}>
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-shimmer" style={{ animationDelay: '0.5s' }} />
                              <Loader2 className="w-5 h-5 animate-spin text-[#FF9500] mb-1" style={{ animationDelay: '0.3s' }} />
                              <span className="text-[9px] text-[#FF9500]/70">Generating...</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`${aspectClass} glass-panel rounded-lg flex items-center justify-center`}><span className="text-[10px] text-white/40">Low Angle</span></div>
                            <div className={`${aspectClass} glass-panel rounded-lg flex items-center justify-center`}><span className="text-[10px] text-white/40">High Angle</span></div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )})}
            </div>
          </CollapsibleSection>
        )}


        {/* ── 8b. FACE ALIGNMENT (Hidden for now) ── */}
        {false && isPpmApproved() && shots.some(s => s.keyframe_url) && (
          <CollapsibleSection
            title="8b. Face Alignment"
            icon={<div className="w-7 h-7 rounded-lg bg-[#FF9500]/10 flex items-center justify-center"><Users className="w-3.5 h-3.5 text-[#FF9500]" /></div>}
            badge={(() => {
              const accepted = shots.filter(s => s.face_swap_status === 'accepted').length;
              const swapped = shots.filter(s => s.face_swap_status === 'swapped').length;
              const detected = shots.filter(s => s.face_detected).length;
              if (accepted > 0) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{accepted} accepted</span>;
              if (swapped > 0) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF9500]/10 text-[#FF9500]">{swapped} ready</span>;
              if (detected > 0) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#0EA5E9]/10 text-[#0EA5E9]">{detected} faces</span>;
              return null;
            })()}
            expanded={expandedSections.faceConsistency}
            onToggle={() => toggleSection('faceConsistency')}
            testId="face-alignment-section"
          >
            <p className="text-xs text-white/60 mb-3">
              Detect faces in keyframes, then assign cast identities. AI will replace faces while preserving composition, lighting, and costume.
            </p>

            {/* Cast Reference Strip */}
            {(() => {
              const castMembers = assetBible?.cast_candidates || [];
              if (!castMembers.length) return (
                <div className="p-3 rounded-lg border border-yellow-500/20 bg-yellow-500/5 mb-4">
                  <span className="text-xs text-yellow-400">No cast in Asset Bible. Add cast members in the Assets stage first.</span>
                </div>
              );
              return null;
            })()}

            {/* Action Buttons Row */}
            <div className="flex items-center gap-3 mb-4 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
              {/* Detect Faces Button */}
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/projects/${projectId}/detect-faces`);
                    toast.success('Face detection started');
                    setProject(p => ({ ...p, face_detect_processing: true, face_detect_completed: 0, face_detect_total: shots.filter(s=>s.keyframe_url).length }));
                    const poll = setInterval(async () => {
                      try {
                        const res = await axios.get(`${API}/projects/${projectId}/face-detect-status`);
                        setProject(p => ({ ...p, face_detect_processing: res.data.processing, face_detect_completed: res.data.completed, face_detect_total: res.data.total }));
                        if (!res.data.processing) {
                          clearInterval(poll);
                          toast.success('Face detection complete');
                          loadProjectData();
                        }
                      } catch { /* ignore */ }
                    }, 2000);
                  } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                }}
                disabled={project.face_detect_processing || project.face_swap_processing}
                data-testid="detect-faces-btn"
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/80 active:scale-95 disabled:opacity-50"
              >
                {project.face_detect_processing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Detecting Faces ({project.face_detect_completed || 0}/{project.face_detect_total || '?'})...</>
                ) : (
                  <><Eye className="w-4 h-4" /> {shots.some(s => s.face_detected !== undefined) ? 'Re-detect Faces' : 'Detect Faces'}</>
                )}
              </button>

              {/* Engine Selector */}
              <select
                id="face-engine-select"
                data-testid="face-engine-select"
                defaultValue="insightface"
                className="text-xs py-2 px-3 rounded-lg bg-white/[0.05] border border-white/10 text-white/80 focus:border-[#FF9500]/50 focus:outline-none"
              >
                <option value="insightface" className="bg-[#1a1a2e]">InsightFace (Geometric)</option>
                <option value="nano_banana" className="bg-[#1a1a2e]">Sketch engine (AI Gen)</option>
              </select>

              {/* Run Face Swap (Batch) — only for shots with face detected + assignments */}
              <button
                onClick={async () => {
                  const assignmentEls = document.querySelectorAll('[data-cast-select]');
                  const assignments = {};
                  assignmentEls.forEach(el => {
                    const shotId = el.dataset.castSelect;
                    const faceDetected = el.dataset.faceDetected;
                    const val = el.value;
                    // Only include shots with face detected AND a cast selection
                    if (val && val !== 'none' && faceDetected === 'true') {
                      assignments[shotId] = [{ cast_index: parseInt(val) }];
                    }
                  });
                  if (Object.keys(assignments).length === 0) {
                    alert('No face-detected shots with cast assigned. Click "Detect Faces" first, then select a cast member for shots with faces.');
                    return;
                  }
                  const engine = document.getElementById('face-engine-select')?.value || 'insightface';
                  try {
                    await axios.post(`${API}/projects/${projectId}/save-cast-assignments`, { assignments });
                    await axios.post(`${API}/projects/${projectId}/execute-face-swaps`, { engine });
                    toast.success(`Face swap started (${engine})`);
                    setProject(p => ({ ...p, face_swap_processing: true, face_swap_completed: 0, face_swap_total: Object.keys(assignments).length, face_swap_corrected: 0 }));
                    const poll = setInterval(async () => {
                      try {
                        const res = await axios.get(`${API}/projects/${projectId}/face-swap-status`);
                        setProject(p => ({ ...p, face_swap_processing: res.data.processing, face_swap_completed: res.data.completed, face_swap_total: res.data.total, face_swap_corrected: res.data.corrected }));
                        if (!res.data.processing) {
                          clearInterval(poll);
                          toast.success(`Face swap complete: ${res.data.corrected}/${res.data.total} swapped`);
                          loadProjectData();
                        }
                      } catch { /* ignore */ }
                    }, 3000);
                  } catch (err) { alert(err.response?.data?.detail || 'Failed to start face swap'); }
                }}
                disabled={project.face_swap_processing || project.face_detect_processing}
                data-testid="run-face-swap-btn"
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 bg-[#FF9500] text-white hover:bg-[#E68A00] active:scale-95 disabled:opacity-50"
              >
                {project.face_swap_processing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Swapping Faces...</>
                ) : (
                  <><Wand2 className="w-4 h-4" /> Run Batch Swap</>
                )}
              </button>

              {/* Accept All swapped */}
              {!project.face_swap_processing && shots.some(s => s.face_swap_status === 'swapped') && (
                <button
                  onClick={async () => {
                    const swapped = shots.filter(s => s.face_swap_status === 'swapped');
                    for (const s of swapped) {
                      try { await axios.post(`${API}/shots/${s.id}/accept-face-swap`); } catch {}
                    }
                    toast.success(`Accepted ${swapped.length} face swaps`);
                    loadProjectData();
                  }}
                  data-testid="accept-all-swaps-btn"
                  className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 border border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10 active:scale-95"
                >
                  <Check className="w-4 h-4" /> Accept All
                </button>
              )}
              {/* Revert All */}
              {!project.face_swap_processing && shots.some(s => s.keyframe_url_original) && (
                <button
                  onClick={async () => {
                    
                    try {
                      const res = await axios.post(`${API}/projects/${projectId}/revert-all-face-swaps`);
                      toast.success(res.data.message);
                      loadProjectData();
                    } catch (err) { alert(err.response?.data?.detail || 'Revert failed'); }
                  }}
                  data-testid="revert-all-swaps-btn"
                  className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 active:scale-95"
                >
                  <RotateCcw className="w-4 h-4" /> Revert All
                </button>
              )}
              {!project.face_swap_processing && (
                <button onClick={() => skipToStage(10, 'Face Alignment')}
                  className="px-3 py-2 text-white/40 rounded-lg text-xs font-medium hover:text-white/60 hover:bg-white/5 transition-all">
                  Skip this stage &rarr;
                </button>
              )}
            </div>

            {/* Progress bar during batch processing */}
            {project.face_swap_processing && (() => {
              const completed = project.face_swap_completed || 0;
              const total = project.face_swap_total || 1;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              return (
                <ProgressBar
                  label={`Swapping faces... (${completed}/${total})`}
                  progress={{ completed, total, percentage: pct }}
                  color="#FF9500"
                />
              );
            })()}

            {/* Face detection progress */}
            {project.face_detect_processing && (() => {
              const completed = project.face_detect_completed || 0;
              const total = project.face_detect_total || 1;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              return (
                <ProgressBar
                  label={`Detecting faces... (${completed}/${total})`}
                  progress={{ completed, total, percentage: pct }}
                  color="#0EA5E9"
                />
              );
            })()}

            {/* Keyframe grid with face alignment cards */}
            {shots.some(s => s.keyframe_url) && (
              <div className={`grid gap-3 mt-4 ${tileGridClass}`}>
                {shots.filter(s => s.keyframe_url).map((shot, idx) => {
                  const castMembers = assetBible?.cast_candidates || [];
                  const isProcessing = project.face_swap_processing;
                  const completedCount = project.face_swap_completed || 0;
                  const isThisProcessing = (isProcessing && shot.face_swap_status !== 'swapped' && shot.face_swap_status !== 'skipped' && shot.face_swap_status !== 'failed' && idx <= completedCount) || shot.face_swap_status === 'processing';
                  const isWaiting = isProcessing && !['swapped','skipped','failed','processing'].includes(shot.face_swap_status) && idx > completedCount;
                  const swapStatus = shot.face_swap_status;
                  const hasSwapResult = swapStatus === 'swapped' || swapStatus === 'accepted';
                  const faceDetected = shot.face_detected;
                  const faceQuality = shot.face_quality;
                  const faceDetectionDone = shot.face_detected !== undefined;
                  
                  // Determine which image to show
                  const displayUrl = swapStatus === 'accepted' ? shot.keyframe_url
                    : swapStatus === 'swapped' ? (shot.face_swap_url || shot.keyframe_url)
                    : shot.keyframe_url;
                  const resolvedUrl = displayUrl?.startsWith('http') ? displayUrl : `${API.replace('/api','')}/api/storage/${displayUrl}`;
                  const originalUrl = shot.keyframe_url_original
                    ? (shot.keyframe_url_original.startsWith('http') ? shot.keyframe_url_original : `${API.replace('/api','')}/api/storage/${shot.keyframe_url_original}`)
                    : null;

                  return (
                    <div key={shot.id} className="relative group rounded-xl overflow-hidden border border-white/[0.06] bg-white/[0.02]" data-testid={`fa-card-${shot.id}`}>
                      <div className={`relative ${aspectClass}`}>
                        <img src={resolvedUrl} alt={`Shot ${shot.shot_number}`}
                          className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                          onClick={() => setGlobalLightbox({ url: resolvedUrl, alt: `Shot ${shot.shot_number}` })} />
                        
                        {/* Processing overlay */}
                        {isThisProcessing && (
                          <div className="absolute inset-0 z-10">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#FF9500]/20 to-transparent animate-shimmer" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <div className="flex flex-col items-center gap-1.5">
                                <Loader2 className="w-6 h-6 text-[#FF9500] animate-spin" />
                                <span className="text-[10px] font-medium text-[#FF9500]">Applying Face...</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {isWaiting && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
                            <span className="text-[10px] text-white/40">Queued</span>
                          </div>
                        )}

                        {/* Status badges */}
                        <div className="absolute top-1.5 left-1.5 flex flex-col gap-1 z-10">
                          {/* Face detection badge */}
                          {faceDetectionDone && !faceDetected && (
                            <div className="px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-[8px] font-bold backdrop-blur-sm">NO FACE</div>
                          )}
                          {faceDetectionDone && faceDetected && !swapStatus && (
                            <div className="px-2 py-0.5 rounded-full bg-[#0EA5E9]/80 text-white text-[8px] font-bold backdrop-blur-sm">FACE FOUND</div>
                          )}
                        </div>
                        <div className="absolute top-1.5 right-1.5 z-10">
                          {swapStatus === 'accepted' && !isProcessing && (
                            <div className="px-2 py-0.5 rounded-full bg-[#22C55E] text-white text-[8px] font-bold">ACCEPTED</div>
                          )}
                          {swapStatus === 'swapped' && !isProcessing && (
                            <div className="px-2 py-0.5 rounded-full bg-[#FF9500] text-white text-[8px] font-bold">REVIEW</div>
                          )}
                          {swapStatus === 'failed' && !isProcessing && (
                            <div className="px-2 py-0.5 rounded-full bg-red-500/80 text-white text-[8px] font-bold">FAILED</div>
                          )}
                          {swapStatus === 'rejected' && !isProcessing && (
                            <div className="px-2 py-0.5 rounded-full bg-white/20 text-white/60 text-[8px] font-bold">ORIGINAL</div>
                          )}
                        </div>
                      </div>

                      <div className="p-2 space-y-1.5">
                        {/* Shot info + engine badge */}
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-medium text-white/70">Shot {shot.shot_number}</span>
                          <div className="flex items-center gap-1">
                            {shot.face_swap_engine && (
                              <span className="text-[8px] px-1.5 py-0.5 rounded-lg bg-[#FF9500]/10 text-[#FF9500] font-medium">
                                {shot.face_swap_engine === 'insightface' ? 'InsightFace' : 'NanoBanana'}
                              </span>
                            )}
                            {faceDetected && shot.face_count > 1 && (
                              <span className="text-[8px] px-1.5 py-0.5 rounded-lg bg-[#0EA5E9]/10 text-[#0EA5E9] font-medium">
                                {shot.face_count} faces
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Cast Thumbnail Strip — only shown if face is detected */}
                        {faceDetected && castMembers.length > 0 && !isProcessing && swapStatus !== 'accepted' && (
                          <div className="space-y-1">
                            <span className="text-[8px] text-white/30 uppercase tracking-wider">Select cast identity:</span>
                            <div className="flex gap-1.5 overflow-x-auto pb-1">
                              {castMembers.map((c, i) => {
                                const isSelected = shot.face_swap_cast_index === i || (shot.cast_assignments?.[0]?.cast_index === i);
                                return (
                                  <button
                                    key={i}
                                    onClick={async () => {
                                      const engine = document.getElementById('face-engine-select')?.value || 'insightface';
                                      try {
                                        // Mark this shot as processing immediately
                                        toast.info(`Applying ${c.name}...`);
                                        // Update the hidden select to match
                                        const sel = document.querySelector(`[data-cast-select="${shot.id}"]`);
                                        if (sel) sel.value = i.toString();
                                        await axios.post(`${API}/shots/${shot.id}/apply-face`, { cast_index: i, engine });
                                        // Poll for completion
                                        const pollSingle = setInterval(async () => {
                                          try {
                                            const sRes = await axios.get(`${API}/projects/${projectId}/shots`);
                                            const updShot = sRes.data.find(s => s.id === shot.id);
                                            if (updShot && updShot.face_swap_status !== 'processing') {
                                              clearInterval(pollSingle);
                                              if (updShot.face_swap_status === 'swapped') {
                                                toast.success(`${c.name} applied to Shot ${shot.shot_number}`);
                                              } else if (updShot.face_swap_status === 'failed') {
                                                toast.error(`Failed for Shot ${shot.shot_number}`);
                                              }
                                              loadProjectData();
                                            }
                                          } catch {}
                                        }, 3000);
                                      } catch (err) { toast.error(err.response?.data?.detail || 'Apply failed'); }
                                    }}
                                    data-testid={`cast-thumb-${shot.id}-${i}`}
                                    className={`flex-shrink-0 flex flex-col items-center gap-0.5 p-1 rounded-lg border transition-all hover:bg-white/5 cursor-pointer ${
                                      isSelected ? 'border-[#FF9500] bg-[#FF9500]/10' : 'border-white/[0.06]'
                                    }`}
                                    title={`Apply ${c.name}`}
                                  >
                                    <img
                                      src={c.mugshot_url || c.url || ''}
                                      alt={c.name}
                                      className="w-8 h-8 rounded-lg object-cover border border-white/10"
                                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect fill="%23333" width="32" height="32"/><text x="16" y="20" text-anchor="middle" fill="%23888" font-size="14">?</text></svg>'; }}
                                    />
                                    <span className="text-[7px] text-white/50 max-w-[48px] truncate">{c.name}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {/* Hidden select for batch compatibility */}
                            <select
                              data-cast-select={shot.id}
                              data-face-detected={faceDetected ? 'true' : 'false'}
                              defaultValue={shot.cast_assignments?.[0]?.cast_index?.toString() || 'none'}
                              className="hidden"
                            >
                              <option value="none">none</option>
                              {castMembers.map((c, i) => <option key={i} value={i}>{c.name}</option>)}
                            </select>
                          </div>
                        )}

                        {/* No face — no cast options, just status */}
                        {faceDetectionDone && !faceDetected && (
                          <div className="text-[9px] text-white/30 italic py-1">
                            {faceQuality === 'tiny' ? 'Face too small for replacement' : 'No visible face in this keyframe'}
                          </div>
                        )}

                        {/* Not yet scanned — show prompt to detect */}
                        {!faceDetectionDone && !isProcessing && swapStatus !== 'accepted' && (
                          <div className="text-[9px] text-white/30 italic py-1">
                            Click "Detect Faces" to scan for faces
                          </div>
                        )}

                        {/* Accepted character name */}
                        {swapStatus === 'accepted' && shot.face_swap_characters && (
                          <span className="text-[9px] text-[#22C55E]/80">{shot.face_swap_characters.join(', ')}</span>
                        )}

                        {/* Accept / Reject buttons */}
                        {swapStatus === 'swapped' && !isProcessing && (
                          <div className="flex gap-1.5">
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/shots/${shot.id}/accept-face-swap`);
                                  toast.success(`Shot ${shot.shot_number}: swap accepted`);
                                  loadProjectData();
                                } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                              }}
                              data-testid={`accept-swap-${shot.id}`}
                              className="flex-1 text-[9px] py-1 px-2 rounded-lg font-bold text-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 transition-all border border-[#22C55E]/20"
                            >
                              ACCEPT
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/shots/${shot.id}/reject-face-swap`);
                                  toast.success(`Shot ${shot.shot_number}: keeping original`);
                                  loadProjectData();
                                } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                              }}
                              data-testid={`reject-swap-${shot.id}`}
                              className="flex-1 text-[9px] py-1 px-2 rounded-lg font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all border border-red-500/20"
                            >
                              REJECT
                            </button>
                          </div>
                        )}

                        {/* Undo for accepted swaps */}
                        {swapStatus === 'accepted' && !isProcessing && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/shots/${shot.id}/reject-face-swap`);
                                toast.success(`Shot ${shot.shot_number}: reverted to original`);
                                loadProjectData();
                              } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                            }}
                            data-testid={`undo-swap-${shot.id}`}
                            className="w-full text-[9px] py-1 px-2 rounded-lg font-bold text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20 transition-all border border-yellow-500/20"
                          >
                            UNDO — Revert to Original
                          </button>
                        )}

                        {/* Before/After toggle */}
                        {hasSwapResult && originalUrl && !isProcessing && (
                          <button
                            onClick={(e) => {
                              const card = e.currentTarget.closest('[data-testid]');
                              const img = card?.querySelector('img');
                              if (img) {
                                const isShowingOriginal = img.dataset.showing === 'original';
                                img.src = isShowingOriginal ? resolvedUrl : originalUrl;
                                img.dataset.showing = isShowingOriginal ? 'swapped' : 'original';
                                e.currentTarget.textContent = isShowingOriginal ? 'Show Original' : 'Show Swapped';
                              }
                            }}
                            data-testid={`toggle-compare-${shot.id}`}
                            className="w-full text-[8px] py-0.5 rounded text-white/40 hover:text-white/60 transition-colors"
                          >
                            Show Original
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CollapsibleSection>
        )}


        {/* ── POST-PPM: STAGES 9-16 ── */}

        {/* ── 6. VLM QA — REMOVED 2026-05-28 ──
            Folded into AI Auditor (Stage 2). Standalone VLM stage was
            redundant: K+ has its own VLM audit baked into generation
            (KFP_DISABLE_VLM flag), and Opus 4.7 auditor already covers
            the same axes (composition, lighting, subject clarity,
            production readiness). Wrapping render in `false &&` keeps
            the code path intact for reference; remove next sweep. */}
        {false && isPpmApproved() && shots.some(s => s.keyframe_url || s.keyframe_hyperreal_url) && (
          <CollapsibleSection
            title="6. Keyframe VLM (Quality Assurance)"
            icon={<div className="w-7 h-7 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center"><Eye className="w-3.5 h-3.5 text-[#0EA5E9]" /></div>}
            badge={shots.some(s => s.vlm_qa_score) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.vlm_qa_passed).length} passed</span>}
            expanded={expandedSections.vlmQa}
            onToggle={() => toggleSection('vlmQa')}
            testId="vlm-qa-section"
          >
            <p className="text-xs text-white/60 mb-3">
              Run AI-powered quality analysis on each keyframe. The VLM checks composition, lighting, subject clarity, and production readiness.
            </p>
            <div className="flex items-center gap-3 mb-4 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
              <button onClick={runVlmQa} disabled={uploadingAction === "vlmqa"} data-testid="run-vlm-qa-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 ${
                  uploading ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-ai-accent/100 text-white hover:bg-ai-accent active:scale-95 shadow-lg shadow-ai-accent/25'
                }`}>
                {uploadingAction === 'vlmqa' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                {shots.some(s => s.vlm_qa_score) ? 'Re-run VLM QA' : 'Run VLM QA'}
              </button>
              {!shots.some(s => s.vlm_qa_score) && (
                <button onClick={() => skipToStage(10, 'VLM QA')} data-testid="skip-vlm-inline"
                  className="px-3 py-2 text-white/40 rounded-lg text-xs font-medium hover:text-white/60 hover:bg-white/5 transition-all">
                  Skip this stage →
                </button>
              )}
              {shots.some(s => s.vlm_qa_score && s.vlm_qa_score < 90) && (
                <button onClick={autoImproveAll} disabled={uploadingAction === "autoImprove"} data-testid="auto-improve-all-btn"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 ${
                    uploading ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-gradient-to-r from-[#0EA5E9] to-[#14B8A6] text-white hover:from-[#0284C7] hover:to-[#0D9488] active:scale-95 shadow-lg shadow-ai-accent/25'
                  }`}>
                  {uploadingAction === 'autoImproveAll' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Auto-Improve All ({shots.filter(s => s.vlm_qa_score && s.vlm_qa_score < 90).length} low scores)
                </button>
              )}
            </div>
            {vlmQaProgress && <ProgressBar label="Analyzing keyframes with EiPiMedia AI Pro..." progress={vlmQaProgress} color="#0EA5E9" onStop={stopGeneration} taskType="vlm" />}
            {autoImproveAllProgress && (
              <div className="mb-4 p-4 rounded-xl border border-ai-border" style={{ background: 'rgba(14, 165, 233, 0.1)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-ai-text flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Auto-improving keyframes...
                  </span>
                  <span className="text-xs text-ai-accent">{autoImproveAllProgress.completed}/{autoImproveAllProgress.total}</span>
                </div>
                <div className="w-full rounded-lg h-2 overflow-hidden" style={{ background: 'rgba(14, 165, 233, 0.2)' }}>
                  <div 
                    className="h-full bg-gradient-to-r from-[#0EA5E9] to-[#14B8A6] rounded-lg transition-all duration-500"
                    style={{ width: `${(autoImproveAllProgress.completed / Math.max(autoImproveAllProgress.total, 1)) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-ai-text/70 mt-2">AI is analyzing feedback, rewriting prompts, and regenerating keyframes...</p>
              </div>
            )}
            {shots.some(s => s.vlm_qa_score) && (
              <div className={`grid ${tileGridClass} gap-3 mt-3`}>
                {shots.filter(s => s.keyframe_url).map(shot => {
                  const details = shot.vlm_qa_details || {};
                  const issues = details.issues || [];
                  const suggestions = details.suggestions || [];
                  const assetNotes = details.asset_notes || [];
                  const assetsChecked = details.assets_checked || {};
                  return (
                    <div key={shot.id} className={`p-3 rounded-xl border ${shot.vlm_qa_passed ? 'bg-[#22C55E]/5 border-[#22C55E]/20' : 'bg-[#FF9500]/5 border-[#FF9500]/20'}`} data-testid={`vlm-qa-card-${shot.id}`}>
                      <div className="flex items-start gap-3">
                        {shot.keyframe_url && (
                          <img src={resolveUrl(shot.keyframe_url)} alt="" className="w-16 h-10 rounded-md object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-xs font-semibold">{shot.scene_number}—{shot.shot_number}</div>
                            <div className={`text-lg font-bold ${shot.vlm_qa_passed ? 'text-[#22C55E]' : 'text-[#FF9500]'}`}>
                              {shot.vlm_qa_score || '—'}
                            </div>
                          </div>
                          {/* Technical sub-scores */}
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-1">
                            {details.composition && <span className="text-[9px] text-white/50">Comp: {details.composition}</span>}
                            {details.lighting && <span className="text-[9px] text-white/50">Light: {details.lighting}</span>}
                            {details.subject_clarity && <span className="text-[9px] text-white/50">Clarity: {details.subject_clarity}</span>}
                            {details.mood_match && <span className="text-[9px] text-white/50">Mood: {details.mood_match}</span>}
                          </div>
                          {/* Asset match scores */}
                          {(details.cast_match || details.costume_match || details.environment_match || details.product_match) && (
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-1.5 border-t border-ai-border pt-1 mt-1">
                              {details.cast_match != null && <span className={`text-[9px] font-medium ${details.cast_match >= 70 ? 'text-[#22C55E]' : 'text-[#FF9500]'}`}>Cast: {details.cast_match}</span>}
                              {details.costume_match != null && <span className={`text-[9px] font-medium ${details.costume_match >= 70 ? 'text-[#22C55E]' : 'text-[#FF9500]'}`}>Costume: {details.costume_match}</span>}
                              {details.environment_match != null && <span className={`text-[9px] font-medium ${details.environment_match >= 70 ? 'text-[#22C55E]' : 'text-[#FF9500]'}`}>Env: {details.environment_match}</span>}
                              {details.product_match != null && <span className={`text-[9px] font-medium ${details.product_match >= 70 ? 'text-[#22C55E]' : 'text-[#FF3B30]'}`}>Product: {details.product_match}</span>}
                            </div>
                          )}
                          {/* Assets checked badge */}
                          {assetsChecked.cast && (
                            <div className="text-[8px] text-white/40 mb-1">
                              Checked against: {[...(assetsChecked.cast || []), ...(assetsChecked.costumes || []), ...(assetsChecked.locations || []), assetsChecked.product].filter(Boolean).join(', ')}
                            </div>
                          )}
                          {/* What's working */}
                          {suggestions.length > 0 && shot.vlm_qa_passed && (
                            <div className="mb-1">
                              <div className="text-[9px] font-semibold text-[#22C55E] mb-0.5">Working well:</div>
                              <ul className="text-[9px] text-white/60 space-y-0.5">
                                {suggestions.slice(0, 2).map((s, i) => <li key={i} className="leading-tight">+ {s}</li>)}
                              </ul>
                            </div>
                          )}
                          {/* What's not working */}
                          {issues.length > 0 && (
                            <div className="mb-1">
                              <div className="text-[9px] font-semibold text-[#FF9500] mb-0.5">Needs attention:</div>
                              <ul className="text-[9px] text-white/60 space-y-0.5">
                                {issues.slice(0, 3).map((s, i) => <li key={i} className="leading-tight">- {s}</li>)}
                              </ul>
                            </div>
                          )}
                          {/* Asset consistency notes */}
                          {assetNotes.length > 0 && (
                            <div>
                              <div className="text-[9px] font-semibold text-ai-accent mb-0.5">Asset Notes:</div>
                              <ul className="text-[9px] text-white/60 space-y-0.5">
                                {assetNotes.slice(0, 3).map((s, i) => <li key={i} className="leading-tight">{s}</li>)}
                              </ul>
                            </div>
                          )}
                          {!issues.length && !suggestions.length && !assetNotes.length && (
                            <div className="text-[9px] text-white/60">{shot.vlm_qa_passed ? 'Passed — production ready' : 'Needs review'}</div>
                          )}
                          {/* Auto-Improve button for low scores */}
                          {shot.vlm_qa_score && shot.vlm_qa_score < 90 && (
                            <button 
                              onClick={() => autoImproveSingle(shot.id)}
                              disabled={autoImproving[shot.id] || uploading}
                              data-testid={`auto-improve-${shot.id}`}
                              className={`mt-2 w-full px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center justify-center gap-1.5 ${
                                autoImproving[shot.id] 
                                  ? 'bg-ai-accent/15 text-ai-accent cursor-not-allowed' 
                                  : 'bg-gradient-to-r from-[#0EA5E9] to-[#14B8A6] text-white hover:from-[#0284C7] hover:to-[#0D9488] active:scale-95 shadow-sm'
                              }`}
                            >
                              {autoImproving[shot.id] ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Improving... (iter {shot.auto_improve_iteration || 1}/3)
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3" />
                                  Auto-Improve to 90+
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {shots.some(s => s.vlm_qa_score) && (
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => skipToStage(10, 'VLM QA')} data-testid="skip-vlm-qa-btn"
                  className="px-4 py-2 text-white/50 rounded-lg text-xs font-medium hover:text-white/70 hover:bg-white/5 transition-all flex items-center gap-1.5">
                  Skip VLM QA <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button onClick={advanceStage} data-testid="approve-vlm-qa-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Approved, Move to Keyframe Lock <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 10. CD KEYFRAME LOCK — REMOVED 2026-05-28 ──
            Auto-lock is now implicit: every advance-stage from the
            Keyframe Gallery / AI Auditor flips `keyframe_locked = true`
            on every shot with a keyframe_url (see lockAllKeyframes call
            wired into advanceStage / Stage 5.5 skip). Standalone lock
            stage was UX noise. Wrapping render in `false &&` keeps the
            code path intact for reference; remove next sweep. */}
        {false && isPpmApproved() && effectiveStage >= 9 && (
          <CollapsibleSection
            title="5b. CD Keyframe Lock"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/15 flex items-center justify-center"><Lock className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={shots.some(s => s.keyframe_locked) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.keyframe_locked).length} locked</span>}
            expanded={expandedSections.keyframeLock}
            onToggle={() => toggleSection('keyframeLock')}
            testId="keyframe-lock-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Lock all keyframes that passed QA. Locked keyframes will be used for video generation.
            </p>
            <button onClick={lockAllKeyframes} disabled={uploadingAction === "lockKeyframes"} data-testid="lock-all-keyframes-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 mb-4 ${
                uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-[#22C55E] text-white hover:bg-ai-accent active:scale-95'
              }`}>
              {uploadingAction === 'lockKeyframes' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Lock All Keyframes ({shots.filter(s => s.keyframe_url && !s.keyframe_locked).length} pending)
            </button>

            {shots.some(s => s.keyframe_locked) && (
              <div className="flex justify-end mt-4">
                <button onClick={advanceStage} data-testid="approve-keyframe-lock-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Proceed to Neutral Master <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 11. NEUTRAL MASTER (Optional) ── */}
        {/* ── 11. NEUTRAL MASTER — HIDDEN 2026-05-28 ──
            Legacy step. Implementation re-generates from text prompt only
            (no image reference, different engine than K+/IGK), causing
            heavy identity drift. Modern i2v models handle stylized
            keyframes fine — no longer needed. Wrapping render in
            `false &&` keeps the code path intact for reference. */}
        {false && isPpmApproved() && effectiveStage >= 10 && (
          <CollapsibleSection
            title="3. Neutral Master (Optional)"
            icon={<div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center"><Image className="w-3.5 h-3.5 text-white/70" /></div>}
            badge={shots.some(s => s.keyframe_neutral_url) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.keyframe_neutral_url).length} generated</span>}
            expanded={expandedSections.neutralMaster}
            onToggle={() => toggleSection('neutralMaster')}
            testId="neutral-master-section"
          >
            <p className="text-xs text-white/60 mb-3">
              Generate neutral/clean versions of locked keyframes. These are used as a cleaner base for video generation. <span className="text-amber-500 font-medium">This step is optional.</span>
            </p>
            <div className="flex gap-2 mb-4">
              <button onClick={generateNeutralMasters} disabled={uploadingAction === "neutral"} data-testid="generate-neutral-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 ${
                  uploadingAction === "neutral" ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-[#0284C7] text-white hover:bg-[#0369A1] active:scale-95'
                }`}>
                {uploadingAction === 'neutral' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                Generate Neutral Masters
              </button>
              <button onClick={advanceStage} data-testid="skip-neutral-btn"
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all bg-ai-accent/10 text-white/70 hover:bg-white/10 active:scale-95">
                Skip & Continue
              </button>
            </div>
            {neutralProgress && <ProgressBar label="Generating neutral masters..." progress={neutralProgress} color="#6B7280" />}
            
            {/* Neutral Masters Gallery with Selection */}
            {shots.some(s => s.keyframe_neutral_url) && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white/80">Choose Version for Each Keyframe</h4>
                  <div className="flex gap-2">
                    <button 
                      onClick={async () => {
                        { // confirm removed - non-blocking
                          await axios.post(`${API}/projects/${projectId}/neutral-master/select-all`, { use_neutral: false });
                          loadProjectData();
                        }
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-ai-accent/10 text-white/70 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      Use All Originals
                    </button>
                    <button 
                      onClick={async () => {
                        { // confirm removed - non-blocking
                          await axios.post(`${API}/projects/${projectId}/neutral-master/select-all`, { use_neutral: true });
                          loadProjectData();
                        }
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-ai-accent/10 text-ai-accent rounded-lg hover:bg-ai-accent/15 transition-colors"
                    >
                      Use All Neutrals
                    </button>
                  </div>
                </div>
                <p className="text-xs text-white/60 mb-4">
                  Select which version to use for video generation. You can regenerate individual neutral masters if needed.
                </p>
                <div className={`grid ${tileGridClass} gap-4`}>
                  {shots.filter(s => s.keyframe_neutral_url || s.keyframe_url).map((shot) => (
                    <div key={shot.id} className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                      shot.use_neutral_master ? 'border-ai-accent bg-ai-accent/10' : 'border-ai-border bg-ai-bg-secondary'
                    }`}>
                      {/* Header */}
                      <div className="p-3 border-b border-ai-border bg-ai-bg-secondary">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-white">{shot.scene_number}—{shot.shot_number}</span>
                          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                            shot.use_neutral_master 
                              ? 'bg-ai-accent/100 text-white' 
                              : 'bg-white/10 text-white/70'
                          }`}>
                            {shot.use_neutral_master ? 'NEUTRAL' : 'ORIGINAL'}
                          </span>
                        </div>
                        <p className="text-[10px] text-white/60 mt-1 line-clamp-2 cursor-pointer hover:text-white/80 transition-colors"
                          onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-2'); }}
                        >{shot.description}</p>
                        {shot.dialogue && (
                          <p className="text-[9px] text-cyan-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none"
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
                            <span className="font-semibold text-cyan-400">DLG:</span> "{shot.dialogue}"
                          </p>
                        )}
                        {shot.voiceover && (
                          <p className="text-[9px] text-amber-400/70 mt-0.5 line-clamp-1 cursor-pointer hover:line-clamp-none"
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.classList.toggle('line-clamp-1'); }}>
                            <span className="font-semibold text-amber-400">VO:</span> "{shot.voiceover}"
                          </p>
                        )}
                      </div>
                      
                      {/* Side-by-side comparison */}
                      <div className="grid grid-cols-2 gap-1 p-2">
                        {/* Original */}
                        <button 
                          onClick={async () => {
                            await axios.post(`${API}/shots/${shot.id}/select-keyframe-version`, { use_neutral: false });
                            loadProjectData();
                          }}
                          className={`relative rounded-lg overflow-hidden transition-all ${
                            !shot.use_neutral_master ? 'ring-2 ring-ai-accent' : 'opacity-60 hover:opacity-100'
                          }`}
                        >
                          <div className={aspectClass}>
                            <img 
                              src={resolveUrl(shot.keyframe_url)} 
                              alt="Original" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className={`absolute bottom-0 inset-x-0 py-1 text-center text-[10px] font-semibold ${
                            !shot.use_neutral_master 
                              ? 'bg-ai-accent/100 text-white' 
                              : 'bg-black/50 text-white'
                          }`}>
                            Original
                          </div>
                          {!shot.use_neutral_master && (
                            <div className="absolute top-1 right-1">
                              <CheckCircle className="w-4 h-4 text-ai-accent bg-ai-bg-secondary rounded-lg" />
                            </div>
                          )}
                        </button>
                        
                        {/* Neutral */}
                        {shot.keyframe_neutral_url ? (
                          <button 
                            onClick={async () => {
                              await axios.post(`${API}/shots/${shot.id}/select-keyframe-version`, { use_neutral: true });
                              loadProjectData();
                            }}
                            className={`relative rounded-lg overflow-hidden transition-all ${
                              shot.use_neutral_master ? 'ring-2 ring-ai-accent' : 'opacity-60 hover:opacity-100'
                            }`}
                          >
                            <div className={aspectClass}>
                              <img 
                                src={resolveUrl(shot.keyframe_neutral_url)} 
                                alt="Neutral" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className={`absolute bottom-0 inset-x-0 py-1 text-center text-[10px] font-semibold ${
                              shot.use_neutral_master 
                                ? 'bg-ai-accent/100 text-white' 
                                : 'bg-black/50 text-white'
                            }`}>
                              Neutral
                            </div>
                            {shot.use_neutral_master && (
                              <div className="absolute top-1 right-1">
                                <CheckCircle className="w-4 h-4 text-ai-accent bg-ai-bg-secondary rounded-lg" />
                              </div>
                            )}
                          </button>
                        ) : (
                          <div className={`${aspectClass} bg-ai-accent/10 rounded-lg flex items-center justify-center`}>
                            <span className="text-[10px] text-white/50">No neutral</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Regenerate button */}
                      <div className="p-2 pt-0">
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/shots/${shot.id}/regenerate-neutral-master`);
                              alert('Regenerating neutral master...');
                              setTimeout(loadProjectData, 3000);
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to regenerate');
                            }
                          }}
                          className="w-full px-3 py-1.5 text-xs font-medium bg-ai-accent/10 text-white/70 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <RefreshCw className="w-3 h-3" /> Regenerate Neutral
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Proceed button */}
                <div className="mt-6 flex justify-end">
                  <button 
                    onClick={advanceStage} 
                    data-testid="proceed-to-lineup-btn"
                    className="px-6 py-3 bg-ai-accent/100 text-white rounded-lg font-medium hover:bg-ai-accent active:scale-95 transition-all shadow-lg shadow-ai-accent/25 flex items-center gap-2"
                  >
                    Proceed to Keyframe Lineup <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 12. KEYFRAME LINEUP ── Gate relaxed to stage >= 10 since
            Neutral Master (was stage 11) is hidden. Operator can reach
            this directly after auto-lock without an empty stage in between. */}
        {isPpmApproved() && effectiveStage >= 10 && (
          <CollapsibleSection
            title="3. Keyframe Lineup"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/10 flex items-center justify-center"><List className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={keyframeLineup && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-ai-accent/10 text-ai-accent">{keyframeLineup.total_shots} shots</span>}
            expanded={expandedSections.lineup}
            onToggle={() => toggleSection('lineup')}
            testId="lineup-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Review the sequence of all locked keyframes before video generation. Estimated runtime: <span className="font-semibold">{keyframeLineup?.estimated_duration_seconds || '—'}s</span>
            </p>
            <button onClick={loadKeyframeLineup} disabled={uploadingAction === "lineup"} data-testid="load-lineup-btn"
              className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 mb-4 bg-ai-accent text-white hover:bg-ai-accent/80 active:scale-95">
              <List className="w-4 h-4" /> Load Keyframe Lineup
            </button>
            {keyframeLineup && (
              // Animatic-style storyboard: keyframes laid out as a
              // continuous strip of images in scene/shot order.
              // Auto-wrapping responsive grid (not vertical rows of
              // cards) so the operator can scan the entire film at a
              // glance — like a real animatic, not a database table.
              // 16:9 thumbnails (aspect-video) so the actual visual
              // composition is legible. Tiny floating chip carries the
              // shot label + duration without crowding the frame.
              // Click → opens the existing per-shot lightbox.
              <div
                className="grid gap-2 max-h-[70vh] overflow-y-auto pr-1"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}
                data-testid="keyframe-lineup-storyboard"
              >
                {keyframeLineup.lineup.map((item, idx) => (
                  <button
                    key={item.shot_id}
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('keyframe-lightbox-open', {
                        detail: { shotId: item.shot_id },
                      }));
                    }}
                    className="group relative block w-full aspect-video rounded-md overflow-hidden bg-ai-bg-secondary border border-ai-border hover:border-ai-accent hover:ring-1 hover:ring-ai-accent transition-all active:scale-[0.98]"
                    title={`${item.scene_number}-${item.shot_number} · ${item.duration_seconds}s\n${item.description || ''}`}
                    data-testid={`lineup-frame-${item.shot_id}`}
                  >
                    {item.keyframe_url ? (
                      <img
                        src={resolveUrl(item.keyframe_url)}
                        alt={`${item.scene_number}-${item.shot_number}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-white/30">no keyframe</div>
                    )}
                    {/* Top-left: sequence index */}
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-bold bg-black/70 text-white rounded">
                      {idx + 1}
                    </span>
                    {/* Top-right: neutral-master flag (if used) */}
                    {item.use_neutral_master && (
                      <span className="absolute top-1 right-1 px-1 py-0.5 text-[8px] font-bold bg-ai-accent text-white rounded" title="Uses neutral master">N</span>
                    )}
                    {/* Bottom strip: shot label + duration — visible on hover */}
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-black/85 via-black/40 to-transparent flex items-end justify-between text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[9px] font-mono font-bold">{item.scene_number}-{item.shot_number}</span>
                      <span className="text-[9px] font-mono">{item.duration_seconds}s</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {keyframeLineup && (
              <div className="flex justify-end mt-4">
                <ActionButton
                  variant="primary"
                  icon={ArrowRight}
                  onClick={confirmKeyframeLineup}
                  data-testid="confirm-lineup-btn"
                >
                  Confirm Lineup & Continue
                </ActionButton>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 13. VIDEO GENERATION ── */}
        {isPpmApproved() && effectiveStage >= 10 && (
          <CollapsibleSection
            title="4. Video Generation"
            icon={<div className="w-7 h-7 rounded-lg bg-[#FF2D55]/10 flex items-center justify-center"><Film className="w-3.5 h-3.5 text-[#FF2D55]" /></div>}
            badge={<>
              {shots.some(s => s.video_status === 'completed') && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.video_status === 'completed').length} videos</span>}
              {shots.some(s => s.video_status === 'processing') && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF9500]/10 text-[#FF9500] animate-pulse">{shots.filter(s => s.video_status === 'processing').length} processing</span>}
            </>}
            expanded={expandedSections.videoGen}
            onToggle={() => toggleSection('videoGen')}
            testId="video-gen-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Generate video clips from each locked keyframe.
            </p>
            <div className="flex items-center gap-2 mb-4 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
              {shots.some(s => s.video_status === 'completed') && (
                <FaceLockAllButton projectId={projectId} media="video" onReload={loadProjectData} />
              )}
              {/* Bulk auto-fill Action / Camera / Emotion across every shot
               *  with a keyframe. Uses the keyframe image + script excerpt
               *  + Stage-2 metadata. `only_empty=true` (default) is safe to
               *  re-run — shots that already have any of the 3 fields are
               *  skipped. Feb 2026: powers the "Auto-fill motion" button. */}
              {shots.some(s => s.keyframe_url) && (
                <button
                  type="button"
                  disabled={autoFillingMotion}
                  onClick={async () => {
                    if (autoFillingMotion) return;
                    const anyPopulated = shots.some(s => s.action_beat || s.camera_movement || s.emotion_beat);
                    const overwrite = anyPopulated
                      ? window.confirm('Some shots already have Action / Camera / Emotion set. Overwrite them?\n\nOK = overwrite all shots · Cancel = skip populated ones')
                      : false;
                    setAutoFillingMotion(true);
                    try {
                      const { data } = await axios.post(
                        `${API}/projects/${projectId}/backfill-motion-fields`,
                        { only_empty: !overwrite },
                      );
                      toast.success(`✨ Motion fields auto-filled — ${data.updated_count} updated · ${data.skipped_count} skipped${data.errors?.length ? ` · ${data.errors.length} errors` : ''}`);
                      if (data.errors?.length) console.warn('[motion-fields:backfill] errors:', data.errors);
                      await loadProjectData();
                    } catch (e) {
                      toast.error('Auto-fill failed: ' + (e?.response?.data?.detail || e.message));
                    } finally {
                      setAutoFillingMotion(false);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all border border-amber-400/30 text-amber-300 hover:bg-amber-400/10 disabled:opacity-50 disabled:cursor-wait"
                  title="Auto-fill Action / Camera / Emotion for every shot from the keyframe image + script"
                  data-testid="video-motion-autofill-btn"
                >
                  {autoFillingMotion
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5" />}
                  Auto-fill motion
                </button>
              )}
              {(() => {
                // Auto-engine routing decision based on the dominant
                // keyframe engine across the project. Shown as a chip
                // next to the dropdown so the operator can see which
                // cascade will run.
                const routing = computePreferredEngineChain(shots);
                const inFlight = uploadingAction?.startsWith('videos');
                return (
                  <>
                    <div className="relative inline-block">
                      <select
                        disabled={inFlight}
                        value="" // controlled by select; reset each render
                        onChange={(e) => {
                          const choice = e.target.value;
                          if (!choice) return;
                          // Re-route to the right call site
                          if (choice === 'batch3') {
                            generateVideos(routing.mode, 'sequential', {
                              engine_chain: routing.chain === 'hyperreal_cascade' ? 'hyperreal_cascade' : undefined,
                              batch_size: 3,
                            });
                          } else if (choice === 'all') {
                            generateVideos(routing.mode, 'parallel', {
                              engine_chain: routing.chain === 'hyperreal_cascade' ? 'hyperreal_cascade' : undefined,
                            });
                          }
                          // Reset the select value
                          e.target.value = '';
                        }}
                        data-testid="video-gen-mode-select"
                        className={`pl-3 pr-8 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all bg-[#FF2D55] text-white hover:bg-[#FF2D55]/80 active:scale-95 cursor-pointer appearance-none ${inFlight ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                      >
                        <option value="" disabled>{inFlight ? 'Generating…' : (shots.some(s => s.video_url) ? 'Regenerate…' : 'Generate…')}</option>
                        <option value="batch3">Generate batch of 3</option>
                        <option value="all">Generate all</option>
                      </select>
                    </div>
                    {/* Auto-routing chip */}
                    {routing.autoRouted && (
                      <span
                        className="px-2 py-1 rounded-full text-[10px] font-medium bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30 whitespace-nowrap"
              title={`${routing.hyperrealCount}/${routing.totalCount} shots are K+/IGK — routing through Seedance with Kling fallback.`}
                        data-testid="video-gen-engine-routing-chip"
                      >
                        Seedance → Kling
                      </span>
                    )}
                    {!routing.autoRouted && routing.totalCount > 0 && (
                      <span
                        className="px-2 py-1 rounded-full text-[10px] font-medium bg-white/5 text-white/60 border border-white/10 whitespace-nowrap"
                        title={`Mixed engines — only ${routing.hyperrealCount}/${routing.totalCount} are K+/IGK. Using platform default cascade (Seedance → Veo → Kling).`}
                      >
                        Default cascade
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
            {videoProgress && (
              <div className="mb-4">
                <ProgressBar 
                  label={`Generating videos${videoProgress.strategy === 'parallel' ? ' (all at once)' : ''}... (${videoProgress.completed}/${videoProgress.total})`} 
                  progress={{ completed: videoProgress.completed, total: videoProgress.total, percentage: videoProgress.percentage }} 
                  color="#FF2D55"
                  onStop={stopGeneration}
                  taskType="videos"
                />
                {videoProgress.strategy === 'parallel' && videoProgress.in_progress > 0 && (
                  <div className="mt-2 px-3 py-2 rounded-lg border border-[#8B5CF6]/20" style={{ background: 'rgba(139,92,246,0.05)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-white/70 font-medium">
                        <Zap className="w-3 h-3 inline mr-1 text-[#8B5CF6]" />
                        {videoProgress.in_progress} shots generating simultaneously
                      </span>
                      <span className="text-[10px] font-mono text-[#8B5CF6]">
                        {videoProgress.completed + videoProgress.failed}/{videoProgress.total} done
                      </span>
                    </div>
                  </div>
                )}
                {videoProgress.strategy !== 'parallel' && videoProgress.current_shot && (
                  <div className="mt-2 px-3 py-2 rounded-lg border border-ai-border" style={{ background: 'rgba(14,165,233,0.05)' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-white/70 font-medium">
                        Current: <span className="text-white font-semibold">{videoProgress.current_shot}</span>
                      </span>
                      <span className="text-[10px] font-mono text-ai-text">
                        {videoProgress.current_engine} — Attempt {videoProgress.current_attempt}/8
                      </span>
                    </div>
                    <div className="h-1.5 rounded-lg overflow-hidden" style={{ background: 'rgba(14,165,233,0.15)' }}>
                      <div className="h-full rounded-lg transition-all duration-500"
                        style={{ 
                          width: `${videoProgress.current_progress || 0}%`,
                          background: videoProgress.current_engine === 'Vertex' ? 'linear-gradient(90deg, #8B5CF6, #6D28D9)'
                            : videoProgress.current_engine === 'Seedance' ? 'linear-gradient(90deg, #22D3EE, #0EA5E9)'
                            : videoProgress.current_engine === 'Veo' ? 'linear-gradient(90deg, #34D399, #059669)'
                            : 'linear-gradient(90deg, #FF2D55, #FF6B35)'
                        }} />
                    </div>
                    <div className="text-[9px] text-white/30 mt-1 text-right">{videoProgress.current_progress || 0}%</div>
                  </div>
                )}
              </div>
            )}
            {shots.some(s => s.keyframe_locked) && (
              <div className={`grid ${tileGridClass} gap-3 mt-3`}>
                {shots.filter(s => s.keyframe_locked).map(shot => (
                  <VideoCard 
                    key={shot.id} 
                    shot={shot} 
                    aspectClass={aspectClass}
                    onGenerateVideo={generateSingleVideo}
                    onFeedback={sendVideoFeedback}
                    onLock={lockSingleVideo}
                    onMotionControl={submitMotionControl}
                    onStopVideo={stopSingleVideo}
                    project={project}
                    assetBible={assetBible}
                    isPremium={isPremiumMj}
                    onReload={loadProjectData}
                    onImageClick={() => {
                      const gallery = shots
                        .filter(s => s.keyframe_locked && s.video_url && s.video_status === 'completed')
                        .map(s => {
                          const bestUrl = [s.video_url, s.video_s3_url, s.video_seedance_url, s.video_kling_url].filter(Boolean)[0];
                          return {
                            url: resolveUrl(bestUrl),
                            type: 'video',
                            label: `Video ${s.scene_number}-${s.shot_number}`,
                            shot_id: s.id,
                          };
                        });
                      const idx = gallery.findIndex(g => g.shot_id === shot.id);
                      if (idx === -1 || gallery.length === 0) return;
                      setGlobalLightbox({ items: gallery, index: idx });
                    }}
                  />
                ))}
              </div>
            )}
            {shots.some(s => s.video_status === 'completed') && (
              <div className="flex justify-end mt-4">
                <button onClick={advanceStage} data-testid="approve-videos-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Proceed to Video QA <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* Legacy Video QA stub — HIDDEN 2026-07. Backend endpoint is kept
           but no operator UI. Flip the false below to true to restore. */}
        {false && isPpmApproved() && effectiveStage >= 13 && (
          <CollapsibleSection
            title="8. Video VLM"
            icon={<div className="w-7 h-7 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center"><Eye className="w-3.5 h-3.5 text-[#0EA5E9]" /></div>}
            badge={shots.some(s => s.video_qa_passed) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.video_qa_passed).length} passed</span>}
            expanded={expandedSections.videoQa}
            onToggle={() => toggleSection('videoQa')}
            testId="video-qa-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Run quality assurance on generated videos to check for artifacts, motion quality, and consistency.
            </p>
            <button onClick={runVideoQa} disabled={uploadingAction === "videoQa"} data-testid="run-video-qa-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 mb-4 ${
                uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-[#0EA5E9] text-white hover:bg-[#0284C7] active:scale-95'
              }`}>
              {uploadingAction === 'videoqa' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Run Video QA
            </button>
            {videoQaProgress && <ProgressBar label="Analyzing videos..." progress={videoQaProgress} color="#0EA5E9" />}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => skipToStage(15, 'Video QA')} data-testid="skip-video-qa-btn"
                className="px-4 py-2 text-white/50 rounded-lg text-xs font-medium hover:text-white/70 hover:bg-white/5 transition-all flex items-center gap-1.5">
                Skip Video QA <ArrowRight className="w-3.5 h-3.5" />
              </button>
              {shots.some(s => s.video_qa_passed) && (
                <button onClick={advanceStage} data-testid="approve-video-qa-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Proceed to Video Lock <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Real frame-by-frame Video VLM — HIDDEN 2026-07 per Rohit.
           Creative director eyeballs clips manually; backend endpoint
           /api/projects/{id}/video-vlm-frames is preserved. Flip the
           false below to true to restore. */}
        {false && isPpmApproved() && effectiveStage >= 13 && shots.some(s => s.video_status === 'completed') && (
          <CollapsibleSection
            title="5. Video VLM (Frame-by-Frame Analysis)"
            icon={<div className="w-7 h-7 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center"><Eye className="w-3.5 h-3.5 text-[#FF3B30]" /></div>}
            badge={shots.some(s => s.video_vlm_frames) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF3B30]/10 text-[#FF3B30]">{shots.filter(s => s.video_vlm_frames?.overall_score).length} analyzed</span>}
            expanded={expandedSections.videoVlmFrames}
            onToggle={() => toggleSection('videoVlmFrames')}
            testId="video-vlm-frames-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Extract frames from each video clip and analyze every frame against the keyframe, prompt, and script.
              The AI maps continuity issues, motion quality, and suggests specific changes.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-xs text-white/50">Sample Rate:</label>
                <select value={vlmFramesSampleRate} onChange={(e) => setVlmFramesSampleRate(Number(e.target.value))}
                  className="text-xs px-3 py-2 border border-ai-border rounded-lg text-white focus:outline-none focus:border-ai-accent" 
                  style={{ background: 'rgba(11, 15, 25, 0.9)' }}
                  data-testid="vlm-fps-select">
                  <option value={24} style={{ background: '#0B0F19', color: 'white' }}>Every frame (24fps) — thorough</option>
                  <option value={12} style={{ background: '#0B0F19', color: 'white' }}>Every 2nd frame (12fps)</option>
                  <option value={6} style={{ background: '#0B0F19', color: 'white' }}>Every 4th frame (6fps) — balanced</option>
                  <option value={3} style={{ background: '#0B0F19', color: 'white' }}>Every 8th frame (3fps) — fast</option>
                  <option value={1} style={{ background: '#0B0F19', color: 'white' }}>1 per second — quickest</option>
                </select>
              </div>
              <button onClick={runVideoVlmFrames} disabled={uploadingAction === "videoVlmFrames"} data-testid="run-video-vlm-frames-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 ${
                  uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-[#FF3B30] text-white hover:bg-[#E0342B] active:scale-95'
                }`}>
                {uploadingAction === 'videoVlmFrames' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Run Frame Analysis
              </button>
            </div>
            {videoVlmFramesProgress?.processing && (
              <ProgressBar label="Extracting & analyzing frames..." progress={{completed: videoVlmFramesProgress.completed, total: videoVlmFramesProgress.total, percentage: Math.round((videoVlmFramesProgress.completed / Math.max(videoVlmFramesProgress.total, 1)) * 100)}} color="#FF3B30" />
            )}
            <div className="space-y-3 mt-3">
              {shots.filter(s => s.video_vlm_frames).map((shot) => {
                const vf = shot.video_vlm_frames;
                const score = vf.overall_score || 0;
                const scoreColor = score >= 80 ? '#22C55E' : score >= 60 ? '#FF9500' : '#FF3B30';
                return (
                  <div key={shot.id} className="rounded-xl p-3 bg-white/[0.02]" data-testid={`vlm-frame-result-${shot.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-white">{shot.scene_number} — {shot.shot_number}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-white/50">{vf.frames_analyzed || 0} frames analyzed</span>
                        <span className="text-sm font-bold" style={{color: scoreColor}}>{score}/100</span>
                      </div>
                    </div>
                    {/* Score bars */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      {[
                        {label: 'Keyframe Fidelity', val: vf.keyframe_fidelity},
                        {label: 'Motion Quality', val: vf.motion_quality},
                        {label: 'Continuity', val: vf.continuity_score},
                        {label: 'Script Adherence', val: vf.script_adherence},
                      ].map((m, mi) => (
                        <div key={mi} className="text-center">
                          <div className="text-[9px] text-white/50 mb-0.5">{m.label}</div>
                          <div className="h-1.5 glass-panel rounded-lg overflow-hidden">
                            <div className="h-full rounded-lg transition-all" style={{width: `${m.val || 0}%`, backgroundColor: (m.val || 0) >= 80 ? '#22C55E' : (m.val || 0) >= 60 ? '#FF9500' : '#FF3B30'}} />
                          </div>
                          <div className="text-[9px] font-semibold mt-0.5">{m.val || 0}</div>
                        </div>
                      ))}
                    </div>
                    {/* Asset match bars */}
                    {(vf.cast_match || vf.costume_match || vf.environment_match || vf.product_match) && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2 border-t border-ai-border pt-2">
                        {[
                          {label: 'Cast Match', val: vf.cast_match},
                          {label: 'Costume', val: vf.costume_match},
                          {label: 'Environment', val: vf.environment_match},
                          {label: 'Product', val: vf.product_match},
                        ].filter(m => m.val != null).map((m, mi) => (
                          <div key={mi} className="text-center">
                            <div className="text-[9px] text-ai-accent mb-0.5">{m.label}</div>
                            <div className="h-1.5 glass-panel rounded-lg overflow-hidden">
                              <div className="h-full rounded-lg transition-all" style={{width: `${m.val || 0}%`, backgroundColor: (m.val || 0) >= 80 ? '#22C55E' : (m.val || 0) >= 60 ? '#FF9500' : '#FF3B30'}} />
                            </div>
                            <div className="text-[9px] font-semibold mt-0.5">{m.val || 0}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {vf.assets_checked && (
                      <div className="text-[8px] text-white/40 mb-1.5">
                        Checked: {[...(vf.assets_checked.cast || []), ...(vf.assets_checked.costumes || []), ...(vf.assets_checked.locations || []), vf.assets_checked.product].filter(Boolean).join(', ') || 'None'}
                      </div>
                    )}
                    {/* Summary */}
                    {vf.summary && <p className="text-[10px] text-white mb-2 glass-panel rounded-lg p-2">{vf.summary}</p>}
                    {/* Frame Issues */}
                    {vf.frame_issues?.length > 0 && (
                      <div className="mb-2">
                        <div className="text-[10px] font-semibold text-[#FF3B30] mb-1">Frame Issues:</div>
                        {vf.frame_issues.map((fi, fii) => (
                          <div key={fii} className="text-[10px] text-white/50 flex gap-1.5 mb-0.5">
                            <span className="text-[#FF3B30] font-mono shrink-0">F{fi.frame_range}:</span>
                            <span>{fi.issue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Suggested Changes */}
                    {vf.suggested_changes?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-semibold text-ai-accent mb-1">Suggested Changes:</div>
                        {vf.suggested_changes.map((sc, sci) => (
                          <div key={sci} className="text-[10px] text-white/50 flex gap-1.5 mb-0.5">
                            <span className="text-ai-accent">-</span>
                            <span>{sc}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Asset Consistency Notes */}
                    {vf.asset_notes?.length > 0 && (
                      <div className="mt-1">
                        <div className="text-[10px] font-semibold text-ai-accent mb-1">Asset Consistency:</div>
                        {vf.asset_notes.map((an, ani) => (
                          <div key={ani} className="text-[10px] text-white/50 flex gap-1.5 mb-0.5">
                            <span className="text-ai-accent">-</span>
                            <span>{an}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {vf.error && <p className="text-[10px] text-[#FF3B30]">Error: {vf.error}</p>}
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* ── 15. VIDEO LOCK ── */}
        {isPpmApproved() && effectiveStage >= 11 && (
          <CollapsibleSection
            title="5. Video Lock"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/15 flex items-center justify-center"><Lock className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={shots.some(s => s.video_locked) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E]">{shots.filter(s => s.video_locked).length} locked</span>}
            expanded={expandedSections.videoLock}
            onToggle={() => toggleSection('videoLock')}
            testId="video-lock-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Lock all videos that passed QA. Locked videos will be included in the final export.
            </p>
            <button onClick={lockAllVideos} disabled={uploadingAction === "lockVideos"} data-testid="lock-all-videos-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 mb-4 ${
                uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-[#22C55E] text-white hover:bg-ai-accent active:scale-95'
              }`}>
              {uploadingAction === 'lockVideos' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Lock All Videos ({shots.filter(s => s.video_status === 'completed' && !s.video_locked).length} pending)
            </button>
            {shots.some(s => s.video_locked) && (
              <div className="flex justify-end mt-4">
                <button onClick={advanceStage} data-testid="approve-video-lock-btn"
                  className="px-4 py-2 bg-ai-accent/100 text-white rounded-lg text-xs font-semibold hover:bg-ai-accent active:scale-95 transition-all flex items-center gap-1.5">
                  Proceed to Export <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ── 16. EXPORT ── */}


        {/* ═══ END PRODUCTION GROUP / START POSTPROD GROUP ═══ */}
        </>)}
        {activeStageGroup === 'postprod' && (<>
        {/* ═══ POST-PROD GROUP (Stages 16-17) ═══ */}
        {/* ── 16. TIMELINE EDITOR (Premiere Pro-style) ── */}
        {effectiveStage >= 14 && (
          <div data-testid="postproduction-section" className="rounded-xl overflow-hidden bg-ai-bg-secondary">
            <button onClick={() => toggleSection('postproduction')}
              className="w-full flex items-center justify-between px-5 py-4 hover:glass-panel transition-colors text-left">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#FF2D55]/10 flex items-center justify-center"><Film className="w-3.5 h-3.5 text-[#FF2D55]" /></div>
                <span className="font-semibold text-sm">16. Timeline Editor</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-ai-accent/10 text-ai-accent">{shots.length} clips</span>
              </div>
              {expandedSections.postproduction ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
            </button>
            {expandedSections.postproduction && (
              <div className="border-t border-ai-border" style={{ height: '520px' }}>
                <TimelineEditor
                  shots={shots}
                  projectName={project.name}
                  baseUrl={BASE_URL}
                  orientation={project?.orientation || '16:9'}
                  variant="splice"
                  token={localStorage.getItem('token')}
                  apiBase={BASE_URL}
                  onProbedurations={async () => {
                    try {
                      const res = await axios.post(`${API}/projects/${projectId}/probe-video-durations`);
                      if (res.data?.updated > 0) {
                        loadProjectData(); // Refresh shots with actual durations
                      }
                    } catch {}
                  }}
                  onExport={async () => {
                    try {
                      const res = await axios.post(`${API}/projects/${projectId}/post-production/render`);
                      toast.success(res.data.message || 'Export started!');
                      const poll = setInterval(async () => {
                        const p = await axios.get(`${API}/projects/${projectId}`);
                        const status = p.data?.post_production?.render_status;
                        if (status === 'complete') {
                          clearInterval(poll);
                          const s3Url = p.data?.post_production?.final_video_s3_url;
                          const localUrl = p.data?.post_production?.final_video_url;
                          const url = s3Url || localUrl;
                          toast.success('Export complete!');
                          if (url) window.open(resolveUrl(url), '_blank');
                          loadProjectData();
                        } else if (status === 'failed') {
                          clearInterval(poll);
                          toast.error('Export failed: ' + (p.data?.post_production?.render_error || 'Unknown'));
                        }
                      }, 3000);
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed to start export');
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* ── 17. EXPORT & FINALIZE ── */}
        {isPpmApproved() && effectiveStage >= 15 && (
          <CollapsibleSection
            title="6. Export & Finalize"
            icon={<div className="w-7 h-7 rounded-lg bg-ai-accent/10 flex items-center justify-center"><Download className="w-3.5 h-3.5 text-ai-accent" /></div>}
            badge={project.status === 'completed' && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#22C55E]/10 text-[#22C55E] flex items-center gap-1"><Check className="w-3 h-3" />Finalized</span>}
            expanded={expandedSections.export}
            onToggle={() => toggleSection('export')}
            testId="export-section"
          >
            <p className="text-xs text-white/50 mb-3">
              Export your final production package including all shots, keyframes, videos, and assets.
            </p>

            {/* ── Video Export Controls ── */}
            <div className="glass-panel rounded-xl p-4 mb-4 space-y-3">
              <div className="text-xs font-semibold text-white mb-2">Video Export</div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-white/50 uppercase tracking-wider font-ai">Format</label>
                  <select
                    value={project.post_production?.export_format || 'mp4'}
                    onChange={async (e) => {
                      await axios.post(`${API}/projects/${projectId}/post-production/settings`, { format: e.target.value });
                      loadProjectData();
                    }}
                    className="bg-ai-bg-secondary text-white text-xs rounded-lg px-3 py-1.5 border border-ai-border focus:outline-none"
                    data-testid="export-format-select"
                  >
                    <option value="mp4">MP4</option>
                    <option value="mov">MOV (ProRes)</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-white/50 uppercase tracking-wider font-ai">Audio</label>
                  {project.post_production?.audio_filename ? (
                    <span className="text-xs text-ai-accent flex items-center gap-1">
                      <Music2 className="w-3 h-3" /> {project.post_production.audio_filename}
                      <button onClick={async () => {
                        await axios.post(`${API}/projects/${projectId}/post-production/settings`, { audio_url: null });
                        loadProjectData();
                      }} className="text-red-400 hover:text-red-300 ml-1" title="Remove audio"><X className="w-3 h-3" /></button>
                    </span>
                  ) : (
                    <label className="cursor-pointer px-3 py-1.5 bg-ai-bg-secondary rounded-lg text-xs border border-ai-border hover:bg-white/10 transition-colors flex items-center gap-1.5">
                      <Upload className="w-3 h-3" /> Upload Audio
                      <input type="file" accept="audio/*" className="hidden" data-testid="upload-audio-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('file', file);
                          try {
                            await axios.post(`${API}/projects/${projectId}/post-production/upload-audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            toast.success('Audio uploaded');
                            loadProjectData();
                          } catch (err) { toast.error('Audio upload failed'); }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Render progress / status */}
              {project.post_production?.render_status === 'rendering' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-ai-accent animate-spin" />
                    <span className="text-xs text-white/70">{project.post_production?.render_status_msg || 'Rendering...'}</span>
                  </div>
                  <div className="w-full bg-ai-bg-secondary rounded-lg h-2 overflow-hidden">
                    <div className="h-2 rounded-lg bg-ai-accent transition-all duration-500" style={{ width: `${project.post_production?.render_progress || 0}%` }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white/40">{project.post_production?.render_progress || 0}%</span>
                    <button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/projects/${projectId}/post-production/cancel-render`);
                          toast.success('Render cancelled');
                          loadProjectData();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
                      }}
                      data-testid="cancel-render-btn"
                      className="px-3 py-1 rounded-lg text-[10px] font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 transition-all flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Stop Export
                    </button>
                  </div>
                </div>
              ) : project.post_production?.render_status === 'complete' ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#22C55E] flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Export complete ({(project.post_production?.final_video_size / 1024 / 1024).toFixed(1)}MB)</span>
                  <a href={project.post_production?.final_video_s3_url || `${API}/projects/${projectId}/download/final-video`}
                    target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-[#22C55E] text-white text-xs font-medium rounded-lg hover:bg-[#22C55E]/80 transition-colors flex items-center gap-1.5"
                    data-testid="download-final-video">
                    <Download className="w-3.5 h-3.5" /> Download .{project.post_production?.export_format || 'mp4'}
                  </a>
                </div>
              ) : project.post_production?.render_status === 'failed' || project.post_production?.render_status === 'cancelled' ? (
                <div className="text-xs text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Export {project.post_production?.render_status}: {project.post_production?.render_error?.slice(0, 100)}
                </div>
              ) : null}

              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/projects/${projectId}/post-production/render`);
                      toast.success(res.data.message || 'Export started!');
                      const poll = setInterval(async () => {
                        const p = await axios.get(`${API}/projects/${projectId}`);
                        const status = p.data?.post_production?.render_status;
                        if (status === 'complete') {
                          clearInterval(poll);
                          toast.success('Export complete!');
                          loadProjectData();
                        } else if (status === 'failed') {
                          clearInterval(poll);
                          toast.error('Export failed');
                          loadProjectData();
                        } else {
                          loadProjectData();
                        }
                      }, 3000);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start export'); }
                  }}
                  disabled={project.post_production?.render_status === 'rendering'}
                  data-testid="export-video-btn"
                  className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 bg-ai-accent text-white hover:bg-ai-accent/80 active:scale-95 disabled:opacity-50">
                  <Film className="w-4 h-4" /> {project.post_production?.render_status === 'complete' ? 'Re-Export Video' : 'Export Video'}
                </button>
                <button onClick={loadExportPackage} data-testid="load-export-btn"
                  className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 bg-ai-bg-secondary text-white hover:bg-white/10 active:scale-95 border border-ai-border">
                  <Package className="w-4 h-4" /> View Package
                </button>
                {project.status !== 'completed' && (
                  <button onClick={finalizeProject} data-testid="finalize-btn"
                    className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap [&_svg]:w-3.5 [&_svg]:h-3.5 transition-all flex items-center gap-2 bg-[#22C55E] text-white hover:bg-[#22C55E]/80 active:scale-95">
                    <Check className="w-4 h-4" /> Finalize
                  </button>
                )}
              </div>
            </div>

            {exportPackage && (
              <div className="glass-panel rounded-xl p-4 space-y-4">
                <div>
                  <div className="text-xs font-semibold mb-2">Project Summary</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-ai-bg-secondary p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-ai-accent">{exportPackage.statistics.total_shots}</div>
                      <div className="text-[10px] text-white/50">Total Shots</div>
                    </div>
                    <div className="bg-ai-bg-secondary p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-[#FF9500]">{exportPackage.statistics.keyframes_generated}</div>
                      <div className="text-[10px] text-white/50">Keyframes</div>
                    </div>
                    <div className="bg-ai-bg-secondary p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-[#FF2D55]">{exportPackage.statistics.videos_generated}</div>
                      <div className="text-[10px] text-white/50">Videos</div>
                    </div>
                    <div className="bg-ai-bg-secondary p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-[#22C55E]">{exportPackage.statistics.estimated_runtime_seconds}s</div>
                      <div className="text-[10px] text-white/50">Runtime</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold mb-2">Downloads</div>
                  <div className="flex gap-2 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1">
                    <button onClick={() => downloadFile('shots')} className="px-4 py-2 bg-ai-bg-secondary rounded-lg text-xs font-medium whitespace-nowrap hover:bg-white/10 transition-colors flex items-center gap-2">
                      <Download className="w-3.5 h-3.5" /> Shot Breakdown (.txt)
                    </button>
                    <button onClick={() => downloadFile('storyboard')} className="px-4 py-2 bg-ai-bg-secondary rounded-lg text-xs font-medium whitespace-nowrap hover:bg-white/10 transition-colors flex items-center gap-2">
                      <Download className="w-3.5 h-3.5" /> Storyboard (.pdf)
                    </button>
                    <button onClick={() => downloadFile('ppm')} className="px-4 py-2 bg-ai-bg-secondary rounded-lg text-xs font-medium whitespace-nowrap hover:bg-white/10 transition-colors flex items-center gap-2">
                      <Download className="w-3.5 h-3.5" /> PPM Package (.pdf)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* ═══ END POSTPROD GROUP ═══ */}
        </>)}

        {/* ── Stage 9 VLM Audit Review Panel ────────────────────────
            Restored 2026-07-08. The dedicated VLM QA collapsible was
            gated with `false &&` on 2026-05-28 because K+ has an
            inline audit. But at Stage 9 (VLM Quality Audit) the user
            has nothing to review — hence this lightweight per-shot
            summary. Read-only, non-blocking. Shows overall match,
            rubric_applied, per-category status, minor_flags (incl.
            product_drift from today's advisory audit). */}
        {effectiveStage === 9 && shots?.length > 0 && (
          <div
            data-testid="vlm-audit-review-panel"
            className="rounded-xl overflow-hidden border border-ai-border bg-ai-bg-secondary"
          >
            <div className="px-4 sm:px-5 py-3 border-b border-ai-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-ai-accent" />
                <span className="text-sm font-semibold text-white">VLM Quality Audit — per-shot review</span>
              </div>
              <span className="text-[10px] text-white/45">
                {shots.filter(s => s.keyframe_hyperreal_audit?.overall_match === true).length} pass /
                {' '}{shots.filter(s => s.keyframe_hyperreal_audit?.overall_match === false).length} fail /
                {' '}{shots.filter(s => !s.keyframe_hyperreal_audit).length} unaudited
              </span>
            </div>
            <div className="divide-y divide-ai-border">
              {shots.slice().sort((a,b) => (a.shot_number||'').localeCompare(b.shot_number||'')).map((s) => {
                const aud = s.keyframe_hyperreal_audit || {};
                const rp = s.ref_plan || {};
                const rubric = rp.audit_rubric || [];
                const overall = aud.overall_match;
                const mflags = aud.minor_flags || [];
                const productDrift = mflags.find(f => (typeof f === 'string' ? f.includes('product_drift') : (f.category || '').toLowerCase().includes('product')));
                const cats = [
                  { key:'cast',        label:'Cast' },
                  { key:'costume',     label:'Costume' },
                  { key:'environment', label:'Env' },
                  { key:'composition', label:'Comp' },
                  { key:'product',     label:'Product', advisory:true },
                ];
                return (
                  <div key={s.id} className="p-3 sm:p-4 flex items-start gap-3" data-testid={`vlm-audit-row-${s.shot_number}`}>
                    <div className="flex-shrink-0 w-16 h-9 rounded overflow-hidden bg-black/40">
                      {(s.keyframe_hyperreal_url || s.keyframe_url) && (
                        <img src={s.keyframe_hyperreal_url || s.keyframe_url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-semibold text-white">{s.shot_number}</span>
                        {overall === true && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">PASS</span>
                        )}
                        {overall === false && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300">FAIL</span>
                        )}
                        {overall === undefined && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-white/50">UNAUDITED</span>
                        )}
                        {rubric.length > 0 && (
                          <span className="text-[9px] text-white/40">rubric: {rubric.join(', ')}</span>
                        )}
                      </div>
                      {/* Per-category chips */}
                      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                        {cats.map(c => {
                          const inRubric = rubric.includes(c.key);
                          const m = aud[`${c.key}_match`];
                          const iss = aud[`${c.key}_issues`] || '';
                          if (!inRubric && m === undefined) return null;
                          if (m === null || m === undefined) return null;
                          const cls = m === true
                            ? 'bg-emerald-500/12 text-emerald-300/80'
                            : c.advisory
                              ? 'bg-amber-500/15 text-amber-300'
                              : 'bg-rose-500/15 text-rose-300';
                          return (
                            <span
                              key={c.key}
                              className={`text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1 ${cls}`}
                              title={iss || (m ? `${c.label} match` : `${c.label} mismatch`)}
                            >
                              {m === true ? '✓' : (c.advisory ? '⚠' : '✕')}
                              <span>{c.label}</span>
                              {c.advisory && !m && <span className="opacity-70">(advisory)</span>}
                            </span>
                          );
                        })}
                      </div>
                      {/* Per-category issue lines (only for mismatches) */}
                      {cats.some(c => aud[`${c.key}_match`] === false && aud[`${c.key}_issues`]) && (
                        <ul className="text-[10px] text-white/60 leading-relaxed space-y-0.5 pl-2 mb-1">
                          {cats.map(c => {
                            const m = aud[`${c.key}_match`];
                            const iss = aud[`${c.key}_issues`] || '';
                            if (m !== false || !iss) return null;
                            return (
                              <li key={c.key} className={c.advisory ? 'text-amber-300/80' : ''}>
                                <span className="font-semibold">{c.label}:</span> {iss}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {/* Minor flags (soft warnings — product drift lives here) */}
                      {mflags.length > 0 && (
                        <div className="mt-1 text-[10px] text-amber-300/70">
                          {mflags.map((f, i) => (
                            <div key={i} className="flex items-start gap-1">
                              <span>⚠</span>
                              <span>
                                {typeof f === 'string'
                                  ? f
                                  : `${f.category || 'FLAG'}: ${(f.detail || f.reason || '').slice(0,180)}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {productDrift && (
                        <div className="mt-1 text-[10px] text-amber-300/90 font-medium">
                          🟠 Product drift detected — use the Iterate button on this shot to swap the SKU manually.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-4 sm:px-5 py-2.5 border-t border-ai-border bg-white/[0.02]">
              <p className="text-[10px] text-white/45 leading-relaxed">
                Audits are cached from the last keyframe run. Product drift is advisory only — it never blocks the pipeline. Costume/cast/environment mismatches show as FAIL but Approve still moves you forward — use Iterate on any shot before approving if you want to fix a drift.
              </p>
            </div>
          </div>
        )}

        {/* ── Persistent stage-advance card ─────────────────────────
            Simple by design (2026-07-08 v2): TWO buttons, always
            visible, always advance by exactly ONE stage. No chain-
            advance, no HIDDEN_STAGES logic — user asked for the
            simplest possible UX:
              • Skip Stage      — bump counter +1, no work.
              • Approve → N+1   — bump counter +1 (same call).
            The two labels are identical in effect but different in
            intent (skip = didn't review; approve = reviewed).
            Every hop snapshots → rollback is one click away. */}
        {project.current_stage < 17 && (() => {
          const STAGE_META = {
            1:  { name:'Script' },
            2:  { name:'Shot Breakdown' },
            3:  { name:'Asset Bible' },
            3.5:{ name:'Mock Shoot' },
            4:  { name:'Composition Sketches' },
            5:  { name:"Director's Prompts" },
            6:  { name:'PPM Lock' },
            7:  { name:'Keyframes' },
            8:  { name:'SAM Composition' },
            9:  { name:'VLM Quality Audit' },
            10: { name:'Keyframe Lock' },
            11: { name:'Neutral Master' },
            12: { name:'Color Zone Analysis' },
            13: { name:'Video Generation' },
            14: { name:'Video QA' },
            15: { name:'Video Lock' },
            16: { name:'Export' },
            17: { name:'Complete' },
          };
          const cur = project.current_stage || 1;
          const next = Math.min(cur + 1, 17);
          const curMeta = STAGE_META[cur] || { name:`Stage ${cur}` };
          const nextMeta = STAGE_META[next] || { name:`Stage ${next}` };

          const advanceOne = async () => {
            try {
              await axios.post(`${API}/projects/${projectId}/advance-stage`);
              await loadProjectData();
              toast.success(`Stage ${cur} → Stage ${next}: ${nextMeta.name}`);
            } catch (e) {
              toast.error('Advance failed: ' + (e.response?.data?.detail || e.message));
            }
          };

          return (
            <div
              data-testid="stage-advance-card"
              className="rounded-xl overflow-hidden border border-emerald-500/25 bg-ai-bg-secondary"
            >
              <div className="p-4 sm:p-5 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4.5 h-4.5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-sm font-semibold text-white">
                      Stage {cur}: {curMeta.name}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-500/15 text-emerald-300">
                      current
                    </span>
                  </div>
                  <p className="text-xs text-white/55 leading-relaxed">
                    Next: <span className="text-white/80 font-medium">Stage {next}: {nextMeta.name}</span>.
                    <span className="block mt-1 text-white/35">
                      A snapshot is saved on every hop — rollback is one click away from the Stages sidebar.
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={advanceOne}
                    data-testid="skip-stage-btn"
                    title={`Skip Stage ${cur} without reviewing — bump to Stage ${next}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10 active:scale-95"
                  >
                    <span>Skip Stage</span>
                  </button>
                  <button
                    type="button"
                    onClick={advanceOne}
                    data-testid="approve-next-stage-btn"
                    title={`Approve Stage ${cur} (${curMeta.name}) and advance to Stage ${next} (${nextMeta.name})`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all bg-emerald-500 text-white hover:bg-emerald-400 active:scale-95"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Approve &amp; Move to Stage {next}</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── STAGE PROGRESS INDICATOR ── */}
        {isPpmApproved() && (
          <div className="border border-ai-border rounded-2xl p-5 shadow-sm" style={{ background: 'rgba(19, 27, 44, 0.6)' }} data-testid="stage-progress">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-white">Production Progress</span>
              <span className="text-sm text-ai-accent font-medium">Stage {project.current_stage}/17</span>
            </div>
            <div className="w-full rounded-lg h-2.5 overflow-hidden" style={{ background: 'rgba(14, 165, 233, 0.2)' }}>
              <div 
                className="h-2.5 rounded-lg transition-all duration-500"
                style={{ width: `${(project.current_stage / 17) * 100}%`, background: 'linear-gradient(90deg, #0EA5E9 0%, #38BDF8 50%, #7DD3FC 100%)' }}
              />
            </div>
            <div className="flex justify-between mt-3 text-xs text-white/50">
              <span>Pre-Production</span>
              <span>Production</span>
              <span>Post-Production</span>
              <span>Final</span>
            </div>
          </div>
        )}

        </div>
        </>
        )}
      </main>

      {/* AI Chat Panel — The Picasso/Machine Side
          On viewports ≥ md (768px) this becomes an inline flex sibling of
          <main>, so opening it "crunches" the main content into the
          remaining space rather than overlaying it. On < md it stays
          `fixed` and slides in as a traditional mobile drawer with
          backdrop. Previously used `lg:relative` (≥1024px) which left
          ~900-1023px laptop viewports in overlay mode — users saw the
          chat sitting on top of the project content instead of beside it. */}
      <EipiAssistantPanel
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        userInitial={(user?.name && user.name[0]) || 'U'}
      />

      {/* Chat toggle button when closed */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          data-testid="open-chat-btn"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-lg flex items-center justify-center z-50 hover:scale-110 transition-all duration-300 overflow-hidden animate-gentle-pulse"
          style={{ background: '#0EA5E9' }}
        >
          <img src="https://customer-assets.emergentagent.com/job_a2435b19-3cc0-4261-90b4-1cf9d208aea9/artifacts/h74r9xmb_image.png" alt="EIPI Bot" className="w-10 h-10 rounded-lg object-cover" />
        </button>
      )}

      {/* Script Analysis Modal — shows while Opus is running (spinner state)
          AND once data arrives (rich breakdown + download CTA). */}
      {showAnalysisModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100]" onClick={() => { if (!analyzingScript) setShowAnalysisModal(false); }}>
          <div className="bg-[#0F172A] border border-ai-border rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
              <Search className="w-5 h-5 text-[#A78BFA]" /> Script Analysis
            </h3>
            <p className="text-xs text-white/50 mb-4">Pre-production breakdown — cast, costume, environment & scene nuances. Powered by EiPiCenter's Director AI.</p>

            {/* Loading state — Opus is running. Show progressive status text
                so users understand longer runs (5-8 min) are normal for big
                scripts with adaptive thinking. */}
            {analyzingScript && !scriptAnalysis && (
              <div className="py-10 text-center">
                <Loader2 className="w-8 h-8 text-[#A78BFA] animate-spin mx-auto mb-3" />
                <p className="text-white/80 text-sm font-medium">Director AI is reasoning over your script…</p>
                <p className="text-white/50 text-xs mt-1">
                  Cast, costume, locations & scene nuances · this can take 5–8 min for longer scripts.
                </p>
                <p className="text-white/30 text-[10px] mt-2 italic">Safe to leave this modal open — we'll auto-refresh when it's ready.</p>
              </div>
            )}

            {/* Error state */}
            {analysisError && !scriptAnalysis && (
              <div className="py-6 text-center">
                <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-red-300 text-sm font-medium">Analysis failed</p>
                <p className="text-white/50 text-xs mt-1 break-words">{analysisError}</p>
                <button
                  onClick={() => { setAnalysisError(''); analyzeScriptBeats(false); }}
                  className="mt-3 px-4 py-2 rounded-lg text-xs font-medium bg-white/10 text-white hover:bg-white/20"
                >Retry</button>
              </div>
            )}

            {/* Ready state */}
            {scriptAnalysis && (
              <>
                {/* Overview + Directorial Note */}
                {scriptAnalysis.overview && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-3">
                    {scriptAnalysis.overview.logline && (
                      <p className="text-sm text-white/90 italic mb-2">"{scriptAnalysis.overview.logline}"</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      {scriptAnalysis.overview.tone && <span className="px-2 py-0.5 rounded-full bg-[#8B5CF6]/15 text-[#A78BFA] border border-[#8B5CF6]/20">{scriptAnalysis.overview.tone}</span>}
                      {scriptAnalysis.overview.genre && <span className="px-2 py-0.5 rounded-full bg-ai-accent/15 text-ai-accent border border-ai-accent/20">{scriptAnalysis.overview.genre}</span>}
                      {scriptAnalysis.overview.pacing && <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/80 border border-white/10">{scriptAnalysis.overview.pacing}</span>}
                    </div>
                  </div>
                )}

                {scriptAnalysis.directorial_note && (
                  <div className="bg-[#C76B16]/10 border border-[#C76B16]/30 rounded-xl p-3 mb-3">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#FDBA74] mb-1">Directorial Note</div>
                    <p className="text-sm text-white/85 italic">{scriptAnalysis.directorial_note}</p>
                  </div>
                )}

                {/* Beat recommendation */}
                <div className="bg-ai-accent/10 rounded-xl p-4 mb-3">
                  <p className="text-white/80 text-sm mb-3">{scriptAnalysis.recommendation}</p>
                  {scriptAnalysis.max_shots_cap_reason && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 mb-3 text-[11px] text-amber-300 flex items-start gap-2" data-testid="duration-cap-notice">
                      <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <b>Max shots capped at {scriptAnalysis.max_shots}.</b>
                        {scriptAnalysis.max_shots_uncapped > scriptAnalysis.max_shots ? <> Director AI suggested {scriptAnalysis.max_shots_uncapped} but that's below the 1.5s/shot minimum for a {(project?.duration_seconds || project?.duration)}s spot — viewers can't follow sub-second cuts.</> : ` (${scriptAnalysis.max_shots_cap_reason})`}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-ai-accent/20 rounded-lg p-3 border border-ai-border">
                      <div className="text-2xl font-bold text-ai-accent">{scriptAnalysis.recommended_shots}</div>
                      <div className="text-[10px] text-white/70 mt-0.5">Recommended</div>
                    </div>
                    <div className="bg-black/30 rounded-lg p-3 border border-white/10">
                      <div className="text-2xl font-bold text-white">{project?.max_shots || 30}</div>
                      <div className="text-[10px] text-white/50 mt-0.5">Your Choice</div>
                    </div>
                  </div>
                </div>

                {/* High-level headcounts */}
                <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                  <div className="bg-white/5 border border-white/10 rounded-lg py-2">
                    <div className="text-lg font-bold text-white">{(scriptAnalysis.cast || []).length}</div>
                    <div className="text-[10px] text-white/50">Cast members</div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg py-2">
                    <div className="text-lg font-bold text-white">{(scriptAnalysis.costumes || []).length}</div>
                    <div className="text-[10px] text-white/50">Costume looks</div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg py-2">
                    <div className="text-lg font-bold text-white">{(scriptAnalysis.locations || []).length}</div>
                    <div className="text-[10px] text-white/50">Locations</div>
                  </div>
                </div>

                {/* Scene list with nuances preview */}
                {scriptAnalysis.scenes && scriptAnalysis.scenes.length > 0 && (
                  <div className="mb-3 max-h-48 overflow-y-auto pr-1">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-2">Scenes · beat count</div>
                    {scriptAnalysis.scenes.map((scene, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm py-1.5 border-b border-white/5">
                        <span className="text-white/70 truncate pr-2">{scene.scene || scene.name}</span>
                        <span className="text-ai-accent font-medium shrink-0">{scene.beats} beats</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Download full breakdown */}
                {analysisDocxReady && (
                  <button
                    onClick={downloadScriptAnalysisDocx}
                    data-testid="download-script-analysis-docx-btn"
                    className="w-full mb-3 py-2.5 rounded-xl font-medium text-sm bg-[#8B5CF6]/15 hover:bg-[#8B5CF6]/25 text-[#A78BFA] border border-[#8B5CF6]/30 flex items-center justify-center gap-2 transition-all"
                  >
                    <Download className="w-4 h-4" /> Download Full Breakdown (.docx)
                  </button>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => proceedWithShots(scriptAnalysis.recommended_shots)}
                    className="flex-1 py-3 rounded-xl font-medium transition-all bg-ai-accent text-white hover:bg-ai-accent/80"
                  >
                    Use Recommended ({scriptAnalysis.recommended_shots})
                  </button>
                  <button
                    onClick={() => proceedWithShots(project?.max_shots || 30)}
                    className="flex-1 py-3 rounded-xl font-medium transition-all bg-white/10 text-white hover:bg-white/20"
                  >
                    Keep My Choice ({project?.max_shots || 30})
                  </button>
                </div>
              </>
            )}

            <button
              onClick={() => { if (!analyzingScript) setShowAnalysisModal(false); }}
              disabled={analyzingScript}
              className="w-full mt-3 py-2 text-sm text-white/50 hover:text-white/80 transition-colors disabled:opacity-40"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
